import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Building2,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Send,
  Pencil,
  Plus,
  X,
  AlertCircle,
  AlertTriangle,
  CircleCheckBig,
  Check,
  Eye,
  Download,
  FileText,
  Upload,
  ArrowLeft,
  Info,
  LockKeyhole,
  Undo2,
  User,
} from 'lucide-react';
import api from '../../api';
import { AppSelect } from '../../components/common/select';
import DocumentFormModal, { FORM_DOC_CODES } from '../../components/DocumentFormModal';
import {
  buildDocumentFormStorageKey,
  loadDocumentFormData,
  saveDocumentFormData,
  exportDocumentFormData,
} from '../../services/auditFindingsService';
import { listAuditOutputTemplates } from '../../services/auditOutputTemplateService';
import {
  buildAuditOutputRuntimeData,
  parseFormDataEntry,
} from '../../utils/auditOutputTemplateData';
import { isIsccAuditCatalogComplete } from './auditCatalogDocumentUtils';
import { buildAuditOutputPdfPreview } from '../../utils/auditOutputTemplatePdf';
import { filterExcludedAuditTasks, isExcludedAuditTaskEntry } from '../../utils/auditTaskUtils';
import {
  getAuditAssignees,
  reassignAuditTask,
  createAuditTask,
  createTechnicalReviewTask,
} from '../Task/api/taskApi';
import { useAuth } from '../../contexts/AuthProvider';
import useModalEffect from '../../hooks/useModalEffect';
import { canEditAuditScheduling, resolveAuditClientId } from './auditProcessAccess';
import {
  dedupeScheduleSites,
  isSchedulingScopeConfirmed,
  resolveActiveCycleKeys,
  resolveAuditSummaryStageKey,
  resolveCurrentCycleKey,
  resolveCycleDisplayState,
  sortAuditCyclesByDate,
  sortAuditProductCategories,
} from './auditCycleUtils';
import { resolveBaseScopeKey, resolveGateAlignedScopeKey } from './auditStageSelectionUtils';
import { buildDocumentPreviewError, isPreviewableDocument } from './documentPreviewUtils';
import { getCid2DocumentsForStage } from './salesPackPreviewUtils';
import {
  clearSchedulingWizardDraft,
  deriveSchedulingWizardProgress,
  loadSchedulingWizardDraft,
  resolveScopeMandaysLimit,
  saveSchedulingWizardDraft,
} from './schedulingWizardUtils';
import {
  buildMethodologySchedulingScopes,
  getMethodologyType,
  isGroupedMethodology,
  resolveMemberStandardNames,
} from './auditMethodologyUtils';
import {
  resolveCertificateIssuanceStandards,
  isPerStandardCertificateScope,
  describeCertificateIssuanceTargets,
  buildFtrd02StandardForms,
  buildFtrd02PersistedForm,
  splitFtrd02FormByStandard,
  readFtrd02StandardOverrides,
  readFtrd02StandardForms,
  readFtrd02SharedForm,
  FTRD_02_DOC_CODE,
} from './certificateIssuanceTargets';
import { getDocumentFormFieldErrors } from '../../components/documentFormValidationUtils';
import { isIsccPlusStandard } from '../../utils/isccStandards';
import { isAnnex1Active } from '../../utils/isccMaterialSchemes';
import { buildInitialStageStepper } from './auditStageStepperUtils';
import { getMandaysValidationErrors, parseSchedulingLockError } from './schedulingGuardUtils';
import { buildAutoBookingSuccessMessage, countAutoBookedAuditors } from './schedulingAutoBookingUtils';
import {
  getEligibleAssignmentRoles,
  getMostSeniorQualificationLevel,
  isQualifiedForScope,
} from './auditorQualificationUtils';
import { buildInitialCertStageDefault, resolveStageMandaysDisplay, resolveFreshStageSiteLocations, splitInitialCertMandays } from './initialCertMandaysUtils';
import { modeLabel } from '../AuditFormCatalog/auditFormCatalogUtils';
import { exportFormToPDF } from '../../utils/pdfExporter';
import {
  deriveConfirmationLetterScheduleData,
  resolveScopeTextForScopeKey,
  mergeConfirmationLetterPrefill,
} from './confirmationLetterScheduleUtils';
import {
  REDUCTION_OF_TYPE_ITEM_ID,
  applyReductionOfTypeAutoAnswer,
  isReductionOfTypeNotApplicable,
} from './applicationReviewChecklistUtils';
import { AUDIT_WORKFLOW_MAJOR_STEPS, buildAuditWorkflowProgress, isWorkflowFullyCompleted, markWorkflowCompleted, markWorkflowPendingAudit } from './auditWorkflowProgressUtils';
import { resolvePrimaryDocTab } from './primaryDocTabUtils';
import {
  isSustainabilityAuditScope,
  resolveAuditDocumentsForScope,
  STAGE2_ONLY_AUDIT_DOC_CODES,
} from './auditCatalogDocumentUtils';
import { getSalesPackCidCode, getSalesPackCidCodes } from '../../utils/auditSalesPackDocuments';
import './AuditProcess.css';

const MODAL_EDIT_ID = 'modal-audit-edit';
const MODAL_CONFIRM_ID = 'modal-audit-confirm';
const MODAL_PREVIEW_ID = 'modal-audit-document-preview';
const MODAL_CONTACT_ID = 'modal-audit-contact';
const AUDIT_SCOPE_SELECTOR_LABEL = 'Audit Scope (Project Service / Standard / Service Item)';
const MODAL_SCHEDULING_ALERT_ID = 'modal-audit-scheduling-alert';
const OUTPUT_TEMPLATE_SELECTION_CODE = 'AUDIT_OUTPUT_SELECTION';
const DOC_TAB_OUTPUT_TYPES = {
  certificate: 'certificate',
  'technical-review': 'opinion',
};

const CONFIRM_MESSAGES = {
  revision: {
    title: 'Send for Revision?',
    body: 'Some checklist items need revision. A task will be created and assigned to the responsible person to address the flagged items.',
    confirmLabel: 'Send for Revision',
    confirmClass: 'btn-danger',
    iconType: 'danger',
  },
  agree: {
    title: 'Approve Application Review?',
    body: 'All checklist items are OK. Application review will be completed and audit plan tasks will be assigned to scheduled auditors.',
    confirmLabel: 'Approve',
    confirmClass: 'btn-primary',
    iconType: 'warning',
  },
  submitReReview: {
    title: 'Submit for Re-review?',
    body: 'This will submit the corrected item(s) back to Application Review for another decision.',
    confirmLabel: 'Submit',
    confirmClass: 'btn-primary',
    iconType: 'warning',
  },
  sendToAr: {
    title: 'Send to Application Review?',
    body: 'This will send the corrected CID documents back to Application Review for re-approval.',
    confirmLabel: 'Send to AR',
    confirmClass: 'btn-primary',
    iconType: 'warning',
  },
};

const FALLBACK_AUDIT_STEPS = [
  { id: 1, label: 'Audit Scheduling', description: 'Description' },
  { id: 2, label: 'Audit Plan', description: 'Description' },
  { id: 3, label: 'Audit', description: 'Description' },
  { id: 4, label: 'Technical Review', description: 'Description' },
];

const DOCUMENT_TABS = [
  { id: 'sales-pack', label: 'Sales Pack', docs: ['Client Contract/Agreement', 'FSAP-01-01 CID-01', 'FSAP-01-02 CID-02'] },
  { id: 'checklist-verification', label: 'Application Review Checklist', docs: [] },
  { id: 'scheduling', label: 'Scheduling', docs: ['FSCP-02 Audit Assignment', 'FSCP-03 Audit Confirmation Letter', 'Organization Chart/Structure'] },
  {
    id: 'audit',
    label: 'Audit',
    // 'Audit Summary Report' is deliberately LAST — it's the auto-generated
    // wrap-up doc, rendered after every real FAPP form (see the 'audit-summary'
    // special-case in renderDocumentTabContent, matched via AUDIT_SUMMARY_DOC_CODE).
    docs: ['FAPP-01 Audit Plan', 'FAPP-02 Three Years Audit Program', 'FAPP-03 Attendance List', 'FAPP-04 Audit Notes', 'FAPP-05 Audit Report', 'FAPP-06 Certificate Information Form', 'FAPP-06 CIF (signed by client)', 'FAPP-07 Finding Form', 'FAPP-07 Finding Form (accepted)', 'FAPP-08 Change Notice Form', 'Audit Summary Report'],
  },
  { id: 'technical-review', label: 'Technical Review', docs: ['FTRD-01 Technical Review and Certification Decision'] },
  { id: 'certificate', label: 'Certificate', docs: ['FTRD-02 Certificate Review and Production'] },
];

// These are terminal certificate lifecycle states. A completed FTRD-02 form may
// still be a draft, so editability is determined from the certificate record rather
// than from the generic "Completed" document badge.
const IMMUTABLE_CERTIFICATE_STATUSES = new Set(['FINAL', 'ISSUED', 'LOCKED', 'EXPIRED', 'REVOKED']);

const AUDIT_SUMMARY_DOC_CODE = 'Audit Summary Report';

const getAuditDocumentDisplayName = (docCode) => ({
  'FAPP-07 Finding Form': 'FAPP-07 Audit Finding Form',
  'FAPP-07 Finding Form (accepted)': 'FAPP-07 Corrective Action & Acceptance Form',
}[docCode] || docCode);

// Conditional/secondary audit docs that must NOT block a stage's completion:
// - 'FAPP-06 CIF (signed by client)' is UPLOAD-only (client returns it signed) — it is stored as an
//   uploaded document, never as a saved form, so it can never satisfy the saved-form check.
// - 'FAPP-07 Finding Form (accepted)' is only relevant when findings require client acceptance.
// Completing the core FAPP-01..08 forms is what advances the Audit stage → Technical Review.
const OPTIONAL_FORM_DOC_CODES = new Set([
  'FAPP-06 CIF (signed by client)',
  'FAPP-07 Finding Form (accepted)',
  // Auto-generated wrap-up report, not a compliance-required FAPP form — must
  // not block the Audit tab's "all forms saved" completion checkmark.
  AUDIT_SUMMARY_DOC_CODE,
]);

const CHECKLIST_ITEMS = [
  { id: 1, label: 'Scope' },
  { id: 2, label: 'EA Code' },
  { id: 3, label: 'Mandays' },
  { id: 4, label: 'Sales Pack Documents' },
  { id: 5, label: 'Reduction Factor' },
  { id: 6, label: 'Additional Factor' },
  { id: 7, label: 'Risk' },
  { id: 8, label: 'Reduction of Type (/Integrated)' },
];

// Checklist items #1/#2/#3 map 1:1 to the Scope/EA Code/Mandays fields in the "View Detail"
// modal — used to gate per-row editability to only the field(s) actually flagged revisi.
const CHECKLIST_FIELD_ITEM_ID = { scope: 1, eaCode: 2, mandays: 3 };

const SCHEDULING_SUB_TABS = [
  { id: 'audit-schedule', label: 'Audit Schedule' },
  { id: 'site-location', label: 'Site Location' },
  { id: 'accomodation-transportation', label: 'Accommodation & Transportation' },
  { id: 'review-confirm', label: 'Review & Confirm' },
];

const createSiteLocation = (index) => ({
  id: `site-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  siteName: `Site - ${index}`,
  country: '',
  city: '',
  address: '',
  contactPerson: '',
  type: 'Customer/ Client',
  email: '',
  phoneNumber: '',
});

const normalizeScheduleSiteOptions = (rows = []) => dedupeScheduleSites((Array.isArray(rows) ? rows : [])
  .map((site, index) => ({
    id: String(site?.id || site?.sourceId || `seed-site-${index + 1}`),
    siteName: site?.siteName || site?.name || `Site - ${index + 1}`,
    country: site?.country || '',
    city: site?.city || '',
    address: site?.address || site?.location || '',
    contactPerson: site?.contactPerson || site?.contactName || '',
    type: site?.type || 'Customer/ Client',
    email: site?.email || site?.contactEmail || '',
    phoneNumber: site?.phoneNumber || site?.contactPhone || '',
    sourceCategory: site?.sourceCategory || '',
    sourceId: site?.sourceId || null,
    projectId: site?.projectId || null,
    proposalId: site?.proposalId || null,
    isPrimary: Boolean(site?.isPrimary),
  }))
  .filter((site) => String(site.siteName || site.address || site.city || site.country).trim()));

const isMeaningfulSiteLocation = (site) => {
  if (!site) return false;
  const name = String(site.siteName || '').trim();
  const defaultName = /^site\s*-\s*\d+$/i.test(name);
  return Boolean(
    String(site.address || '').trim()
    || String(site.city || '').trim()
    || String(site.country || '').trim()
    || String(site.contactPerson || '').trim()
    || String(site.email || '').trim()
    || String(site.phoneNumber || '').trim()
    || String(site.sourceCategory || '').trim()
    || (name && !defaultName)
  );
};

const buildSeedSiteLocations = (siteOptions = []) => {
  const normalized = normalizeScheduleSiteOptions(siteOptions);
  return normalized.length > 0 ? normalized : [createSiteLocation(1)];
};

const createAuditSchedule = (index, stageKey) => ({
  id: `auditor-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  employeeLabel: `Auditor -${index}`,
  employeeId: '',
  startDate: '',
  endDate: '',
  employeeName: '',
  role: '',
  mandays: '',
  mandayDetails: [],
  auditType: 'On-site',
  scheduleStatus: 'Booked',
  siteLocationIds: [],
  siteLocationDetails: [],
  ...(stageKey ? { stageKey } : {}),
});

// Initial Certification helpers — Stage 1/Stage 2 split (Audit Platform only, no CRM data change)
// Matches "Initial Certification", "Initial Audit", "Initial Audit (Stage 1 & Stage 2)", etc.
const isInitialCertificationScope = (serviceItemName = '') =>
  /\binitial\b/i.test(String(serviceItemName));
// Every completed cycle can be explicitly sent to Certificate Issuance by the
// Technical Reviewer's final decision.  Stage 1 remains the sole exception.
const canUseCertificateWorkflow = (scope = {}) => {
  const scopeKey = scope?.key || scope?.scopeKey || '';
  const serviceItemName = scope?.serviceItemName || '';
  if (!scopeKey && !serviceItemName) return true;
  return resolveScopeStageKey(scopeKey) !== 'stage1';
};
// CMF (Certificate Maintenance) and Fee line items (License Fee, Recertification Fee, etc.)
// are billing/maintenance rows, not audit cycles — they must never appear in the audit
// process, even when their name also happens to contain a cycle keyword (e.g.
// "Recertification Fee" contains "recert"). buildSchedulingScopes already hides them from
// scheduling scopes; this keeps them out of the rendered cycle list too.
const isCmfScope = (...parts) => {
  const text = parts.filter(Boolean).join(' ').toLowerCase();
  return text.includes('cmf') || text.includes('fee');
};
// Sustainability/ISCC audit process handling: no Stage 1/Stage 2 split for Certification,
// and only Certification/Surveillance/Recertification cycles are audit-process items — billing
// line items (CMF, Certification Fee, License Fee, etc.) never appear in the process.
// Sustainability scope can also be identified purely via an ISCC scope/standard label
// (e.g. proposal standard named "ISCC EU"/"ISCC PLUS") even when the service name itself
// doesn't literally say "sustainability" -- match either signal.
const isSustainabilityOrIsccText = (...parts) => {
  const haystack = parts.filter(Boolean).join(' ').toLowerCase();
  return haystack.includes('sustainability') || haystack.includes('iscc');
};
const isSustainabilityServiceName = (serviceName) =>
  isSustainabilityOrIsccText(serviceName);
const isAuditCycleServiceItem = (serviceItemName) => {
  const s = String(serviceItemName || '').toLowerCase();
  return s.includes('initial')
    || s.includes('certification')
    || s.includes('surveillance')
    || s.includes('recert')
    || s.includes('re-cert');
};
const resolveBaseScheduleKey = resolveBaseScopeKey;

const scopeCycleLabel = (scope = {}) => String(
  scope?.serviceItemName || scope?.serviceItem || scope?.cycleLabel || scope?.stageName || scope?.label || ''
).toLowerCase();

const taskCycleLabel = (task = {}) => String(
  task?.serviceItemName || task?.serviceItem || task?.auditCycle || task?.auditCycleName || task?.auditType || task?.cycleName || ''
).toLowerCase();

const isRecurringScope = (scope = {}) => /surveillance|recert|re-cert/.test(scopeCycleLabel(scope));

const hasMatchingRecurringCycle = (task, scope) => {
  const scopeServiceItemIds = new Set((scope?.includedServiceItemIds || [])
    .filter((id) => id != null)
    .map((id) => String(id)));
  const taskServiceItemId = task?.serviceItemId ?? task?.auditServiceItemId ?? task?.projectServiceItemId ?? null;
  if (taskServiceItemId != null && scopeServiceItemIds.size > 0) {
    return scopeServiceItemIds.has(String(taskServiceItemId));
  }

  const expected = scopeCycleLabel(scope);
  const actual = taskCycleLabel(task);
  if (!expected || !actual) return false;
  if (expected.includes('recert')) return actual.includes('recert') || actual.includes('re-cert');
  if (expected.includes('surveillance')) {
    const expectedSecond = /2|second|ii\b/.test(expected);
    const actualSecond = /2|second|ii\b/.test(actual);
    return actual.includes('surveillance') && expectedSecond === actualSecond;
  }
  return false;
};

const parseAuditTaskScope = (scopeOrKey) => {
  if (scopeOrKey && typeof scopeOrKey === 'object') {
    const scopeKey = scopeOrKey.key || scopeOrKey.scopeKey || '';
    return {
      projectServiceId: scopeOrKey.projectServiceId ?? null,
      projectServiceStandardId: scopeOrKey.projectServiceStandardId ?? null,
      stageKey: scopeOrKey.stageKey ?? resolveScopeStageKey(scopeKey),
      scope: scopeOrKey,
    };
  }
  const [projectServiceId, projectServiceStandardId] = resolveBaseScheduleKey(scopeOrKey).split(':');
  if (!projectServiceId || !projectServiceStandardId) return null;
  return {
    projectServiceId,
    projectServiceStandardId,
    stageKey: resolveScopeStageKey(scopeOrKey),
    scope: { key: scopeOrKey },
  };
};

const isApplicationReviewTaskCode = (taskTypeCode) => (
  ['APPLICATION_REVIEW', 'ASSIGN_NEXT_PROCESS_APPLICATION_REVIEWER']
    .includes(String(taskTypeCode || '').trim().toUpperCase())
);

const isSchedulingTaskCode = (taskTypeCode) => (
  ['AUDIT_SCHEDULING', 'ASSIGN_NEXT_PROCESS_SALES', 'ASSIGN_NEXT_PROCESS_SCHEDULER']
    .includes(String(taskTypeCode || '').trim().toUpperCase())
);

const isAuditTaskCode = (taskTypeCode) => (
  ['AUDIT', 'AUDIT_PLAN', 'AUDIT_REPORT']
    .includes(String(taskTypeCode || '').trim().toUpperCase())
);

const isTechnicalReviewTaskCode = (taskTypeCode) => (
  ['TECHNICAL_REVIEW', 'ASSIGN_NEXT_PROCESS_TECHNICAL', 'ASSIGN_NEXT_PROCESS_TECHNICAL_REVIEWER']
    .includes(String(taskTypeCode || '').trim().toUpperCase())
);

const isCertificateDecisionTaskCode = (taskTypeCode) => (
  ['CERTIFICATE_DECISION', 'CERTIFICATE_ISSUANCE', 'ASSIGN_NEXT_PROCESS_CERTIFICATION_AUTHORITY']
    .includes(String(taskTypeCode || '').trim().toUpperCase())
);

const isCertificateIssuanceTaskCode = (taskTypeCode) => (
  ['ISSUE_CERTIFICATE', 'ASSIGN_NEXT_PROCESS_CERTIFICATE_ISSUER']
    .includes(String(taskTypeCode || '').trim().toUpperCase())
);

const isInitialCertificateTaskCode = (taskTypeCode) => (
  isCertificateDecisionTaskCode(taskTypeCode) || isCertificateIssuanceTaskCode(taskTypeCode)
);

const selectTasksForAuditScope = (tasks, scopeOrKey) => {
  const source = Array.isArray(tasks) ? tasks : [];
  const scope = parseAuditTaskScope(scopeOrKey);
  if (!scope) return source;

  // Integrated/Combined scopes have a presentation key (`mg-…`) and use the
  // first standard only as their persistence anchor. Workflow tasks, however,
  // are scoped to every member standard. Match the full member set so a
  // grouped multi-site/multi-standard card neither hides the other standard's
  // AR/Scheduling task nor reports completion from the primary standard alone.
  const memberStandardIds = new Set([
    scope.projectServiceStandardId,
    ...(Array.isArray(scope.scope?.includedProjectServiceStandardIds)
      ? scope.scope.includedProjectServiceStandardIds
      : []),
  ].filter((id) => id != null).map((id) => String(id)));
  const isMethodologyGroup = Boolean(scope.scope?.isMethodologyGroup);

  const scoped = source.filter((task) => (
    (isMethodologyGroup
      ? memberStandardIds.has(String(task?.projectServiceStandardId ?? ''))
      : (String(task?.projectServiceId ?? '') === String(scope.projectServiceId)
        && String(task?.projectServiceStandardId ?? '') === String(scope.projectServiceStandardId)))
    && (() => {
      const taskStageKey = task?.stageKey ?? null;
      if (scope.stageKey) {
        // Application Review and Scheduling are deliberately shared by Initial
        // Stage 1/2.  Other untagged tasks are ambiguous and must never be
        // borrowed by Stage 2 from Stage 1.
        return taskStageKey === scope.stageKey
          || (taskStageKey === null && (isApplicationReviewTaskCode(task?.taskTypeCode) || isSchedulingTaskCode(task?.taskTypeCode)));
      }
      if (taskStageKey !== null) return false;
      // A future recurring cycle must have a cycle-specific task identity.
      // Unknown/legacy task rows stay pending rather than inheriting Initial
      // Certification progress for the same standard.
      return !isRecurringScope(scope.scope) || hasMatchingRecurringCycle(task, scope.scope);
    })()
  ));

  // Projects created before scoped audit tasks existed only have project-level
  // workflow rows. Keep that as a compatibility fallback, never alongside a
  // real scoped task set -- but NEVER for a staged (Stage 1/Stage 2) scope: a
  // legacy unscoped task structurally cannot know which stage it belongs to,
  // so letting Stage 2's lookup fall back to it (when Stage 2's own scoped
  // task hasn't been created yet) made both stages resolve to the SAME shared
  // row -- completing one completed both. An empty result here is correct;
  // it means this stage genuinely has no task yet.
  if (scope.stageKey || isRecurringScope(scope.scope)) return scoped;
  // Certificate issuance is exclusive to Initial Stage 2. A legacy issuance
  // task can be untagged, so it must not be treated as Surveillance/Recert
  // work merely because those cycles share the same service and standard.
  const cycleTasks = scoped.filter((task) => !isInitialCertificateTaskCode(task?.taskTypeCode));
  return cycleTasks.length > 0
    ? cycleTasks
    : source.filter((task) => (
      task?.projectServiceId == null
      && task?.projectServiceStandardId == null
      && (task?.stageKey ?? null) === null
      && !isInitialCertificateTaskCode(task?.taskTypeCode)
    ));
};
const resolveScopeStageKey = (scopeKey) => {
  if (String(scopeKey || '').endsWith(':s1')) return 'stage1';
  if (String(scopeKey || '').endsWith(':s2')) return 'stage2';
  return null;
};

// Audit task assignments can be stored against an exact scope, its non-stage
// scope, or at project level (older projects). Never borrow an assignment from
// another scoped task: an explicitly unassigned current task must stay unassigned.
const getAssignedAuditTaskName = (tasks, scopeOrKey, matchesTask) => {
  const source = Array.isArray(tasks) ? tasks : [];
  const scopeKey = typeof scopeOrKey === 'object'
    ? (scopeOrKey?.key || scopeOrKey?.scopeKey || '')
    : scopeOrKey;
  const getTaskAssigneeName = (task) => {
    const assignedUser = task?.assignedUser || task?.assignedTo || task?.assignee;
    if (typeof assignedUser === 'string' && assignedUser.trim()) return assignedUser.trim();
    if (assignedUser && typeof assignedUser === 'object') {
      return assignedUser.fullName || assignedUser.username || assignedUser.name || null;
    }
    return task?.assignedUsername || task?.assignedUserName || task?.assignedUserEmail || null;
  };
  const scopes = [scopeOrKey, ...[...new Set([
    String(scopeKey || '').trim(),
    resolveBaseScheduleKey(scopeKey),
  ].filter(Boolean))]];
  for (const scope of scopes) {
    const candidates = selectTasksForAuditScope(source, scope).filter(matchesTask);
    if (candidates.length > 0) {
      const assigned = candidates.find((task) => getTaskAssigneeName(task) || task?.assignedUserId != null);
      return assigned ? getTaskAssigneeName(assigned) : null;
    }
  }
  // Compatibility fallback for genuinely legacy project-level tasks only.
  const legacyTasks = source.filter((task) => matchesTask(task)
    && task?.projectServiceId == null
    && task?.projectServiceStandardId == null
    && (task?.stageKey ?? null) === null);
  if (legacyTasks.length === 0) return null;
  const legacyTask = legacyTasks.find((task) => getTaskAssigneeName(task) || task?.assignedUserId != null);
  return legacyTask ? getTaskAssigneeName(legacyTask) : null;
};

const AUDIT_TYPE_OPTIONS = ['On-site', 'Online'];
const SCHEDULE_STATUS_OPTIONS = ['Booked', 'In-progress', 'CL Sent', 'Confirmed'];

const createMandayDetail = (date = '', mandays = '') => ({
  id: `manday-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  date,
  mandays,
});

const createSiteLocationDetail = (date = '', siteLocationIds = []) => ({
  id: `site-detail-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  date,
  siteLocationIds,
});

const sanitizeSiteLocationDetails = (rows) => {
  const source = Array.isArray(rows) ? rows : [];
  return source.map((row) => ({
    id: row?.id || `site-detail-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    date: row?.date ? String(row.date) : '',
    siteLocationIds: Array.isArray(row?.siteLocationIds) ? row.siteLocationIds : [],
  }));
};

const rebuildSiteLocationDetailsByRange = (startDate, endDate, existingRows) => {
  const rangeDates = buildDateRange(startDate, endDate);
  if (rangeDates.length === 0) return sanitizeSiteLocationDetails(existingRows);

  const existingMap = new Map((Array.isArray(existingRows) ? existingRows : [])
    .filter((row) => row?.date)
    .map((row) => [row.date, row]));

  return rangeDates.map((date) => {
    const current = existingMap.get(date);
    if (current) {
      return {
        id: current.id || `site-detail-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        date,
        siteLocationIds: current.siteLocationIds,
      };
    }
    return createSiteLocationDetail(date, []);
  });
};

const normalizeAuditType = (value) => {
  const text = String(value || '').trim().toLowerCase();
  if (text === 'on-site' || text === 'onsite') return 'On-site';
  if (text === 'online' || text === 'remote' || text === 'hybrid') return 'Online';
  return 'On-site';
};

const normalizeScheduleStatus = (value) => {
  const text = String(value || '').trim().toLowerCase();
  if (text === 'booked') return 'Booked';
  if (text === 'in-progress' || text === 'in progress') return 'In-progress';
  if (text === 'cl sent' || text === 'cl_sent') return 'CL Sent';
  if (text === 'confirmed') return 'Confirmed';
  return 'Booked';
};

const toIsoDate = (value) => {
  if (!value) return null;
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

const formatIsoDate = (dateObj) => {
  const y = dateObj.getFullYear();
  const m = `${dateObj.getMonth() + 1}`.padStart(2, '0');
  const d = `${dateObj.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const buildDateRange = (startDate, endDate) => {
  const start = toIsoDate(startDate);
  const end = toIsoDate(endDate);
  if (!start || !end || start > end) return [];

  const rows = [];
  const cursor = new Date(start.getTime());
  while (cursor <= end) {
    rows.push(formatIsoDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return rows;
};

const calculateTotalMandays = (mandayDetails) => {
  const rows = Array.isArray(mandayDetails) ? mandayDetails : [];
  const total = rows.reduce((sum, row) => {
    const parsed = Number(row?.mandays);
    return Number.isFinite(parsed) && parsed > 0 ? sum + parsed : sum;
  }, 0);
  return total > 0 ? `${total}` : '';
};

const sanitizeMandayDetails = (rows) => {
  const source = Array.isArray(rows) ? rows : [];
  return source.map((row) => ({
    id: row?.id || `manday-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    date: row?.date ? String(row.date) : '',
    mandays: row?.mandays != null ? String(row.mandays) : '',
  }));
};

const rebuildMandayDetailsByRange = (startDate, endDate, existingRows) => {
  const rangeDates = buildDateRange(startDate, endDate);
  if (rangeDates.length === 0) return sanitizeMandayDetails(existingRows);

  const existingMap = new Map((Array.isArray(existingRows) ? existingRows : [])
    .filter((row) => row?.date)
    .map((row) => [row.date, row]));

  return rangeDates.map((date) => {
    const current = existingMap.get(date);
    if (current) {
      return {
        id: current.id || `manday-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        date,
        mandays: current.mandays != null ? String(current.mandays) : '',
      };
    }
    return createMandayDetail(date, '');
  });
};

const createAccommodation = () => ({
  id: `accommodation-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  purpose: 'Transport Ticket',
  scheduledAuditorId: '',
  scheduledScheduleId: '',
  selectedScheduleDates: [],
  auditStartDate: '',
  auditEndDate: '',
  duration: 1,
  amount: '',
  needPickupDropOff: true,
  contacts: [{ id: `contact-${Date.now()}`, name: '', phoneNumber: '', note: '' }],
  supportingDocuments: [],
  providedByClient: false,
});

// Definition + progress computation moved to auditWorkflowProgressUtils.js so
// the collapsed-Audit-step fix (see that file) is unit-testable without
// mounting this whole page.

const DEFAULT_CONFIRMATION_LETTER = { number: '', salutation: 'Mr.', recipient: '', place: 'Jakarta', date: '', auditType: 'On-site', conductedOn: '', scope: '', confirmationDueDate: '', lessThanWorkingDays: '15', chargePercentage: '15', servicesFee: '10.000.000,-', serviceAgreementPoint: '3.10', emailQuestions: 'operation@cbqaglobal.com', emailFeedback: 'technical@cbqaglobal.com' };

const AuditProcess = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [activeDocTab, setActiveDocTab] = useState('sales-pack');
  const [approvedDocTabIds] = useState([]);
  const [expandedStages, setExpandedStages] = useState({});
  // Manual override for the Mandays/EA Code/Scope detail grid on a COMPLETED
  // cycle card — collapsed to a one-line summary by default (see
  // renderStageFieldsToggle) since a fully completed stage's full 3-column
  // grid is rarely needed at a glance; still one click away. In-progress
  // cycles always show the full grid (that's actively relevant working data).
  const [expandedStageFields, setExpandedStageFields] = useState({});
  // FSCP-03 Audit Confirmation Letter starts collapsed -- it's a long form that
  // otherwise dominates the Scheduling step; users open it deliberately.
  const [isConfirmationLetterExpanded, setIsConfirmationLetterExpanded] = useState(false);
  const [stages, setStages] = useState([]);
  const [checklistValues, setChecklistValues] = useState({});
  const [checklistNotes, setChecklistNotes] = useState({});
  const [checklistScopeKey, setChecklistScopeKey] = useState('');
  const [checklistSaving, setChecklistSaving] = useState(false);
  const checklistLoadRequestRef = useRef(0);
  const [projectAuditTasks, setProjectAuditTasks] = useState([]);
  const [projectWorkflowTasks, setProjectWorkflowTasks] = useState([]);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignTargetTask, setReassignTargetTask] = useState(null);
  const [reassignSelectedUser, setReassignSelectedUser] = useState(null);
  const [reassignNotes, setReassignNotes] = useState('');
  const [reassignSearch, setReassignSearch] = useState('');
  const [reassignAssignees, setReassignAssignees] = useState([]);
  const [isLoadingReassignees, setIsLoadingReassignees] = useState(false);
  const [projectHistory, setProjectHistory] = useState([]);
  const [salesPackDocuments, setSalesPackDocuments] = useState([]);
  const [eaCodeApiOptions, setEaCodeApiOptions] = useState([]);
  const [projectMeta, setProjectMeta] = useState({
    clientId: 'default',
    auditTitle: 'Audit Process',
    projectCode: projectId ? `P-${projectId}` : '-',
    locationName: '-',
  });
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [pageError, setPageError] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingStageKey, setEditingStageKey] = useState(null);
  const [editForm, setEditForm] = useState({ project: '', serviceItem: '', eaCode: [], mandays: '', scope: '' });
  const [confirmModalType, setConfirmModalType] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [schedulingAlertModalOpen, setSchedulingAlertModalOpen] = useState(false);
  const [schedulingAlertTitle, setSchedulingAlertTitle] = useState('Scheduling Validation');
  const [schedulingAlertType, setSchedulingAlertType] = useState('warning');
  const [schedulingAlertMessage, setSchedulingAlertMessage] = useState('Please select an auditor for each schedule before saving.');
  const [schedulingConflict, setSchedulingConflict] = useState(null);
  const [previewDocument, setPreviewDocument] = useState({ name: '', url: '' });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [cid2SalesPackModal, setCid2SalesPackModal] = useState({ open: false, stageName: '', documents: [] });
  const [checklistDocsModalOpen, setChecklistDocsModalOpen] = useState(false);
  const [checklistFieldModalOpen, setChecklistFieldModalOpen] = useState(false);
  // Per-row edit toggle: only the field(s) whose checklist item is actually flagged
  // "Need Revision" can be unlocked for editing — e.g. if only Scope needs revision,
  // EA Code and Mandays stay read-only even though the modal is open.
  const [checklistFieldEditableRows, setChecklistFieldEditableRows] = useState({ scope: false, eaCode: false, mandays: false });
  const [checklistFieldValues, setChecklistFieldValues] = useState({ mandays: '', eaCode: [], scope: '', isSustainability: false, mandaysLockedByScheduling: false });
  const [checklistFieldStageKey, setChecklistFieldStageKey] = useState(null);
  const [checklistSaveConfirmOpen, setChecklistSaveConfirmOpen] = useState(false);
  const [applyScopeEaCodeToNextCycle, setApplyScopeEaCodeToNextCycle] = useState(false);
  const [auditContacts, setAuditContacts] = useState({
    representative: { person: '-', phone: '-', email: '-' },
    correspondence: { person: '-', phone: '-', email: '-', sameAsRepresentative: true },
    finance: { person: '-', phone: '-', email: '-', sameAsRepresentative: true },
    certificate: { person: '-', phone: '-', email: '-', sameAsRepresentative: true },
  });
  const [isSavingContacts, setIsSavingContacts] = useState(false);
  const [contactValidationErrors, setContactValidationErrors] = useState({});
  const [certificateIssuanceState, setCertificateIssuanceState] = useState({ saving: false, scopeKey: '' });
  const [certificateFormLoadingScope, setCertificateFormLoadingScope] = useState('');
  const [certificateLifecycleByStandard, setCertificateLifecycleByStandard] = useState({});
  const [certificateLifecycleCheck, setCertificateLifecycleCheck] = useState({ loading: false, available: false });

  useEffect(() => {
    if (!projectId) {
      setCertificateLifecycleByStandard({});
      setCertificateLifecycleCheck({ loading: false, available: false });
      return undefined;
    }
    let cancelled = false;
    setCertificateLifecycleCheck({ loading: true, available: false });
    api.get(`/audit/projects/${projectId}/certificate`)
      .then((response) => {
        if (cancelled) return;
        const rows = Array.isArray(response?.data?.certificates) ? response.data.certificates : [];
        const next = {};
        rows.forEach((row) => {
          if (row?.projectServiceStandardId != null) {
            next[String(row.projectServiceStandardId)] = row;
          }
        });
        if (response?.data) next.__default = response.data;
        setCertificateLifecycleByStandard(next);
        setCertificateLifecycleCheck({ loading: false, available: true });
      })
      .catch(() => {
        if (!cancelled) {
          setCertificateLifecycleByStandard({});
          setCertificateLifecycleCheck({ loading: false, available: false });
        }
      });
    return () => { cancelled = true; };
  }, [projectId]);
  const [activeSchedulingSubTab, setActiveSchedulingSubTab] = useState('audit-schedule');
  const [openDatePickerScheduleId, setOpenDatePickerScheduleId] = useState(null);
  const [datePickerTempValues, setDatePickerTempValues] = useState({});
  const [calendarDisplayMonth, setCalendarDisplayMonth] = useState({});
  const [auditorAvailabilityByDate, setAuditorAvailabilityByDate] = useState({});

  useEffect(() => {
    if (!projectId) {
      setAuditorAvailabilityByDate({});
      return undefined;
    }

    let cancelled = false;
    api.get('/audit/calendar/overview', { params: { scope: 'ALL' } })
      .then((response) => {
        if (cancelled) return;
        const nextAvailability = {};
        const projects = Array.isArray(response?.data?.projects) ? response.data.projects : [];
        projects.forEach((project) => {
          const projectSchedules = Array.isArray(project?.schedules) ? project.schedules : [];
          projectSchedules.forEach((savedSchedule) => {
            const rows = Array.isArray(savedSchedule?.auditSchedules) ? savedSchedule.auditSchedules : [];
            rows.forEach((row) => {
              const employeeId = String(row?.employeeId || '').trim();
              const status = String(row?.scheduleStatus || '').toLowerCase().replace(/[_ ]/g, '-');
              if (!employeeId || !['booked', 'confirmed', 'in-progress'].includes(status)) return;

              const details = Array.isArray(row?.mandayDetails)
                ? row.mandayDetails.filter((detail) => detail?.date)
                : [];
              const dates = details.length > 0
                ? details.map((detail) => String(detail.date).slice(0, 10))
                : buildDateRange(row?.startDate, row?.endDate);
              if (dates.length === 0) return;

              const totalMandays = Number(row?.mandays);
              const fallbackPerDate = Number.isFinite(totalMandays) && totalMandays > 0
                ? totalMandays / dates.length
                : 1;
              dates.forEach((date) => {
                const detail = details.find((item) => String(item.date).slice(0, 10) === date);
                const mandays = Number(detail?.mandays);
                const bookedMandays = Number.isFinite(mandays) && mandays > 0 ? mandays : fallbackPerDate;
                if (!nextAvailability[employeeId]) nextAvailability[employeeId] = {};
                if (!nextAvailability[employeeId][date]) nextAvailability[employeeId][date] = [];
                nextAvailability[employeeId][date].push({
                  projectId: String(project?.projectId || ''),
                  scheduleId: String(savedSchedule?.id || ''),
                  mandays: bookedMandays,
                });
              });
            });
          });
        });
        setAuditorAvailabilityByDate(nextAvailability);
      })
      .catch(() => {
        if (!cancelled) setAuditorAvailabilityByDate({});
      });

    return () => { cancelled = true; };
  }, [projectId]);

  const generateCalendarDays = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    let current = new Date(startDate);
    while (current <= lastDay || current.getDay() !== 0) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return days;
  };

  const formatDateForCalendar = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const [siteLocations, setSiteLocations] = useState([createSiteLocation(1)]);
  const [auditSchedules, setAuditSchedules] = useState([createAuditSchedule(1)]);
  const [accommodations, setAccommodations] = useState([createAccommodation()]);
  const [schedulingScopes, setSchedulingScopes] = useState([]);
  const [selectedSchedulingScopeKey, setSelectedSchedulingScopeKey] = useState('0:0');
  // Mirrors the selection for the reload path: loadAuditProcessData re-runs on every
  // savedFormDataMap change (i.e. after every form save) and must not throw the user
  // back to the project-global pointer cycle it computes as the default scope.
  const selectedSchedulingScopeKeyRef = useRef(selectedSchedulingScopeKey);
  useEffect(() => {
    selectedSchedulingScopeKeyRef.current = selectedSchedulingScopeKey;
  }, [selectedSchedulingScopeKey]);
  const [projectAuditInfo, setProjectAuditInfo] = useState({ auditMethodology: null, multiSite: false, numberOfSites: null });
  const [schedulingSiteOptions, setSchedulingSiteOptions] = useState([]);
  const [schedulingDataByScope, setSchedulingDataByScope] = useState({});
  const [auditorOptions, setAuditorOptions] = useState([]);
  const [auditorQualificationOptions, setAuditorQualificationOptions] = useState([]);
  const schedulingDraftReadyRef = React.useRef(false);
  const schedulingDraftSuppressedScopeRef = React.useRef(null);
  // Auto-select re-applies ONLY when the resolved active scope actually changes
  // (a real stage advance). Without this, every save -> setStages -> new array
  // identity re-ran the effect and dragged the selection back to Stage 1.
  const autoSelectedScopeRef = React.useRef(null);
  // Which project the current scope selection belongs to, so a post-save refetch
  // preserves it but a project switch does not (see loadAuditProcessData).
  const selectionOwnerProjectRef = React.useRef(null);
  // DOM nodes for each document row in the Documents/Forms list, keyed by
  // docCode, so closing the (often very long) inline form can scroll back to
  // the row the operator opened it from -- see docRowRefs/handleBack below.
  const docRowRefs = React.useRef({});
  // Which row to scroll back to once the inline form closes. Set inside
  // renderInlineFormView's handleBack (covers Save, Cancel, breadcrumb and the
  // modal's own close) right before activeInlineForm is cleared, since that is
  // the last point docCode is still known.
  const pendingFormScrollDocCodeRef = React.useRef(null);
  // Point-4 UX: land the user on their own actionable tab (e.g. Application
  // Review Checklist for an AR reviewer) once, the first moment their role in
  // this project is known -- guarded so it never re-fires and overrides a
  // manual tab click afterward. See primaryDocTabUtils.js.
  const hasAutoSelectedDocTabRef = React.useRef(false);
  const primaryDocSectionRef = React.useRef(null);
  const schedulingWizardRef = React.useRef(null);
  const [schedulingStepJustSaved, setSchedulingStepJustSaved] = useState(false);

  // Enrich auditorOptions with isScheduled/isLead/role flags from current auditSchedules
  const enrichedAuditorOptions = React.useMemo(() => {
    if (!auditorOptions?.length) return auditorOptions || [];
    const scheduledIds = new Set(
      auditSchedules.filter(s => s.employeeId).map(s => String(s.employeeId))
    );
    // Name-based fallback set for when numeric IDs mismatch across API sources
    const scheduledNames = new Set(
      auditSchedules
        .filter(s => s.employeeName)
        .map(s => (s.employeeName || '').trim().toLowerCase())
    );
    // Lead = auditor with 'lead' in role, else first scheduled auditor
    const leadRow =
      auditSchedules.find(s => /lead/i.test(s.role || '') && s.employeeId) ||
      auditSchedules.find(s => s.employeeId);
    const leadId = leadRow?.employeeId ? String(leadRow.employeeId) : null;
    const leadName = leadRow?.employeeName ? (leadRow.employeeName || '').trim().toLowerCase() : null;

    return auditorOptions.map(opt => {
      const normalizedAuditorId = String(opt.userId || opt.id || opt.employeeId || '');
      const normalizedName = (opt.fullName || opt.label || opt.name || '').trim().toLowerCase();
      const isScheduledById = normalizedAuditorId ? scheduledIds.has(normalizedAuditorId) : false;
      const isScheduledByName = normalizedName ? scheduledNames.has(normalizedName) : false;
      const isScheduled = isScheduledById || isScheduledByName;
      // Find the matching schedule row to surface the assigned role
      const scheduleRow = auditSchedules.find(s =>
        (normalizedAuditorId && String(s.employeeId) === normalizedAuditorId) ||
        (normalizedName && (s.employeeName || '').trim().toLowerCase() === normalizedName)
      );
      const isLeadById = leadId !== null && normalizedAuditorId === leadId;
      const isLeadByName = leadName !== null && normalizedName === leadName;
      return {
        ...opt,
        userId: opt.userId || opt.id || opt.employeeId || null,
        name: opt.fullName || opt.label || opt.name || '',
        isScheduled,
        isLead: isLeadById || isLeadByName,
        role: scheduleRow?.role || null,
      };
    });
  }, [auditorOptions, auditSchedules]);

  const getEnrichedAuditorOptionsForScope = useCallback((scopeKey) => {
    const normalizedScopeKey = String(scopeKey || '').trim();
    const scopeAuditSchedules = normalizedScopeKey
      ? (schedulingDataByScope?.[normalizedScopeKey]?.auditSchedules || [])
      : [];

    const schedules = scopeAuditSchedules.length > 0
      ? scopeAuditSchedules
      : (normalizedScopeKey && normalizedScopeKey === String(selectedSchedulingScopeKey || '')
        ? (auditSchedules || [])
        : []);

    if (!Array.isArray(auditorOptions) || auditorOptions.length === 0) {
      return [];
    }

    const scheduledIds = new Set(
      schedules
        .filter((row) => row?.employeeId != null && String(row.employeeId).trim() !== '')
        .map((row) => String(row.employeeId))
    );

    const scheduledNames = new Set(
      schedules
        .map((row) => String(row?.employeeName || '').trim().toLowerCase())
        .filter(Boolean)
    );

    const leadRow =
      schedules.find((row) => /lead/i.test(String(row?.role || '')) && row?.employeeId != null)
      || schedules.find((row) => row?.employeeId != null);

    const leadId = leadRow?.employeeId != null ? String(leadRow.employeeId) : null;
    const leadName = String(leadRow?.employeeName || '').trim().toLowerCase() || null;

    return auditorOptions.map((opt) => {
      const normalizedAuditorId = String(opt?.userId || opt?.id || opt?.employeeId || '');
      const normalizedName = String(opt?.fullName || opt?.label || opt?.name || '').trim().toLowerCase();

      const scheduleRow = schedules.find((row) => (
        (normalizedAuditorId && String(row?.employeeId || '') === normalizedAuditorId)
        || (normalizedName && String(row?.employeeName || '').trim().toLowerCase() === normalizedName)
      ));

      const isScheduled = (normalizedAuditorId && scheduledIds.has(normalizedAuditorId))
        || (normalizedName && scheduledNames.has(normalizedName));

      const isLead = (leadId && normalizedAuditorId === leadId)
        || (leadName && normalizedName === leadName);

      return {
        ...opt,
        userId: opt?.userId || opt?.id || opt?.employeeId || null,
        name: opt?.fullName || opt?.label || opt?.name || '',
        isScheduled: Boolean(isScheduled),
        isLead: Boolean(isLead),
        role: scheduleRow?.role || null,
      };
    });
  }, [auditorOptions, schedulingDataByScope, selectedSchedulingScopeKey, auditSchedules]);
  const [isSavingScheduling, setIsSavingScheduling] = useState(false);
  const [isCompletingReview, setIsCompletingReview] = useState(false);
  const [reviewCompleteError, setReviewCompleteError] = useState('');
  const [lastMandaysWarningKey, setLastMandaysWarningKey] = useState('');
  const [schedulingScopeMandaysLimit, setSchedulingScopeMandaysLimit] = useState(null);
  const [schedulingLock, setSchedulingLock] = useState(null);
  const [mandaysValidationErrors, setMandaysValidationErrors] = useState({});
  const [auditDocuments, setAuditDocuments] = useState({});
  const [uploadingDocKey, setUploadingDocKey] = useState(null);
  const [deletingDocId, setDeletingDocId] = useState(null);
  const [isExportingAuditSummary, setIsExportingAuditSummary] = useState(false);
  const [isccFormCatalogItems, setIsccFormCatalogItems] = useState([]);
  const [isccFormCatalogLoading, setIsccFormCatalogLoading] = useState(false);
  const [isccScopeCatalog, setIsccScopeCatalog] = useState([]);
  const [docTabScopeKey, setDocTabScopeKey] = useState('');
  const [docFormModal, setDocFormModal] = useState({ show: false, docCode: null, category: null, scopeKey: '' });
  const [savedFormDataMap, setSavedFormDataMap] = useState({});
  const [auditOutputTemplates, setAuditOutputTemplates] = useState([]);
  const [loadingAuditOutputTemplates, setLoadingAuditOutputTemplates] = useState(false);
  const [savingAuditOutputSelection, setSavingAuditOutputSelection] = useState(false);
  const [isChecklistReviewMode, setIsChecklistReviewMode] = useState(false);
  const [checklistValidationErrors, setChecklistValidationErrors] = useState({});
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [checklistSaved, setChecklistSaved] = useState(false);
  const [reviewCompleted, setReviewCompleted] = useState(false);
  // Application Review Status: tracks which of Draft / Approved / Approved with
  // Notes / Rejected the checklist is currently in, so the UI always shows the
  // real state instead of leaving the user to guess from button availability.
  const [applicationReviewStatus, setApplicationReviewStatus] = useState(null);
  // Application Review Follow-up: when Approved with Notes still leaves a checklist
  // item flagged "Need Revision", this tracks whether Sales needs to re-upload the
  // Sales Pack and/or the field-edit modal is expected to be used, independent of the
  // primary decision above (which stays finalized/locked).
  const [applicationReviewFollowup, setApplicationReviewFollowup] = useState(null);
  // Scope key Sales just resubmitted CID documents for — shows a "Waiting for AR approval"
  // confirmation panel until Application Review makes a new decision (approved/rejected/etc)
  // for that scope, at which point applicationReviewStatus changes and this is cleared below.
  const [awaitingArApprovalScopeKey, setAwaitingArApprovalScopeKey] = useState(null);

  // TR decision state (Fase 5.3) — the OK/Need Revision submission itself now lives inside
  // FTRD-01's own Save flow (DocumentFormModal), driven by its "Stage 1 Decision" dropdown.
  // trDecisionData is still loaded/refreshed here for the stage stepper's "needs revision"
  // marker (see renderInitialStageOverview below).
  const [trDecisionData, setTrDecisionData] = useState(null); // latest decision from BE
  const [activeInlineForm, setActiveInlineForm] = useState(null); // { docCode, category }

  // Closing the inline form (Save, Cancel, or the breadcrumb) drops the operator
  // back on the document list at whatever scroll position the long form left the
  // page at -- usually well past the bottom of the now much shorter list. Once
  // the list is back (activeInlineForm null again), scroll the row the form was
  // opened from back into view. pendingFormScrollDocCodeRef is set in
  // renderInlineFormView's handleBack, the last point docCode is still known.
  useEffect(() => {
    if (activeInlineForm) return;
    const targetDocCode = pendingFormScrollDocCodeRef.current;
    if (!targetDocCode) return;
    pendingFormScrollDocCodeRef.current = null;
    const timer = setTimeout(() => {
      docRowRefs.current[targetDocCode]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
    return () => clearTimeout(timer);
  }, [activeInlineForm]);
  const [inlineFormDraftData, setInlineFormDraftData] = useState(null);
  const [ftrd02StandardOverrides, setFtrd02StandardOverrides] = useState({});
  // Each standard's complete FTRD-02 snapshot, so no field can be lost per standard.
  const [ftrd02StandardForms, setFtrd02StandardForms] = useState({});
  const [activeFtrd02StandardId, setActiveFtrd02StandardId] = useState(null);
  const [confirmationLetter, setConfirmationLetter] = useState(DEFAULT_CONFIRMATION_LETTER);
  const [savingConfirmationLetter, setSavingConfirmationLetter] = useState(false);
  const [confirmationLetterSaved, setConfirmationLetterSaved] = useState(false);
  // Stage gate: keyed by baseScopeKey (without :s1/:s2), holds { stage1Completed, stage2Locked, ... }
  const [stageGateMap, setStageGateMap] = useState({});

  const showAuditNotification = (message, options = {}) => {
    const title = options?.title || 'Notification';
    const type = options?.type || 'warning';
    setSchedulingAlertTitle(title);
    setSchedulingAlertType(type);
    setSchedulingAlertMessage(message);
    setSchedulingAlertModalOpen(true);
  };

  // Audit uploads used to be stored at project/category level, before the
  // cycle scope was added to the API. Keep legacy rows visible only for a
  // cycle that is already complete; otherwise an upcoming cycle would look
  // complete simply because the previous cycle uploaded the same catalog.
  const getAuditDocumentsForScope = (documents, scopeKey) => {
    if (!Array.isArray(documents) || !scopeKey) return Array.isArray(documents) ? documents : [];
    const selectedStage = (stages || []).find((stage) => String(stage?.key) === String(scopeKey));
    const selectedCycleCompleted = Boolean(selectedStage?.isFullyCompleted || selectedStage?.done);
    return documents.filter((document) => {
      const documentScopeKey = document?.scheduleScopeKey || document?.scopeKey;
      if (documentScopeKey) return String(documentScopeKey) === String(scopeKey);
      return selectedCycleCompleted;
    });
  };

  const isTechnicalReviewAvailableForScope = (scopeKey) => {
    const selectedStage = (stages || []).find((stage) => String(stage?.key) === String(scopeKey));
    const currentMajorId = String(selectedStage?.workflowProgress?.currentMajorId || '').toLowerCase();
    // A cycle is eligible for Technical Review once the workflow has reached
    // Technical Review or any downstream step.  Surveillance/recertification
    // cycles continue to Certificate Decision after the audit, so requiring the
    // literal "Audit Completed" label here made an already-saved FTRD-01 look
    // blank and Not Started when it was reopened.
    const workflowReachedTechnicalReview = [
      'technical-review',
      'certificate-issuance',
      'audit-completed',
    ].includes(currentMajorId);
    return Boolean(
      selectedStage?.isFullyCompleted
      || selectedStage?.done
      || String(selectedStage?.statusLabel || '').toLowerCase() === 'audit completed'
      || workflowReachedTechnicalReview
    );
  };

  useEffect(() => {
    setInlineFormDraftData(null);
    setActiveFtrd02StandardId(null);
    // A grouped FTRD-02 stores every standard's certificate fields in one saved document.
    // Seed them back into state when the form is opened, otherwise the standard tabs the
    // user is not editing would be written back empty by the next save.
    const savedCertificate = activeInlineForm?.docCode === FTRD_02_DOC_CODE
      ? parseFormDataEntry(getSavedFormEntry(FTRD_02_DOC_CODE, activeInlineForm?.scopeKey))
      : null;
    setFtrd02StandardOverrides(readFtrd02StandardOverrides(savedCertificate));
    setFtrd02StandardForms(readFtrd02StandardForms(savedCertificate));
  // getSavedFormEntry is intentionally excluded: savedFormDataMap changes after every save,
  // and re-running this effect then would discard the operator's unsaved keystrokes.
  // eslint-disable-next-line
  }, [activeInlineForm?.docCode, activeInlineForm?.category, activeInlineForm?.scopeKey]);

  useEffect(() => {
    const saved = parseFormDataEntry(getSavedFormEntry('FSCP-03 Audit Confirmation Letter', selectedSchedulingScopeKey));
    if (saved && Object.keys(saved).length > 0) {
      setConfirmationLetter({ ...DEFAULT_CONFIRMATION_LETTER, ...saved });
      setConfirmationLetterSaved(true);
    } else {
      // No saved letter for this scope — reset instead of leaving the previously
      // selected scope's (e.g. Stage 1's) data showing under this one (e.g. Stage 2).
      setConfirmationLetter(DEFAULT_CONFIRMATION_LETTER);
      setConfirmationLetterSaved(false);
    }
  }, [selectedSchedulingScopeKey, savedFormDataMap]); // scope draft is hydrated after saved forms load

  // FSCP-03 prefill. The scope text comes from the matching `stages[]` row -- the same value
  // the stage card shows. It used to be read off `schedulingScopes[].scope`, a field those
  // objects never carry, so the Scope box was always prefilled with '' regardless of the
  // proposal's real scope.
  //
  // This also keeps running AFTER the letter has been saved, but in fill-blanks-only mode:
  // a letter saved while Scope was still empty (every letter saved before the fix above) can
  // then pick the real scope up on its next visit, while anything the reviewer typed or
  // already saved is never overwritten. An unsaved letter keeps the original behaviour of
  // tracking the current schedule outright.
  useEffect(() => {
    const scheduled = deriveConfirmationLetterScheduleData({
      auditSchedules,
      scope: resolveScopeDisplayText(resolveScopeTextForScopeKey({
        stages,
        schedulingScopes,
        scopeKey: selectedSchedulingScopeKey,
      })),
    });
    setConfirmationLetter((previous) => (confirmationLetterSaved
      ? mergeConfirmationLetterPrefill(previous, scheduled)
      : { ...previous, ...scheduled }));
  }, [auditSchedules, confirmationLetterSaved, selectedSchedulingScopeKey, schedulingScopes, stages]);

  const loadAuditDocuments = useCallback(async (categoryId) => {
    if (!projectId || !categoryId) return;
    try {
      const res = await api.get(`/audit/projects/${projectId}/documents`, { params: { category: categoryId } });
      const docs = Array.isArray(res?.data) ? res.data : [];
      setAuditDocuments((prev) => ({ ...prev, [categoryId]: docs }));
    } catch (err) {
      console.warn('Failed to load audit documents for category', categoryId, err);
      setAuditDocuments((prev) => ({ ...prev, [categoryId]: [] }));
    }
  }, [projectId]);

  useEffect(() => {
    let mounted = true;
    const loadIsccFormCatalog = async () => {
      setIsccFormCatalogLoading(true);
      try {
        const res = await api.get('/audit/form-catalog', { params: { serviceLine: 'ISCC' } });
        const items = Array.isArray(res?.data?.items)
          ? res.data.items
          : (Array.isArray(res?.data) ? res.data : []);
        if (mounted) setIsccFormCatalogItems(items);
      } catch (err) {
        console.warn('Failed to load ISCC audit form catalog', err);
        if (mounted) setIsccFormCatalogItems([]);
      } finally {
        if (mounted) setIsccFormCatalogLoading(false);
      }
    };

    loadIsccFormCatalog();
    return () => {
      mounted = false;
    };
  }, [projectId]);

  useEffect(() => {
    let mounted = true;
    const loadIsccScopeCatalog = async () => {
      try {
        const res = await api.get('/md/iscc-scope/all');
        const raw = Array.isArray(res?.data)
          ? res.data
          : (Array.isArray(res?.data?.content) ? res.data.content
            : (Array.isArray(res?.data?.data) ? res.data.data
              : (Array.isArray(res?.data?.items) ? res.data.items : [])));
        if (mounted) setIsccScopeCatalog(raw);
      } catch (err) {
        console.warn('Failed to load ISCC scope catalog', err);
      }
    };
    loadIsccScopeCatalog();
    return () => {
      mounted = false;
    };
  }, []);

  const loadSavedFormData = useCallback(async () => {
    if (!projectId) return;
    try {
      const entries = await loadDocumentFormData(projectId);
      if (Array.isArray(entries)) {
        const map = {};
        entries.forEach((entry) => {
          if (entry?.formDataKey) {
            map[entry.formDataKey] = entry;
          } else if (entry?.documentCode) {
            map[entry.documentCode] = entry;
          }
        });
        setSavedFormDataMap(map);
      }
    } catch (err) {
      console.warn('Failed to load saved form data', err);
    }
  }, [projectId]);

  const getSavedFormEntry = useCallback((docCode, scopeKey = '') => {
    if (!docCode) return null;
    const scopedKey = buildDocumentFormStorageKey(docCode, scopeKey);
    if (scopeKey) {
      return savedFormDataMap[scopedKey] || null;
    }
    return savedFormDataMap[docCode] || null;
  }, [savedFormDataMap]);

  // Certificate Issuance is decided per completed audit cycle by FTRD-01.
  // Until a reviewer decides, keep the potential step visible; an explicit
  // continue-without-certificate decision closes the cycle at Technical Review.
  const shouldIncludeCertificateIssuance = useCallback((scope = {}) => {
    if (!canUseCertificateWorkflow(scope)) return false;
    const scopeKey = scope?.key || scope?.scopeKey || '';
    const decision = parseFormDataEntry(
      getSavedFormEntry('FTRD-01 Technical Review and Certification Decision', scopeKey),
    )?.certificationDecision;
    return decision !== 'RECOMMEND_CONTINUE_WITHOUT_CERTIFICATE';
  }, [getSavedFormEntry]);

  useEffect(() => {
    let mounted = true;
    const loadTemplates = async () => {
      setLoadingAuditOutputTemplates(true);
      try {
        const templates = await listAuditOutputTemplates();
        if (mounted) {
          setAuditOutputTemplates(Array.isArray(templates) ? templates : []);
        }
      } catch (error) {
        if (mounted) {
          setAuditOutputTemplates([]);
        }
      } finally {
        if (mounted) {
          setLoadingAuditOutputTemplates(false);
        }
      }
    };

    loadTemplates();
    return () => {
      mounted = false;
    };
  }, []);

  const getAuditOutputTypeForTab = useCallback((docTabId) => DOC_TAB_OUTPUT_TYPES[docTabId] || null, []);

  const getSelectedAuditOutputEntry = useCallback((docTabId) => {
    if (!docTabId) return {};
    return parseFormDataEntry(getSavedFormEntry(OUTPUT_TEMPLATE_SELECTION_CODE, docTabId));
  }, [getSavedFormEntry]);

  const getSelectedAuditOutputTemplateId = useCallback((docTabId) => {
    const selection = getSelectedAuditOutputEntry(docTabId);
    return selection?.templateId ? String(selection.templateId) : '';
  }, [getSelectedAuditOutputEntry]);

  const getAuditOutputTemplatesForTab = useCallback((docTabId) => {
    const outputType = getAuditOutputTypeForTab(docTabId);
    if (!outputType) return [];
    return auditOutputTemplates.filter((template) => template.outputType === outputType);
  }, [auditOutputTemplates, getAuditOutputTypeForTab]);

  const buildCurrentAuditOutputRuntimeData = useCallback(async (docTabId) => {
    const outputType = getAuditOutputTypeForTab(docTabId);
    if (!outputType) return null;

    const certificateForm = readFtrd02SharedForm(
      parseFormDataEntry(getSavedFormEntry(FTRD_02_DOC_CODE, docTabScopeKey)));
    const certificateInfoForm = parseFormDataEntry(
      getSavedFormEntry('FAPP-06 Certificate Information Form', docTabScopeKey));
    const technicalReviewForm = parseFormDataEntry(
      getSavedFormEntry('FTRD-01 Technical Review and Certification Decision', docTabScopeKey));
    const summaryData = outputType === 'report' ? await generateAuditSummaryReportData() : null;

    return buildAuditOutputRuntimeData({
      template: null,
      project: projectMeta,
      projectContacts: auditContacts,
      certificateForm: outputType === 'opinion' ? { ...certificateForm, ...technicalReviewForm } : certificateForm,
      certificateInfoForm,
      summaryData,
      siteLocations,
      auditSchedules,
      selectedScopeLabel: docTabScopeKey
        ? (schedulingScopes.find((scope) => scope.key === docTabScopeKey)?.label || '')
        : '',
    });
  }, [
    auditContacts,
    auditSchedules,
    docTabScopeKey,
    generateAuditSummaryReportData,
    getAuditOutputTypeForTab,
    getSavedFormEntry,
    projectMeta,
    schedulingScopes,
    siteLocations,
  ]);

  const handleAuditOutputTemplateSelection = useCallback(async (docTabId, templateId) => {
    setSavingAuditOutputSelection(true);
    try {
      const selected = auditOutputTemplates.find((template) => String(template.id) === String(templateId)) || null;
      const payload = {
        templateId: selected?.id || null,
        templateName: selected?.templateName || '',
        outputType: selected?.outputType || getAuditOutputTypeForTab(docTabId),
      };
      const result = await saveDocumentFormData(projectId, {
        documentCode: OUTPUT_TEMPLATE_SELECTION_CODE,
        category: 'audit-output',
        formDataJson: JSON.stringify(payload),
        scopeKey: docTabId,
      });
      const formKey = result?.formDataKey || buildDocumentFormStorageKey(OUTPUT_TEMPLATE_SELECTION_CODE, docTabId);
      setSavedFormDataMap((prev) => ({
        ...prev,
        [formKey]: { ...(prev[formKey] || {}), ...result, formDataJson: payload },
      }));
    } catch (error) {
      showAuditNotification('Failed to save output template selection.', { title: 'Template Selection', type: 'danger' });
    } finally {
      setSavingAuditOutputSelection(false);
    }
  }, [auditOutputTemplates, getAuditOutputTypeForTab, projectId, showAuditNotification]);

  async function handleAuditOutputPreview(docTabId) {
    const selectedTemplateId = getSelectedAuditOutputTemplateId(docTabId);
    const template = getAuditOutputTemplatesForTab(docTabId)
      .find((item) => String(item.id) === String(selectedTemplateId));
    if (!template) {
      showAuditNotification('Select a master output template first.', { title: 'Preview', type: 'warning' });
      return;
    }

    const runtimeData = await buildCurrentAuditOutputRuntimeData(docTabId);
    openAuditOutputGeneratedDocument(template, runtimeData, `${template.templateName}.pdf`);
  }

  async function handleAuditOutputDownload(docTabId) {
    const selectedTemplateId = getSelectedAuditOutputTemplateId(docTabId);
    const template = getAuditOutputTemplatesForTab(docTabId)
      .find((item) => String(item.id) === String(selectedTemplateId));
    if (!template) {
      showAuditNotification('Select a master output template first.', { title: 'Download', type: 'warning' });
      return;
    }

    const runtimeData = await buildCurrentAuditOutputRuntimeData(docTabId);
    downloadAuditOutputGeneratedDocument(template, runtimeData, `${template.templateName}.pdf`);
  }

  const loadChecklistForScope = useCallback(async (scope) => {
    if (!projectId || !scope) return;
    const requestId = checklistLoadRequestRef.current + 1;
    checklistLoadRequestRef.current = requestId;
    // Clear the previous cycle immediately. Otherwise the previous scope's
    // checked radios remain visible while the new scope is loading.
    setChecklistValues({});
    setChecklistNotes({});
    setChecklistSaved(false);
    setReviewCompleted(false);
    setIsChecklistReviewMode(false);
    setApplicationReviewStatus(null);
    setApplicationReviewFollowup(null);
    try {
      const res = await api.get(`/audit/projects/${projectId}/checklists`, { params: { scope } });
      if (requestId !== checklistLoadRequestRef.current) return;
      const payload = res?.data;
      const items = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload?.items) ? payload.items : []));

      const nextValues = {};
      const nextNotes = {};
      items.forEach((item) => {
        if (item?.checklistItemId == null) return;
        nextValues[item.checklistItemId] = item.passed ? 'ok' : 'revisi';
        if (item.notes) nextNotes[item.checklistItemId] = item.notes;
      });

      const loadedStatus = String(payload?.applicationReviewStatus || '').toLowerCase();
      const hasFinalizedDecision = Boolean(payload?.locked)
        || ['approved', 'approved_with_notes', 'rejected'].includes(loadedStatus);
      // Some projects contain pre-seeded all-OK checklist rows. They are not
      // a completed review until a decision exists, so do not render those
      // seed values as checked for a new cycle.
      // Item 8 is intentionally locked for single-standard projects, but it is
      // still an automatic OK/N/A decision. Merge that value after loading the
      // server response so the checklist does not become impossible to save
      // when the load replaces the initial auto-answer.
      const autoAnsweredValues = isReductionOfTypeNotApplicable(schedulingScopes)
        ? { [REDUCTION_OF_TYPE_ITEM_ID]: 'ok' }
        : {};
      const loadedValues = hasFinalizedDecision || loadedStatus === 'draft' ? nextValues : {};
      setChecklistValues({ ...loadedValues, ...autoAnsweredValues });
      setChecklistNotes(hasFinalizedDecision || loadedStatus === 'draft' ? nextNotes : {});
      setChecklistSaved(hasFinalizedDecision);
      setIsChecklistReviewMode(Boolean(payload?.locked));
      setApplicationReviewStatus(loadedStatus || null);
      setReviewCompleted(['approved', 'approved_with_notes'].includes(loadedStatus));
      setApplicationReviewFollowup(payload?.followup || null);
    } catch (err) {
      if (requestId !== checklistLoadRequestRef.current) return;
      console.warn('Failed to load checklist for scope', scope, err);
    }
  }, [projectId, schedulingScopes]);

  // CID 1 / CID 2 are PDF-only, matching the Leads document upload rule (DocumentSection.jsx's
  // Dropzone accept={{'application/pdf': ['.pdf']}}) — validate here too since this is a plain
  // <input type="file"> with no drag-and-drop library enforcing the MIME type client-side.
  const isPdfFile = (file) => (
    file?.type === 'application/pdf' || /\.pdf$/i.test(file?.name || '')
  );

  const handleSalesPackCidUpload = (code, file) => {
    if (!file) return;
    if (!isPdfFile(file)) {
      showAuditNotification(`${code} must be a PDF file.`, { title: 'Invalid File', type: 'danger' });
      return;
    }
    handleDocumentUpload('sales-pack', code, file);
  };

  const handleDocumentUpload = async (categoryId, documentCode, file) => {
    if (!projectId || !file) return;
    const key = `${categoryId}::${documentCode}`;
    setUploadingDocKey(key);
    try {
      const formData = new FormData();
      formData.append('category', categoryId);
      formData.append('documentCode', documentCode);
      formData.append('description', documentCode);
      formData.append('file', file);
      const documentScopeKey = docTabScopeKey || (categoryId === 'scheduling' ? selectedSchedulingScopeKey : '');
      if (documentScopeKey) formData.append('scheduleScopeKey', documentScopeKey);
      await api.post(`/audit/projects/${projectId}/documents/upload`, formData);
      await loadAuditDocuments(categoryId);
      
      // Log to project history
      await api.post(`/audit/projects/${projectId}/history-process`, {
        processName: `DOCUMENT_${categoryId.toUpperCase()}`,
        actionType: 'DOCUMENT_UPLOADED',
        description: `Document uploaded: ${documentCode} (${file.name})`,
      }).catch(() => console.warn('Failed to log document upload to history'));
      
      showAuditNotification('Document uploaded successfully.', { title: 'Upload', type: 'success' });
    } catch (err) {
      console.error('Failed to upload document:', err);
      showAuditNotification('Failed to upload document.', { title: 'Upload', type: 'danger' });
    } finally {
      setUploadingDocKey(null);
    }
  };

  const handleDocumentDelete = async (categoryId, docId) => {
    if (!projectId || !docId) return;
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) return;
    setDeletingDocId(docId);
    try {
      await api.delete(`/audit/projects/${projectId}/documents/${docId}`);
      await loadAuditDocuments(categoryId);
      
      // Log to project history
      await api.post(`/audit/projects/${projectId}/history-process`, {
        processName: `DOCUMENT_${categoryId.toUpperCase()}`,
        actionType: 'DOCUMENT_DELETED',
        description: `Document deleted from ${categoryId}`,
      }).catch(() => console.warn('Failed to log document deletion to history'));
      
      showAuditNotification('Document deleted successfully.', { title: 'Delete', type: 'success' });
    } catch (err) {
      console.error('Failed to delete document:', err);
      showAuditNotification('Failed to delete document.', { title: 'Delete', type: 'danger' });
    } finally {
      setDeletingDocId(null);
    }
  };

  // Scheduling evidence belongs to the scheduling scope, not to one wizard step.
  // Keeping it outside the step renderer means it remains visible from steps 1–5.
  const renderSchedulingSupportingDocuments = () => {
    const documents = Array.isArray(auditDocuments.scheduling) ? auditDocuments.scheduling : [];
    const uploadKey = 'scheduling::Supporting Documents';
    const isUploading = uploadingDocKey === uploadKey;
    return (
      <section className="card border mt-4" aria-labelledby="scheduling-supporting-documents">
        <div className="card-header bg-light">
          <h3 id="scheduling-supporting-documents" className="card-title mb-0">Supporting Documents</h3>
          <div className="text-muted small mt-1">Available throughout all scheduling steps for the selected audit scope.</div>
        </div>
        <div className="card-body">
          <label className={`btn btn-outline-secondary btn-sm mb-3 ${isUploading ? 'disabled' : ''}`}>
            <Upload size={14} className="me-1" />{isUploading ? 'Uploading…' : 'Upload supporting document'}
            <input type="file" className="d-none" disabled={isUploading} onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) handleDocumentUpload('scheduling', 'Supporting Documents', file);
              event.target.value = '';
            }} />
          </label>
          {documents.length === 0 ? <div className="text-muted small">No supporting documents uploaded yet.</div> : (
            <div className="d-flex flex-column gap-2">
              {documents.map((document) => (
                <div key={document.id} className="d-flex align-items-center gap-2 border rounded p-2">
                  <FileText size={15} className="text-muted" />
                  <span className="small flex-grow-1 text-truncate">{document.fileName || document.originalFileName || document.name || 'Supporting document'}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-link"
                    onClick={() => openDocumentPreview({
                      name: document.fileName || document.originalFileName || document.name || 'Supporting document',
                      sourceUrl: `${api.defaults.baseURL}/audit/projects/${projectId}/documents/${document.id}/download`,
                    })}
                  >
                    View
                  </button>
                </div>
              ))}
            </div>
          )}
          <hr className="my-4" />
          <div className="border rounded bg-white overflow-hidden">
            <button
              type="button"
              className="audit-process-fscp03-toggle"
              onClick={() => setIsConfirmationLetterExpanded((prev) => !prev)}
              aria-expanded={isConfirmationLetterExpanded}
            >
              <span className="d-flex align-items-center gap-2">
                <FileText size={16} className="text-primary" />
                <strong>FSCP-03 Audit Confirmation Letter</strong>
                {confirmationLetterSaved && <span className="badge bg-success-lt text-success">Saved</span>}
              </span>
              <ChevronDown size={18} className={`audit-process-fscp03-toggle-chevron ${isConfirmationLetterExpanded ? 'open' : ''}`} />
            </button>
            {isConfirmationLetterExpanded && (
            <div className="p-3 p-lg-4 border-top">
            <div className="row g-3 border-bottom pb-3 mb-4">
              <div className="col-md-3"><label className="form-label small fw-semibold">Number</label><input className="form-control" value={confirmationLetter.number} onChange={(e) => setConfirmationLetter((p) => ({ ...p, number: e.target.value }))} /></div>
              <div className="col-md-5"><label className="form-label small fw-semibold">Recipient Name</label><div className="input-group"><select className="form-select" style={{ maxWidth: 92 }} value={confirmationLetter.salutation} onChange={(e) => setConfirmationLetter((p) => ({ ...p, salutation: e.target.value }))}><option>Mr.</option><option>Ms.</option><option>Mrs.</option></select><input className="form-control" value={confirmationLetter.recipient} onChange={(e) => setConfirmationLetter((p) => ({ ...p, recipient: e.target.value }))} /></div></div>
              <div className="col-md-2"><label className="form-label small fw-semibold">Place</label><input className="form-control" value={confirmationLetter.place} onChange={(e) => setConfirmationLetter((p) => ({ ...p, place: e.target.value }))} /></div>
              <div className="col-md-2"><label className="form-label small fw-semibold">Date</label><input type="date" className="form-control" value={confirmationLetter.date} onChange={(e) => setConfirmationLetter((p) => ({ ...p, date: e.target.value }))} /></div>
            </div>
            <div className="border rounded p-3" style={{ background: '#fbfcfd' }}>
              <p className="mb-3">We would like to inform you that the audit will be conducted as follows:</p>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3"><span>Audit</span><select className="form-select form-select-sm" style={{ width: 130 }} value={confirmationLetter.auditType} onChange={(e) => setConfirmationLetter((p) => ({ ...p, auditType: e.target.value }))}><option>On-site</option><option>Online</option><option>Off-site</option></select><span>on</span><input className="form-control form-control-sm" style={{ minWidth: 230, flex: 1 }} placeholder="Audit dates" value={confirmationLetter.conductedOn} onChange={(e) => setConfirmationLetter((p) => ({ ...p, conductedOn: e.target.value }))} /></div>
              <div className="mb-3"><label className="form-label small fw-semibold">Scope</label><textarea className="form-control" rows={2} value={confirmationLetter.scope} onChange={(e) => setConfirmationLetter((p) => ({ ...p, scope: e.target.value }))} /></div>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3"><span>We will appreciate your confirmation by signing this letter and returning it latest by</span><input type="date" className="form-control form-control-sm" style={{ width: 175 }} value={confirmationLetter.confirmationDueDate} onChange={(e) => setConfirmationLetter((p) => ({ ...p, confirmationDueDate: e.target.value }))} />.</div>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3"><span>If you postpone the services with less than</span><input className="form-control form-control-sm" style={{ width: 52 }} value={confirmationLetter.lessThanWorkingDays} onChange={(e) => setConfirmationLetter((p) => ({ ...p, lessThanWorkingDays: e.target.value }))} /><span>working days' notice, CBQA Global reserves the right to charge</span><input className="form-control form-control-sm" style={{ width: 52 }} value={confirmationLetter.chargePercentage} onChange={(e) => setConfirmationLetter((p) => ({ ...p, chargePercentage: e.target.value }))} /><span>% or one man-day at Rp</span><input className="form-control form-control-sm" style={{ width: 135 }} value={confirmationLetter.servicesFee} onChange={(e) => setConfirmationLetter((p) => ({ ...p, servicesFee: e.target.value }))} /><span>(Agreement point</span><input className="form-control form-control-sm" style={{ width: 60 }} value={confirmationLetter.serviceAgreementPoint} onChange={(e) => setConfirmationLetter((p) => ({ ...p, serviceAgreementPoint: e.target.value }))} /><span>).</span></div>
              <div className="row g-3"><div className="col-md-6"><label className="form-label small">Questions email</label><input type="email" className="form-control" value={confirmationLetter.emailQuestions} onChange={(e) => setConfirmationLetter((p) => ({ ...p, emailQuestions: e.target.value }))} /></div><div className="col-md-6"><label className="form-label small">Feedback email</label><input type="email" className="form-control" value={confirmationLetter.emailFeedback} onChange={(e) => setConfirmationLetter((p) => ({ ...p, emailFeedback: e.target.value }))} /></div></div>
            </div>
            <div className="d-flex gap-2 mt-3">
            <button type="button" className="btn btn-primary btn-sm" disabled={savingConfirmationLetter || !selectedSchedulingScopeKey} onClick={async () => {
              setSavingConfirmationLetter(true);
              try {
                const saved = await saveDocumentFormData(projectId, { documentCode: 'FSCP-03 Audit Confirmation Letter', category: 'scheduling', scopeKey: selectedSchedulingScopeKey, formDataJson: JSON.stringify(confirmationLetter) });
                const key = saved?.formDataKey || buildDocumentFormStorageKey('FSCP-03 Audit Confirmation Letter', selectedSchedulingScopeKey);
                setSavedFormDataMap((previous) => ({ ...previous, [key]: { ...saved, formDataJson: confirmationLetter } }));
                setConfirmationLetterSaved(true);
                showAuditNotification('Confirmation Letter saved.', { title: 'Scheduling', type: 'success' });
              } catch (_) { showAuditNotification('Failed to save Confirmation Letter.', { title: 'Scheduling', type: 'danger' }); }
              finally { setSavingConfirmationLetter(false); }
            }}>{savingConfirmationLetter ? 'Saving…' : 'Save Confirmation Letter'}</button>
            <button type="button" className="btn btn-outline-primary btn-sm" disabled={!confirmationLetterSaved} onClick={() => exportFormToPDF(confirmationLetter, { code: 'FSCP-03' }, { projectId, exportId: projectId })}><Download size={14} className="me-1" />Export PDF</button>
            </div>
            </div>
            )}
          </div>
        </div>
      </section>
    );
  };

  const downloadAuditProjectDocument = async (projectDocId, fileName) => {
    if (!projectId || !projectDocId) return;
    try {
      const res = await api.get(`/audit/projects/${projectId}/documents/${projectDocId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || 'document');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download document:', err);
      showAuditNotification('Failed to download document.', { title: 'Download', type: 'danger' });
    }
  };

  const getSelectedSchedulingScope = () => schedulingScopes.find((scope) => scope.key === selectedSchedulingScopeKey) || null;

  // Must resolve against the scope actually selected in the Scheduling wizard's
  // own "Audit Scope" dropdown, not whichever accordion card happens to be
  // expanded elsewhere on the page -- those can legitimately differ (see
  // resolveScopeMandaysLimit for the regression this guards against).
  const getTopMandaysLimit = (scopeKey = selectedSchedulingScopeKey) =>
    resolveScopeMandaysLimit(stages, scopeKey, { expandedStages });

  // The Mandays cap follows the selected scope's stage card, whose `mandays`
  // field can be edited inline (setStages inside handleEditSubmit and the
  // checklist field save) with no scope change at all. Kept as its own effect,
  // decoupled from the scope auto-select effect further below -- that effect
  // is deliberately idempotent per resolved scope key (to stop it clobbering a
  // manual selection after every save) and would otherwise skip recomputing
  // this on a mandays-only edit. The explicit setSchedulingScopeMandaysLimit
  // calls elsewhere (scope change, initial load, auto-select) keep doing their
  // own immediate set to avoid a one-frame lag; this effect is the safety net.
  useEffect(() => {
    setSchedulingScopeMandaysLimit(getTopMandaysLimit(selectedSchedulingScopeKey));
    // eslint-disable-next-line
  }, [stages, selectedSchedulingScopeKey, expandedStages]);

  const getTotalScheduledMandays = (schedules = auditSchedules) => {
    const rows = Array.isArray(schedules) ? schedules : [];
    return rows.reduce((sum, schedule) => {
      const details = sanitizeMandayDetails(schedule?.mandayDetails);
      const detailTotal = details.reduce((acc, row) => {
        const parsed = Number(row?.mandays);
        return Number.isFinite(parsed) && parsed > 0 ? acc + parsed : acc;
      }, 0);

      if (detailTotal > 0) return sum + detailTotal;

      const fallback = Number(schedule?.mandays);
      return Number.isFinite(fallback) && fallback > 0 ? sum + fallback : sum;
    }, 0);
  };

  const getScopeStandardId = (scope) => {
    const parsed = Number(scope?.standardId);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  const getScopeStandardIds = (scope) => {
    const groupedIds = Array.isArray(scope?.includedStandardIds)
      ? scope.includedStandardIds.map(Number).filter((id) => Number.isFinite(id) && id > 0)
      : [];
    const singleId = getScopeStandardId(scope);
    return groupedIds.length > 0 ? Array.from(new Set(groupedIds)) : (singleId ? [singleId] : []);
  };

  const getAvailableAuditorsForScope = (scope) => {
    const scopeStandardIds = getScopeStandardIds(scope);
    if (scopeStandardIds.length === 0) return auditorOptions;

    return auditorOptions.filter((auditor) => isQualifiedForScope(auditor, scopeStandardIds));
  };

  const getRoleOptionsForSchedule = (schedule, scope) => {
    const scopeStandardIds = getScopeStandardIds(scope);
    const selectedAuditor = auditorOptions.find((auditor) => String(auditor?.userId) === String(schedule?.employeeId));
    return getEligibleAssignmentRoles(selectedAuditor, scopeStandardIds, auditorQualificationOptions);
  };

  // Project History merges 3 backend sources with inconsistent actionType casing --
  // project_history's own type ('revision'/'creation') is lowercase, while
  // audit_history_process's actionType ('SUBMITTED'/'RESOLVED'/'MANDAYS_REALLOCATED'/
  // 'REVISION_REQUESTED'/etc.) is uppercase. A case-sensitive .includes() check against uppercase
  // keywords silently never matched the lowercase rows (every project_history entry rendered as
  // grey "secondary"), and several newer action types introduced this session had no keyword
  // mapping at all. Normalize to uppercase once, and cover every actionType this page can produce.
  const resolveHistoryBadgeColor = (actionTypeRaw) => {
    const value = String(actionTypeRaw || '').toUpperCase();
    if (!value) return 'secondary';
    if (value.includes('DELETE') || value.includes('REJECT') || value.includes('CANCEL') || value.includes('FAILED')) {
      return 'danger';
    }
    if (value.includes('REASSIGN') || value.includes('REVISION_REQUESTED') || value.includes('NEED_REVISION')) {
      return 'warning';
    }
    if (value.includes('CREATE') || value.includes('UPLOADED') || value.includes('COMPLETED')
      || value.includes('RESOLVED') || value.includes('APPROVED')) {
      return 'success';
    }
    if (value.includes('UPDATE') || value.includes('SUBMITTED') || value.includes('REALLOCATED') || value.includes('REVISION')) {
      return 'info';
    }
    return 'secondary';
  };

  const toSentenceCase = (value) => {
    const v = String(value || '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
    if (!v) return '';
    return v.charAt(0).toUpperCase() + v.slice(1);
  };

  const formatHistoryActionLabel = (actionTypeRaw) => {
    const normalized = String(actionTypeRaw || '').toUpperCase();
    const labels = {
      OK: 'Approved',
      NEED_REVISION: 'Need Revision',
      AUDIT_COMPLETED: 'Audit Completed',
      REVIEW_COMPLETED: 'Review Completed',
    };
    return labels[normalized] || toSentenceCase(actionTypeRaw || 'Activity');
  };

  const formatHistoryEntityLabel = (entityTypeRaw) => {
    const normalized = String(entityTypeRaw || '').toUpperCase();
    const labels = {
      AUDIT: 'Audit',
      AUDIT_SCHEDULING: 'Audit Scheduling',
      AUDIT_CONTACTS: 'Audit Contacts',
      AUDIT_DOCUMENT: 'Audit Document',
      AUDIT_EVIDENCE: 'Audit Evidence',
      APPLICATION_REVIEW: 'Application Review',
      APPLICATION_REVIEW_FOLLOWUP: 'Application Review Follow-up',
      TECHNICAL_REVIEW: 'Technical Review',
      CERTIFICATE_ISSUANCE: 'Certificate Issuance',
      STAGE_TRANSITION: 'Stage Transition',
    };
    return labels[normalized] || toSentenceCase(entityTypeRaw || 'Activity');
  };

  // Process-history payloads carry the authoritative scope key/standard IDs,
  // but the old timeline discarded that payload and rendered only the generic
  // process name. Resolve the metadata against the loaded project scopes so a
  // multi-standard entry is never ambiguous.
  const getHistoryScopeMetadata = (entry) => {
    let payload = entry?.payload ?? entry?.payloadJson ?? entry?.metadata;
    if (typeof payload === 'string') {
      try { payload = JSON.parse(payload); } catch (_) { payload = null; }
    }
    if (!payload || typeof payload !== 'object') return null;
    const scopeKey = payload.scopeKey || payload.scheduleScopeKey || payload.auditScopeKey || '';
    const directScope = (schedulingScopes || []).find((scope) => String(scope?.key || '') === String(scopeKey));
    const standardId = payload.projectServiceStandardId || payload.standardId;
    const standardScope = standardId != null
      ? (schedulingScopes || []).find((scope) => String(scope?.standardId || '') === String(standardId))
      : null;
    const scope = directScope || standardScope;
    const standard = payload.standardName || payload.standardCode || payload.standard || scope?.standardName || scope?.standardCode || '';
    const stage = payload.stage || payload.auditStage || payload.stageName || payload.stageKey || scope?.stageLabel || '';
    if (!standard && !stage && !scopeKey) return null;
    return { standard, stage, scopeKey };
  };

  const formatHistoryDescription = (entry) => {
    const process = String(entry?.entityType || '').toUpperCase();
    const action = String(entry?.actionType || '').toUpperCase();
    const raw = String(entry?.description || '').trim();

    // Normalize older rows at presentation time as well as new rows written by the
    // backend, so the history feed remains professional for existing projects.
    if (process === 'TECHNICAL_REVIEW' && action === 'OK' && /audit may proceed/i.test(raw)) {
      return 'Technical Review approved. The audit record was accepted for continued certification processing.';
    }
    if (process === 'APPLICATION_REVIEW' && action === 'COMPLETED') {
      const taskCount = raw.match(/\((\d+) task\(s\) closed\)/i)?.[1];
      if (taskCount) {
        const noun = taskCount === '1' ? 'workflow task' : 'workflow tasks';
        return `Application Review completed successfully. ${taskCount} ${noun} closed.`;
      }
      return 'Application Review completed successfully. The approved checklist was finalized.';
    }
    if (process === 'AUDIT_SCHEDULING' && ['UPDATE', 'SCHEDULE_UPDATED'].includes(action)) {
      return 'Audit Scheduling updated successfully. Schedule assignments and supporting details were saved.';
    }
    if (process === 'AUDIT_CONTACTS' && action === 'UPDATE') {
      return 'Audit contacts updated successfully.';
    }
    if (process === 'AUDIT_DOCUMENT' && action === 'UPLOAD') {
      return 'Audit document uploaded successfully.';
    }
    if (process === 'AUDIT_EVIDENCE' && action === 'UPLOAD') {
      return raw || 'Audit evidence uploaded successfully.';
    }
    return raw || 'No description available.';
  };

  const formatHistoryTime = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
  };

  const getLeadDocumentUrl = (fileName) => {
    if (!fileName) return '';
    const baseUrl = process.env.REACT_APP_API_ENDPOINT || '';
    return `${baseUrl}/api/public/displaypdf-leads/${fileName}`;
  };

  const isccScopeMap = useMemo(() => {
    const map = new Map();
    isccScopeCatalog.forEach((scope) => {
      const id = scope?.id ?? scope?.value ?? scope?.code;
      if (id == null) return;
      const label = scope?.code && scope?.name
        ? `${scope.code} - ${scope.name}`
        : (scope?.label || scope?.name || null);
      if (label) map.set(String(id), label);
    });
    return map;
  }, [isccScopeCatalog]);

  // ISCC scope is stored on the proposal standard as a raw catalog id (or list of ids) --
  // resolve each purely-numeric token against isccScopeMap so the UI shows "code - name"
  // (e.g. "1 - Agricultural Biomass") instead of the bare id.
  const resolveScopeToken = (token) => {
    const trimmed = String(token || '').trim();
    if (!trimmed) return '';
    if (/^\d+$/.test(trimmed) && isccScopeMap.has(trimmed)) {
      return isccScopeMap.get(trimmed);
    }
    return trimmed;
  };

  const normalizeScopeValue = (value) => {
    if (Array.isArray(value)) {
      const rows = value.map((item) => resolveScopeToken(item)).filter(Boolean);
      return rows.length > 0 ? rows.join(', ') : '-';
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return '-';
      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        try {
          const parsed = JSON.parse(trimmed);
          return normalizeScopeValue(parsed);
        } catch (_) {
          return trimmed;
        }
      }
      if (trimmed.includes(',')) {
        const rows = trimmed.split(',').map((part) => resolveScopeToken(part)).filter(Boolean);
        return rows.length > 0 ? rows.join(', ') : trimmed;
      }
      return resolveScopeToken(trimmed) || trimmed;
    }
    if (value == null) return '-';
    return resolveScopeToken(value) || String(value);
  };

  // Stage cards are built once (before the async ISCC catalog fetch resolves), so
  // stage.scope can still hold raw un-resolved ids by the time it's baked into state.
  // Re-resolve against the live isccScopeMap at render time so it self-corrects once the
  // catalog arrives, without needing to rebuild the whole stage list.
  const resolveScopeDisplayText = (text) => {
    if (!text || text === '-') return text;
    const parts = String(text).split(/\s*\|\s*|,\s*/).map((part) => resolveScopeToken(part)).filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : text;
  };

  const parseOtherFiles = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value !== 'string' || !value.trim()) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  };

  const pickText = (...candidates) => {
    for (const value of candidates) {
      if (value == null) continue;
      const text = String(value).trim();
      if (text) return text;
    }
    return '';
  };

  const getContactDisplayValue = (...candidates) => {
    const value = pickText(...candidates);
    return value || '-';
  };

  const getPersonName = (contact) => {
    if (!contact || typeof contact !== 'object') return '';
    const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
    if (fullName) return fullName;
    return pickText(contact.name, contact.fullName, contact.contactName);
  };

  const buildAuditContactData = (project, leadDetail) => {
    const representative = {
      person: getContactDisplayValue(
        project?.contactPerson,
        project?.contact_person,
        project?.picName,
        getPersonName(leadDetail?.contact),
        leadDetail?.contactPerson,
        leadDetail?.contact_person,
        leadDetail?.contactName
      ),
      phone: getContactDisplayValue(
        project?.contactPhone,
        project?.contact_phone,
        project?.contactPhoneNumber,
        leadDetail?.contactPhoneNumber,
        leadDetail?.contact_phone_number,
        leadDetail?.contactPhone,
        leadDetail?.phone,
        leadDetail?.contact?.phone
      ),
      email: getContactDisplayValue(
        project?.contactEmail,
        project?.contact_email,
        project?.companyEmail,
        project?.company_email,
        leadDetail?.email,
        leadDetail?.contactEmail,
        leadDetail?.contact_email,
        leadDetail?.contact?.email
      ),
    };

    const resolveRole = (roleKeys) => {
      const personRaw = pickText(...roleKeys.person);
      const phoneRaw = pickText(...roleKeys.phone);
      const emailRaw = pickText(...roleKeys.email);
      const hasOwnValue = Boolean(personRaw || phoneRaw || emailRaw);

      return {
        person: hasOwnValue ? getContactDisplayValue(personRaw, representative.person) : representative.person,
        phone: hasOwnValue ? getContactDisplayValue(phoneRaw, representative.phone) : representative.phone,
        email: hasOwnValue ? getContactDisplayValue(emailRaw, representative.email) : representative.email,
        sameAsRepresentative: !hasOwnValue,
      };
    };

    const correspondence = resolveRole({
      person: [
        project?.correspondenceContactPerson,
        project?.correspondence_contact_person,
        leadDetail?.correspondenceContactPerson,
        leadDetail?.correspondence_contact_person,
      ],
      phone: [
        project?.correspondenceContactPhone,
        project?.correspondence_contact_phone,
        project?.correspondencePhoneNumber,
        leadDetail?.correspondenceContactPhone,
        leadDetail?.correspondence_contact_phone,
      ],
      email: [
        project?.correspondenceContactEmail,
        project?.correspondence_contact_email,
        leadDetail?.correspondenceContactEmail,
        leadDetail?.correspondence_contact_email,
      ],
    });

    const finance = resolveRole({
      person: [
        project?.financeContactPerson,
        project?.finance_contact_person,
        leadDetail?.financeContactPerson,
        leadDetail?.finance_contact_person,
      ],
      phone: [
        project?.financeContactPhone,
        project?.finance_contact_phone,
        project?.financePhoneNumber,
        leadDetail?.financeContactPhone,
        leadDetail?.finance_contact_phone,
      ],
      email: [
        project?.financeContactEmail,
        project?.finance_contact_email,
        leadDetail?.financeContactEmail,
        leadDetail?.finance_contact_email,
      ],
    });

    const certificate = resolveRole({
      person: [
        project?.certificateContactPerson,
        project?.certificate_contact_person,
        leadDetail?.certificateContactPerson,
        leadDetail?.certificate_contact_person,
      ],
      phone: [
        project?.certificateContactPhone,
        project?.certificate_contact_phone,
        project?.certificatePhoneNumber,
        leadDetail?.certificateContactPhone,
        leadDetail?.certificate_contact_phone,
      ],
      email: [
        project?.certificateContactEmail,
        project?.certificate_contact_email,
        leadDetail?.certificateContactEmail,
        leadDetail?.certificate_contact_email,
      ],
    });

    return {
      representative,
      correspondence,
      finance,
      certificate,
    };
  };

  const normalizeContactPayload = (payload, fallbackData) => {
    const fallback = fallbackData || {
      representative: { person: '-', phone: '-', email: '-' },
      correspondence: { person: '-', phone: '-', email: '-', sameAsRepresentative: true },
      finance: { person: '-', phone: '-', email: '-', sameAsRepresentative: true },
      certificate: { person: '-', phone: '-', email: '-', sameAsRepresentative: true },
    };

    const normalizeSection = (section, fallbackSection, includeSame = false) => {
      const out = {
        person: getContactDisplayValue(section?.person, fallbackSection?.person),
        phone: getContactDisplayValue(section?.phone, fallbackSection?.phone),
        email: getContactDisplayValue(section?.email, fallbackSection?.email),
      };
      if (includeSame) {
        out.sameAsRepresentative = section?.sameAsRepresentative != null
          ? Boolean(section.sameAsRepresentative)
          : Boolean(fallbackSection?.sameAsRepresentative);
      }
      return out;
    };

    return {
      representative: normalizeSection(payload?.representative, fallback.representative),
      correspondence: normalizeSection(payload?.correspondence, fallback.correspondence, true),
      finance: normalizeSection(payload?.finance, fallback.finance, true),
      certificate: normalizeSection(payload?.certificate, fallback.certificate, true),
    };
  };

  const toContactInputValue = (value) => {
    const text = String(value ?? '');
    return text === '-' ? '' : text;
  };

  const toContactApiValue = (value) => {
    const text = String(value || '').trim();
    return text || '-';
  };

  const getContactFieldKey = (sectionKey, field) => `${sectionKey}.${field}`;

  const clearContactFieldError = (sectionKey, field) => {
    const key = getContactFieldKey(sectionKey, field);
    setContactValidationErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validateEmail = (value) => {
    const text = toContactInputValue(value).trim();
    if (!text) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text);
  };

  const validatePhone = (value) => {
    const text = toContactInputValue(value).trim();
    if (!text) return true;
    if (!/^[0-9+()\-\s]+$/.test(text)) return false;
    const digitsOnly = text.replace(/\D/g, '');
    return digitsOnly.length >= 6 && digitsOnly.length <= 20;
  };

  const validateAuditContacts = (contacts) => {
    const errors = {};

    const validateSection = (sectionKey, sectionValue, options = {}) => {
      const person = toContactInputValue(sectionValue?.person).trim();
      const phone = toContactInputValue(sectionValue?.phone).trim();
      const email = toContactInputValue(sectionValue?.email).trim();

      if (options.requirePerson && !person) {
        errors[getContactFieldKey(sectionKey, 'person')] = 'Contact person is required.';
      }

      if (phone && !validatePhone(phone)) {
        errors[getContactFieldKey(sectionKey, 'phone')] = 'Phone number format is invalid.';
      }

      if (email && !validateEmail(email)) {
        errors[getContactFieldKey(sectionKey, 'email')] = 'Email format is invalid.';
      }
    };

    validateSection('representative', contacts.representative, { requirePerson: true });

    ['correspondence', 'finance', 'certificate'].forEach((sectionKey) => {
      const section = contacts?.[sectionKey] || {};
      if (section.sameAsRepresentative) return;
      validateSection(sectionKey, section, { requirePerson: true });
    });

    return errors;
  };

  const normalizeSectorOptions = (payload) => {
    const raw = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.sectors)
        ? payload.sectors
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.content)
            ? payload.content
            : [];

    const records = raw
      .map((item) => {
        const code = String(item?.eaCode || item?.code || item?.sectorCode || '').trim();
        const name = String(
          item?.name ||
          item?.label ||
          item?.sectorName ||
          item?.namaSector ||
          item?.sector_name ||
          ''
        ).trim();
        // Keep the real sector id (backend PATCH .../scope-details expects eaCodeSectorIds) --
        // dropped previously since the UI only ever displayed the "CODE - Name" text.
        const id = item?.id ?? item?.sectorId ?? null;
        return { code, name, id };
      })
      .filter((record) => record.code || record.name);

    const seen = new Set();
    return records.filter((record) => {
      const dedupeKey = `${record.code}|${record.name}`;
      if (seen.has(dedupeKey)) return false;
      seen.add(dedupeKey);
      return true;
    });
  };

  const formatSectorLabel = (record) => (
    record?.code && record?.name
      ? `${record.code} - ${record.name}`
      : (record?.code || record?.name || '')
  );

  // EA Code is multi-select (a standard can carry more than one sector's EA Code) but is
  // stored/displayed as a single comma-joined string (matches the backend's resolved
  // "CODE - Name, CODE2 - Name2" text) -- split back into an array to seed a multi-select.
  const splitEaCodeText = (text) => (
    text && text !== '-'
      ? String(text).split(',').map((part) => part.trim()).filter(Boolean)
      : []
  );

  const loadEaCodeOptions = async () => {
    const endpoints = ['/dashboard/sectors', '/md/sector'];

    for (const endpoint of endpoints) {
      try {
        const response = await api.get(endpoint);
        const options = normalizeSectorOptions(response?.data);
        if (options.length > 0) {
          setEaCodeApiOptions(options);
          return;
        }
      } catch (_) {
        // Try next endpoint.
      }
    }

    setEaCodeApiOptions([]);
  };

  const normalizeTaskCode = (value) => String(value || '').trim().toUpperCase();

  const syncTaskStatusByTypeCodes = async (codes = [], status = 'open') => {
    const normalizedCodes = (Array.isArray(codes) ? codes : [])
      .map((code) => normalizeTaskCode(code))
      .filter(Boolean);

    if (normalizedCodes.length === 0) {
      console.debug('No audit task codes to sync');
      return [];
    }

    // docTabScopeKey is the scope actually being edited (Audit/TR/Certificate tabs' own
    // selector). selectedSchedulingScopeKey is a DIFFERENT, unrelated piece of state (the
    // Scheduling wizard's own pending selection) that defaults to the literal placeholder
    // '0:0' until that wizard is touched -- using it here as a fallback let the check
    // silently run against a scope that doesn't exist, matching nothing and never
    // completing anything. Never include it in this resolution.
    const activeScopeKey = docTabScopeKey || currentAuditCycle?.key;
    const activeScopeContext = (schedulingScopes || []).find((scope) => scope?.key === activeScopeKey)
      || currentAuditCycle
      || activeScopeKey;
    const scopedTasks = selectTasksForAuditScope(projectAuditTasks, activeScopeContext);
    // Some backend-created tasks can temporarily lack the scope pointers while
    // their project/service metadata is still valid. For a single active
    // project process, do not leave a completed catalog stranded just because
    // the task's scope metadata is incomplete; fall back to the project's task
    // list and still require an exact workflow task code below.
    const taskSource = scopedTasks.length > 0 ? scopedTasks : (projectAuditTasks || []);
    const targetTasks = taskSource.filter((task) => {
      const code = normalizeTaskCode(task?.taskTypeCode);
      return task?.id && normalizedCodes.includes(code);
    });

    if (targetTasks.length === 0) {
      console.debug('No matching audit task found for codes:', normalizedCodes);
      return [];
    }

    const payloadStatus = String(status || '').trim().toLowerCase() === 'done' ? 'done' : 'open';
    const nextStatus = payloadStatus === 'done' ? 'DONE' : null;
    const completedAt = payloadStatus === 'done' ? new Date().toISOString() : null;

    const results = await Promise.allSettled(
      targetTasks.map((targetTask) => api.patch(`/audit/tasks/${targetTask.id}/status`, { status: payloadStatus }))
    );

    const succeededIds = new Set();
    results.forEach((result, index) => {
      const targetTask = targetTasks[index];
      if (result.status === 'fulfilled') {
        succeededIds.add(Number(targetTask.id));
        console.debug('Audit task status synced:', {
          taskId: targetTask.id,
          taskTypeCode: targetTask.taskTypeCode,
          status: payloadStatus,
          response: result.value?.data,
        });
      } else {
        console.warn('Failed to auto-sync audit task status:', {
          taskId: targetTask.id,
          taskTypeCode: targetTask.taskTypeCode,
          payloadStatus,
          error: result.reason?.message || String(result.reason),
          response: result.reason?.response?.data,
        });
      }
    });

    if (succeededIds.size > 0) {
      setProjectAuditTasks((prev) => (prev || []).map((task) => (
        succeededIds.has(Number(task?.id))
          ? { ...task, status: nextStatus, completedAt }
          : task
      )));
    }

    // Auto-trigger next-step task creation on completion. Keep the set outside the
    // hand-off branches so the same successful transition can also be recorded in the
    // project history. Previously the auditor task was patched to DONE and the next task
    // was created, but no history-process row was written for the auditor's completion.
    if (payloadStatus === 'done' && succeededIds.size > 0 && projectId) {
      const completedCodes = new Set(
        targetTasks
          .filter((t) => succeededIds.has(Number(t.id)))
          .map((t) => String(t?.taskTypeCode || '').toUpperCase())
      );

      if (['AUDIT', 'AUDIT_PLAN', 'AUDIT_REPORT'].some((code) => completedCodes.has(code))) {
        const stageLabel = resolveScopeStageKey(activeScopeKey) === 'stage1' ? 'Stage 1' : 'Stage 2';
        await api.post(`/audit/projects/${projectId}/history-process`, {
          processName: 'AUDIT',
          actionType: 'AUDIT_COMPLETED',
          description: `${stageLabel} audit completed by the assigned auditor. Audit forms and report were completed successfully.`,
        }).catch(() => console.warn('Failed to log audit completion to history'));
      }

      try {
        // AR/Scheduling handoff is backend-authoritative. The two endpoints evaluate the
        // same scoped prerequisites and create idempotently; this page only refreshes state.
        if (['AUDIT_SCHEDULING', 'ASSIGN_NEXT_PROCESS_SALES', 'ASSIGN_NEXT_PROCESS_SCHEDULER'].some((c) => completedCodes.has(c))) {
          await reloadAuditTasks();
        // Application Review done uses the same backend handoff and only refreshes the view.
        } else if (['APPLICATION_REVIEW', 'ASSIGN_NEXT_PROCESS_APPLICATION_REVIEWER'].some((c) => completedCodes.has(c))) {
          await reloadAuditTasks();
        } else if (['AUDIT', 'AUDIT_PLAN', 'AUDIT_REPORT'].some((c) => completedCodes.has(c))) {
          // Methodology-group scopes use opaque keys (mg-...) and therefore cannot
          // be parsed by parseAuditTaskScope. Resolve the selected scheduling scope
          // first so the handoff preserves the real project-service-standard and
          // Stage 1/Stage 2 key for integrated audits as well.
          const parsedTechnicalScope = parseAuditTaskScope(activeScopeKey);
          const resolvedTechnicalScope = parsedTechnicalScope || (() => {
            const selectedScope = (schedulingScopes || []).find((scope) => (
              scope?.key === activeScopeKey
              || resolveBaseScheduleKey(scope?.key) === resolveBaseScheduleKey(activeScopeKey)
            ));
            if (!selectedScope) return {};
            return {
              projectServiceId: selectedScope.projectServiceId,
              projectServiceStandardId: selectedScope.projectServiceStandardId,
              stageKey: resolveScopeStageKey(activeScopeKey),
            };
          })();
          await createTechnicalReviewTask(projectId, resolvedTechnicalScope);
          await reloadAuditTasks();
        }
      } catch (e) {
        console.warn('Auto-next-step trigger failed:', e?.message || String(e));
      }
    }

    return results;
  };

  const reloadAuditTasks = useCallback(async () => {
    if (!projectId) return;
    try {
      const [tasksRes, workflowRes] = await Promise.all([
        api.get('/audit/tasks', { params: { projectId: Number(projectId) } }),
        api.get(`/audit/projects/${Number(projectId)}/workflow-tasks`),
      ]);
      const tasks = Array.isArray(tasksRes?.data) ? tasksRes.data : [];
      const workflowTasks = Array.isArray(workflowRes?.data) ? workflowRes.data : tasks;
      setProjectAuditTasks(tasks);
      setProjectWorkflowTasks(workflowTasks);
      // Rebuild workflow progress
      const workflowProgress = buildAuditWorkflowProgress(workflowTasks);
      setStages((prev) => prev.map((stage) => ({
        ...stage,
        statusClass: workflowProgress.completedSubStepIds.includes('application-review')
          ? 'badge badge-audit-completed'
          : stage.statusClass,
      })));
    } catch (err) {
      console.warn('Failed to reload audit tasks:', err);
    }
  }, [projectId]);

  const isTaskDone = (status) => {
    const normalized = String(status || '').trim().toUpperCase();
    return normalized === 'DONE' || normalized === 'COMPLETED' || normalized === 'CLOSED';
  };

  // FTRD-01's own Save flow (DocumentFormModal) now submits the OK/Need Revision decision
  // itself (POST /tr-decision) instead of a separate panel here. After either path saves,
  // refresh the latest decision (drives the stage stepper's "needs revision" marker, see
  // renderInitialStageOverview) and the task list (Need Revision reopens the AUDIT task on
  // the backend; OK marks Technical Review done there directly).
  const refreshAfterFtrd01Save = useCallback((savedDocCode) => {
    if (savedDocCode !== 'FTRD-01 Technical Review and Certification Decision' || !projectId) return;
    api.get(`/audit/projects/${projectId}/tr-decision`)
      .then((res) => { if (res.data?.found) setTrDecisionData(res.data); })
      .catch(() => {});
    reloadAuditTasks();
  }, [projectId, reloadAuditTasks]);

  // Derive current user's role in this project from task assignments + system roles
  const myProjectRole = useMemo(() => {
    // Profiles from older auth records store role names without the ROLE_
    // prefix, while newer records use Spring Security names. Treat both
    // forms consistently so Admin can review every workflow tab.
    const hasAnyRole = (...roles) => roles.some((role) => hasRole(role) || hasRole(role.replace(/^ROLE_/, '')));
    const profileRoleValues = [
      user?.role,
      user?.userRole,
      user?.jobTitle,
      ...(Array.isArray(user?.roles) ? user.roles : []),
      ...(Array.isArray(user?.authorities) ? user.authorities : []),
    ].map((value) => (typeof value === 'object' ? value?.name || value?.authority : value))
      .map((value) => String(value || '').trim().toUpperCase().replace(/\s+/g, '_'));
    const isAdminOrManager = hasAnyRole('ROLE_ADMIN', 'ROLE_DIRECTOR', 'ROLE_MANAGER')
      || hasAnyRole('ROLE_SUPERADMIN', 'ROLE_SUPER_ADMIN')
      || profileRoleValues.some((role) => ['ADMIN', 'ROLE_ADMIN', 'ADMINISTRATOR', 'ROLE_ADMINISTRATOR', 'SUPERADMIN', 'SUPER_ADMIN'].includes(role));
    const currentUserId = user?.id;
    const tasks = projectAuditTasks || [];
    // Admin/Manager sees all tasks → filter by own assignment to find own role
    const myTasks = isAdminOrManager
      ? tasks.filter((t) => String(t?.assignedUserId) === String(currentUserId))
      : tasks;
    const codes = new Set(myTasks.map((t) => String(t?.taskTypeCode || '').toUpperCase()));
    return {
      isAdminOrManager,
      isSales: hasRole('ROLE_SALES'),
      isApplicationReviewer: codes.has('APPLICATION_REVIEW') || codes.has('ASSIGN_NEXT_PROCESS_APPLICATION_REVIEWER'),
      isScheduler: codes.has('AUDIT_SCHEDULING') || codes.has('ASSIGN_NEXT_PROCESS_SALES') || codes.has('ASSIGN_NEXT_PROCESS_SCHEDULER'),
      isSchedulingEditor: codes.has('AUDIT_SCHEDULING') || codes.has('ASSIGN_NEXT_PROCESS_SCHEDULER'),
      isAuditor: codes.has('AUDIT') || codes.has('AUDIT_PLAN') || codes.has('AUDIT_REPORT'),
      isTechnicalReviewer: codes.has('TECHNICAL_REVIEW') || codes.has('ASSIGN_NEXT_PROCESS_TECHNICAL') || codes.has('ASSIGN_NEXT_PROCESS_TECHNICAL_REVIEWER'),
      isCertificationAuthority: codes.has('CERTIFICATE_DECISION') || codes.has('CERTIFICATE_ISSUANCE') || codes.has('ASSIGN_NEXT_PROCESS_CERTIFICATION_AUTHORITY'),
      isCertificationIssuer: codes.has('ISSUE_CERTIFICATE') || codes.has('ASSIGN_NEXT_PROCESS_CERTIFICATE_ISSUER'),
    };
  }, [projectAuditTasks, user, hasRole]);

  const canEditScheduling = useMemo(() => canEditAuditScheduling({
    isAssignedScheduler: myProjectRole.isSchedulingEditor,
    hasRole,
  }), [myProjectRole.isSchedulingEditor, hasRole]);

  // Filter sidebar tabs based on current user's role in this project
  const visibleDocTabs = useMemo(() => {
    const {
      isAdminOrManager, isSales, isApplicationReviewer, isScheduler,
      isAuditor, isTechnicalReviewer, isCertificationAuthority, isCertificationIssuer,
    } = myProjectRole;
    const canSeeAuditContent = isAdminOrManager || isAuditor || isTechnicalReviewer || isCertificationAuthority || isCertificationIssuer;
    const canSeeCertContent = isAdminOrManager || isTechnicalReviewer || isCertificationAuthority || isCertificationIssuer;
    return DOCUMENT_TABS.filter((tab) => {
      switch (tab.id) {
        case 'sales-pack':
          // Everyone EXCEPT pure auditor
          return isAdminOrManager || isSales || isApplicationReviewer || isScheduler || isTechnicalReviewer || isCertificationAuthority || isCertificationIssuer;
        case 'checklist-verification':
          // Sales now needs to see this tab too — Approved with Notes / Reject can flag
          // items that require Sales to act (re-upload Sales Pack), so they need visibility
          // into the checklist and its Application Review Status/follow-up banner.
          return isAdminOrManager || isSales || isApplicationReviewer || isScheduler || isTechnicalReviewer || isCertificationAuthority || isCertificationIssuer;
        case 'scheduling':
          return isAdminOrManager || isSales || isApplicationReviewer || isScheduler || isAuditor;
        case 'audit':
          return canSeeAuditContent;
        case 'technical-review':
        case 'certificate':
          return canSeeCertContent;
        case 'audit-summary':
          return isAdminOrManager || isAuditor || isTechnicalReviewer || isCertificationAuthority || isCertificationIssuer;
        default:
          return true;
      }
    });
  }, [myProjectRole]);

  // If active tab becomes hidden (role change), reset to first visible tab
  useEffect(() => {
    if (visibleDocTabs.length > 0 && !visibleDocTabs.some((t) => t.id === activeDocTab)) {
      setActiveDocTab(visibleDocTabs[0].id);
    }
  }, [visibleDocTabs, activeDocTab]);

  // Land the user straight on their own actionable tab (e.g. Application
  // Review Checklist for an AR reviewer) instead of always opening on Sales
  // Pack. Fires once, the first time any role flag this project cares about
  // is known to be true -- a user with no tracked role (admin, sales-only,
  // no assignment yet) never triggers it and keeps today's default.
  useEffect(() => {
    if (hasAutoSelectedDocTabRef.current) return;
    const hasAnyTrackedRole = [
      'isApplicationReviewer', 'isScheduler', 'isAuditor',
      'isTechnicalReviewer', 'isCertificationAuthority', 'isCertificationIssuer',
    ].some((flag) => myProjectRole[flag]);
    if (!hasAnyTrackedRole || visibleDocTabs.length === 0) return;

    hasAutoSelectedDocTabRef.current = true;
    const primaryTab = resolvePrimaryDocTab(myProjectRole, visibleDocTabs.map((t) => t.id));
    setActiveDocTab(primaryTab);
    if (primaryTab !== 'sales-pack') {
      setTimeout(() => {
        primaryDocSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }, [myProjectRole, visibleDocTabs]);

  // Auto-complete tasks when all required form docs in a section are saved.
  // Sustainability/ISCC projects use catalog-backed uploads instead of the
  // legacy FAPP saved-form keys, so evaluate that catalog path as well.
  useEffect(() => {
    if (!projectId) return;
    // docTabScopeKey (the Audit/TR/Certificate tabs' own scope selector) is what actually
    // drove the form saves being checked here. selectedSchedulingScopeKey is a DIFFERENT,
    // unrelated piece of state (the Scheduling wizard's own pending selection) that defaults
    // to the literal placeholder '0:0' until that wizard is touched -- using it as a fallback
    // here let this check silently run against a scope that doesn't exist, matching nothing
    // and never completing the task. Never include it in this resolution.
    const activeScopeKey = docTabScopeKey || currentAuditCycle?.key;
    const auditTab = DOCUMENT_TABS.find((t) => t.id === 'audit');
    // Stage-aware: isTabAllFormsSaved alone never returns true for Stage 1 -- FAPP-06/FAPP-08
    // are hidden from Stage 1 (STAGE2_ONLY_AUDIT_DOC_CODES) but aren't OPTIONAL_FORM_DOC_CODES,
    // so the scope-blind check would wait forever for forms Stage 1 can never save. Mirrors
    // isTabCompletedForSelectedScope's own scope-aware check below.
    const activeScope = (schedulingScopes || []).find((scope) => scope?.key === activeScopeKey);
    const isIsccScope = isSustainabilityAuditScope(activeScope);
    const isccAuditComplete = isIsccScope
      && !isccFormCatalogLoading
      && isIsccAuditCatalogComplete({
        catalogItems: isccFormCatalogItems,
        uploadedDocuments: getAuditDocumentsForScope(auditDocuments.audit, activeScopeKey),
        scope: activeScope,
      });
    const legacyAuditComplete = Object.keys(savedFormDataMap).length > 0
      && auditTab && (activeScopeKey
      ? isTabAllFormsSavedForScope(auditTab, activeScopeKey)
      : isTabAllFormsSaved(auditTab));
    if (auditTab && (legacyAuditComplete || isccAuditComplete)) {
      const activeScopeTasks = selectTasksForAuditScope(projectAuditTasks, activeScope || activeScopeKey);
      const auditTaskPending = activeScopeTasks.some((t) => {
        const tc = String(t?.taskTypeCode || '').toUpperCase();
        return ['AUDIT', 'AUDIT_PLAN', 'AUDIT_REPORT'].includes(tc) && !isTaskDone(t?.status);
      });
      if (auditTaskPending) {
        syncTaskStatusByTypeCodes(['AUDIT', 'AUDIT_PLAN', 'AUDIT_REPORT'], 'done').catch(() => {});
      }
    }
    const trTab = DOCUMENT_TABS.find((t) => t.id === 'technical-review');
    // For an Initial Certification Stage 1 scope, FTRD-01's own "Stage 1 Decision" dropdown
    // now drives completion (see DocumentFormModal's handleSave/submitTrNeedRevision) --
    // saving the form with the decision left unset or set to "Need Revision" must NOT
    // auto-complete Technical Review here; only "Continue to Stage 2" may. Stage 2 and
    // non-staged scopes have no such dropdown and keep the plain "form saved = complete"
    // behavior.
    const isStage1TrScope = resolveScopeStageKey(activeScopeKey) === 'stage1';
    const stage1TrDecision = isStage1TrScope
      ? parseFormDataEntry(getSavedFormEntry('FTRD-01 Technical Review and Certification Decision', activeScopeKey)).decision
      : null;
    const trFormsReadyToComplete = !isStage1TrScope || stage1TrDecision === 'Continue to Stage 2';
    if (trTab && trFormsReadyToComplete && (activeScopeKey
      ? isTabAllFormsSavedForScope(trTab, activeScopeKey)
      : isTabAllFormsSaved(trTab))) {
      const activeScopeTasks = selectTasksForAuditScope(projectAuditTasks, activeScope || activeScopeKey);
      const trTaskPending = activeScopeTasks.some((t) => {
        const tc = String(t?.taskTypeCode || '').toUpperCase();
        return ['TECHNICAL_REVIEW', 'ASSIGN_NEXT_PROCESS_TECHNICAL', 'ASSIGN_NEXT_PROCESS_TECHNICAL_REVIEWER'].includes(tc) && !isTaskDone(t?.status);
      });
      if (trTaskPending) {
        syncTaskStatusByTypeCodes(['TECHNICAL_REVIEW', 'ASSIGN_NEXT_PROCESS_TECHNICAL', 'ASSIGN_NEXT_PROCESS_TECHNICAL_REVIEWER'], 'done').catch(() => {});
      }
    }
  // `currentAuditCycle` is declared later in the component. It is safe to read
  // inside the effect callback, but referencing it in this dependency array
  // evaluates it during render before initialization and causes a TDZ error.
  }, [savedFormDataMap, docTabScopeKey, auditDocuments.audit, isccFormCatalogItems, isccFormCatalogLoading, schedulingScopes, stages]); // eslint-disable-line

  // Load and refresh stage gate status from the authoritative BE contract.
  // Scope discovery must come from schedulingScopes: saved form keys are stored
  // as "scope::documentCode" and therefore do not end with :s1/:s2.
  useEffect(() => {
    if (!projectId) return;
    const baseScopeKeys = new Set(
      (schedulingScopes || [])
        .map((scope) => String(scope?.key || ''))
        .filter((scopeKey) => /:s[12]$/.test(scopeKey))
        .map((scopeKey) => resolveBaseScheduleKey(scopeKey)),
    );
    if (baseScopeKeys.size === 0) return;

    let cancelled = false;
    Promise.all(Array.from(baseScopeKeys).map(async (scopeBase) => {
      try {
        const res = await api.get(`/audit/projects/${projectId}/stage-gate`, { params: { scopeBase } });
        return [scopeBase, res.data];
      } catch {
        return null;
      }
    })).then((entries) => {
      if (cancelled) return;
      const next = entries.filter(Boolean).reduce((acc, [scopeBase, gate]) => ({
        ...acc,
        [scopeBase]: gate,
      }), {});
      if (Object.keys(next).length > 0) {
        setStageGateMap((prev) => ({ ...prev, ...next }));
      }
    });

    return () => { cancelled = true; };
  }, [projectId, schedulingScopes, savedFormDataMap]);

  const openReassignModal = async (task) => {
    setReassignTargetTask(task);
    setReassignSelectedUser(null);
    setReassignNotes('');
    setReassignSearch('');
    setReassignAssignees([]);
    setShowReassignModal(true);
    setIsLoadingReassignees(true);
    try {
      const data = await getAuditAssignees(task?.taskTypeCode);
      setReassignAssignees(Array.isArray(data) ? data : []);
    } catch (e) {
      setReassignAssignees([]);
    } finally {
      setIsLoadingReassignees(false);
    }
  };

  const handleAuditReassign = async () => {
    if (!reassignTargetTask?.id || !reassignSelectedUser?.id) return;
    try {
      await reassignAuditTask(reassignTargetTask.id, reassignSelectedUser.id, reassignNotes);
      setProjectAuditTasks((prev) =>
        (prev || []).map((t) =>
          String(t?.id) === String(reassignTargetTask.id)
            ? { ...t, assignedUserId: reassignSelectedUser.id, assignedUsername: reassignSelectedUser.fullName }
            : t
        )
      );
      setShowReassignModal(false);
    } catch (e) {
      console.error('Reassign failed:', e);
    }
  };

  const pickSectorText = (...candidates) => {
    for (const value of candidates) {
      if (value == null) continue;
      if (typeof value === 'object') {
        const nested = value?.name || value?.label || value?.sectorName || value?.namaSector || value?.sector_name;
        if (nested != null && String(nested).trim()) return String(nested).trim();
      }
      const text = String(value).trim();
      if (text && text !== '-') return text;
    }
    return '-';
  };

  const mapLeadDocuments = (leadServices, projectServices) => {
    if (!Array.isArray(leadServices) || leadServices.length === 0) return [];

    const projectServiceById = new Map(
      (Array.isArray(projectServices) ? projectServices : []).map((service) => [
        Number(service?.serviceId),
        service,
      ])
    );

    return leadServices.map((service, index) => {
      const serviceId = Number(service?.serviceId);
      const matchedProjectService = projectServiceById.get(serviceId);
      const serviceName =
        matchedProjectService?.serviceName ||
        service?.serviceName ||
        service?.name ||
        `Service ${index + 1}`;

      const cid1 = service?.cid1FileName
        ? [{ name: service.cid1FileName, url: getLeadDocumentUrl(service.cid1FileName) }]
        : [];
      const cid2 = service?.cid2FileName
        ? [{ name: service.cid2FileName, url: getLeadDocumentUrl(service.cid2FileName) }]
        : [];
      const other = parseOtherFiles(service?.otherFilesNames).map((fileName) => ({
        name: fileName,
        url: getLeadDocumentUrl(fileName),
      }));

      return {
        key: `sales-pack-${service?.id || serviceId || index}`,
        serviceId,
        serviceName,
        scope: normalizeScopeValue(service?.scope),
        cid1,
        cid2,
        other,
      };
    });
  };

  const flattenLeadServices = (projectServices) => {
    if (!Array.isArray(projectServices)) return [];
    const out = [];

    const walk = (standards, context) => {
      if (!Array.isArray(standards)) return;
      standards.forEach((item) => {
        if (!item) return;
        out.push({ ...item, __serviceContext: context });
        if (Array.isArray(item.children) && item.children.length > 0) {
          walk(item.children, context);
        }
      });
    };

    projectServices.forEach((service) => {
      const context = {
        projectServiceId: service?.id ?? null,
        serviceId: service?.serviceId,
        serviceName: service?.serviceName || '-',
      };
      walk(service?.standards || [], context);
    });

    return out;
  };

  const getSchedulingScopeKey = (projectServiceId, projectServiceStandardId) => {
    const serviceKey = projectServiceId == null ? '0' : String(projectServiceId);
    const standardKey = projectServiceStandardId == null ? '0' : String(projectServiceStandardId);
    return `${serviceKey}:${standardKey}`;
  };

  const buildSchedulingScopes = (projectServices) => {
    const services = Array.isArray(projectServices) ? projectServices : [];
    const scopes = [];

    const shouldHideScope = (...parts) => {
      const text = parts.filter(Boolean).join(' ').toLowerCase();
      return text.includes('cmf') || text.includes('fee');
    };

    const walkStandards = (service, standards) => {
      if (!Array.isArray(standards)) return;
      standards.forEach((standard) => {
        if (!standard) return;
        const projectServiceId = service?.id ?? null;
        const projectServiceStandardId = standard?.id ?? null;
        const serviceName = service?.serviceName || '-';
        const standardName = standard?.standardName || '-';
        const serviceItemName = standard?.serviceItemName || '-';
        const standardId = standard?.standardId ?? null;
        if (
          shouldHideScope(serviceName, standardName, serviceItemName)
          || (isSustainabilityServiceName(serviceName) && !isAuditCycleServiceItem(serviceItemName))
        ) {
          if (Array.isArray(standard.children) && standard.children.length > 0) {
            walkStandards(service, standard.children);
          }
          return;
        }
        const baseKey = getSchedulingScopeKey(projectServiceId, projectServiceStandardId);
        if (isInitialCertificationScope(serviceItemName) && !isSustainabilityServiceName(serviceName)) {
          const baseLabel = `${serviceName} / ${standardName} / Initial Certification`;
          scopes.push({
            key: `${baseKey}:s1`,
            projectServiceId,
            projectServiceStandardId,
            standardId,
            serviceName,
            standardName,
            serviceItemName,
            stageKey: 'stage1',
            auditDate: standard?.proposeDate || null,
            totalMandays: standard?.qty ?? null,
            label: `${baseLabel} — Stage 1 (Document Review)`,
          });
          scopes.push({
            key: `${baseKey}:s2`,
            projectServiceId,
            projectServiceStandardId,
            standardId,
            serviceName,
            standardName,
            serviceItemName,
            stageKey: 'stage2',
            auditDate: standard?.proposeDate || null,
            totalMandays: standard?.qty ?? null,
            label: `${baseLabel} — Stage 2 (Main Audit)`,
          });
        } else {
          scopes.push({
            key: baseKey,
            projectServiceId,
            projectServiceStandardId,
            standardId,
            serviceName,
            standardName,
            serviceItemName,
            auditDate: standard?.proposeDate || null,
            label: `${serviceName} / ${standardName} / ${serviceItemName}`,
          });
        }

        if (Array.isArray(standard.children) && standard.children.length > 0) {
          walkStandards(service, standard.children);
        }
      });
    };

    services.forEach((service) => {
      const projectServiceId = service?.id ?? null;
      const serviceName = service?.serviceName || '-';
      // if (!shouldHideScope(serviceName)) {
      //   scopes.push({
      //     key: getSchedulingScopeKey(projectServiceId, null),
      //     projectServiceId,
      //     projectServiceStandardId: null,
      //     standardId: null,
      //     label: `${serviceName} / All Standards`,
      //   });
      // }
      walkStandards(service, service?.standards || []);
    });

    if (scopes.length === 0) {
      scopes.push({
        key: getSchedulingScopeKey(null, null),
        projectServiceId: null,
        projectServiceStandardId: null,
        standardId: null,
        label: 'General Project Scope',
      });
    }

    const unique = new Map();
    scopes.forEach((scope) => {
      if (!unique.has(scope.key)) unique.set(scope.key, scope);
    });
    return sortAuditCyclesByDate(Array.from(unique.values()));
  };

  const buildSchedulingScopesFromApiOptions = (options) => {
    const services = Array.isArray(options?.projectServices) ? options.projectServices : [];
    if (services.length === 0) return [];

    const scopes = [];
    const shouldHideScope = (...parts) => {
      const text = parts.filter(Boolean).join(' ').toLowerCase();
      return text.includes('cmf') || text.includes('fee');
    };

    services.forEach((service) => {
      const projectServiceId = service?.projectServiceId ?? null;
      const serviceName = service?.serviceName || '-';
      // if (!shouldHideScope(serviceName)) {
      //   scopes.push({
      //     key: getSchedulingScopeKey(projectServiceId, null),
      //     projectServiceId,
      //     projectServiceStandardId: null,
      //     standardId: null,
      //     label: `${serviceName} / All Standards`,
      //   });
      // }

      const standards = Array.isArray(service?.standards) ? service.standards : [];
      standards.forEach((standard) => {
        const projectServiceStandardId = standard?.projectServiceStandardId ?? null;
        const standardId = standard?.standardId ?? null;
        const standardName = standard?.standardName || '-';
        const serviceItemName = standard?.serviceItemName || '-';
        if (
          shouldHideScope(serviceName, standardName, serviceItemName)
          || (isSustainabilityServiceName(serviceName) && !isAuditCycleServiceItem(serviceItemName))
        ) return;
        const baseKey = getSchedulingScopeKey(projectServiceId, projectServiceStandardId);
        if (isInitialCertificationScope(serviceItemName) && !isSustainabilityServiceName(serviceName)) {
          const baseLabel = `${serviceName} / ${standardName} / Initial Certification`;
          scopes.push({
            key: `${baseKey}:s1`,
            projectServiceId,
            projectServiceStandardId,
            standardId,
            serviceName,
            standardName,
            serviceItemName,
            stageKey: 'stage1',
            auditDate: standard?.proposeDate || null,
            totalMandays: standard?.qty ?? null,
            label: `${baseLabel} — Stage 1 (Document Review)`,
          });
          scopes.push({
            key: `${baseKey}:s2`,
            projectServiceId,
            projectServiceStandardId,
            standardId,
            serviceName,
            standardName,
            serviceItemName,
            stageKey: 'stage2',
            auditDate: standard?.proposeDate || null,
            totalMandays: standard?.qty ?? null,
            label: `${baseLabel} — Stage 2 (Main Audit)`,
          });
        } else {
          scopes.push({
            key: baseKey,
            projectServiceId,
            projectServiceStandardId,
            standardId,
            serviceName,
            standardName,
            serviceItemName,
            auditDate: standard?.proposeDate || null,
            label: `${serviceName} / ${standardName} / ${serviceItemName}`,
          });
        }
      });
    });

    const unique = new Map();
    scopes.forEach((scope) => {
      if (!unique.has(scope.key)) unique.set(scope.key, scope);
    });
    return sortAuditCyclesByDate(Array.from(unique.values()));
  };

  const createDefaultSchedulingData = (siteOptions = []) => ({
    siteLocations: buildSeedSiteLocations(siteOptions),
    auditSchedules: [createAuditSchedule(1)],
    accommodations: [createAccommodation()],
  });

  const normalizeAuditScheduleRow = (row, index) => {
    const base = createAuditSchedule(index + 1);
    const siteLocationIds = Array.isArray(row?.siteLocationIds) ? row.siteLocationIds : [];

    let mandayDetails = sanitizeMandayDetails(row?.mandayDetails);
    if (mandayDetails.length === 0 && (row?.startDate || row?.endDate)) {
      mandayDetails = rebuildMandayDetailsByRange(row?.startDate, row?.endDate, []);
    }
    if (mandayDetails.length === 0 && row?.mandays) {
      mandayDetails = [createMandayDetail('', row.mandays)];
    }

    let siteLocationDetails = sanitizeSiteLocationDetails(row?.siteLocationDetails);
    if (siteLocationDetails.length === 0 && (row?.startDate || row?.endDate)) {
      siteLocationDetails = rebuildSiteLocationDetailsByRange(row?.startDate, row?.endDate, []);
    }

    // A date changed through the calendar updates mandayDetails first. Keep
    // the legacy range fields in sync as well; staged saves merge sibling
    // rows, and the backend validates both representations.
    const detailDates = mandayDetails
      .map((detail) => String(detail?.date || '').trim())
      .filter(Boolean)
      .sort();
    const synchronizedStartDate = detailDates[0] || row?.startDate || '';
    const synchronizedEndDate = detailDates[detailDates.length - 1] || row?.endDate || synchronizedStartDate;

    return {
      ...base,
      ...row,
      startDate: synchronizedStartDate,
      endDate: synchronizedEndDate,
      employeeLabel: `Auditor -${index + 1}`,
      employeeId: row?.employeeId != null ? String(row.employeeId) : '',
      employeeName: row?.employeeName || '',
      auditType: normalizeAuditType(row?.auditType),
      scheduleStatus: normalizeScheduleStatus(row?.scheduleStatus),
      siteLocationIds, // Keep for backward compatibility
      siteLocationDetails,
      mandayDetails,
      mandays: calculateTotalMandays(mandayDetails),
    };
  };

  const normalizeSchedulingData = (source, siteOptions = []) => {
    const defaultData = createDefaultSchedulingData(siteOptions);
    const scheduleRows = Array.isArray(source?.auditSchedules) && source.auditSchedules.length > 0
      ? source.auditSchedules.map((row, index) => normalizeAuditScheduleRow(row, index))
      : defaultData.auditSchedules;
    const sourceSites = Array.isArray(source?.siteLocations) ? source.siteLocations : [];
    const hasMeaningfulSites = sourceSites.some(isMeaningfulSiteLocation);

    return {
      siteLocations: hasMeaningfulSites ? sourceSites : defaultData.siteLocations,
      auditSchedules: scheduleRows,
      accommodations: Array.isArray(source?.accommodations) && source.accommodations.length > 0
        ? source.accommodations
        : defaultData.accommodations,
    };
  };

  const applySchedulingScopeData = (scopeKey, cache, siteOptions = schedulingSiteOptions) => {
    const normalized = normalizeSchedulingData(cache?.[scopeKey], siteOptions);
    setSiteLocations(normalized.siteLocations);
    setAuditSchedules(normalized.auditSchedules);
    setAccommodations(normalized.accommodations);
  };

  const resolveUnifiedScopeKey = (scopeKey) => {
    if (!scopeKey) return '';
    const exact = schedulingScopes.find((scope) => String(scope.key) === String(scopeKey));
    if (exact) return exact.key;
    return schedulingScopes.find((scope) => resolveBaseScheduleKey(scope.key) === String(scopeKey))?.key || scopeKey;
  };

  // Prefer the BE gate's active stage over the task-derived cycle pointer.
  // Application Review/Scheduling tasks are shared by Stage 1 and Stage 2, so
  // the task-derived pointer can stay stuck on Stage 1 long after the gate has
  // authoritatively advanced. Falls back to resolveUnifiedScopeKey for
  // single-stage cycles (Surveillance/Recertification) or while the gate
  // hasn't loaded yet.
  const resolveActiveScopeKey = (scopeKey) => {
    const unified = resolveUnifiedScopeKey(scopeKey);
    if (!unified) return '';
    return resolveGateAlignedScopeKey(
      unified,
      stageGateMap,
      schedulingScopes.map((scope) => scope.key),
    ) || unified;
  };

  const syncAuditScopeSelectors = (scopeKey) => {
    const resolvedScopeKey = resolveUnifiedScopeKey(scopeKey);
    if (!resolvedScopeKey) return '';
    setDocTabScopeKey(resolvedScopeKey);
    setChecklistScopeKey(resolveBaseScheduleKey(resolvedScopeKey));
    return resolvedScopeKey;
  };

  const handleSchedulingScopeChange = (scopeKey) => {
    // Server schedule data is authoritative once a scope has been loaded. A
    // local wizard draft may contain an older sibling-stage row (for example,
    // the original Stage 1 date) and must not overwrite the persisted cache.
    const hasServerScopeData = Boolean(schedulingDataByScope?.[scopeKey]);
    const recoveredDraft = hasServerScopeData
      ? null
      : loadSchedulingWizardDraft({ projectId, scopeKey });
    let nextCache = recoveredDraft
      ? {
        ...schedulingDataByScope,
        [scopeKey]: normalizeSchedulingData(recoveredDraft.data, schedulingSiteOptions),
      }
      : schedulingDataByScope;
    if (recoveredDraft) {
      setSchedulingDataByScope(nextCache);
      setActiveSchedulingSubTab(recoveredDraft.activeStep);
    }

    // Initial Certification Stage 1/Stage 2: on first-ever open (no saved/recovered
    // schedule yet), default mandays + date from the proposal's total mandays split
    // instead of leaving the fields blank. Never overrides existing saved data.
    const isFreshScope = !recoveredDraft && !schedulingDataByScope[scopeKey];
    if (isFreshScope) {
      const scope = schedulingScopes.find((s) => s.key === scopeKey);
      if (scope?.stageKey && scope?.totalMandays != null) {
        const baseKey = resolveBaseScheduleKey(scopeKey);
        const stage1CacheKey = `${baseKey}:s1`;
        const stage1Date = schedulingDataByScope[stage1CacheKey]?.auditSchedules?.[0]?.startDate || scope.auditDate || null;
        const defaultRow = buildInitialCertStageDefault({
          stageKey: scope.stageKey,
          totalMandays: scope.totalMandays,
          stage1Date,
        });
        if (defaultRow) {
          nextCache = {
            ...nextCache,
            [scopeKey]: {
              siteLocations: resolveFreshStageSiteLocations({ schedulingDataByScope, baseKey, stageKey: scope.stageKey }),
              auditSchedules: [{ ...createAuditSchedule(1, scope.stageKey), ...defaultRow }],
              accommodations: [],
            },
          };
        }
      }
    }

    setSelectedSchedulingScopeKey(scopeKey);
    syncAuditScopeSelectors(scopeKey);
    applySchedulingScopeData(scopeKey, nextCache);
    setLastMandaysWarningKey('');
    setSchedulingScopeMandaysLimit(getTopMandaysLimit(scopeKey));
    setMandaysValidationErrors({});
    setSchedulingConflict(null);
  };

  const handleSaveScheduling = async (schedulesOverride = null, { silent = false } = {}) => {
    if (!projectId) return false;
    if (schedulingLock?.locked) return false;

    const selectedScope = schedulingScopes.find((scope) => scope.key === selectedSchedulingScopeKey) || {
      projectServiceId: null,
      projectServiceStandardId: null,
      standardId: null,
    };
    const allowedAuditors = getAvailableAuditorsForScope(selectedScope);
    const allowedAuditorIds = new Set(allowedAuditors.map((row) => String(row?.userId)));

    const sourceAuditSchedules = Array.isArray(schedulesOverride) ? schedulesOverride : auditSchedules;
    const nextMandaysErrors = getMandaysValidationErrors(sourceAuditSchedules);
    setMandaysValidationErrors(nextMandaysErrors);
    if (Object.keys(nextMandaysErrors).length > 0) {
      showAuditNotification('Enter Mandays greater than 0 for every selected audit date.', { title: 'Scheduling Validation', type: 'danger' });
      return false;
    }
    const normalizedAuditSchedules = sourceAuditSchedules.map((schedule, index) => {
      const cleanedRows = sanitizeMandayDetails(schedule.mandayDetails)
        .filter((row) => row.date || row.mandays);
      const cleanedSiteLocationDetails = sanitizeSiteLocationDetails(schedule.siteLocationDetails)
        .filter((row) => row.date && row.siteLocationIds.length > 0);
      return {
        ...schedule,
        employeeLabel: `Auditor -${index + 1}`,
        mandayDetails: cleanedRows,
        siteLocationDetails: cleanedSiteLocationDetails,
        mandays: calculateTotalMandays(cleanedRows),
      };
    });

    const currentStageKey = resolveScopeStageKey(selectedSchedulingScopeKey);
    const topMandaysLimit = schedulingScopeMandaysLimit;
    // Skip combined mandays limit for staged scopes — each stage manages its own mandays
    if (!currentStageKey) {
      const totalScheduledMandays = getTotalScheduledMandays(normalizedAuditSchedules);
      if (topMandaysLimit != null && totalScheduledMandays > topMandaysLimit) {
        showAuditNotification(
          `Warning: Total Mandays in Audit Schedule (${totalScheduledMandays}) exceeds top Mandays limit (${topMandaysLimit}).`,
          { title: 'Scheduling Validation', type: 'warning' }
        );
        return false;
      }
    }

    const hasMissingScheduleDates = normalizedAuditSchedules.some((schedule) => {
      const hasDateInRows = (Array.isArray(schedule.mandayDetails) ? schedule.mandayDetails : [])
        .some((row) => String(row?.date || '').trim());
      if (hasDateInRows) return false;
      return buildDateRange(schedule?.startDate, schedule?.endDate).length === 0;
    });

    if (hasMissingScheduleDates) {
      showAuditNotification('Please select audit schedule dates for each schedule before saving.', { title: 'Scheduling Validation', type: 'warning' });
      return false;
    }

    const hasMissingAuditor = normalizedAuditSchedules.some((schedule) => !String(schedule?.employeeId || '').trim());

    const hasInvalidAuditorByScope = normalizedAuditSchedules.some((schedule) => {
      if (!schedule?.employeeId) return false;
      return !allowedAuditorIds.has(String(schedule.employeeId));
    });

    if (hasInvalidAuditorByScope) {
      showAuditNotification('Selected auditor must have approved competency for the selected project standard.', { title: 'Scheduling Validation', type: 'danger' });
      return false;
    }

    const hasInvalidRoleByLevel = normalizedAuditSchedules.some((schedule) => {
      if (!schedule?.employeeId || !schedule?.role) return false;
      const allowedRoles = getRoleOptionsForSchedule(schedule, selectedScope);
      return !allowedRoles.some((row) => row?.qualification === schedule.role);
    });

    if (hasInvalidRoleByLevel) {
      showAuditNotification('Assign Project Roles must follow approved auditor competency level rule.', { title: 'Scheduling Validation', type: 'danger' });
      return false;
    }

    // For Initial Cert staged scopes: tag current rows and merge with the other stage's rows
    let payloadAuditSchedules = normalizedAuditSchedules;
    if (currentStageKey) {
      // Submit only the active stage. The backend merges this with the
      // persisted sibling stage, preventing an old sibling draft from being
      // revalidated as part of the current save.
      payloadAuditSchedules = normalizedAuditSchedules.map((s) => ({ ...s, stageKey: currentStageKey }));
    }
    const payload = {
      projectServiceId: selectedScope.projectServiceId,
      projectServiceStandardId: selectedScope.projectServiceStandardId,
      methodologyScopeKey: selectedScope.isMethodologyGroup
        ? resolveBaseScheduleKey(selectedScope.methodologyScopeKey || selectedScope.key)
        : null,
      methodologyType: selectedScope.isMethodologyGroup ? selectedScope.methodologyType : null,
      includedProjectServiceStandardIds: selectedScope.includedProjectServiceStandardIds || [],
      includedStandardIds: selectedScope.includedStandardIds || [],
      includedServiceItemIds: selectedScope.includedServiceItemIds || [],
      siteLocations,
      auditSchedules: payloadAuditSchedules,
      accommodations,
    };

    setIsSavingScheduling(true);
    try {
      const res = await api.put(`/audit/projects/${Number(projectId)}/schedules`, payload);
      const rawSavedData = res?.data?.data;
      const savedData = normalizeSchedulingData(rawSavedData || payload, schedulingSiteOptions);
      // For staged scopes: re-split saved data into stage1/stage2 cache entries
      if (currentStageKey) {
        const baseKey = resolveBaseScheduleKey(selectedSchedulingScopeKey);
        const allSavedRows = savedData.auditSchedules || [];
        const s1Rows = allSavedRows.filter((r) => r.stageKey === 'stage1');
        const s2Rows = allSavedRows.filter((r) => r.stageKey === 'stage2' || !r.stageKey);
        setSchedulingDataByScope((prev) => ({
          ...prev,
          [baseKey]: savedData,
          [`${baseKey}:s1`]: { ...savedData, auditSchedules: s1Rows.length ? s1Rows : [createAuditSchedule(1, 'stage1')] },
          [`${baseKey}:s2`]: { ...savedData, auditSchedules: s2Rows.length ? s2Rows : [createAuditSchedule(1, 'stage2')] },
        }));
      } else {
        setSchedulingDataByScope((prev) => ({
          ...prev,
          [selectedSchedulingScopeKey]: savedData,
        }));
      }
      // Sync server-assigned autoBooked fields back to local schedule state (current stage only)
      if (rawSavedData?.auditSchedules) {
        const allServerRows = normalizeSchedulingData(rawSavedData, schedulingSiteOptions).auditSchedules;
        const serverSchedules = currentStageKey
          ? allServerRows.filter((r) => r.stageKey === currentStageKey || (!r.stageKey && currentStageKey === 'stage2'))
          : allServerRows;
        setAuditSchedules(serverSchedules);
      }

      // Backend evaluates every project scope and is the source of truth for task completion.
      const backendTaskCompleted = res?.data?.schedulingTaskCompleted === true;
      await reloadAuditTasks();

      const autoBookedCount = countAutoBookedAuditors(rawSavedData?.auditSchedules);
      const successMsg = autoBookedCount > 0
        ? buildAutoBookingSuccessMessage(rawSavedData?.auditSchedules)
        : hasMissingAuditor
          ? 'Scheduling saved. The system will attempt to auto-book qualified auditors for unassigned slots.'
          : backendTaskCompleted
            ? 'Scheduling confirmed. Audit Scheduling task completed.'
            : 'Scheduling data updated successfully.';
      // Intermediate "Save & Next" steps save silently (no modal) -- the success
      // modal is reserved for the final "Confirm Scheduling" action so users
      // aren't interrupted by a popup on every wizard step.
      if (!silent) showAuditNotification(successMsg, { title: 'Scheduling', type: 'success' });

      schedulingDraftSuppressedScopeRef.current = selectedSchedulingScopeKey;
      clearSchedulingWizardDraft({ projectId, scopeKey: selectedSchedulingScopeKey });
      if (currentStageKey) {
        const baseKey = resolveBaseScheduleKey(selectedSchedulingScopeKey);
        clearSchedulingWizardDraft({ projectId, scopeKey: `${baseKey}:s1` });
        clearSchedulingWizardDraft({ projectId, scopeKey: `${baseKey}:s2` });
      }

      return true;
    } catch (error) {
      console.error('Failed to save scheduling data:', error);
      const lock = parseSchedulingLockError(error);
      if (lock) {
        setSchedulingLock(lock);
        setOpenDatePickerScheduleId(null);
        showAuditNotification(lock.message, { title: 'Scheduling Locked', type: 'warning' });
        return false;
      }
      const schedulingMessage = error?.response?.data?.message;
      const isSchedulingConflict = error?.response?.status === 409;
      if (isSchedulingConflict) {
        setSchedulingConflict({
          message: schedulingMessage || 'The selected auditor is unavailable for one or more audit dates.',
        });
      }
      showAuditNotification(
        isSchedulingConflict
          ? `${schedulingMessage || 'The selected auditor is unavailable for one or more audit dates.'} Choose another auditor or audit date, then save again.`
          : (schedulingMessage || 'Failed to save scheduling data.'),
        { title: isSchedulingConflict ? 'Scheduling conflict' : 'Scheduling', type: 'danger' }
      );
      return false;
    } finally {
      setIsSavingScheduling(false);
    }
  };

  const [isRefreshingHistory, setIsRefreshingHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PAGE_SIZE = 10;

  // "Project History" is actually backed by THREE separate, unrelated backend tables --
  // audit_history (generic workflow/task audit trail), project_history (Scope/EA Code/Mandays
  // edits + project creation, written by ProjectController), and audit_history_process
  // (Application Review decisions/follow-up actions, scheduling saves, written by
  // AuditHistoryProcessService). This panel used to only ever fetch the first one, so refreshing
  // it after e.g. a Scope/Mandays edit or an Application Review action never showed anything new
  // -- not a refresh bug, a wrong/incomplete data source. Now merges all three into one
  // chronological feed.
  const loadProjectHistory = async () => {
    if (!projectId) return;
    const parsedProjectId = Number(projectId);
    if (Number.isNaN(parsedProjectId)) return;
    setIsRefreshingHistory(true);
    try {
      const [auditHistoryRes, projectHistoryRes, processHistoryRes] = await Promise.allSettled([
        api.get(`/audit-history/project/${parsedProjectId}`),
        api.get(`/crm/projects/${parsedProjectId}/history`),
        api.get(`/audit/projects/${parsedProjectId}/history-process`),
      ]);

      const auditHistoryRows = (auditHistoryRes.status === 'fulfilled' && Array.isArray(auditHistoryRes.value?.data)
        ? auditHistoryRes.value.data
        : []
      ).map((row) => ({ ...row, id: `audit-history-${row.id}` }));

      const projectHistoryRows = (projectHistoryRes.status === 'fulfilled' && Array.isArray(projectHistoryRes.value?.data)
        ? projectHistoryRes.value.data
        : []
      ).map((row) => ({
        id: `project-history-${row.id}`,
        entityType: row.type || 'PROJECT',
        actionType: row.type,
        actionTimestamp: row.date,
        description: row.notes,
        userName: row.user,
        payload: row.payload,
      }));

      const processHistoryRows = (processHistoryRes.status === 'fulfilled' && Array.isArray(processHistoryRes.value?.data)
        ? processHistoryRes.value.data
        : []
      ).map((row) => ({
        id: `process-history-${row.id}`,
        entityType: row.type,
        actionType: row.actionType,
        actionTimestamp: row.date || row.createdAt,
        description: row.notes,
        userName: row.user,
      }));

      // Older completed certificate tasks may predate the certificate history
      // event. Preserve the reviewer identity from the completed task so the
      // history panel still answers who handled the certification.
      const hasCertificateIssuanceHistory = processHistoryRows.some((row) => (
        String(row?.entityType || '').toUpperCase() === 'CERTIFICATE_ISSUANCE'
      ));
      const legacyCertificateHistoryRows = !hasCertificateIssuanceHistory
        ? (projectAuditTasks || [])
          .filter((task) => String(task?.taskTypeCode || '').toUpperCase() === 'CERTIFICATE_ISSUANCE')
          .filter((task) => isTaskDone(task?.status) && (task?.assignedUsername || task?.assignedUserName))
          .map((task) => ({
            id: `certificate-task-history-${task.id}`,
            entityType: 'CERTIFICATE_ISSUANCE',
            actionType: 'REVIEW_COMPLETED',
            actionTimestamp: task.completedAt || task.updatedAt || task.createdAt,
            description: 'Certificate review and issuance task completed.',
            userName: task.assignedUsername || task.assignedUserName,
          }))
        : [];

      const merged = [...auditHistoryRows, ...projectHistoryRows, ...processHistoryRows, ...legacyCertificateHistoryRows]
        .sort((a, b) => new Date(b.actionTimestamp || 0).getTime() - new Date(a.actionTimestamp || 0).getTime());
      setProjectHistory(merged);
      setHistoryPage(1);
    } catch (e) {
      console.error('Failed to load project history:', e);
    } finally {
      setIsRefreshingHistory(false);
    }
  };

  const loadAuditProcessData = async () => {
    if (!projectId) {
      setPageError('Project ID is required.');
      return;
    }

    const parsedProjectId = Number(projectId);
    if (Number.isNaN(parsedProjectId)) {
      setPageError('Invalid project ID.');
      return;
    }

    setIsLoadingPage(true);
    schedulingDraftReadyRef.current = false;
    setPageError('');

    try {
      const [taskTypesRes, tasksRes, workflowTasksRes, projectsRes, scheduleRes] = await Promise.allSettled([
        api.get('/audit/task-types'),
        api.get(`/audit/tasks?projectId=${parsedProjectId}`),
        api.get(`/audit/projects/${parsedProjectId}/workflow-tasks`),
        // Single-project lookup, not the bulk /crm/projects list — that endpoint does an
        // unpaginated findAll() + N+1 branch/standards filtering over every project in the
        // system (documented perf issue) and was taking 80s+ just to find this one project.
        api.get(`/crm/projects/${parsedProjectId}`),
        api.get(`/audit/projects/${parsedProjectId}/schedules`),
      ]);

      const taskTypes = taskTypesRes.status === 'fulfilled' && Array.isArray(taskTypesRes.value?.data)
        ? filterExcludedAuditTasks(taskTypesRes.value.data)
        : [];
      const auditTasks = tasksRes.status === 'fulfilled' && Array.isArray(tasksRes.value?.data)
        ? filterExcludedAuditTasks(tasksRes.value.data)
        : [];
      const workflowTasks = workflowTasksRes.status === 'fulfilled' && Array.isArray(workflowTasksRes.value?.data)
        ? filterExcludedAuditTasks(workflowTasksRes.value.data)
        : auditTasks;
      setProjectAuditTasks(auditTasks);
      setProjectWorkflowTasks(workflowTasks);
      const project = projectsRes.status === 'fulfilled' && projectsRes.value?.data?.id
        ? projectsRes.value.data
        : null;
      // Fire-and-forget (own try/catch, sets its own state) rather than inlined here — Project
      // History is actually backed by THREE separate tables (audit_history/project_history/
      // audit_history_process, see loadProjectHistory's comment) and this used to only merge two
      // of them inline here, silently missing Scope/EA Code/Mandays edits and other
      // project_history-only entries on initial page load.
      loadProjectHistory();
      const workflowProgress = buildAuditWorkflowProgress(workflowTasks);

      const scheduleLeadId = scheduleRes.status === 'fulfilled' ? Number(scheduleRes.value?.data?.leadId || 0) : 0;
      const resolvedLeadId = Number(project?.leadId || scheduleLeadId || auditTasks[0]?.leadId || 0);

      let leadServices = [];
      let leadDetail = null;
      let fullProjectServices = project?.projectServices || [];
      let proposalSectorName = null;
      // Sustainability/ISCC-scope stage fields (Registration Number, Input/Output
      // Material, GHG Calculation) are persisted on the proposal record, not per-standard.
      let proposalRegistrationNumber = null;
      let proposalGhgCalculation = null;
      let proposalInputMaterial = null;
      let proposalOutputMaterial = null;
      // Scope / EA Code / Mandays are entered on the proposal (proposal_standard_items) and the
      // project's own project_service_standards columns are only a copy taken at conversion time --
      // they can go stale if the proposal is edited afterwards. Keyed by proposalStandardItemId (the
      // FK each project standard already carries back to its source proposal row) so the real
      // proposal values can be looked up and preferred over the project's copy.
      const proposalDataByStandardItemId = new Map();

      if (!Number.isNaN(resolvedLeadId) && resolvedLeadId > 0) {
        try {
          const leadRes = await api.get(`/crm/leads/${resolvedLeadId}`);
          leadDetail = leadRes?.data || null;
          if (Array.isArray(leadRes?.data?.services)) {
            leadServices = leadRes.data.services;
          }
        } catch (leadError) {
          console.warn('Failed to load lead services for sales pack mapping:', leadError);
        }

        // Fetch proposal + full project to get complete projectServices with qty and sector
        try {
          const proposalDetailRes = await api.get(`/crm/proposals/detail/lead/${resolvedLeadId}`);
          const proposalData = proposalDetailRes?.data;
          proposalSectorName = proposalData?.sectorName || proposalData?.sector_name || proposalData?.sector?.name || null;
          proposalRegistrationNumber = proposalData?.registrationNumber || proposalData?.registration_number || null;
          proposalGhgCalculation = proposalData?.ghgCalculation || proposalData?.ghg_calculation || null;
          proposalInputMaterial = proposalData?.inputMaterial || proposalData?.input_material || null;
          proposalOutputMaterial = proposalData?.outputMaterial || proposalData?.output_material || null;
          // NOTE: the top-level field on ProposalDetailResponse is `proposal_sections`, NOT
          // `services` -- using the wrong key here silently no-ops (Array.isArray(undefined) is
          // false), which is why this map was empty despite the proposal itself having real data.
          if (Array.isArray(proposalData?.proposal_sections)) {
            proposalData.proposal_sections.forEach((section) => {
              (section?.services || []).forEach((svc) => {
                if (svc?.proposalStandardItemId != null) {
                  proposalDataByStandardItemId.set(Number(svc.proposalStandardItemId), {
                    scope: svc.scope,
                    eaCode: svc.eaCode,
                    qty: svc.qty,
                  });
                }
                (svc?.sub_services || []).forEach((subSvc) => {
                  if (subSvc?.proposalStandardItemId != null) {
                    proposalDataByStandardItemId.set(Number(subSvc.proposalStandardItemId), {
                      scope: subSvc.scope ?? svc?.scope,
                      eaCode: subSvc.eaCode ?? svc?.eaCode,
                      qty: subSvc.qty,
                    });
                  }
                });
              });
            });
          }
          // TEMP DIAGNOSTIC (remove once the EA Code/Scope-from-proposal mapping is confirmed
          // working end-to-end): logs the resolved proposal id/leadId and every
          // proposalStandardItemId -> {scope, eaCode, qty} entry actually loaded from the proposal.
          console.debug('[AuditProcess] proposal detail fetched', {
            resolvedLeadId,
            proposalId: proposalData?.id,
            proposalDataByStandardItemId: Object.fromEntries(proposalDataByStandardItemId),
          });
          if (proposalData?.id) {
            try {
              const fullProjectRes = await api.get(`/crm/projects/by-proposal/${proposalData.id}`);
              if (Array.isArray(fullProjectRes?.data?.projectServices) && fullProjectRes.data.projectServices.length > 0) {
                fullProjectServices = fullProjectRes.data.projectServices;
              }
            } catch (_) {
              // Keep existing project services as fallback
            }
          }
        } catch (_) {
          // Keep existing project services as fallback
        }
        const needsQtyFallback = !fullProjectServices.some((svc) =>
          Array.isArray(svc?.standards) && svc.standards.some((std) => (std?.qty ?? std?.quantity) != null)
        );
        if (needsQtyFallback) {
          try {
            const directProjectRes = await api.get(`/crm/projects/${parsedProjectId}`);
            if (Array.isArray(directProjectRes?.data?.projectServices) && directProjectRes.data.projectServices.length > 0) {
              fullProjectServices = directProjectRes.data.projectServices;
            }
          } catch (_) {
            // Keep existing fallback
          }
        }
      }

      const standards = flattenLeadServices(fullProjectServices);
      const hasProjectStandards = standards.length > 0;
      const projectSectorText = pickSectorText(
        project?.sectorName,
        project?.namaSector,
        project?.sector_name,
        project?.sector?.name,
        project?.sector,
        proposalSectorName,
        leadDetail?.sectorName,
        leadDetail?.namaSector,
        leadDetail?.sector_name,
        leadDetail?.sector?.name,
        leadDetail?.sector
      );
      const visibleStandards = sortAuditProductCategories(standards.filter((item) => {
        if (isExcludedAuditTaskEntry({
          serviceName: item?.__serviceContext?.serviceName,
          serviceItemName: item?.serviceItemName,
          standardName: item?.standardName,
          productCategoryName: item?.productCategoryName
        })) {
          return false;
        }

        const haystack = [
          item?.productCategoryName,
          item?.standardName,
          item?.serviceItemName,
          item?.__serviceContext?.serviceName,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return !haystack.includes('cmf');
      }));

      const projectBasedStages = visibleStandards.map((item, index) => {
        const serviceName = item?.__serviceContext?.serviceName || '-';
        const serviceId = Number(item?.__serviceContext?.serviceId);
        const matchedLeadService = leadServices.find((s) => Number(s?.serviceId) === serviceId);
        const stageName = item?.productCategoryName || item?.standardName || item?.serviceItemName || `Item ${index + 1}`;
        const serviceItemName = item?.serviceItemName || '-';
        // Mandays / EA Code / Scope all source from the proposal (proposal_standard_items) via the
        // proposalStandardItemId FK each project standard carries back to its source proposal row --
        // the project's own copies (item.qty/eaCode/scope) and the lead-service fields are fallbacks
        // only, for older data predating this FK link or when the proposal lookup failed to load.
        const proposalRow = item?.proposalStandardItemId != null
          ? proposalDataByStandardItemId.get(Number(item.proposalStandardItemId))
          : undefined;
        // TEMP DIAGNOSTIC (remove once confirmed working): shows whether this project standard's
        // proposalStandardItemId actually matched an entry loaded from the proposal.
        console.debug('[AuditProcess] stage proposal-row match', {
          stageName: item?.productCategoryName || item?.standardName || item?.serviceItemName,
          itemProposalStandardItemId: item?.proposalStandardItemId,
          matched: Boolean(proposalRow),
          proposalRow,
          itemEaCode: item?.eaCode,
          itemScope: item?.scope,
        });
        const rawQty = proposalRow?.qty ?? item?.qty ?? item?.quantity ?? item?.mandays ?? null;
        const qtyText = rawQty != null ? String(rawQty) : '-';
        const eaCodeText = proposalRow?.eaCode || item?.eaCode || matchedLeadService?.eaCode || '-';
        const scopeText = normalizeScopeValue(proposalRow?.scope ?? item?.scope ?? matchedLeadService?.scope);
        const isSustainability = isSustainabilityOrIsccText(
          serviceName,
          item?.standardName,
          item?.productCategoryName,
          serviceItemName
        );

        return {
          key: getSchedulingScopeKey(item?.__serviceContext?.projectServiceId, item?.id || index),
          salesPackServiceIds: Number.isFinite(serviceId) ? [serviceId] : [],
          auditDate: item?.proposeDate || null,
          stageName,
          statusClass: workflowProgress.currentMajorId === 'audit-completed' ? 'badge badge-audit-completed' : 'badge badge-checklist',
          statusLabel: workflowProgress.currentLabel,
          project: project?.projectName || 'Audit Process',
          serviceItem: serviceItemName,
          mandays: qtyText,
          eaCode: eaCodeText,
          scope: scopeText,
          isSustainability,
          registrationNumber: proposalRegistrationNumber || '-',
          ghgCalculation: proposalGhgCalculation || '-',
          inputMaterial: proposalInputMaterial || '-',
          outputMaterial: proposalOutputMaterial || '-',
          done: workflowProgress.currentMajorId === 'audit-completed',
          workflowProgress,
        };
      });
      const projectStageByScopeKey = new Map(projectBasedStages.map((stage) => [stage.key, stage]));

      const normalizedSteps = (taskTypes.length > 0 ? taskTypes : FALLBACK_AUDIT_STEPS)
        .map((step, index) => ({
          id: step.id ?? index + 1,
          taskTypeId: step.id ?? null,
          code: step.code || null,
          label: step.taskType || step.label || `Stage ${index + 1}`,
          description: step.wording || step.description || 'Description',
          sortOrder: step.sortOrder ?? index + 1,
        }))
        .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));

      const fallbackTaskStages = normalizedSteps.map((step, index) => {
        const relatedTask = auditTasks.find((task) => {
          if (step.taskTypeId != null && task?.taskTypeId != null) {
            return Number(task.taskTypeId) === Number(step.taskTypeId);
          }
          if (step.code && task?.taskTypeCode) {
            return String(task.taskTypeCode).toLowerCase() === String(step.code).toLowerCase();
          }
          return false;
        }) || null;

        const isDone = String(relatedTask?.status || '').toUpperCase() === 'DONE';

        return {
          key: `stage-${step.id}`,
          stageName: `Initial - Stage ${index + 1}`,
          statusClass: workflowProgress.currentMajorId === 'audit-completed' || isDone ? 'badge badge-audit-completed' : 'badge badge-checklist',
          statusLabel: workflowProgress.currentLabel,
          project: project?.projectName || relatedTask?.title || 'Audit Process',
          serviceItem: relatedTask?.taskType || '-',
          salesPackServiceIds: [],
          mandays: '-',
          eaCode: projectSectorText,
          scope: relatedTask?.description || project?.projectName || '-',
          done: workflowProgress.currentMajorId === 'audit-completed' || isDone,
          workflowProgress,
        };
      });

      setSalesPackDocuments(mapLeadDocuments(leadServices, fullProjectServices));

      const scheduleOptions = scheduleRes.status === 'fulfilled' ? scheduleRes.value?.data?.options : null;
      const resolvedCompanyId = resolveAuditClientId(project, leadDetail, auditTasks[0], scheduleOptions);
      const proposalScope = projectBasedStages
        .map((stage) => String(stage?.scope || '').trim())
        .find((scope) => scope && scope !== '-') || '';

      setProjectMeta({
        clientId: resolvedCompanyId,
        clientName: project?.companyName || auditTasks[0]?.companyName || '-',
        companyName: project?.companyName || auditTasks[0]?.companyName || '-',
        auditTitle: project?.projectName || auditTasks[0]?.title || 'Audit Process',
        projectCode: project?.projectCode || auditTasks[0]?.projectCode || `P-${parsedProjectId}`,
        locationName: project?.companyName || auditTasks[0]?.companyName || '-',
        address: project?.companyAddress || project?.address || leadDetail?.companyAddress || leadDetail?.address || '',
        // Temporary fallback for FAPP-01's PIC field: auto-fill from the lead's contact
        // name until that form has its own contact/PIC picker.
        picName: leadDetail?.pic || '',
        projectDate: project?.projectDate || project?.project_date || '',
        validUntil: project?.validUntil || project?.valid_until || '',
        eaCode: projectSectorText || '',
        scope: proposalScope,
        proposalScope,
        proposalInputMaterial,
        proposalOutputMaterial,
        ghgCalculation: proposalGhgCalculation,
      });

      let scopes = buildSchedulingScopes(fullProjectServices);
      let nextSchedulingCache = {};
      let nextAuditorOptions = [];
      let nextAuditorQualificationOptions = [];
      let nextSiteOptions = [];
      try {
        const scheduleOptions = scheduleRes?.value?.data?.options || {};
        nextSiteOptions = normalizeScheduleSiteOptions(scheduleOptions.siteOptions);
        setSchedulingSiteOptions(nextSiteOptions);

        const apiScopes = buildSchedulingScopesFromApiOptions(scheduleOptions);
        if (apiScopes.length > 0) {
          scopes = apiScopes;
        }
        // buildMethodologySchedulingScopes flatMaps backend scopeGroups as-is (no date
        // sort) — re-sort here so scopes[0] (used below to pick the "current"/active
        // cycle) matches the same Initial → Surveillance 1st → 2nd → Re-cert order the
        // stage cards are displayed in. Without this, an out-of-order backend
        // scopeGroups array made a later cycle (e.g. Surveillance 2nd) show as active.
        scopes = sortAuditCyclesByDate(buildMethodologySchedulingScopes(scheduleOptions.auditMethodologyGroup, scopes));
        nextAuditorOptions = Array.isArray(scheduleOptions.auditors)
          ? scheduleOptions.auditors
          : [];
        nextAuditorQualificationOptions = Array.isArray(scheduleOptions.auditorQualifications)
          ? scheduleOptions.auditorQualifications
          : [];
        const rows = Array.isArray(scheduleRes?.value?.data?.schedules) ? scheduleRes.value.data.schedules : [];
        rows.forEach((row) => {
          const key = row?.methodologyScopeKey
            || getSchedulingScopeKey(row?.projectServiceId, row?.projectServiceStandardId);
          const normalized = normalizeSchedulingData({
            siteLocations: row?.siteLocations,
            auditSchedules: row?.auditSchedules,
            accommodations: row?.accommodations,
          }, nextSiteOptions);
          nextSchedulingCache[key] = normalized;
          // If this scope is an Initial Certification, split rows into Stage 1 / Stage 2 sub-caches
          const hasStageScopes = scopes.some((s) => s.key === `${key}:s1` || s.key === `${key}:s2`);
          if (hasStageScopes) {
            const allRows = normalized.auditSchedules || [];
            const s1Rows = allRows.filter((r) => r.stageKey === 'stage1');
            // Legacy untagged rows (before this feature) are treated as Stage 2
            const s2Rows = allRows.filter((r) => r.stageKey === 'stage2' || !r.stageKey);
            nextSchedulingCache[`${key}:s1`] = {
              ...normalized,
              auditSchedules: s1Rows.length ? s1Rows : [createAuditSchedule(1, 'stage1')],
            };
            nextSchedulingCache[`${key}:s2`] = {
              ...normalized,
              auditSchedules: s2Rows.length ? s2Rows : [createAuditSchedule(1, 'stage2')],
            };
          }
        });

        // Pre-populate startDate from proposeDate for scopes with no saved schedule
        const optServices = scheduleOptions.projectServices;
        if (Array.isArray(optServices)) {
          optServices.forEach(svc => {
            if (!Array.isArray(svc?.standards)) return;
            svc.standards.forEach(std => {
              if (!std.proposeDate) return;
              const scopeKey = getSchedulingScopeKey(svc.projectServiceId, std.projectServiceStandardId);
              if (!nextSchedulingCache[scopeKey]) {
                nextSchedulingCache[scopeKey] = normalizeSchedulingData({
                  auditSchedules: [{ startDate: std.proposeDate, endDate: std.proposeDate }],
                  siteLocations: nextSiteOptions,
                  accommodations: [],
                }, nextSiteOptions);
              }
            });
          });
        }

        // Grouped methodology scopes use the backend-provided group key while
        // preserving existing per-standard schedule records as a safe fallback.
        scopes.filter((scope) => scope?.isMethodologyGroup).forEach((scope) => {
          const baseGroupKey = resolveBaseScheduleKey(scope.key);
          if (nextSchedulingCache[scope.key] || nextSchedulingCache[baseGroupKey]) return;
          const memberFallback = (scope.memberScopes || [])
            .map((member) => nextSchedulingCache[member.key] || nextSchedulingCache[resolveBaseScheduleKey(member.key)])
            .find(Boolean);
          const seeded = memberFallback || normalizeSchedulingData({
            auditSchedules: scope.auditDate ? [{ startDate: scope.auditDate, endDate: scope.auditDate }] : [],
            siteLocations: nextSiteOptions,
            accommodations: [],
          }, nextSiteOptions);
          nextSchedulingCache[baseGroupKey] = seeded;
          if (scope.stageKey) {
            const stageRows = (seeded.auditSchedules || []).filter((row) => (
              row.stageKey === scope.stageKey || (!row.stageKey && scope.stageKey === 'stage2')
            ));
            nextSchedulingCache[scope.key] = {
              ...seeded,
              auditSchedules: stageRows.length ? stageRows : [createAuditSchedule(1, scope.stageKey)],
            };
          } else {
            nextSchedulingCache[scope.key] = seeded;
          }
        });

        // Read project-level audit info from schedule options
        setProjectAuditInfo({
          auditMethodology: scheduleOptions.auditMethodology || null,
          multiSite: Boolean(scheduleOptions.multiSite),
          numberOfSites: scheduleOptions.numberOfSites || null,
          siteCount: nextSiteOptions.length,
          // #17: authoritative methodology grouping from backend (preferred over
          // client-side label reconstruction). Null-safe for older API responses.
          methodologyGroup: scheduleOptions.auditMethodologyGroup || null,
        });
      } catch (scheduleError) {
        console.warn('Failed to load audit schedules, using local defaults:', scheduleError);
      }
      setSchedulingScopes(scopes);
      setAuditorOptions(nextAuditorOptions);
      setAuditorQualificationOptions(nextAuditorQualificationOptions);

      const scopeDrivenStages = scopes
        .filter((scope) => scope?.projectServiceId != null || scope?.projectServiceStandardId != null)
        .map((scope, index) => {
          const memberProjectStages = scope?.isMethodologyGroup
            ? (scope.memberScopes || [])
              .map((member) => projectStageByScopeKey.get(resolveBaseScheduleKey(member.key)))
              .filter(Boolean)
            : [];
          // scope.key can carry a ':s1'/':s2' suffix (Initial Certification Stage 1/Stage 2),
          // which never matches projectStageByScopeKey (built from unsuffixed standard keys) --
          // same mismatch already worked around for mandays via stageMandaysDisplay below. Without
          // this fallback, the real proposal scope text (item.scope) is unreachable for staged
          // scopes and both Stage 1 and Stage 2 silently show the generic label instead.
          const projectStage = projectStageByScopeKey.get(scope.key)
            || memberProjectStages[0]
            || projectStageByScopeKey.get(resolveBaseScheduleKey(scope.key));
          const groupedMandays = memberProjectStages.reduce((total, stage) => {
            const value = Number(stage?.mandays);
            return Number.isFinite(value) && value > 0 ? total + value : total;
          }, 0);
          const groupedEaCodes = Array.from(new Set(memberProjectStages.map((stage) => stage?.eaCode).filter((value) => value && value !== '-')));
          const groupedProposalScopes = Array.from(new Set(memberProjectStages.map((stage) => stage?.scope).filter((value) => value && value !== '-')));
          const groupedSalesPackServiceIds = Array.from(new Set(memberProjectStages
            .flatMap((stage) => stage?.salesPackServiceIds || [])
            .filter((id) => Number.isFinite(Number(id)))));
          const labelParts = String(scope?.label || '')
            .split(' / ')
            .map((part) => part.trim());
          const scopeServiceName = labelParts[0] || '-';
          const scopeStandardName = labelParts[1] || '-';
          const scopeServiceItemName = labelParts[2] || '-';
          const stageLabel = [scopeServiceName, scopeStandardName]
            .filter((part) => part && part !== '-')
            .join(' / ');
          // Grouped methodology keys are presentation keys (`mg-…`), not the
          // API's `serviceId:standardId` task key.  Always pass the complete
          // scope object so task selection retains the cycle and stage identity.
          const exactScopeTasks = selectTasksForAuditScope(workflowTasks, scope);
          const hasOpenWorkflowTasks = exactScopeTasks.some((task) => !isTaskDone(task?.status));
          const legacyProjectTasks = workflowTasks.filter((task) => (
            task?.projectServiceId == null && task?.projectServiceStandardId == null
          ));
          const scopeSchedulingCompleted = isSchedulingScopeConfirmed(nextSchedulingCache[scope.key]);
          const scopeWorkflowProgress = buildAuditWorkflowProgress(
            exactScopeTasks,
            {
              schedulingConfirmed: scopeSchedulingCompleted,
              // Stage 1 stops at Audit Completed right after Technical Review —
              // Certificate Issuance only happens once, in Stage 2.
              isInitialStage1: scope?.stageKey === 'stage1',
              includeCertificateIssuance: shouldIncludeCertificateIssuance(scope || {}),
              completedSubStepIds: [],
            },
          );

          // Initial Certification Stage 1 / Stage 2 scopes carry a ':s1'/':s2' suffixed
          // key, which never matches projectStageByScopeKey (built from unsuffixed
          // standard keys) — that mismatch always fell through to '-'. For these scopes,
          // resolve mandays from what's actually been scheduled so far, falling back to
          // the proposal-derived default split (Stage 1 = 1 fixed, Stage 2 = remainder).
          const stageMandaysDisplay = scope?.stageKey
            ? resolveStageMandaysDisplay({
              stageKey: scope.stageKey,
              totalMandays: scope.totalMandays,
              scheduledMandays: getTotalScheduledMandays(nextSchedulingCache[scope.key]?.auditSchedules),
            })
            : (projectStage?.mandays || '-');
          const isSustainability = isSustainabilityOrIsccText(
            scopeServiceName,
            scopeStandardName,
            scopeServiceItemName,
            scope?.label
          ) || Boolean(projectStage?.isSustainability);

          return {
            key: scope.key,
            projectServiceId: scope.projectServiceId,
            projectServiceStandardId: scope.projectServiceStandardId,
            // Carried so resolveCycleTrackKey can tell one standard's independent
            // audit apart from another's (Single Audit, multi standard) and treat
            // an Integrated/Combined group as one shared track.
            standardId: scope.standardId ?? null,
            isMethodologyGroup: Boolean(scope?.isMethodologyGroup),
            salesPackServiceIds: scope?.isMethodologyGroup
              ? groupedSalesPackServiceIds
              : (projectStage?.salesPackServiceIds || []),
            auditDate: scope.auditDate || null,
            stageName: stageLabel || projectStage?.stageName || scope.label || `Item ${index + 1}`,
            statusClass: scopeWorkflowProgress.currentMajorId === 'audit-completed' ? 'badge badge-audit-completed' : 'badge badge-checklist',
            statusLabel: scopeWorkflowProgress.currentLabel,
            project: project?.projectName || 'Audit Process',
            serviceItem: scopeServiceItemName !== '-' ? scopeServiceItemName : (projectStage?.serviceItem || '-'),
            standardName: scopeStandardName !== '-' ? scopeStandardName : null,
            mandays: scope?.isMethodologyGroup && groupedMandays > 0 ? String(groupedMandays) : stageMandaysDisplay,
            // EA Code is per-standard (proposal standard's selected sectors), not the
            // whole-company Sector -- never fall back to projectSectorText here, or every
            // standard missing its own EA Code silently shows the company's Sector instead.
            eaCode: scope?.isMethodologyGroup && groupedEaCodes.length > 0 ? groupedEaCodes.join(', ') : (projectStage?.eaCode || '-'),
            scope: scope?.isMethodologyGroup && groupedProposalScopes.length > 0 ? groupedProposalScopes.join(' | ') : (projectStage?.scope || scope.label || '-'),
            isSustainability,
            registrationNumber: proposalRegistrationNumber || '-',
            ghgCalculation: proposalGhgCalculation || '-',
            inputMaterial: proposalInputMaterial || '-',
            outputMaterial: proposalOutputMaterial || '-',
            done: scopeWorkflowProgress.currentMajorId === 'audit-completed',
            schedulingCompleted: scopeSchedulingCompleted,
            workflowProgress: scopeWorkflowProgress,
            isFullyCompleted: isWorkflowFullyCompleted(scopeWorkflowProgress),
            hasOpenWorkflowTasks,
          };
        });

      const rawStages = sortAuditCyclesByDate(scopeDrivenStages.length > 0
        ? scopeDrivenStages
        : (hasProjectStandards ? projectBasedStages : fallbackTaskStages))
        .filter((stage) => !isCmfScope(stage?.serviceItem, stage?.stageName));
      // Activation follows workflow completion, not literal calendar date -- a cycle
      // whose Certificate has already been issued hands off to the next cycle
      // immediately (see resolveCurrentCycleKey), instead of staying "active" until
      // its own Audit Date happens to pass. For a fresh/in-progress project (nothing
      // completed yet) this still resolves to the same date-sorted first stage as before.
      const currentStageKey = resolveCurrentCycleKey(rawStages) || rawStages[0]?.key || null;
      // Activation is PER TRACK, not per project. A Single Audit sold with several
      // standards runs one independent audit per standard, so each standard keeps
      // its own active cycle; an Integrated/Combined group is one shared track.
      // Without this, the standard that happens to sort first owned the only
      // "active" cycle and every other standard's live cycle was demoted to
      // "Upcoming Audit Cycle" and locked out of the document scope selector.
      const activeCycleKeys = resolveActiveCycleKeys(rawStages);
      const activeStageKeys = activeCycleKeys.length > 0
        ? activeCycleKeys
        : [currentStageKey].filter((key) => key != null);
      // A completed cycle keeps its own "Audit Completed" badge; only cycles that
      // have not run yet become "Upcoming Audit Cycle" -- see resolveCycleDisplayState.
      const mappedStages = rawStages.map((stage) => ({
        ...stage,
        ...resolveCycleDisplayState(stage, activeStageKeys),
      }));

      // A reload must not steal an explicit scope selection: this runs again on every
      // savedFormDataMap change (after every form save), and `currentStageKey` is the
      // project-global pointer (resolveCurrentCycleKey), so on a multi-track project it
      // is regularly a different standard's cycle than the one being worked on.
      const retainedScopeKey = (selectedSchedulingScopeKeyRef.current
        && selectedSchedulingScopeKeyRef.current !== '0:0'
        && scopes.some((scope) => scope.key === selectedSchedulingScopeKeyRef.current))
        ? selectedSchedulingScopeKeyRef.current
        : null;

      setStages(mappedStages);
      // A form save triggers a full refetch (see the savedFormDataMap effect
      // below). Keep whichever card(s) the user had open across that refetch
      // instead of collapsing back to only the active cycle every time.
      setExpandedStages((previous) => {
        const stillOpenKeys = Object.keys(previous || {})
          .filter((key) => previous[key] && mappedStages.some((stage) => stage.key === key));
        const openKeys = stillOpenKeys.length > 0 ? stillOpenKeys : activeStageKeys;
        return mappedStages.reduce((acc, stage) => ({ ...acc, [stage.key]: openKeys.includes(stage.key) }), {});
      });

      // The scheduling wizard must open on the same cycle selected by the
      // workflow resolver, not simply the first date-sorted scope (which may
      // already be completed after activation advances) -- UNLESS this is a
      // post-save refetch of the same project, in which case the user's own
      // selection (still valid in the rebuilt scope list) wins so a save does
      // not snap the selector back to Stage 1 (see resolveActiveScopeKey/
      // autoSelectedScopeRef for the auto-select side of this).
      const isSameProjectRefetch = selectionOwnerProjectRef.current === String(parsedProjectId);
      const preservedScopeKey = isSameProjectRefetch
        && scopes.some((scope) => scope.key === selectedSchedulingScopeKey)
        ? selectedSchedulingScopeKey
        : null;
      const resolverScopeKey = (currentStageKey && scopes.some((scope) => scope.key === currentStageKey))
        ? currentStageKey
        : (scopes[0]?.key || getSchedulingScopeKey(null, null));
      const defaultScopeKey = preservedScopeKey || resolverScopeKey;
      selectionOwnerProjectRef.current = String(parsedProjectId);
      // Resolve against the scope the Scheduling wizard will actually default to
      // (defaultScopeKey), not just mappedStages[0] -- they normally match, but
      // resolving explicitly keeps this in lockstep with resolveScopeMandaysLimit's
      // selected-scope-wins contract used everywhere else.
      setSchedulingScopeMandaysLimit(resolveScopeMandaysLimit(mappedStages, defaultScopeKey));
      let recoveredDefaultDraft = null;
      scopes.forEach((scope) => {
        const draft = loadSchedulingWizardDraft({ projectId: parsedProjectId, scopeKey: scope.key });
        if (!draft) return;
        nextSchedulingCache[scope.key] = normalizeSchedulingData(draft.data, nextSiteOptions);
        if (scope.key === defaultScopeKey) recoveredDefaultDraft = draft;
      });
      if (!recoveredDefaultDraft) {
        recoveredDefaultDraft = loadSchedulingWizardDraft({ projectId: parsedProjectId, scopeKey: defaultScopeKey });
        if (recoveredDefaultDraft) {
          nextSchedulingCache[defaultScopeKey] = normalizeSchedulingData(recoveredDefaultDraft.data, nextSiteOptions);
        }
      }
      setSchedulingDataByScope(nextSchedulingCache);
      setSelectedSchedulingScopeKey(defaultScopeKey);
      applySchedulingScopeData(defaultScopeKey, nextSchedulingCache, nextSiteOptions);
      if (recoveredDefaultDraft) setActiveSchedulingSubTab(recoveredDefaultDraft.activeStep);
      schedulingDraftReadyRef.current = true;

      const fallbackContacts = buildAuditContactData(project, leadDetail);
      try {
        const contactsRes = await api.get(`/audit/projects/${parsedProjectId}/contacts`);
        setAuditContacts(normalizeContactPayload(contactsRes?.data, fallbackContacts));
        setContactValidationErrors({});
      } catch (contactError) {
        console.warn('Failed to load audit contacts, using fallback derived data:', contactError);
        setAuditContacts(fallbackContacts);
        setContactValidationErrors({});
      }
    } catch (error) {
      console.error('Failed to load audit process data:', error);
      setPageError('Failed to load audit process data.');
      setSalesPackDocuments([]);
      setAuditContacts({
        representative: { person: '-', phone: '-', email: '-' },
        correspondence: { person: '-', phone: '-', email: '-', sameAsRepresentative: true },
        finance: { person: '-', phone: '-', email: '-', sameAsRepresentative: true },
        certificate: { person: '-', phone: '-', email: '-', sameAsRepresentative: true },
      });
      const fallbackScopeKey = getSchedulingScopeKey(null, null);
      const fallbackData = createDefaultSchedulingData();
      setSchedulingScopes([{ key: fallbackScopeKey, projectServiceId: null, projectServiceStandardId: null, standardId: null, label: 'General Project Scope' }]);
      setSchedulingDataByScope({ [fallbackScopeKey]: fallbackData });
      setAuditorOptions([]);
      setAuditorQualificationOptions([]);
      setSelectedSchedulingScopeKey(fallbackScopeKey);
      applySchedulingScopeData(fallbackScopeKey, { [fallbackScopeKey]: fallbackData });
      const fallbackDraft = loadSchedulingWizardDraft({ projectId: parsedProjectId, scopeKey: fallbackScopeKey });
      if (fallbackDraft) {
        const recoveredData = normalizeSchedulingData(fallbackDraft.data);
        setSchedulingDataByScope({ [fallbackScopeKey]: recoveredData });
        applySchedulingScopeData(fallbackScopeKey, { [fallbackScopeKey]: recoveredData });
        setActiveSchedulingSubTab(fallbackDraft.activeStep);
      }
      schedulingDraftReadyRef.current = true;
      setSchedulingScopeMandaysLimit(null);
      setContactValidationErrors({});
    } finally {
      setIsLoadingPage(false);
    }
  };

  useEffect(() => {
    // A new project needs a fresh auto-selected stage/cycle, not whatever the
    // previous project last resolved to.
    autoSelectedScopeRef.current = null;
    loadAuditProcessData();
    loadEaCodeOptions();
    loadSavedFormData();
  }, [projectId]);

  // Cycle activation depends on the saved FTRD-01 decision. The initial page
  // load fetches project/tasks and form data in parallel, so the first cycle
  // calculation can briefly assume certificate issuance is still required.
  // Rebuild the cycle cards once scoped form data arrives; otherwise an
  // explicit "Continue Without Certificate" decision leaves the completed
  // cycle looking like an upcoming cycle after the next cycle activates.
  useEffect(() => {
    if (!projectId || Object.keys(savedFormDataMap || {}).length === 0) return;
    loadAuditProcessData();
  }, [projectId, savedFormDataMap]);

  // Auto-refresh project history when user switches back to this tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && projectId) {
        loadProjectHistory();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Auto-poll every 30 seconds while page is open
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') loadProjectHistory();
    }, 30000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, [projectId]);

  useEffect(() => {
    if (!checklistScopeKey) return;
    loadChecklistForScope(checklistScopeKey);
  }, [checklistScopeKey, loadChecklistForScope]);

  // Clear the "Waiting for AR approval" panel once Application Review has made a fresh
  // decision (approved/approved_with_notes/rejected) for the scope Sales resubmitted.
  useEffect(() => {
    if (!awaitingArApprovalScopeKey) return;
    if (checklistScopeKey !== awaitingArApprovalScopeKey) return;
    if (applicationReviewStatus && applicationReviewStatus !== 'draft') {
      setAwaitingArApprovalScopeKey(null);
    }
  }, [applicationReviewStatus, checklistScopeKey, awaitingArApprovalScopeKey]);

  // Load latest TR decision when entering technical-review tab (Fase 5.3)
  useEffect(() => {
    if (activeDocTab !== 'technical-review' || !projectId) return;
    api.get(`/audit/projects/${projectId}/tr-decision`)
      .then(res => { if (res.data?.found) setTrDecisionData(res.data); })
      .catch(() => {});
  }, [activeDocTab, projectId]); // eslint-disable-line

  useEffect(() => {
    if (!selectedSchedulingScopeKey) return;
    setSchedulingDataByScope((prev) => ({
      ...prev,
      [selectedSchedulingScopeKey]: {
        siteLocations,
        auditSchedules,
        accommodations,
      },
    }));
  }, [selectedSchedulingScopeKey, siteLocations, auditSchedules, accommodations]);

  useEffect(() => {
    if (!schedulingDraftReadyRef.current || !projectId || !selectedSchedulingScopeKey || !canEditScheduling) return;
    if (schedulingDraftSuppressedScopeRef.current === selectedSchedulingScopeKey) {
      schedulingDraftSuppressedScopeRef.current = null;
      return;
    }
    saveSchedulingWizardDraft({
      projectId,
      scopeKey: selectedSchedulingScopeKey,
      activeStep: activeSchedulingSubTab,
      data: { siteLocations, auditSchedules, accommodations },
    });
  }, [
    accommodations,
    activeSchedulingSubTab,
    auditSchedules,
    canEditScheduling,
    projectId,
    selectedSchedulingScopeKey,
    siteLocations,
  ]);

  useEffect(() => {
    const selectedScope = getSelectedSchedulingScope();
    const allowedAuditors = getAvailableAuditorsForScope(selectedScope);
    const allowedAuditorIds = new Set(allowedAuditors.map((row) => String(row?.userId)));

    setAuditSchedules((prev) => prev.map((schedule) => {
      const hasAuditor = schedule?.employeeId && allowedAuditorIds.has(String(schedule.employeeId));
      const nextSchedule = hasAuditor
        ? schedule
        : { ...schedule, employeeId: '', employeeName: '', role: '' };

      const allowedRoles = getRoleOptionsForSchedule(nextSchedule, selectedScope);
      const isCurrentRoleValid = allowedRoles.some((row) => row?.qualification === nextSchedule.role);
      if (isCurrentRoleValid || !nextSchedule.role) return nextSchedule;
      return {
        ...nextSchedule,
        role: allowedRoles[0]?.qualification || '',
      };
    }));
  }, [selectedSchedulingScopeKey, auditorOptions, auditorQualificationOptions]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openDatePickerScheduleId && !e.target.closest('.audit-process-date-range-picker-wrapper')) {
        setOpenDatePickerScheduleId(null);
      }
    };
    if (openDatePickerScheduleId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openDatePickerScheduleId]);

  useEffect(() => {
    if (activeSchedulingSubTab !== 'audit-schedule') return;

    const limit = schedulingScopeMandaysLimit;
    if (limit == null) return;

    const total = getTotalScheduledMandays(auditSchedules);
    if (total <= limit) {
      if (lastMandaysWarningKey) setLastMandaysWarningKey('');
      return;
    }

    const warningKey = `${limit}:${total}`;
    if (warningKey === lastMandaysWarningKey) return;

    setLastMandaysWarningKey(warningKey);
    showAuditNotification(
      `Warning: Total Mandays in Audit Schedule (${total}) exceeds top Mandays limit (${limit}).`,
      { title: 'Scheduling Validation', type: 'warning' }
    );
  }, [activeSchedulingSubTab, auditSchedules, schedulingScopeMandaysLimit, lastMandaysWarningKey]);

  // Auto-select Scheduling Scope as soon as it's known — NOT gated on the Application
  // Review Checklist tab being active. applicationReviewStatus/applicationReviewFollowup
  // (loaded via loadChecklistForScope, keyed off checklistScopeKey) drive the revision
  // card on the Sales Pack tab too, so waiting for the user to visit the checklist tab
  // first meant that card silently didn't appear until after a tab switch.
  // AR checklist is ONE per standard — never split by Stage 1/Stage 2, so we
  // collapse any Initial Certification stage suffix (:s1/:s2) to the base key.
  useEffect(() => {
    // '0:0' is the placeholder selectedSchedulingScopeKey starts as (getSchedulingScopeKey(null,
    // null)) before real scheduling data loads -- treat it as "not seeded yet" too, otherwise
    // this fires once on mount with the placeholder and then never re-syncs once the real scope
    // key arrives, leaving checklistScopeKey permanently stuck at '0:0'.
    if (selectedSchedulingScopeKey && selectedSchedulingScopeKey !== '0:0'
      && (!checklistScopeKey || checklistScopeKey === '0:0')) {
      setChecklistScopeKey(resolveBaseScheduleKey(selectedSchedulingScopeKey));
    }
  }, [selectedSchedulingScopeKey, checklistScopeKey]);

  // Auto-expand Audit stage when workflow is at Audit Plan or later
  useEffect(() => {
    if (stages.length === 0) return;
    
    const workflowProgress = buildAuditWorkflowProgress(projectWorkflowTasks);
    const currentMajorId = workflowProgress?.currentMajorId;
    
    // Check if workflow is at 'audit' stage or later (not at initial-certification)
    if (currentMajorId === 'audit' || currentMajorId === 'technical-review' || currentMajorId === 'certificate-issuance' || currentMajorId === 'audit-completed') {
      const auditStage = stages.find((stage) => stage?.stageName?.toLowerCase().includes('audit'));
      if (auditStage) {
        // Auto-expand audit stage and switch to audit tab
        setExpandedStages((prev) => ({ ...prev, [auditStage.key]: true }));
        // Only auto-switch to audit tab if not already on another important tab
        if (!['scheduling', 'checklist-verification'].includes(activeDocTab)) {
          setActiveDocTab('audit');
        }
      }
    }
  }, [stages, projectWorkflowTasks]);

  // Check if the selected scope's review was already completed when tasks change.
  // This must never inspect the first completed APPLICATION_REVIEW task on the
  // project, because a later Surveillance/Recertification cycle has its own
  // checklist and must remain independent.
  useEffect(() => {
    if (projectWorkflowTasks.length === 0 || !checklistScopeKey) return;
    const checklistScope = (schedulingScopes || []).find((scope) => (
      scope?.key === checklistScopeKey
      || resolveBaseScheduleKey(scope?.key) === resolveBaseScheduleKey(checklistScopeKey)
    )) || checklistScopeKey;
    const reviewTask = selectTasksForAuditScope(projectWorkflowTasks, checklistScope).find((task) =>
      String(task?.taskTypeCode || '').toUpperCase() === 'APPLICATION_REVIEW'
    );
    if (reviewTask && isTaskDone(reviewTask.status)) {
      setReviewCompleted(true);
      setChecklistSaved(true);
    }
  }, [projectWorkflowTasks, checklistScopeKey, schedulingScopes]);

  const toggleStage = (stage) => {
    const willOpen = !expandedStages[stage];
    setExpandedStages((prev) => {
      const shouldOpen = !prev[stage];
      if (!shouldOpen) {
        return { ...prev, [stage]: false };
      }

      const next = {};
      stages.forEach((item) => {
        next[item.key] = item.key === stage;
      });
      return next;
    });

    // Auto-select the audit-scope dropdown to match the card just opened, so the
    // Documents/Forms status (green checks) follows the active/toggled card. Only
    // auto-select when the card belongs to the currently active audit process — the
    // dropdown is locked to that process (upcoming cycles are view-only).
    if (willOpen) {
      const toggledBase = resolveBaseScheduleKey(stage);
      const activeBase = resolveBaseScheduleKey(currentAuditCycle?.key || '');
      // For a multi-stage cycle (Initial Cert), land on the currently ACTIVE stage's
      // scope (the in-progress Stage 1 or Stage 2) instead of always the first stage.
      const activeStageMatch = activeBase && toggledBase === activeBase && currentAuditCycle?.key
        ? schedulingScopes.find((s) => s.key === currentAuditCycle.key)
        : null;
      const match = activeStageMatch
        || schedulingScopes.find((s) => s.key === stage)
        || schedulingScopes.find((s) => resolveBaseScheduleKey(s.key) === toggledBase);
      if (match && (!activeBase || resolveBaseScheduleKey(match.key) === activeBase)) {
        setDocTabScopeKey(match.key);
      }
    }
  };

  const handleEditStage = (e, stage) => {
    e.stopPropagation();
    setEditingStageKey(stage.key);
    setEditForm({
      project: stage.project || '',
      serviceItem: stage.serviceItem || '',
      eaCode: splitEaCodeText(stage.eaCode),
      mandays: stage.mandays || '',
      scope: stage.scope || '',
    });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingStageKey(null);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditSubmit = () => {
    if (!editingStageKey) {
      showAuditNotification('Failed to save. Please re-open the stage and try again.', { title: 'Edit Validation', type: 'danger' });
      return;
    }

    const nextMandays = String(editForm.mandays || '').trim();
    if (nextMandays && Number.isNaN(Number(nextMandays))) {
      showAuditNotification('Mandays must be numeric value.', { title: 'Edit Validation', type: 'danger' });
      return;
    }

    let found = false;
    const nextEaCode = Array.isArray(editForm.eaCode) ? editForm.eaCode.join(', ') : (editForm.eaCode || '-');
    setStages((prev) => prev.map((stage) => {
      if (stage.key !== editingStageKey) return stage;
      found = true;
      return {
        ...stage,
        ...editForm,
        eaCode: nextEaCode || '-',
        mandays: nextMandays,
      };
    }));

    if (!found) {
      showAuditNotification('Failed to save. Stage not found.', { title: 'Edit Validation', type: 'danger' });
      return;
    }

    closeEditModal();
  };

  const findStageByScopeKey = (scopeKey) => {
    if (!scopeKey) return null;
    const exact = stages.find((stage) => stage.key === scopeKey);
    if (exact) return exact;

    // Initial Certification Stage 1/Stage 2 share one collapsed base key here (AR checklist
    // is one per standard), so multiple stage entries in `stages` can match by base key alone.
    // Prefer whichever one the user actually has selected/expanded in Scheduling, otherwise
    // fall back to the first match, to avoid silently showing the sibling stage's data.
    const candidates = stages.filter((stage) => resolveBaseScheduleKey(stage.key) === scopeKey);
    if (candidates.length <= 1) return candidates[0] || null;
    // Initial Certification Stage 1/Stage 2 both render at once under one collapsed AR checklist
    // scope, so there's no single "current" cycle to fall back on -- prefer whichever specific
    // stage (:s1/:s2) the user actually has selected elsewhere on this page. docTabScopeKey is the
    // most recently touched stage-aware selector (Sales Pack/Audit/Certificate tabs all use it and
    // let the user explicitly pick Stage 1 vs Stage 2); selectedSchedulingScopeKey is the same kind
    // of selector scoped to the Scheduling tab. Only fall back to the first match if neither points
    // at one of these candidates.
    return candidates.find((stage) => stage.key === docTabScopeKey)
      || candidates.find((stage) => stage.key === selectedSchedulingScopeKey)
      || candidates[0];
  };

  // A field (scope/eaCode/mandays) can only be unlocked for editing when its matching
  // checklist item (#1/#2/#3) is actually flagged "Need Revision" — an AR/Sales/Scheduler
  // fixing one flagged field shouldn't be able to also change fields nobody flagged.
  const canEditChecklistField = (fieldKey) => {
    const roleAllowed = hasRole('ROLE_SALES') || hasRole('ROLE_SCHEDULER') || hasRole('ROLE_APPLICATION_REVIEW') || hasRole('ROLE_ADMIN');
    if (!roleAllowed) return false;
    return checklistValues[CHECKLIST_FIELD_ITEM_ID[fieldKey]] === 'revisi';
  };

  // Saving Scope / EA Code / Mandays from the "View Detail" modal always asks first whether
  // the change needs an addendum -- validate here, then hand off to the confirmation modal
  // (Yes / No / Cancel) rather than writing the change immediately.
  const handleChecklistFieldSave = () => {
    if (!checklistFieldStageKey) {
      setChecklistFieldEditableRows({ scope: false, eaCode: false, mandays: false });
      return;
    }

    const nextMandays = String(checklistFieldValues.mandays || '').trim();
    if (nextMandays && Number.isNaN(Number(nextMandays))) {
      showAuditNotification('Mandays must be numeric value.', { title: 'Edit Validation', type: 'danger' });
      return;
    }

    setChecklistSaveConfirmOpen(true);
  };

  const handleChecklistFieldSaveCancel = () => {
    setChecklistSaveConfirmOpen(false);
  };

  const handleChecklistFieldSaveConfirm = async (requiresAddendum) => {
    setChecklistSaveConfirmOpen(false);

    if (!checklistFieldStageKey) {
      setChecklistFieldEditableRows({ scope: false, eaCode: false, mandays: false });
      return;
    }

    const targetStage = findStageByScopeKey(checklistFieldStageKey);
    const standardId = targetStage?.projectServiceStandardId;
    const nextMandays = String(checklistFieldValues.mandays || '').trim();
    const nextEaCode = Array.isArray(checklistFieldValues.eaCode)
      ? checklistFieldValues.eaCode.join(', ')
      : checklistFieldValues.eaCode;
    const nextScope = checklistFieldValues.scope || targetStage?.scope || '';

    // Once Audit Scheduling already has mandays entered for a (non-staged) scope, THAT scheduled
    // value -- not the proposal's standard.qty total -- is what the card shows (see
    // resolveStageMandaysDisplay in initialCertMandaysUtils.js). Editing Mandays here in that
    // case must reallocate the already-scheduled auditor row(s) via the dedicated endpoint below,
    // not overwrite standard.qty -- that would silently corrupt the split and have no visible
    // effect on the card anyway.
    //
    // Initial Certification Stage 1/Stage 2 is a special case of this: both stages share ONE
    // standard.qty total, and BOTH almost always already have their own auto-booked scheduled
    // mandays by the time AR opens this view (see AuditScheduleAutoBookingService's
    // seedOrBackfillInitialCertStages, backend). Editing "Mandays" here must therefore treat the
    // entered value as the TOTAL to re-split (Stage 1 = 1 fixed / Stage 2 = remainder), and push
    // each stage's new share into whichever of the two already has real scheduled rows -- not
    // just the one stage being edited, otherwise the sibling silently keeps showing its old
    // number (or the raw unsplit total lands on the edited stage alone).
    const isInitialCertStagedEdit = Boolean(resolveScopeStageKey(checklistFieldStageKey));
    const editingScheduledMandays = !isInitialCertStagedEdit
      && checklistFieldEditableRows.mandays && checklistFieldValues.mandaysLockedByScheduling;

    // Previously this only updated local React state (setStages) and never reached the
    // backend -- Scope / EA Code / Mandays edited here silently reverted on refresh. Persist
    // to project_service_standards via PATCH before updating local state.
    if (!standardId) {
      showAuditNotification(
        'Could not save: this stage is not linked to a project standard yet.',
        { title: 'Save Failed', type: 'danger' }
      );
      return;
    }

    try {
      const scopeDetailsBody = {
        scope: nextScope,
        eaCodeSectorIds: mapEaCodeLabelsToSectorIds(checklistFieldValues.eaCode),
        requiresAddendum,
        applyToNextCycle: applyScopeEaCodeToNextCycle,
      };
      // For Initial Certification the total (standard.qty) always needs to resync to what was
      // typed -- it's the source the split derives from, regardless of whether Scheduling also
      // has its own per-stage rows to update (handled separately below).
      if (!editingScheduledMandays) {
        scopeDetailsBody.mandays = nextMandays;
      }

      const response = await api.patch(`/crm/projects/standards/${standardId}/scope-details`, scopeDetailsBody);
      const saved = response?.data || {};
      const nextCycle = saved.nextCycle || null;

      let scheduledMandaysResult = null;
      if (editingScheduledMandays) {
        const mandaysResponse = await api.patch(
          `/audit/projects/${projectId}/schedules/${checklistFieldStageKey}/mandays`,
          { mandays: nextMandays }
        );
        scheduledMandaysResult = mandaysResponse?.data || null;
        if (Array.isArray(scheduledMandaysResult?.auditSchedules)) {
          setSchedulingDataByScope((prev) => {
            const existingRows = prev?.[checklistFieldStageKey]?.auditSchedules || [];
            const updatedById = new Map(
              scheduledMandaysResult.auditSchedules.map((row) => [String(row.employeeId), row])
            );
            const mergedRows = existingRows.map((row) => updatedById.get(String(row.employeeId)) || row);
            return {
              ...prev,
              [checklistFieldStageKey]: {
                ...prev?.[checklistFieldStageKey],
                auditSchedules: mergedRows,
              },
            };
          });
        }
      }

      const stagedBaseKey = isInitialCertStagedEdit ? resolveBaseScheduleKey(checklistFieldStageKey) : null;
      const stagedSiblingKey = isInitialCertStagedEdit
        ? (resolveScopeStageKey(checklistFieldStageKey) === 'stage1' ? `${stagedBaseKey}:s2` : `${stagedBaseKey}:s1`)
        : null;
      const stagedTotalMandays = saved.mandays != null ? saved.mandays : nextMandays;

      let editedStageMandaysDisplay = null;
      let siblingStageMandaysDisplay = null;

      if (isInitialCertStagedEdit) {
        const split = splitInitialCertMandays(stagedTotalMandays);
        const editedStageKind = resolveScopeStageKey(checklistFieldStageKey);
        const siblingStageKind = resolveScopeStageKey(stagedSiblingKey);
        const editedShare = split ? (editedStageKind === 'stage2' ? split.stage2Mandays : split.stage1Mandays) : null;
        const siblingShare = split ? (siblingStageKind === 'stage2' ? split.stage2Mandays : split.stage1Mandays) : null;

        // Push the freshly-split share into each stage's own AuditSchedule rows, but only for a
        // stage that actually already has scheduled auditor rows -- a stage never scheduled yet
        // has nothing to update and simply shows the split via resolveStageMandaysDisplay below.
        const editedHasSchedule = getTotalScheduledMandays(schedulingDataByScope?.[checklistFieldStageKey]?.auditSchedules) > 0;
        const siblingHasSchedule = getTotalScheduledMandays(schedulingDataByScope?.[stagedSiblingKey]?.auditSchedules) > 0;

        let editedScheduledResult = null;
        let siblingScheduledResult = null;

        if (editedHasSchedule && editedShare != null) {
          const res = await api.patch(
            `/audit/projects/${projectId}/schedules/${checklistFieldStageKey}/mandays`,
            { mandays: editedShare }
          );
          editedScheduledResult = res?.data || null;
        }
        if (siblingHasSchedule && siblingShare != null) {
          const res = await api.patch(
            `/audit/projects/${projectId}/schedules/${stagedSiblingKey}/mandays`,
            { mandays: siblingShare }
          );
          siblingScheduledResult = res?.data || null;
        }

        setSchedulingDataByScope((prev) => {
          let next = prev;
          if (Array.isArray(editedScheduledResult?.auditSchedules)) {
            const existingRows = next?.[checklistFieldStageKey]?.auditSchedules || [];
            const updatedById = new Map(editedScheduledResult.auditSchedules.map((row) => [String(row.employeeId), row]));
            next = {
              ...next,
              [checklistFieldStageKey]: {
                ...next?.[checklistFieldStageKey],
                auditSchedules: existingRows.map((row) => updatedById.get(String(row.employeeId)) || row),
              },
            };
          }
          if (Array.isArray(siblingScheduledResult?.auditSchedules)) {
            const existingRows = next?.[stagedSiblingKey]?.auditSchedules || [];
            const updatedById = new Map(siblingScheduledResult.auditSchedules.map((row) => [String(row.employeeId), row]));
            next = {
              ...next,
              [stagedSiblingKey]: {
                ...next?.[stagedSiblingKey],
                auditSchedules: existingRows.map((row) => updatedById.get(String(row.employeeId)) || row),
              },
            };
          }
          return next;
        });

        editedStageMandaysDisplay = resolveStageMandaysDisplay({
          stageKey: editedStageKind,
          totalMandays: stagedTotalMandays,
          scheduledMandays: editedScheduledResult?.mandays != null
            ? editedScheduledResult.mandays
            : getTotalScheduledMandays(schedulingDataByScope?.[checklistFieldStageKey]?.auditSchedules),
        });
        siblingStageMandaysDisplay = resolveStageMandaysDisplay({
          stageKey: siblingStageKind,
          totalMandays: stagedTotalMandays,
          scheduledMandays: siblingScheduledResult?.mandays != null
            ? siblingScheduledResult.mandays
            : getTotalScheduledMandays(schedulingDataByScope?.[stagedSiblingKey]?.auditSchedules),
        });
      }

      setStages((prev) => prev.map((stage) => {
        if (stage.key === checklistFieldStageKey) {
          return {
            ...stage,
            eaCode: saved.eaCode || nextEaCode || stage.eaCode,
            scope: saved.scope || nextScope || stage.scope,
            mandays: isInitialCertStagedEdit
              ? (editedStageMandaysDisplay && editedStageMandaysDisplay !== '-' ? editedStageMandaysDisplay : stage.mandays)
              : editingScheduledMandays
                ? (scheduledMandaysResult?.mandays != null ? String(scheduledMandaysResult.mandays) : (nextMandays || stage.mandays))
                : (saved.mandays != null ? String(saved.mandays) : (nextMandays || stage.mandays)),
            requiresAddendum,
          };
        }
        if (isInitialCertStagedEdit && stage.key === stagedSiblingKey) {
          return {
            ...stage,
            mandays: siblingStageMandaysDisplay && siblingStageMandaysDisplay !== '-' ? siblingStageMandaysDisplay : stage.mandays,
          };
        }
        if (nextCycle && String(stage.projectServiceStandardId) === String(nextCycle.standardId)) {
          return {
            ...stage,
            scope: nextCycle.scope || stage.scope,
            eaCode: nextCycle.eaCodeSkipped ? stage.eaCode : (nextCycle.eaCode || stage.eaCode),
          };
        }
        return stage;
      }));

      setChecklistFieldEditableRows({ scope: false, eaCode: false, mandays: false });
      setApplyScopeEaCodeToNextCycle(false);
      let cascadeSuffix = '';
      if (saved.cascadedToNextCycle) {
        cascadeSuffix = nextCycle?.eaCodeSkipped
          ? ' Scope applied to the next audit cycle, but its EA Code could not be updated (that cycle is not linked to a proposal standard).'
          : ' Also applied to the next audit cycle.';
      }
      showAuditNotification(
        (requiresAddendum
          ? 'Changes saved. Marked as requiring an addendum.'
          : 'Changes saved. No addendum required.') + cascadeSuffix,
        { title: 'Scope / EA Code / Mandays Updated', type: 'success' }
      );
    } catch (saveError) {
      console.error('Failed to save scope/EA code/mandays:', saveError);
      showAuditNotification(
        saveError?.response?.data?.message || saveError?.response?.data?.error || 'Failed to save changes. Please try again.',
        { title: 'Save Failed', type: 'danger' }
      );
    }
  };

  const projectOptions = [projectMeta.auditTitle].filter(Boolean);
  const serviceItemOptions = Array.from(new Set(stages.map((s) => s.serviceItem).filter(Boolean)));

  const eaCodeSelectOptions = Array.from(
    new Map(
      [
        ...(Array.isArray(eaCodeApiOptions) ? eaCodeApiOptions : []),
        ...stages.map((s) => ({ code: '', name: s.eaCode })).filter((r) => r.name),
      ].map((record) => [formatSectorLabel(record), record])
    ).values()
  ).map((record) => ({ value: formatSectorLabel(record), label: formatSectorLabel(record) }));

  // Maps a displayed "CODE - Name" label back to its real sector id, so a multi-select of
  // labels can be turned into eaCodeSectorIds for the backend PATCH. Only built from real
  // sector records (eaCodeApiOptions) -- legacy display-only strings pulled from `stages` have
  // no backing sector id and are simply omitted (skipped, never sent as a bogus id).
  const eaCodeLabelToSectorId = new Map(
    (Array.isArray(eaCodeApiOptions) ? eaCodeApiOptions : [])
      .filter((record) => record?.id != null)
      .map((record) => [formatSectorLabel(record), record.id])
  );
  const mapEaCodeLabelsToSectorIds = (labels) => (
    (Array.isArray(labels) ? labels : [])
      .map((label) => eaCodeLabelToSectorId.get(label))
      .filter((id) => id != null)
  );

  const anyModalOpen = editModalOpen || !!confirmModalType || previewModalOpen || contactModalOpen || schedulingAlertModalOpen || checklistSaveConfirmOpen;
  useModalEffect(anyModalOpen);

  useEffect(() => {
    const uploadableTabs = DOCUMENT_TABS.filter(
      (t) => t.id !== 'sales-pack' && t.id !== 'checklist-verification' && t.id !== 'scheduling'
    );
    const isUploadableTab = uploadableTabs.some((t) => t.id === activeDocTab);
    if (!isUploadableTab) return;
    if (auditDocuments[activeDocTab] !== undefined) return;
    loadAuditDocuments(activeDocTab);
  // eslint-disable-next-line
  }, [activeDocTab, projectId]);

  useEffect(() => {
    if (activeDocTab === 'scheduling' && auditDocuments.scheduling === undefined) {
      loadAuditDocuments('scheduling');
    }
  }, [activeDocTab, auditDocuments.scheduling, loadAuditDocuments]);

  // 'sales-pack' is excluded from the tab-based auto-loader above (it's not one of the
  // generic document tabs — it renders via the revision panel instead), so it needs its
  // own load call independent of activeDocTab. Without this, uploaded CID 1/CID 2 revision
  // files only ever appeared in state right after upload and vanished on refresh, since
  // nothing else ever re-fetched this category.
  useEffect(() => {
    if (projectId && auditDocuments['sales-pack'] === undefined) {
      loadAuditDocuments('sales-pack');
    }
  }, [projectId, auditDocuments['sales-pack'], loadAuditDocuments]);

  // "Reduction of Type (/Integrated)" has nothing for the reviewer to decide on a project
  // audited against a single standard -- there is no second type to reduce against. It is
  // auto-answered OK (the checklist stores a boolean `passed`, so OK is how "nothing to
  // object to" is expressed) and locked, with an explicit N/A badge in the row so the lock
  // reads as deliberate rather than broken.
  const isReductionOfTypeNA = useMemo(
    () => isReductionOfTypeNotApplicable(schedulingScopes),
    [schedulingScopes],
  );

  // Keyed on the item's own current value, not just the scope: loadChecklistForScope() resets
  // checklistValues to {} twice per scope change (immediately, then again when the response
  // lands). Depending only on the scope key meant the auto-answer ran once and was then wiped
  // by that async load, leaving item 8 blank with both radios disabled -- which blocked Save
  // and Approve outright, with no control the reviewer could use to recover.
  const reductionOfTypeValue = checklistValues[REDUCTION_OF_TYPE_ITEM_ID];

  useEffect(() => {
    if (!isReductionOfTypeNA || reductionOfTypeValue) return;
    // applyReductionOfTypeAutoAnswer only fills an unanswered item, so a decision already
    // recorded for this scope (including one made while the project still had several
    // standards) is left exactly as it is.
    setChecklistValues((prev) => applyReductionOfTypeAutoAnswer(prev, true));
  }, [isReductionOfTypeNA, reductionOfTypeValue, checklistScopeKey]);

  const setChecklistItem = (itemId, value) => {
    setChecklistValues((prev) => ({ ...prev, [itemId]: value }));
  };

  const setChecklistNote = (itemId, note) => {
    setChecklistNotes((prev) => ({ ...prev, [itemId]: note }));
  };

  const hasRevisionItems = () => {
    return CHECKLIST_ITEMS.some((item) => checklistValues[item.id] === 'revisi');
  };

  const canSaveChecklist = () => {
    if (!checklistScopeKey) return false;

    // Save is the final decision for this scope, so every item must be reviewed.
    const allItemsSelected = CHECKLIST_ITEMS.every((item) => checklistValues[item.id] === 'ok' || checklistValues[item.id] === 'revisi');
    if (!allItemsSelected) return false;

    // If any revision items exist, they must have notes
    for (const item of CHECKLIST_ITEMS) {
      if (checklistValues[item.id] === 'revisi') {
        const note = (checklistNotes[item.id] ?? '').trim();
        if (!note) return false;
      }
    }
    return true;
  };

  const canAgreeAndContinue = () => {
    for (const item of CHECKLIST_ITEMS) {
      const value = checklistValues[item.id];
      if (value !== 'ok' && value !== 'revisi') return false;
      if (value === 'revisi') {
        const note = (checklistNotes[item.id] ?? '').trim();
        if (!note) return false;
      }
    }
    return true;
  };

  const APPLICATION_REVIEW_STATUS_LABELS = {
    draft: 'Draft',
    approved: 'Approved',
    approved_with_notes: 'Approved with Notes',
    rejected: 'Rejected',
  };

  // Single place that updates the Application Review Status: sets in-memory
  // state for instant feedback (the backend has already persisted the same
  // value on the matching AuditTask.review_decision column as a side effect
  // of the endpoint call this follows — see AuditChecklistService on the
  // backend) and logs it to the project history/activity feed so it's always
  // visible to any user what happened and when.
  const recordApplicationReviewStatus = async (status, scope) => {
    setApplicationReviewStatus(status);
    const label = APPLICATION_REVIEW_STATUS_LABELS[status] || status;
    await api.post(`/audit/projects/${projectId}/history-process`, {
      processName: 'APPLICATION_REVIEW_STATUS',
      actionType: `APPLICATION_REVIEW_${status.toUpperCase()}`,
      description: `Application Review Status set to ${label}.`,
    }).catch(() => console.warn('Failed to log Application Review Status to history'));
  };

  // Whoever is fixing a flagged item (Sales re-uploading the pack, or AR/Scheduler
  // editing scope/EA code/mandays after Approved with Notes) calls this once done.
  const [submittingFollowup, setSubmittingFollowup] = useState(false);
  const [sendingCidToAr, setSendingCidToAr] = useState(false);
  const handleSendCidToApplicationReview = async () => {
    setSendingCidToAr(true);
    try {
      await api.post(`/audit/projects/${projectId}/resubmit-application-review`, { scopeKey: checklistScopeKey || resolveBaseScheduleKey(selectedSchedulingScopeKey) });
      await reloadAuditTasks();
      // The backend clears reviewDecision back to null on resubmission, but
      // applicationReviewStatus (loaded separately via loadChecklistForScope) was never
      // refreshed here — it stayed stuck at 'rejected', which kept this revision panel
      // visible even after the task itself flipped back to OPEN.
      if (checklistScopeKey) await loadChecklistForScope(checklistScopeKey);
      setAwaitingArApprovalScopeKey(checklistScopeKey || resolveBaseScheduleKey(selectedSchedulingScopeKey));
      showAuditNotification('CID documents sent to Application Review.', { title: 'Application Review', type: 'success' });
    } catch (error) {
      showAuditNotification(error?.response?.data?.message || 'Failed to send CID documents.', { title: 'Application Review', type: 'danger' });
    } finally {
      setSendingCidToAr(false);
    }
  };

  const handleSubmitApplicationReviewFollowup = async () => {
    if (!projectId || !checklistScopeKey) return;
    setSubmittingFollowup(true);
    try {
      await api.post(`/audit/projects/${projectId}/application-review-followup/submit`, {
        scopeKey: checklistScopeKey,
      });
      await reloadAuditTasks();
      await loadChecklistForScope(checklistScopeKey);
      showAuditNotification('Follow-up submitted for re-review.', { title: 'Application Review', type: 'success' });
    } catch (error) {
      showAuditNotification(
        error?.response?.data?.message || 'Failed to submit follow-up.',
        { title: 'Application Review', type: 'danger' }
      );
    } finally {
      setSubmittingFollowup(false);
    }
  };

  // AR reviews the resubmitted pack/edits and confirms everything is fixed — closes the
  // follow-up loop. Does not touch the underlying checklist item values.
  const [resolvingFollowup, setResolvingFollowup] = useState(false);
  const handleResolveApplicationReviewFollowup = async () => {
    if (!projectId || !checklistScopeKey) return;
    setResolvingFollowup(true);
    try {
      // Persist any OK/notes changes AR just made to the flagged item(s) — the backend
      // only allows this narrow write while a follow-up is actively open for this scope.
      const items = CHECKLIST_ITEMS
        .map((item) => {
          const value = checklistValues[item.id];
          if (value !== 'ok' && value !== 'revisi') return null;
          return {
            checklistItemId: item.id,
            checklist_item_id: item.id,
            passed: value === 'ok',
            notes: value === 'revisi' ? (checklistNotes[item.id] || '') : '',
          };
        })
        .filter(Boolean);
      if (items.length > 0) {
        await api.post(`/audit/projects/${projectId}/checklists/save`, items, { params: { scope: checklistScopeKey } });
      }

      await api.post(`/audit/projects/${projectId}/application-review-followup/resolve`, {
        scopeKey: checklistScopeKey,
      });
      await loadChecklistForScope(checklistScopeKey);
      // Resolving the follow-up only flips the follow-up TASK to DONE on the backend — it
      // never touches projectAuditTasks/projectWorkflowTasks (the stepper, tab-completion
      // checks, and myProjectRole all read from that state) or the visible Application
      // Review status badge, which stayed stuck on "Approved with Notes" forever even once
      // everything was actually fixed. Every other terminal AR decision here (Approve,
      // Approved with Notes, Reject) already calls both of these; this path was the one
      // missing them.
      await reloadAuditTasks();
      await recordApplicationReviewStatus('approved', checklistScopeKey);
      showAuditNotification('Follow-up resolved. Application Review is now complete.', { title: 'Application Review', type: 'success' });
    } catch (error) {
      showAuditNotification(
        error?.response?.data?.message || 'Failed to resolve follow-up.',
        { title: 'Application Review', type: 'danger' }
      );
    } finally {
      setResolvingFollowup(false);
    }
  };

  // AR reviews the resubmitted pack/edits and is NOT satisfied — sends it back to Sales for
  // another round instead of resolving. Requires AR to have re-marked at least one item back to
  // "Need Revision" (with notes) first; backend enforces this too.
  const [requestingFollowupRevision, setRequestingFollowupRevision] = useState(false);
  const handleRequestFollowupRevision = async () => {
    if (!projectId || !checklistScopeKey) return;
    setRequestingFollowupRevision(true);
    try {
      // Persist whatever OK/Need Revision + notes AR just set on the flagged item(s) first —
      // same narrow write resolve already does, so the backend sees the up-to-date revisi notes.
      const items = CHECKLIST_ITEMS
        .map((item) => {
          const value = checklistValues[item.id];
          if (value !== 'ok' && value !== 'revisi') return null;
          return {
            checklistItemId: item.id,
            checklist_item_id: item.id,
            passed: value === 'ok',
            notes: value === 'revisi' ? (checklistNotes[item.id] || '') : '',
          };
        })
        .filter(Boolean);
      if (items.length > 0) {
        await api.post(`/audit/projects/${projectId}/checklists/save`, items, { params: { scope: checklistScopeKey } });
      }

      await api.post(`/audit/projects/${projectId}/application-review-followup/request-revision`, {
        scopeKey: checklistScopeKey,
      });
      await loadChecklistForScope(checklistScopeKey);
      showAuditNotification('Sent back to Sales for further revision.', { title: 'Application Review', type: 'warning' });
    } catch (error) {
      showAuditNotification(
        error?.response?.data?.message || 'Failed to send follow-up back for revision.',
        { title: 'Application Review', type: 'danger' }
      );
    } finally {
      setRequestingFollowupRevision(false);
    }
  };

  const handleAgreeAndContinue = async () => {
    setConfirmModalType(null);
    setIsCompletingReview(true);
    setReviewCompleteError('');

    // Validate all items are OK
    const allOk = CHECKLIST_ITEMS.every((item) => checklistValues[item.id] === 'ok');
    if (!allOk) {
      setReviewCompleteError('All checklist items must be marked as OK to complete the application review.');
      setIsCompletingReview(false);
      return;
    }

    try {
      const checklistItems = CHECKLIST_ITEMS.map((item) => ({
        checklistItemId: item.id,
        checklist_item_id: item.id,
        passed: true,
        notes: '',
      }));
      await api.post(`/audit/projects/${projectId}/checklists/save`, checklistItems, {
        params: { scope: checklistScopeKey },
      });

      // Build checklist payload for audit log
      const checklistPayload = CHECKLIST_ITEMS.reduce((acc, item) => {
        acc[item.label] = {
          value: checklistValues[item.id] || 'ok',
          note: checklistNotes[item.id] || '',
        };
        return acc;
      }, {});

      const _arScope = schedulingScopes.find((item) => resolveBaseScheduleKey(item.key) === checklistScopeKey);
      const response = await api.post(`/audit/projects/${projectId}/complete-application-review`, {
        scopeKey: checklistScopeKey,
        methodologyScopeKey: _arScope?.methodologyScopeKey || _arScope?.key || checklistScopeKey,
        includedProjectServiceStandardIds: _arScope?.includedProjectServiceStandardIds
          || (_arScope?.projectServiceStandardId ? [_arScope.projectServiceStandardId] : []),
        checklistValues: checklistPayload,
      });

      const createdTasksCount = response?.data?.createdTasksCount || 0;

      // Mark review as completed - shows done status on tab
      setReviewCompleted(true);
      setChecklistSaved(true);

      // Reload tasks to reflect new statuses + newly assigned Audit Plan tasks
      await reloadAuditTasks();

      const applicationReviewMessage = createdTasksCount > 0
        ? `Application review completed successfully. ${createdTasksCount} audit plan task(s) created for scheduled auditors.`
        : 'Application review completed successfully. Audit plan tasks will be created after audit scheduling is confirmed.';

      // Log to project history
      await api.post(`/audit/projects/${projectId}/history-process`, {
        processName: 'APPLICATION_REVIEW',
        actionType: 'REVIEW_COMPLETED',
        description: applicationReviewMessage,
      }).catch(() => console.warn('Failed to log history'));
      await recordApplicationReviewStatus('approved', checklistScopeKey);

      showAuditNotification(
        applicationReviewMessage,
        { title: 'Application Review', type: 'success' }
      );
    } catch (err) {
      console.error('Failed to complete application review:', err);
      const msg = err?.response?.data?.message || 'Failed to complete application review. Please try again.';
      setReviewCompleteError(msg);
    } finally {
      setIsCompletingReview(false);
    }
  };

  const handleConfirmAction = async () => {
    if (confirmModalType === 'agree') await handleAgreeAndContinue();
    else if (confirmModalType === 'submitReReview') await handleSubmitApplicationReviewFollowup();
    else if (confirmModalType === 'sendToAr') await handleSendCidToApplicationReview();
    setConfirmModalType(null);
  };

  const handleApproveWithNotes = async () => {
    if (!canSaveChecklist()) return;
    setChecklistSaving(true);
    try {
      const items = CHECKLIST_ITEMS.map((item) => ({
        checklistItemId: item.id,
        checklist_item_id: item.id,
        passed: checklistValues[item.id] === 'ok',
        notes: checklistValues[item.id] === 'revisi' ? (checklistNotes[item.id] || '') : '',
      }));
      await api.post(`/audit/projects/${projectId}/checklists/save`, items, { params: { scope: checklistScopeKey } });
      const scope = schedulingScopes.find((item) => resolveBaseScheduleKey(item.key) === checklistScopeKey);
      await api.post(`/audit/projects/${projectId}/complete-application-review`, {
        scopeKey: checklistScopeKey,
        methodologyScopeKey: scope?.methodologyScopeKey || scope?.key || checklistScopeKey,
        includedProjectServiceStandardIds: scope?.includedProjectServiceStandardIds || (scope?.projectServiceStandardId ? [scope.projectServiceStandardId] : []),
        approvedWithNotes: true,
      });
      setReviewCompleted(true);
      setChecklistSaved(true);
      setIsChecklistReviewMode(true);
      await reloadAuditTasks();
      await recordApplicationReviewStatus('approved_with_notes', checklistScopeKey);
      showAuditNotification('Application Review approved with notes.', { title: 'Application Review', type: 'success' });
    } catch (error) {
      showAuditNotification(error?.response?.data?.message || 'Failed to approve Application Review with notes.', { title: 'Application Review', type: 'danger' });
    } finally { setChecklistSaving(false); }
  };

  const handleUnifiedChecklistSave = async () => {
    if (!projectId || !checklistScopeKey) {
      showAuditNotification('Please select a scheduling scope before saving.', { title: 'Checklist', type: 'warning' });
      return;
    }

    // Validate checklist items
    const items = CHECKLIST_ITEMS
      .map((item) => {
        const value = checklistValues[item.id];
        if (value !== 'ok' && value !== 'revisi') return null;
        return {
          checklistItemId: item.id,
          checklist_item_id: item.id,
          passed: value === 'ok',
          notes: value === 'revisi' ? (checklistNotes[item.id] || '') : '',
        };
      })
      .filter(Boolean);

    if (items.length === 0) {
      showAuditNotification('Please mark at least one item as OK or Need Revision before saving.', { title: 'Checklist', type: 'warning' });
      return;
    }

    // Validate revision items have notes
    const errors = {};
    for (const item of CHECKLIST_ITEMS) {
      if (checklistValues[item.id] === 'revisi') {
        if (!(checklistNotes[item.id] ?? '').trim()) {
          errors[item.id] = 'Notes are required for items marked as Need Revision';
        }
      }
    }

    if (Object.keys(errors).length > 0) {
      setChecklistValidationErrors(errors);
      showAuditNotification('Please add notes for all items marked as Need Revision.', { title: 'Validation Error', type: 'danger' });
      return;
    }

    setChecklistSaving(true);
    try {
      // Save checklist items
      const checklistResponse = await api.post(`/audit/projects/${projectId}/checklists/save`, items, { params: { scope: checklistScopeKey } });
      if (checklistResponse?.data?.success === false) {
        throw new Error(checklistResponse.data.message || 'Checklist save failed');
      }

      // Mark checklist as saved - show done status on tab
      setChecklistSaved(true);
      
      // Check if there are revision items
      const hasRevisions = hasRevisionItems();
      
      if (hasRevisions) {
        // Create revision task
        const revisionItems = CHECKLIST_ITEMS
          .filter((item) => checklistValues[item.id] === 'revisi')
          .map((item) => ({
            label: item.label,
            note: (checklistNotes[item.id] ?? '').trim(),
          }));

        const revisionResponse = await api.post(`/audit/projects/${projectId}/request-revision`, {
          scopeKey: checklistScopeKey,
          checklistItems: revisionItems,
          message: 'Revision required from Application Review',
        });
        setIsChecklistReviewMode(true);
        await reloadAuditTasks();
        await recordApplicationReviewStatus('rejected', checklistScopeKey);

        showAuditNotification(
          `Checklist finalized. Application Review task moved to ${revisionResponse?.data?.assignedTo || 'the assigned Sales owner'}.`,
          { title: 'Checklist Saved', type: 'warning' }
        );
      } else {
        // Gate: Application Review may only COMPLETE — which creates the Audit Plan
        // tasks, advances the stepper past "Review & Audit Scheduling", and lets the
        // auditor run the audit process — when EVERY checklist item is OK. If any item
        // is not OK yet, keep the checklist saved but do NOT complete; the workflow
        // stays in Review & Audit Scheduling and no auditor task is created.
        const allChecklistOk = CHECKLIST_ITEMS.every((item) => checklistValues[item.id] === 'ok');
        if (!allChecklistOk) {
          showAuditNotification(
            'Checklist saved. All items must be marked OK before the audit process can start — it stays in Review & Audit Scheduling.',
            { title: 'Checklist Saved', type: 'warning' }
          );
          return;
        }
        if (!reviewCompleted) {
          const _arScope2 = schedulingScopes.find((item) => resolveBaseScheduleKey(item.key) === checklistScopeKey);
          await api.post(`/audit/projects/${projectId}/complete-application-review`, {
            scopeKey: checklistScopeKey,
            methodologyScopeKey: _arScope2?.methodologyScopeKey || _arScope2?.key || checklistScopeKey,
            includedProjectServiceStandardIds: _arScope2?.includedProjectServiceStandardIds
              || (_arScope2?.projectServiceStandardId ? [_arScope2.projectServiceStandardId] : []),
            checklistValues: CHECKLIST_ITEMS.reduce((acc, item) => {
              acc[item.label] = {
                value: checklistValues[item.id] || 'ok',
                note: checklistNotes[item.id] || '',
              };
              return acc;
            }, {}),
          });
        } else {
          await syncTaskStatusByTypeCodes(['APPLICATION_REVIEW', 'ASSIGN_NEXT_PROCESS_APPLICATION_REVIEWER'], 'done');
        }
        setReviewCompleted(true);
        setIsChecklistReviewMode(true);
        await reloadAuditTasks();

        // All items are OK - ready for completion
        showAuditNotification(
          'Checklist saved. Application Review completed and Audit process is ready when scheduling is complete.',
          { title: 'Checklist Saved', type: 'success' }
        );

        // Log to history
        await api.post(`/audit/projects/${projectId}/history-process`, {
          processName: 'APPLICATION_REVIEW',
          actionType: 'CHECKLIST_SAVED_OK',
          description: 'Checklist saved with all items marked as OK. Application Review completion flow executed.',
        }).catch(() => console.warn('Failed to log history'));
      }
    } catch (err) {
      console.error('Failed to save checklist:', err);
      const message = err?.response?.data?.message || err?.message || 'Failed to save checklist. Please try again.';
      showAuditNotification(message, { title: 'Checklist', type: 'danger' });
    } finally {
      setChecklistSaving(false);
    }
  };

  const handleSaveChecklist = handleUnifiedChecklistSave;

  // Save as Draft: persists checklist item values/notes only. Unlike
  // handleUnifiedChecklistSave, it never calls complete-application-review or
  // request-revision, and never sets isChecklistReviewMode — the checklist
  // stays editable so the user can come back and change it before finalizing.
  const handleSaveChecklistDraft = async () => {
    if (!projectId || !checklistScopeKey) {
      showAuditNotification('Please select a scheduling scope before saving.', { title: 'Checklist', type: 'warning' });
      return;
    }

    const items = CHECKLIST_ITEMS
      .map((item) => {
        const value = checklistValues[item.id];
        if (value !== 'ok' && value !== 'revisi') return null;
        return {
          checklistItemId: item.id,
          checklist_item_id: item.id,
          passed: value === 'ok',
          notes: value === 'revisi' ? (checklistNotes[item.id] || '') : '',
        };
      })
      .filter(Boolean);

    if (items.length === 0) {
      showAuditNotification('Please mark at least one item as OK or Need Revision before saving.', { title: 'Checklist', type: 'warning' });
      return;
    }

    const errors = {};
    for (const item of CHECKLIST_ITEMS) {
      if (checklistValues[item.id] === 'revisi') {
        if (!(checklistNotes[item.id] ?? '').trim()) {
          errors[item.id] = 'Notes are required for items marked as Need Revision';
        }
      }
    }

    if (Object.keys(errors).length > 0) {
      setChecklistValidationErrors(errors);
      showAuditNotification('Please add notes for all items marked as Need Revision.', { title: 'Validation Error', type: 'danger' });
      return;
    }

    setChecklistSaving(true);
    try {
      const checklistResponse = await api.post(`/audit/projects/${projectId}/checklists/save`, items, { params: { scope: checklistScopeKey } });
      if (checklistResponse?.data?.success === false) {
        throw new Error(checklistResponse.data.message || 'Checklist save failed');
      }

      setChecklistSaved(true);
      await recordApplicationReviewStatus('draft', checklistScopeKey);
      showAuditNotification(
        'Draft saved. You can keep editing the checklist and save again anytime.',
        { title: 'Draft Saved', type: 'success' }
      );
    } catch (err) {
      console.error('Failed to save checklist draft:', err);
      const message = err?.response?.data?.message || err?.message || 'Failed to save checklist draft. Please try again.';
      showAuditNotification(message, { title: 'Checklist', type: 'danger' });
    } finally {
      setChecklistSaving(false);
    }
  };

  const handleContactFieldChange = (sectionKey, field, value) => {
    setAuditContacts((prev) => {
      const next = {
        ...prev,
        [sectionKey]: {
          ...prev[sectionKey],
          [field]: value,
        },
      };

      if (sectionKey === 'representative') {
        ['correspondence', 'finance', 'certificate'].forEach((key) => {
          if (next[key]?.sameAsRepresentative) {
            next[key] = {
              ...next[key],
              [field]: value,
            };
          }
        });
      }

      return next;
    });

    clearContactFieldError(sectionKey, field);
  };

  const handleSameAsRepresentativeChange = (sectionKey, checked) => {
    if (sectionKey === 'representative') return;
    setAuditContacts((prev) => {
      const rep = prev.representative || { person: '-', phone: '-', email: '-' };
      return {
        ...prev,
        [sectionKey]: {
          ...prev[sectionKey],
          sameAsRepresentative: checked,
          person: checked ? rep.person : prev[sectionKey]?.person,
          phone: checked ? rep.phone : prev[sectionKey]?.phone,
          email: checked ? rep.email : prev[sectionKey]?.email,
        },
      };
    });

    if (checked) {
      setContactValidationErrors((prev) => {
        const next = { ...prev };
        delete next[getContactFieldKey(sectionKey, 'person')];
        delete next[getContactFieldKey(sectionKey, 'phone')];
        delete next[getContactFieldKey(sectionKey, 'email')];
        return next;
      });
    }

  };

  const handleSaveAuditContacts = async () => {
    if (!projectId) return;

    const validationErrors = validateAuditContacts(auditContacts);
    if (Object.keys(validationErrors).length > 0) {
      setContactValidationErrors(validationErrors);
      showAuditNotification('Please fix the highlighted contact fields.', { title: 'Audit Contacts', type: 'danger' });
      return;
    }

    setIsSavingContacts(true);
    try {
      const payload = {
        representative: {
          person: toContactApiValue(auditContacts.representative?.person),
          phone: toContactApiValue(auditContacts.representative?.phone),
          email: toContactApiValue(auditContacts.representative?.email),
        },
        correspondence: {
          person: toContactApiValue(auditContacts.correspondence?.person),
          phone: toContactApiValue(auditContacts.correspondence?.phone),
          email: toContactApiValue(auditContacts.correspondence?.email),
          sameAsRepresentative: Boolean(auditContacts.correspondence?.sameAsRepresentative),
        },
        finance: {
          person: toContactApiValue(auditContacts.finance?.person),
          phone: toContactApiValue(auditContacts.finance?.phone),
          email: toContactApiValue(auditContacts.finance?.email),
          sameAsRepresentative: Boolean(auditContacts.finance?.sameAsRepresentative),
        },
        certificate: {
          person: toContactApiValue(auditContacts.certificate?.person),
          phone: toContactApiValue(auditContacts.certificate?.phone),
          email: toContactApiValue(auditContacts.certificate?.email),
          sameAsRepresentative: Boolean(auditContacts.certificate?.sameAsRepresentative),
        },
      };

      const res = await api.put(`/audit/projects/${Number(projectId)}/contacts`, payload);
      setAuditContacts((prev) => normalizeContactPayload(res?.data, prev));
      setContactValidationErrors({});
      
      // Log to project history
      await api.post(`/audit/projects/${projectId}/history-process`, {
        processName: 'AUDIT_SCHEDULING',
        actionType: 'CONTACTS_UPDATED',
        description: 'Audit contacts (representative, correspondence, finance, certificate) updated',
      }).catch(() => console.warn('Failed to log contacts update to history'));
      
      showAuditNotification('Audit contacts updated successfully.', { title: 'Audit Contacts', type: 'success' });
    } catch (error) {
      console.error('Failed to save audit contacts:', error);
      showAuditNotification('Failed to save audit contacts.', { title: 'Audit Contacts', type: 'danger' });
    } finally {
      setIsSavingContacts(false);
    }
  };

  async function openDocumentPreview(doc) {
    const name = doc?.name || 'Document';
    const sourceUrl = doc?.sourceUrl || doc?.url || '';
    setPreviewDocument({ name, url: '', sourceUrl });
    setPreviewError('');
    setPreviewModalOpen(true);
    if (!sourceUrl) {
      setPreviewError('This document does not have a valid storage path.');
      return;
    }

    setPreviewLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      const response = await fetch(sourceUrl, {
        credentials: 'include',
        headers: user?.token ? { Authorization: `Bearer ${user.token}` } : {},
      });
      if (!response.ok) throw Object.assign(new Error('Preview request failed'), { status: response.status });
      const blob = await response.blob();
      if (!isPreviewableDocument(name, blob.type)) {
        setPreviewError('This file type is not supported for browser preview. Download the original file to open it.');
        return;
      }
      setPreviewDocument((prev) => ({ ...prev, url: URL.createObjectURL(blob), sourceUrl }));
    } catch (error) {
      setPreviewError(buildDocumentPreviewError(error?.status));
    } finally {
      setPreviewLoading(false);
    }
  }

  function closeDocumentPreview() {
    if (previewDocument.url?.startsWith('blob:')) URL.revokeObjectURL(previewDocument.url);
    setPreviewModalOpen(false);
    setPreviewLoading(false);
    setPreviewError('');
  }

  function openStageCid2SalesPack(stage) {
    const documents = getCid2DocumentsForStage(salesPackDocuments, stage?.salesPackServiceIds);
    setCid2SalesPackModal({
      open: true,
      stageName: stage?.stageName || 'Audit stage',
      documents,
    });
  }

  function handleDocumentDownload(doc) {
    const downloadUrl = doc?.sourceUrl || doc?.url;
    if (!downloadUrl) return;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = doc.name || 'document';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async function openAuditOutputGeneratedDocument(template, runtimeData, fileName) {
    const { url } = await buildAuditOutputPdfPreview(template, runtimeData);
    openDocumentPreview({
      name: fileName || `${template?.templateName || 'audit-output'}.pdf`,
      url,
    });
  }

  async function downloadAuditOutputGeneratedDocument(template, runtimeData, fileName) {
    const { url } = await buildAuditOutputPdfPreview(template, runtimeData);
    handleDocumentDownload({
      name: fileName || `${template?.templateName || 'audit-output'}.pdf`,
      url,
    });
  }

  const pickCertificateValue = (source, keys, fallback = '') => {
    for (const key of keys) {
      const value = source?.[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') return value;
    }
    return fallback;
  };

  const buildCertificateIssuePayload = (runtimeData, certificateForm = {}, certificateInfoForm = {}, template, finalPdfBase64, scopeKey = '', targetStandards = null) => {
    const certificate = runtimeData?.certificate || {};
    const standards = Array.isArray(certificateForm?.applicableStandards)
      ? certificateForm.applicableStandards
      : String(certificateForm?.applicableStandards || '').split(',').map((value) => value.trim()).filter(Boolean);
    const certificateStandardText = [...standards, ...(runtimeData?.certificate?.standards || [])]
      .map((value) => value?.label || value?.value || value?.standardName || value?.standard_name || value?.name || value)
      .join(' ');
    const isIscc = /\biscc\b/i.test(certificateStandardText);
    const rawIssueDate = isIscc
      ? pickCertificateValue(certificateForm, ['validFrom', 'valid_from', 'valid_from_start_date'])
      : pickCertificateValue(certificateForm, ['issueDate', 'issue_date', 'issuedDate', 'issued_date']);
    const rawExpiryDate = isIscc
      ? pickCertificateValue(certificateForm, ['validTo', 'valid_to', 'valid_from_end_date'])
      : pickCertificateValue(certificateForm, ['expiryDate', 'expiry_date', 'expiredDate', 'expired_date']);
    return {
      certificateNumber: pickCertificateValue(certificateForm, ['certificateNumber', 'certificate_number', 'certNumber'], certificate.certificateNumber || ''),
      issueDate: rawIssueDate,
      issuedDate: rawIssueDate,
      expiryDate: rawExpiryDate,
      expiredDate: rawExpiryDate,
      initialCertificationDate: pickCertificateValue(certificateForm, ['initialCertificationDate', 'initialDate', 'validFrom']),
      latestCertificationDate: pickCertificateValue(certificateForm, ['latestCertificationDate', 'latestDate']),
      // Last-resort address fallback only. FTRD-02 no longer collects a Client Address field:
      // CertificateRenderContextFactory treats the Sites list as the single source of truth and
      // overwrites this with sites[0].address whenever any site row exists, then falls back to the
      // CRM company address. This value is therefore only reached by a payload carrying no sites
      // at all, so it is still seeded from projectMeta rather than dropped.
      addressClient: pickCertificateValue(certificateForm, ['addressClient', 'clientAddress', 'companyAddress', 'address'],
        pickCertificateValue(certificateInfoForm, ['addressClient', 'clientAddress', 'address'], projectMeta?.address || '')),
      scope: pickCertificateValue(certificateForm, ['certificateScope', 'scopeOfCertification', 'scope'], certificate.scope || certificate.scopeStatement || ''),
      certificateScope: pickCertificateValue(certificateForm, ['certificateScope', 'scopeOfCertification', 'scope'], certificate.scope || certificate.scopeStatement || ''),
      accreditationBody: pickCertificateValue(certificateForm, ['accreditationBody', 'accreditation_body']),
      issuer: pickCertificateValue(certificateForm, ['issuer', 'signatureName', 'approvedBy'], certificate.signatureName || ''),
      statementOfApplicability: pickCertificateValue(certificateForm, ['statementOfApplicability', 'soa', 'verificationOfScope'],
        pickCertificateValue(certificateInfoForm, ['statementOfApplicability', 'soa'], certificate.statementOfApplicability || '')),
      conditions: pickCertificateValue(certificateForm, ['certificateConditions', 'conditions', 'remarks', 'notes'], certificate.remarks || ''),
      signatureTitle: pickCertificateValue(certificateForm, ['signatureTitle', 'signatoryTitle']),
      signatureName: pickCertificateValue(certificateForm, ['signatureName', 'approvedBy', 'issuer']),
      siteLocations: Array.isArray(certificateForm?.siteLocations) ? certificateForm.siteLocations : [],
      finalPdfBase64,
      finalPdfPath: `${template?.templateName || 'certificate'}-${projectId || projectMeta.projectCode || 'project'}.pdf`,
      scopeKey,
      // Integrated/combined audits run one grouped scope but must issue one certificate PER
      // STANDARD. The grouped scope key alone carries no standard reference, so the member
      // standards travel with the request and the backend splits issuance across them.
      standards: Array.isArray(targetStandards)
        ? targetStandards
        : resolveCertificateIssuanceStandards(schedulingScopes.find((scope) => scope.key === scopeKey) || null),
      // Annex I is the operator's decision, gated on the standard being ISCC PLUS --
      // AuditQ's own gate is `is_iscc_plus && iscc_annex_1`, where iscc_annex_1 is a
      // checkbox on the certificate form. FTRD-02 now stores that as `isccAnnex1`;
      // this was previously read from isccAnnex1Required/annex1Required, which nothing
      // ever wrote, so it was permanently false. Legacy keys are still honoured for
      // documents saved before the checkbox existed.
      isccAnnex1Required: isAnnex1Active(
        (Array.isArray(certificateForm?.applicableStandards)
          ? certificateForm.applicableStandards
          : [certificateForm?.standardName]
        ).find(isIsccPlusStandard) || '',
        certificateForm?.isccAnnex1 === true
          || certificateForm?.isccAnnex1Required === true
          || certificateForm?.annex1Required === true,
      ),
      annex1Materials: Array.isArray(certificateForm?.annex1Materials)
        ? certificateForm.annex1Materials
        : undefined,
      isccMaterials: Array.isArray(certificateForm?.isccMaterials)
        ? certificateForm.isccMaterials
        : undefined,
    };
  };

  const requestAuditqCertificateDraftPdf = async (certificateForm, certificateInfoForm, scopeKey = '', targetStandards = null) => {
    if (!projectId) throw new Error('Project is required to render the certificate draft.');
    const response = await api.post(
      `/audit/projects/${projectId}/certificate/draft-preview`,
      buildCertificateIssuePayload({}, certificateForm, certificateInfoForm, null, null, scopeKey, targetStandards),
      { responseType: 'blob' },
    );
    if (!(response?.data instanceof Blob) || response.data.size === 0) {
      throw new Error('The certificate draft preview is empty.');
    }
    return response.data;
  };

  const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || '').split(',')[1] || '');
    reader.onerror = () => reject(new Error('Failed to prepare the certificate PDF for issuance.'));
    reader.readAsDataURL(blob);
  });

  const openAuditqCertificateDraftPreview = async (certificateForm, certificateInfoForm, scopeKey = '', targetStandards = null) => {
    setPreviewError('');
    setPreviewModalOpen(true);
    setPreviewLoading(true);
    try {
      const pdf = await requestAuditqCertificateDraftPdf(certificateForm, certificateInfoForm, scopeKey, targetStandards);
      setPreviewDocument({
        name: 'Certificate Draft.pdf',
        url: URL.createObjectURL(pdf),
        sourceUrl: '',
      });
    } catch (error) {
      setPreviewError(error?.response?.data?.message || error?.message || 'Failed to generate the certificate draft preview.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const downloadAuditqCertificateDraft = async (certificateForm, certificateInfoForm, scopeKey = '', targetStandards = null) => {
    try {
      const pdf = await requestAuditqCertificateDraftPdf(certificateForm, certificateInfoForm, scopeKey, targetStandards);
      const url = URL.createObjectURL(pdf);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificate-draft-${projectMeta.projectCode || projectId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (error) {
      showAuditNotification(error?.response?.data?.message || error?.message || 'Failed to download the certificate draft.', { title: 'Certificate Draft', type: 'danger' });
    }
  };

  const issueCertificateFromAuditqDraft = async ({ certificateForm, certificateInfoForm, scopeKey, targetStandards = null }) => {
    if (!projectId) return;
    setCertificateIssuanceState({ saving: true, scopeKey: scopeKey || '' });
    try {
      const issuanceScope = schedulingScopes.find((scope) => scope.key === scopeKey) || null;
      const perStandardIssuance = isPerStandardCertificateScope(issuanceScope);

      // One uploaded PDF cannot be correct for several standards, so grouped scopes let the
      // backend render the authoritative final PDF per standard instead of sending one here.
      let finalPdfBase64 = null;
      if (!perStandardIssuance) {
        const certificatePdf = await requestAuditqCertificateDraftPdf(certificateForm, certificateInfoForm, scopeKey);
        finalPdfBase64 = await blobToBase64(certificatePdf);
        if (!finalPdfBase64) throw new Error('Failed to render final certificate PDF.');
      }

      const response = await api.post(`/audit/projects/${projectId}/certificate/issue`,
        buildCertificateIssuePayload({}, certificateForm, certificateInfoForm, null, finalPdfBase64, scopeKey, targetStandards));

      const issuedCount = Number(response?.data?.issuedCount) || 1;
      const issuedNumbers = (Array.isArray(response?.data?.certificates) ? response.data.certificates : [])
        .map((certificate) => certificate?.certificateNumber)
        .filter(Boolean);
      showAuditNotification(
        issuedCount > 1
          ? `${issuedCount} certificates finalized and issued successfully${issuedNumbers.length ? ` (${issuedNumbers.join(', ')})` : ''}.`
          : 'Certificate finalized and issued successfully.',
        { title: 'Certificate Issuance', type: 'success' });
      await reloadAuditTasks();
    } catch (error) {
      const data = error?.response?.data;
      const batchFailureSuffix = data?.code === 'CERTIFICATE_BATCH_ISSUANCE_FAILED'
        ? ` No certificates were issued; "${data.failedStandardName || 'a standard'}" blocked the batch.`
        : '';
      showAuditNotification(
        `${data?.message || error?.message || 'Failed to issue certificate.'}${batchFailureSuffix}`,
        { title: 'Certificate Issuance', type: 'danger' });
    } finally {
      setCertificateIssuanceState({ saving: false, scopeKey: '' });
    }
  };

  async function generateAuditSummaryReportData() {
    try {
      // Fetch findings for this project
      const findingsRes = await api.get(`/audit/findings/projects/${projectId}`).catch(() => ({ data: [] }));
      const findings = Array.isArray(findingsRes?.data) ? findingsRes.data : [];

      // Count findings by severity. NOTE: the Finding entity only has
      // CRITICAL/MAJOR/MINOR severities today — there is no real "OFI"
      // (Opportunity For Improvement) severity value, so this always
      // resolves to 0/empty. Not fabricated data — the PPTX template's OFI
      // slide will correctly show "None identified" until a real OFI/
      // OBSERVATION severity is added to the data model.
      const majorNC = findings.filter((f) => f.severity === 'MAJOR' || f.severity === 'CRITICAL').length;
      const minorNC = findings.filter((f) => f.severity === 'MINOR').length;
      const observations = findings.filter((f) => f.severity === 'OBSERVATION' || f.severity === 'OFI').length;

      // Get audit dates from schedules
      const scheduleData = schedulingDataByScope[selectedSchedulingScopeKey];
      const auditStartDate = scheduleData?.auditSchedules?.[0]?.startDate || new Date().toISOString().split('T')[0];
      const auditEndDate = scheduleData?.auditSchedules?.[0]?.endDate || new Date().toISOString().split('T')[0];

      // Get auditor names
      const auditTeam = (scheduleData?.auditSchedules || []).map((schedule) => ({
        name: schedule.employeeName || `Auditor - ${schedule.employeeLabel}`,
        role: schedule.role || 'Lead Auditor',
      }));

      // Build summary
      const majorConformities = findings.filter((f) => f.status === 'ACCEPTED').length;
      const conclusion = majorNC > 0 ? 'Recommend for Recertification with Verification' : 'Recommend Certification';
      const findingsDisplay = findings.length > 0 ? `${majorNC} major NC and ${minorNC} minor NC` : 'No non-conformities';

      // Stage/template selection + cover/scope fields the PPTX template engine
      // needs (see docs/superpowers/specs/2026-07-28-audit-process-ui-and-pptx-design.md
      // section E). Kept alongside the existing summary fields rather than a
      // separate call so save/export always sends one consistent payload.
      const activeScope = schedulingScopes.find((s) => s.key === selectedSchedulingScopeKey) || currentAuditCycle || null;
      const stageKey = resolveAuditSummaryStageKey(currentAuditCycle || activeScope);
      const isUpcomingCycleScope = (scope) => {
        const label = String(scope?.label || scope?.stageName || '').toLowerCase();
        return { label, isSurveillance1: label.includes('surveillance') && !/2|second|ii\b/.test(label),
          isSurveillance2: label.includes('surveillance') && /2|second|ii\b/.test(label),
          isRecert: label.includes('recert') || label.includes('re-cert') };
      };
      const nextCycleDates = schedulingScopes.reduce((acc, scope) => {
        const { isSurveillance1, isSurveillance2, isRecert } = isUpcomingCycleScope(scope);
        if (scope.auditDate) {
          if (isSurveillance1 && !acc.surveillance1) acc.surveillance1 = scope.auditDate;
          if (isSurveillance2 && !acc.surveillance2) acc.surveillance2 = scope.auditDate;
          if (isRecert && !acc.recertification) acc.recertification = scope.auditDate;
        }
        return acc;
      }, { surveillance1: null, surveillance2: null, recertification: null });

      return {
        stage: stageKey,
        companyName: projectMeta.companyName || projectMeta.clientName || '',
        companyAddress: projectMeta.address || '',
        scopeText: projectMeta.scope || projectMeta.proposalScope || '',
        standardName: activeScope?.standardName || '',
        leadAuditorName: auditTeam[0]?.name || '',
        location: projectMeta.locationName || projectMeta.companyName || '',
        nextCycleDates,
        auditDate: auditStartDate,
        auditStartDate,
        auditEndDate,
        auditTeam,
        majorNC,
        minorNC,
        observations,
        conformities: majorConformities,
        summaryNarrative: `Audit conducted from ${auditStartDate} to ${auditEndDate}. ${findingsDisplay} were found.`,
        conclusion,
        recommendations: majorNC > 0 ? 'Address identified non-conformities and submit verification report within 30 days.' : 'No recommendations. Client is recommended for certification.',
      };
    } catch (err) {
      console.warn('Failed to generate audit summary report data:', err);
      return null;
    }
  }

  // Audit Summary Report is auto-generated-then-exported in one click — no
  // separate manual "Generate Report" step. Generates fresh data from the
  // project/findings every time (never relies on stale previously-saved data).
  async function handleExportAuditSummaryReport(scopeKey) {
    setIsExportingAuditSummary(true);
    try {
      const summaryData = await generateAuditSummaryReportData();
      if (!summaryData) {
        showAuditNotification('Could not generate the Audit Summary Report from current project data.', { title: 'Audit Summary', type: 'danger' });
        return;
      }
      // formDataJson must be the JSON-encoded payload, not spread top-level
      // fields (the backend only reads body.formDataJson — spreading here
      // silently saved an empty {} on every export in the prior version).
      await saveDocumentFormData(projectId, {
        documentCode: 'SUMMARY',
        scopeKey,
        category: 'audit',
        formDataJson: JSON.stringify(summaryData),
      });
      setSavedFormDataMap((prev) => ({
        ...prev,
        [buildDocumentFormStorageKey(AUDIT_SUMMARY_DOC_CODE, scopeKey)]: { formDataJson: summaryData },
      }));
      await exportDocumentFormData(projectId, 'SUMMARY', scopeKey, 'pptx');
    } catch (err) {
      console.error('Failed to generate/export Audit Summary Report:', err);
      showAuditNotification('Failed to generate/export the Audit Summary Report.', { title: 'Export', type: 'danger' });
    } finally {
      setIsExportingAuditSummary(false);
    }
  }

  const renderDocumentRows = (docs, keyPrefix) => (
    <div className="audit-process-doc-file-list">
      {docs.map((doc, idx) => (
        <div key={`${keyPrefix}-${doc.name}-${idx}`} className="audit-process-doc-file-row">
          <div className="audit-process-doc-file-meta">
            <span className="audit-process-doc-file-name">{doc.name}</span>
          </div>
          <div className="audit-process-doc-file-actions">
            <button
              type="button"
              className="audit-process-doc-icon-btn"
              onClick={() => openDocumentPreview(doc)}
              title="Preview"
              aria-label={`Preview ${doc.name}`}
            >
              <Eye size={18} />
            </button>
            <button
              type="button"
              className="audit-process-doc-icon-btn"
              onClick={() => handleDocumentDownload(doc)}
              title="Download"
              aria-label={`Download ${doc.name}`}
            >
              <Download size={18} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderSalesPackContent = () => {
    const isIsccSalesPack = salesPackDocuments.some((section) => isSustainabilityOrIsccText(
      section?.serviceName, section?.scope, projectMeta?.scope, projectMeta?.auditTitle,
    ));
    const cidDocumentCode = (cidNumber) => getSalesPackCidCode(isIsccSalesPack, cidNumber);
    const cidDocumentCodes = (cidNumber) => getSalesPackCidCodes(isIsccSalesPack, cidNumber);
    const cidDocumentLabel = (cidNumber) => cidDocumentCode(cidNumber);
    // Two independent signals, either one is enough: the raw task-list scope match (legacy
    // path, can miss if checklistScopeKey/selectedSchedulingScopeKey don't line up with how the
    // task's scope was stored) OR applicationReviewStatus === 'rejected' (already loaded for
    // checklistScopeKey specifically via loadChecklistForScope — the same value the "Rejected"
    // badge above is driven by, so it can't disagree with what the user is looking at).
    const isSalesRevisionOwner = hasRole('ROLE_SALES') && (
      applicationReviewStatus === 'rejected'
      || selectTasksForAuditScope(
        projectAuditTasks,
        (schedulingScopes || []).find((scope) => (
          scope?.key === (checklistScopeKey || selectedSchedulingScopeKey)
          || resolveBaseScheduleKey(scope?.key) === resolveBaseScheduleKey(checklistScopeKey || selectedSchedulingScopeKey)
        )) || checklistScopeKey || selectedSchedulingScopeKey,
      )
        .some((task) => String(task?.status || '').toUpperCase() === 'REVISION_REQUESTED')
    );
    const followupNeedsSalesPack = hasRole('ROLE_SALES')
      && applicationReviewFollowup?.exists
      && applicationReviewFollowup?.needsSalesPack
      && String(applicationReviewFollowup?.status || '').toUpperCase() === 'OPEN';
    // Previously this returned early (hiding EVERYTHING below, including the reject/revision
    // panel) whenever salesPackDocuments was empty. salesPackDocuments is built purely from
    // LEGACY lead-level fields (mapLeadDocuments, cid1FileName/cid2FileName) -- it has nothing
    // to do with whether Application Review actually rejected this scope. A project with no
    // legacy lead-service data would never show the reject panel/upload chips at all, even
    // while genuinely rejected. No early return anymore; the "nothing to show" fallback is
    // computed further down instead, after every real signal has had a chance to render.
    const canUploadSalesPack = myProjectRole.isSales || myProjectRole.isAdminOrManager;
    const uploadedDocs = Array.isArray(auditDocuments['sales-pack']) ? auditDocuments['sales-pack'] : [];

    const renderCidRevisionPanel = ({ title, description, actionLabel, actionBusyLabel, actionIcon, onAction, actionBusy, variant = 'warning' }) => (
      <div className={`audit-process-revision-card ${variant === 'success' ? 'audit-process-revision-card--success' : ''}`}>
        <div className="audit-process-revision-card__icon">
          {variant === 'success' ? <CircleCheckBig size={18} /> : <AlertTriangle size={18} />}
        </div>
        <div className="audit-process-revision-card__body">
          <div className="audit-process-revision-card__title">{title}</div>
          <div className="audit-process-revision-card__desc">{description}</div>
          {onAction && (
            <>
              <div className="audit-process-revision-card__files">
                {[1, 2].map((cidNumber) => {
                  const code = cidDocumentCode(cidNumber);
                  const isUploaded = (Array.isArray(auditDocuments['sales-pack']) ? auditDocuments['sales-pack'] : [])
                    .some((doc) => cidDocumentCodes(cidNumber).includes(doc?.documentCode));
                  return (
                    <label
                      key={code}
                      className={`audit-process-revision-card__file-chip ${isUploaded ? 'is-uploaded' : ''}`}
                    >
                      {isUploaded ? <CircleCheckBig size={14} className="text-success" /> : <Upload size={14} />}
                      <span>{cidDocumentLabel(cidNumber)}</span>
                      <input
                        type="file"
                        accept="application/pdf,.pdf"
                        className="d-none"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          handleSalesPackCidUpload(code, file);
                          event.target.value = '';
                        }}
                      />
                    </label>
                  );
                })}
              </div>
              <div className="audit-process-revision-card__footer">
                <button type="button" className="btn btn-teal btn-sm" disabled={actionBusy} onClick={onAction}>
                  {actionIcon}{actionBusy ? actionBusyLabel : actionLabel}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );

    return (
      <div className="d-flex flex-column gap-3">
        {isSalesRevisionOwner && renderCidRevisionPanel({
          title: 'Application Review revision requested',
          description: isIsccSalesPack
            ? 'Upload the corrected ISCC CORSIA and ISCC CORSIA PLUS forms, then send the pack back to Application Review.'
            : 'Upload the corrected CID 1 and CID 2, then send the pack back to Application Review.',
          actionLabel: 'Send to AR',
          actionBusyLabel: 'Sending…',
          actionIcon: <Send size={14} className="me-1" />,
          actionBusy: sendingCidToAr,
          onAction: () => setConfirmModalType('sendToAr'),
        })}
        {followupNeedsSalesPack && renderCidRevisionPanel({
          title: 'Application Review approved with notes',
          description: isIsccSalesPack
            ? 'Re-upload the corrected ISCC CORSIA and ISCC CORSIA PLUS forms, then submit for re-review.'
            : 'Re-upload the corrected CID 1 and CID 2, then submit for re-review.',
          actionLabel: 'Submit',
          actionBusyLabel: 'Submitting…',
          actionIcon: <Send size={14} className="me-1" />,
          actionBusy: submittingFollowup,
          onAction: handleSubmitApplicationReviewFollowup,
        })}
        {!isSalesRevisionOwner
          && hasRole('ROLE_SALES')
          && awaitingArApprovalScopeKey
          && awaitingArApprovalScopeKey === (checklistScopeKey || selectedSchedulingScopeKey)
          && renderCidRevisionPanel({
            title: 'Waiting for AR approval',
            description: 'The corrected CID documents have been sent to Application Review. Waiting for their decision.',
            variant: 'success',
          })}
        {salesPackDocuments.length === 0 && (
          // salesPackDocuments (legacy lead-level fields) has nothing in it for this project --
          // still give Sales somewhere to upload CID 1/CID 2 for the very first time, sourced
          // from the REAL project-documents API (the one every upload/download actually uses),
          // instead of silently depending on legacy data existing at all.
          <div className="audit-process-doc-section">
            <div className="audit-process-doc-section-title">Sales Pack Documents</div>
            {[1, 2].map((cidNumber) => {
              const code = cidDocumentCode(cidNumber);
              const docs = uploadedDocs.filter((d) => cidDocumentCodes(cidNumber).includes(d?.documentCode));
              return (
                <div key={`primary-cid${cidNumber}`} className="audit-process-doc-group">
                  <div className="audit-process-doc-group-title d-flex align-items-center justify-content-between gap-2">
                    <span>{cidDocumentLabel(cidNumber)}</span>
                    {canUploadSalesPack && (
                      <label
                        className="audit-process-doc-icon-btn"
                        title={`Upload ${cidDocumentLabel(cidNumber)}`}
                        aria-label={`Upload ${cidDocumentLabel(cidNumber)}`}
                      >
                        <Upload size={16} />
                        <input
                          type="file"
                          accept="application/pdf,.pdf"
                          className="d-none"
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            handleSalesPackCidUpload(code, file);
                            event.target.value = '';
                          }}
                        />
                      </label>
                    )}
                  </div>
                  {docs.length > 0 ? (
                    <div className="audit-process-doc-file-list">
                      {docs.map((doc) => {
                        const displayName = doc.originalFileName || doc.fileName || 'document';
                        return (
                          <div key={doc.id} className="audit-process-doc-file-row">
                            <div className="audit-process-doc-file-meta">
                              <span className="audit-process-doc-file-name">{displayName}</span>
                            </div>
                            <div className="audit-process-doc-file-actions">
                              <button
                                type="button"
                                className="audit-process-doc-icon-btn"
                                onClick={() => downloadAuditProjectDocument(doc.id, displayName)}
                                title="Download"
                                aria-label={`Download ${displayName}`}
                              >
                                <Download size={18} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-muted small">No file uploaded yet.</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {salesPackDocuments.length === 0
          && uploadedDocs.length === 0
          && !canUploadSalesPack
          && !isSalesRevisionOwner
          && !followupNeedsSalesPack
          && (
            <div className="text-muted small">No Sales Pack documents found.</div>
          )}
        {(() => {
          // CID 1/CID 2 revision uploads go to /audit/projects/{id}/documents (category
          // 'sales-pack'), NOT the legacy leadServices.cid1FileName/cid2FileName fields
          // salesPackDocuments below is built from — so a freshly uploaded revision file
          // never showed up here even though the upload itself succeeded. Render the real
          // uploaded documents here, separate from the legacy lead-derived list. Only relevant
          // when salesPackDocuments has legacy sections to attach to -- the empty case is
          // already handled by the primary upload block above.
          if (salesPackDocuments.length === 0 || uploadedDocs.length === 0) return null;
          const groups = [
            { id: 'cid1', label: cidDocumentLabel(1), docs: uploadedDocs.filter((d) => cidDocumentCodes(1).includes(d?.documentCode)) },
            { id: 'cid2', label: cidDocumentLabel(2), docs: uploadedDocs.filter((d) => cidDocumentCodes(2).includes(d?.documentCode)) },
            { id: 'other', label: 'Other', docs: uploadedDocs.filter((d) => ![...cidDocumentCodes(1), ...cidDocumentCodes(2)].includes(d?.documentCode)) },
          ].filter((group) => group.docs.length > 0);
          if (groups.length === 0) return null;
          return (
            <div className="audit-process-doc-section">
              <div className="audit-process-doc-section-title">Revision Documents</div>
              {groups.map((group) => (
                <div key={`revision-${group.id}`} className="audit-process-doc-group">
                  <div className="audit-process-doc-group-title">{group.label}</div>
                  <div className="audit-process-doc-file-list">
                    {group.docs.map((doc) => {
                      const displayName = doc.originalFileName || doc.fileName || 'document';
                      return (
                        <div key={doc.id} className="audit-process-doc-file-row">
                          <div className="audit-process-doc-file-meta">
                            <span className="audit-process-doc-file-name">{displayName}</span>
                          </div>
                          <div className="audit-process-doc-file-actions">
                            <button
                              type="button"
                              className="audit-process-doc-icon-btn"
                              onClick={() => downloadAuditProjectDocument(doc.id, displayName)}
                              title="Download"
                              aria-label={`Download ${displayName}`}
                            >
                              <Download size={18} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
        {salesPackDocuments.map((section) => {
          // CID 1 / CID 2 must always offer an upload control, not just when a revision/
          // follow-up is active -- previously these groups only rendered at all once
          // `group.docs.length > 0`, so a section with NO Sales Pack file yet (the very first
          // upload, before any Application Review has happened) showed a plain "No uploaded
          // documents." message with nothing actionable, and Sales had no way to upload.
          const canUploadSalesPack = myProjectRole.isSales || myProjectRole.isAdminOrManager;
          return (
            <div key={section.key} className="audit-process-doc-section">
              <div className="audit-process-doc-section-title">{section.serviceName}</div>
              <div className="text-muted small mb-2">Scope: {section.scope}</div>
              {[1, 2].map((cidNumber) => {
                const code = cidDocumentCode(cidNumber);
                const docs = cidNumber === 1 ? section.cid1 : section.cid2;
                return (
                  <div key={`${section.key}-cid${cidNumber}`} className="audit-process-doc-group">
                    <div className="audit-process-doc-group-title d-flex align-items-center justify-content-between gap-2">
                      <span>{cidDocumentLabel(cidNumber)}</span>
                      {canUploadSalesPack && (
                        <label
                          className="audit-process-doc-icon-btn"
                          title={`Upload ${cidDocumentLabel(cidNumber)}`}
                          aria-label={`Upload ${cidDocumentLabel(cidNumber)}`}
                        >
                          <Upload size={16} />
                          <input
                            type="file"
                            accept="application/pdf,.pdf"
                            className="d-none"
                            onChange={(event) => {
                              const file = event.target.files?.[0];
                              handleSalesPackCidUpload(code, file);
                              event.target.value = '';
                            }}
                          />
                        </label>
                      )}
                    </div>
                    {docs.length > 0
                      ? renderDocumentRows(docs, `${section.key}-cid${cidNumber}`)
                      : <div className="text-muted small">No file uploaded yet.</div>}
                  </div>
                );
              })}
              {section.other.length > 0 && (
                <div className="audit-process-doc-group">
                  <div className="audit-process-doc-group-title">Other</div>
                  {renderDocumentRows(section.other, `${section.key}-other`)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const handleSiteLocationChange = (siteId, field, value) => {
    setSiteLocations((prev) => prev.map((site) => (
      site.id === siteId ? { ...site, [field]: value } : site
    )));
  };

  const addSiteLocation = () => {
    setSiteLocations((prev) => [...prev, createSiteLocation(prev.length + 1)]);
  };

  const removeSiteLocation = (siteId) => {
    setSiteLocations((prev) => prev.filter((site) => site.id !== siteId));
    setAuditSchedules((prev) => prev.map((schedule) => ({
      ...schedule,
      siteLocationIds: (schedule.siteLocationIds || []).filter((id) => id !== siteId),
      siteLocationDetails: (schedule.siteLocationDetails || []).map((siteDetail) => ({
        ...siteDetail,
        siteLocationIds: (siteDetail.siteLocationIds || []).filter((id) => id !== siteId),
      })),
    })));
  };

  const handleAuditScheduleChange = (scheduleId, field, value) => {
    setAuditSchedules((prev) => prev.map((schedule) => {
      if (schedule.id !== scheduleId) return schedule;

      if (field === 'startDate' || field === 'endDate') {
        const nextSchedule = {
          ...schedule,
          [field]: value,
        };
        const mandayDetails = rebuildMandayDetailsByRange(
          nextSchedule.startDate,
          nextSchedule.endDate,
          nextSchedule.mandayDetails
        );
        const siteLocationDetails = rebuildSiteLocationDetailsByRange(
          nextSchedule.startDate,
          nextSchedule.endDate,
          nextSchedule.siteLocationDetails
        );
        return {
          ...nextSchedule,
          mandayDetails,
          siteLocationDetails,
          mandays: calculateTotalMandays(mandayDetails),
        };
      }

      return {
        ...schedule,
        [field]: value,
      };
    }));
  };

  const handleAuditScheduleAuditorChange = (scheduleId, userId) => {
    setSchedulingConflict(null);
    const selectedScope = getSelectedSchedulingScope();
    const roleOptions = getRoleOptionsForSchedule({ employeeId: userId }, selectedScope);
    const selected = auditorOptions.find((row) => String(row?.userId) === String(userId));
    setAuditSchedules((prev) => prev.map((schedule) => {
      if (schedule.id !== scheduleId) return schedule;
      return {
        ...schedule,
        employeeId: userId,
        employeeName: selected?.fullName || selected?.label || '',
        role: roleOptions.some((row) => row?.qualification === schedule.role)
          ? schedule.role
          : (roleOptions[0]?.qualification || ''),
      };
    }));
  };

  const addMandayDetailRow = (scheduleId) => {
    setAuditSchedules((prev) => prev.map((schedule) => {
      if (schedule.id !== scheduleId) return schedule;
      const mandayDetails = [...sanitizeMandayDetails(schedule.mandayDetails), createMandayDetail('', '')];
      return {
        ...schedule,
        mandayDetails,
        mandays: calculateTotalMandays(mandayDetails),
      };
    }));
  };

  const removeMandayDetailRow = (scheduleId, detailId) => {
    setAuditSchedules((prev) => prev.map((schedule) => {
      if (schedule.id !== scheduleId) return schedule;

      const removedMandayDetail = sanitizeMandayDetails(schedule.mandayDetails).find(row => row.id === detailId);
      const mandayDetails = sanitizeMandayDetails(schedule.mandayDetails).filter((row) => row.id !== detailId);
      const mandays = calculateTotalMandays(mandayDetails);

      let siteLocationDetails = sanitizeSiteLocationDetails(schedule.siteLocationDetails);
      if (removedMandayDetail && removedMandayDetail.date) {
        siteLocationDetails = siteLocationDetails.filter(sd => sd.date !== removedMandayDetail.date);
      }

      return {
        ...schedule,
        mandayDetails,
        siteLocationDetails, // Update siteLocationDetails too
        mandays,
      };
    }));
  };

  const handleMandayDetailChange = (scheduleId, detailId, field, value) => {
    if (field === 'mandays') {
      setMandaysValidationErrors((prev) => {
        const next = { ...prev };
        const normalizedValue = String(value ?? '').trim();
        if (!normalizedValue || !Number.isFinite(Number(normalizedValue)) || Number(normalizedValue) <= 0) {
          next[`${scheduleId}:${detailId}`] = 'Mandays is required and must be greater than 0.';
        } else {
          delete next[`${scheduleId}:${detailId}`];
        }
        return next;
      });
    }
    setAuditSchedules((prev) => prev.map((schedule) => {
      if (schedule.id !== scheduleId) return schedule;

      const mandayDetails = sanitizeMandayDetails(schedule.mandayDetails).map((row) => {
        if (row.id !== detailId) return row;
        return {
          ...row,
          [field]: value,
        };
      });

      return {
        ...schedule,
        mandayDetails,
        mandays: calculateTotalMandays(mandayDetails),
      };
    }));
  };

  const addAuditSchedule = () => {
    const stageKey = resolveScopeStageKey(selectedSchedulingScopeKey);
    setAuditSchedules((prev) => [...prev, createAuditSchedule(prev.length + 1, stageKey)]);
  };

  const removeAuditSchedule = (scheduleId) => {
    setAuditSchedules((prev) => prev.filter((schedule) => schedule.id !== scheduleId));
  };

  const handleToggleSiteLocationDetail = (scheduleId, siteDetailId, siteId) => {
    setAuditSchedules((prev) => prev.map((schedule) => {
      if (schedule.id !== scheduleId) return schedule;

      const nextSiteLocationDetails = (schedule.siteLocationDetails || []).map((siteDetail) => {
        if (siteDetail.id !== siteDetailId) return siteDetail;
        const currentIds = Array.isArray(siteDetail.siteLocationIds) ? siteDetail.siteLocationIds : [];
        const isSelected = currentIds.includes(siteId);
        return {
          ...siteDetail,
          siteLocationIds: isSelected
            ? currentIds.filter((id) => id !== siteId)
            : [...currentIds, siteId],
        };
      });

      return {
        ...schedule,
        siteLocationDetails: nextSiteLocationDetails,
      };
    }));
  };

  const handleAccommodationChange = (accommodationId, field, value) => {
    const createAccommodationContact = () => ({
      id: `contact-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: '',
      phoneNumber: '',
      note: '',
    });

    const getScheduleDates = (schedule) => {
      const rows = sanitizeMandayDetails(schedule?.mandayDetails)
        .map((row) => String(row?.date || '').trim())
        .filter(Boolean)
        .sort();

      if (rows.length > 0) return rows;
      return buildDateRange(schedule?.startDate, schedule?.endDate);
    };

    setAccommodations((prev) => prev.map((item) => {
      if (item.id !== accommodationId) return item;

      if (field === 'providedByClient') {
        const providedByClient = Boolean(value);
        return {
          ...item,
          providedByClient,
          ...(providedByClient
            ? {
              scheduledAuditorId: '',
              scheduledScheduleId: '',
              selectedScheduleDates: [],
              auditStartDate: '',
              auditEndDate: '',
              duration: 1,
              needPickupDropOff: false,
              contacts: [],
            }
            : {}),
        };
      }

      if (field === 'scheduledAuditorId') {
        const selectedAuditorId = String(value || '');
        const nextDates = Array.from(new Set(
          auditSchedules
            .filter((schedule) => String(schedule?.employeeId || '') === selectedAuditorId)
            .flatMap((schedule) => getScheduleDates(schedule))
        )).sort();

        return {
          ...item,
          scheduledAuditorId: selectedAuditorId,
          scheduledScheduleId: '',
          selectedScheduleDates: nextDates,
          auditStartDate: nextDates[0] || '',
          auditEndDate: nextDates[nextDates.length - 1] || '',
          duration: nextDates.length,
        };
      }

      if (field === 'scheduledScheduleId') {
        const selectedSchedule = auditSchedules.find(
          (schedule) => String(schedule?.id) === String(value)
            && String(schedule?.employeeId || '') === String(item?.scheduledAuditorId || '')
        );
        const selectedDates = getScheduleDates(selectedSchedule);

        return {
          ...item,
          scheduledScheduleId: String(value || ''),
          selectedScheduleDates: selectedDates,
          auditStartDate: selectedDates[0] || '',
          auditEndDate: selectedDates[selectedDates.length - 1] || '',
          duration: selectedDates.length > 0 ? selectedDates.length : 1,
        };
      }

      if (field === 'selectedScheduleDates') {
        const nextDates = Array.isArray(value?.dates)
          ? value.dates.map((date) => String(date || '').trim()).filter(Boolean).sort()
          : [];
        return {
          ...item,
          selectedScheduleDates: nextDates,
          auditStartDate: nextDates[0] || '',
          auditEndDate: nextDates[nextDates.length - 1] || '',
          duration: nextDates.length,
        };
      }

      if (field === 'needPickupDropOff') {
        const needPickupDropOff = Boolean(value);
        return {
          ...item,
          needPickupDropOff,
          contacts: needPickupDropOff
            ? ((item.contacts || []).length > 0 ? item.contacts : [createAccommodationContact()])
            : [],
        };
      }

      return { ...item, [field]: value };
    }));
  };

  const addAccommodation = () => {
    setAccommodations((prev) => [...prev, createAccommodation()]);
  };

  const removeAccommodation = (accommodationId) => {
    setAccommodations((prev) => prev.filter((item) => item.id !== accommodationId));
  };

  const addAccommodationContact = (accommodationId) => {
    setAccommodations((prev) => prev.map((item) => {
      if (item.id !== accommodationId) return item;
      return {
        ...item,
        contacts: [
          ...(item.contacts || []),
          {
            id: `contact-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            name: '',
            phoneNumber: '',
            note: '',
          },
        ],
      };
    }));
  };

  const handleAccommodationContactChange = (accommodationId, contactId, field, value) => {
    setAccommodations((prev) => prev.map((item) => {
      if (item.id !== accommodationId) return item;
      return {
        ...item,
        contacts: (item.contacts || []).map((contact) => (
          contact.id === contactId ? { ...contact, [field]: value } : contact
        )),
      };
    }));
  };

  const handleAccommodationUpload = (accommodationId, files) => {
    const uploaded = Array.from(files || []).map((file) => ({
      id: `doc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: file.name,
      sizeKb: Math.max(1, Math.round(file.size / 1024)),
    }));

    if (uploaded.length === 0) return;

    setAccommodations((prev) => prev.map((item) => {
      if (item.id !== accommodationId) return item;
      return {
        ...item,
        supportingDocuments: [...(item.supportingDocuments || []), ...uploaded],
      };
    }));
  };

  const getSiteNameById = (siteId) => {
    const found = siteLocations.find((site) => site.id === siteId);
    return found?.siteName || 'Unknown Site';
  };

  const getScheduleScopeDetails = (scopeKey) => {
    if (!scopeKey) return { mandays: '-', eaCode: '-', scope: '-' };
    
    // First, try to find in stages (which should have all the data)
    const stageMatch = stages.find((stage) => stage.key === scopeKey);
    if (stageMatch) {
      // Only use if mandays is not empty/dash
      if (stageMatch.mandays && stageMatch.mandays !== '-') {
        return {
          mandays: stageMatch.mandays,
          eaCode: stageMatch.eaCode || '-',
          scope: stageMatch.scope || '-',
        };
      }
      // Still use EA Code and Scope from stage even if mandays is empty
      return {
        mandays: stageMatch.mandays || '-',
        eaCode: stageMatch.eaCode || '-',
        scope: stageMatch.scope || '-',
      };
    }
    
    // If not found in stages, try schedulingScopes for basic label info
    const scope = schedulingScopes.find((s) => s.key === scopeKey);
    if (scope) {
      return {
        mandays: '-',
        eaCode: '-',
        scope: scope.label || '-',
      };
    }
    
    return {
      mandays: '-',
      eaCode: '-',
      scope: '-',
    };
  };

  const renderSchedulingScopeSelector = () => {
    const scopeOptions = schedulingScopes.length > 0
      ? schedulingScopes
      : [{ key: getSchedulingScopeKey(null, null), label: 'General Project Scope' }];
    const canAdminReviewScope = myProjectRole.isAdminOrManager;
    return (
      <div className={`audit-process-scope-selector ${selectedSchedulingScopeKey ? 'is-selected' : 'is-pending'}`}>
        <div className="audit-process-scope-selector__prompt">
          <Info size={16} />
          <span><strong>Select the audit scope first.</strong> Scheduling data below follows this selection.</span>
        </div>
        <label className="form-label" htmlFor="audit-scheduling-scope">{AUDIT_SCOPE_SELECTOR_LABEL} <span className="text-danger">*</span></label>
        <select
          id="audit-scheduling-scope"
          className="form-select audit-process-scope-select"
          value={selectedSchedulingScopeKey}
          onChange={(e) => handleSchedulingScopeChange(e.target.value)}
          disabled={schedulingLock?.locked || (!canEditScheduling && !canAdminReviewScope)}
          required
        >
          <option value="" disabled>Select an audit scope...</option>
          {scopeOptions.map((scope) => (
            <option key={scope.key} value={scope.key}>{scope.label}</option>
          ))}
        </select>
        <small className="audit-process-scope-selector__hint">
          {canAdminReviewScope && !canEditScheduling
            ? 'Select any audit scope to review scheduling data. Editing remains restricted to Scheduler and Operation users.'
            : 'Confirm this selection before editing auditor dates, sites, or schedule status.'}
        </small>
      </div>
    );
  };

  const renderSchedulingContent = () => {
    const selectedScope = getSelectedSchedulingScope();
    const filteredAuditors = getAvailableAuditorsForScope(selectedScope);
    const getAuditorBookingForDate = (employeeId, date) => {
      const bookings = auditorAvailabilityByDate[String(employeeId || '')]?.[date] || [];
      const otherProjectBookings = bookings.filter((booking) => String(booking.projectId) !== String(projectId));
      const bookedMandays = otherProjectBookings.reduce((total, booking) => total + (Number(booking.mandays) || 0), 0);
      if (bookedMandays <= 0) return null;
      return {
        level: bookedMandays >= 1 ? 'full' : 'half',
        mandays: bookedMandays,
      };
    };
    const topMandaysLimit = schedulingScopeMandaysLimit;
    const currentTotalMandays = getTotalScheduledMandays(auditSchedules);
    const isMandaysOverLimit = topMandaysLimit != null && currentTotalMandays > topMandaysLimit;
    const schedulingConfirmed = isSchedulingScopeConfirmed({ auditSchedules });
    const schedulingStatusLabel = schedulingConfirmed
      ? 'Confirmed'
      : auditSchedules.some((row) => String(row?.employeeId || '').trim())
        ? 'In Progress'
        : 'Draft';
    const mandaysErrors = getMandaysValidationErrors(auditSchedules);
    const hasMandaysErrors = Object.keys(mandaysErrors).length > 0;
    
    // Get the current stage details for the selected scope using the key directly
    const scopeDetails = getScheduleScopeDetails(selectedSchedulingScopeKey);

    // Whoever the Audit Scheduling task for this scope is assigned to is the person actually
    // doing the scheduling — surface it so anyone viewing this tab knows who to follow up with.
    // Audit Scheduling is usually created as ONE project-level (unscoped) task, not per-standard
    // — and even when scoped, it's never stage-split (Initial Certification Stage 1/Stage 2 share
    // ONE schedule/task, only mandays are divided). Try the scope-aware lookup first (stripping
    // any :s1/:s2 suffix, since selectTasksForAuditScope's strict stageKey match would find
    // nothing for a staged scope), then fall back to any Audit Scheduling task on the project —
    // there is normally only one, so this is a safe last resort rather than a guess.
    const schedulingAssigneeName = getAssignedAuditTaskName(
      [...(projectAuditTasks || []), ...(projectWorkflowTasks || [])],
      schedulingScopes.find((scope) => scope?.key === selectedSchedulingScopeKey) || selectedSchedulingScopeKey,
      (task) => isSchedulingTaskCode(task?.taskTypeCode),
    );

    const schedulingToolbar = (
      <div className="audit-process-scheduling-toolbar mb-3">
        {schedulingLock?.locked && (
          <div className="audit-process-scheduling-lock" role="status" aria-live="polite">
            <span className="audit-process-scheduling-lock__icon" aria-hidden="true"><LockKeyhole size={18} /></span>
            <div>
              <strong>Scheduling locked</strong>
              <p className="mb-0">{schedulingLock.message} Existing scheduling remains available for review, but editing is disabled.</p>
            </div>
          </div>
        )}
        {(projectAuditInfo.auditMethodology || projectAuditInfo.multiSite) && (() => {
          const grouped = isGroupedMethodology(projectAuditInfo.auditMethodology);
          const methodologyType = getMethodologyType(projectAuditInfo.auditMethodology);
          const memberStandards = grouped ? resolveMemberStandardNames(projectAuditInfo.methodologyGroup, schedulingScopes) : [];
          return (
            <div className="alert alert-info d-flex flex-wrap align-items-center gap-3 mb-3 py-2">
              {projectAuditInfo.auditMethodology && (
                <span>
                  <strong>Methodology:</strong> {projectAuditInfo.auditMethodology}
                  {grouped && (
                    <span className={`badge ms-2 ${methodologyType === 'integrated' ? 'bg-purple' : 'bg-indigo'} text-white`}>
                      {methodologyType === 'integrated' ? 'Integrated' : 'Combined'}
                    </span>
                  )}
                </span>
              )}
              {grouped && memberStandards.length > 0 && (
                <span className="d-inline-flex flex-wrap align-items-center gap-1">
                  <span className="text-muted small me-1">Standards:</span>
                  {memberStandards.map((std) => (
                    <span key={std} className="badge bg-teal-lt text-teal border border-teal">{std}</span>
                  ))}
                </span>
              )}
              {projectAuditInfo.multiSite && (
                <span className="badge bg-teal text-white">
                  Multi-site{projectAuditInfo.numberOfSites ? ` (${projectAuditInfo.numberOfSites} sites)` : ''}
                </span>
              )}
            </div>
          );
        })()}
        <div className="d-flex justify-content-end align-items-center gap-2 mb-2">
          {selectedSchedulingScopeKey && (
            <span className="audit-process-scheduling-assignee">
              <User size={13} />
              Scheduling Owner: <strong>{schedulingAssigneeName || 'Unassigned'}</strong>
            </span>
          )}
          <span className={`audit-process-scheduling-status audit-process-scheduling-status-${schedulingStatusLabel.toLowerCase().replace(/\s+/g, '-')}`}>
            Status: {schedulingStatusLabel}
          </span>
        </div>
        {scopeDetails && (
          <div className="row g-2 mt-3">
            <div className="col-12">
              <div className="border p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-0">
                      <span className="text-muted small d-block">Mandays</span>
                      <span className="fw-semibold">{scopeDetails.mandays || '-'}</span>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-0">
                      <span className="text-muted small d-block">EA Code</span>
                      <span className="fw-semibold">{scopeDetails.eaCode || '-'}</span>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-0">
                      <span className="text-muted small d-block">Scope</span>
                      <span className="fw-semibold">{scopeDetails.scope || '-'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );

    if (activeSchedulingSubTab === 'review-confirm') {
      const assignedAuditors = auditSchedules.filter((row) => String(row?.employeeId || '').trim());
      const scheduledDates = Array.from(new Set(auditSchedules.flatMap((row) => sanitizeMandayDetails(row.mandayDetails).map((detail) => detail.date).filter(Boolean)))).sort();
      const confirmScheduling = async () => {
        const confirmedSchedules = auditSchedules.map((row) => ({ ...row, scheduleStatus: 'Confirmed' }));
        setAuditSchedules(confirmedSchedules);
        await handleSaveScheduling(confirmedSchedules);
      };
      return (
        <div className="audit-process-scheduling-content">
          {schedulingToolbar}
          <div className="audit-process-review-grid">
            <div className="audit-process-review-metric"><span>Audit Dates</span><strong>{scheduledDates.length}</strong><small>{scheduledDates.join(', ') || 'Not selected'}</small></div>
            <div className="audit-process-review-metric"><span>Sites</span><strong>{siteLocations.length}</strong><small>{siteLocations.map((site) => site.siteName).filter(Boolean).join(', ') || 'Not configured'}</small></div>
            <div className="audit-process-review-metric"><span>Audit Team</span><strong>{assignedAuditors.length}</strong><small>{assignedAuditors.map((row, index) => `${index === 0 ? 'Lead' : `Auditor ${index + 1}`}: ${row.employeeName || '-'}`).join(' · ') || 'Not assigned'}</small></div>
            <div className="audit-process-review-metric"><span>Mandays</span><strong>{currentTotalMandays}</strong><small>Configured for this scope</small></div>
          </div>
          <section className="audit-process-scheduling-card mt-3" aria-labelledby="audit-team-assignment-heading">
            <div className="audit-process-scheduling-card-header">
              <div>
                <h5 id="audit-team-assignment-heading" className="mb-0">Auditor Assignment</h5>
                <small className="text-muted">Review the competency-based team assignment before confirming.</small>
              </div>
            </div>
            <div className="audit-process-scheduling-card-body p-0">
              {assignedAuditors.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-vcenter mb-0">
                    <thead>
                      <tr>
                        <th>Team member</th>
                        <th>Assignment</th>
                        <th>Audit dates</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignedAuditors.map((row, index) => (
                        <tr key={row.id || `${row.employeeId}-${index}`}>
                          <td>
                            <div className="fw-semibold">{row.employeeName || `User #${row.employeeId}`}</div>
                            <small className="text-muted">{index === 0 ? 'Primary audit team member' : 'Additional audit team member'}</small>
                          </td>
                          <td><span className="badge bg-teal-lt text-teal border border-teal">{row.role || (index === 0 ? 'Lead Auditor' : 'Auditor')}</span></td>
                          <td>{sanitizeMandayDetails(row.mandayDetails).map((detail) => detail.date).filter(Boolean).join(', ') || 'Not selected'}</td>
                          <td><span className="badge bg-azure-lt text-azure">{row.scheduleStatus || 'Draft'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-muted small p-3">No qualified auditor has been assigned yet.</div>
              )}
            </div>
          </section>
          <div className={`alert ${schedulingConfirmed ? 'alert-success' : 'alert-info'} mt-3 mb-0`} role="status">
            {schedulingConfirmed
              ? 'Scheduling is confirmed. All mandatory workflow steps are complete.'
              : 'Review the information above, then confirm to complete Audit Scheduling.'}
          </div>
          {canEditScheduling && !schedulingLock?.locked && !schedulingConfirmed && (
            <div className="d-flex justify-content-end mt-3">
              <button type="button" className="btn btn-success" onClick={confirmScheduling} disabled={isSavingScheduling || assignedAuditors.length === 0 || scheduledDates.length === 0 || hasMandaysErrors}>
                <Check size={16} className="me-1" />{isSavingScheduling ? 'Confirming…' : 'Confirm Scheduling'}
              </button>
            </div>
          )}
        </div>
      );
    }

    if (activeSchedulingSubTab === 'audit-schedule') {
      return (
        <div className="audit-process-scheduling-content">
          {schedulingToolbar}

          {schedulingConflict && (
            <div className="alert alert-danger d-flex align-items-start gap-2" role="alert">
              <AlertTriangle size={19} className="mt-1 flex-shrink-0" />
              <div>
                <div className="fw-semibold">Scheduling conflict</div>
                <div>{schedulingConflict.message}</div>
                <div className="small mt-1">Choose another auditor or audit date, then save again.</div>
              </div>
            </div>
          )}

          {auditSchedules.length === 0 ? (
            <div className="text-muted small">No auditor schedule yet. Click Add Auditor.</div>
          ) : auditSchedules.map((schedule, index) => {
            const roleOptions = getRoleOptionsForSchedule(schedule, selectedScope);
            const selectedEmployeeIdsBefore = new Set(
              auditSchedules
                .slice(0, index)
                .map((row) => String(row?.employeeId || ''))
                .filter(Boolean)
            );
            const availableAuditorsForRow = filteredAuditors.filter((auditor) => {
              const auditorId = String(auditor?.userId || '');
              return !selectedEmployeeIdsBefore.has(auditorId) || auditorId === String(schedule?.employeeId || '');
            });
            const mandayDateRows = sanitizeMandayDetails(schedule.mandayDetails)
              .map((row) => row.date)
              .filter(Boolean)
              .sort();
            const selectedScheduleDates = mandayDateRows.length > 0
              ? mandayDateRows
              : buildDateRange(schedule.startDate, schedule.endDate);
            const isPickerOpen = openDatePickerScheduleId === schedule.id;
            const tempData = datePickerTempValues[schedule.id] || { selectedDates: new Set() };
            const selectedDatesSet = tempData.selectedDates instanceof Set ? tempData.selectedDates : new Set(tempData.selectedDates || []);
            const isConflictingSchedule = schedulingConflict
              && String(schedule.employeeName || '').trim()
              && String(schedulingConflict.message || '').toLowerCase().includes(String(schedule.employeeName).toLowerCase());
            
            const calendarMonth = calendarDisplayMonth[schedule.id] || (schedule.startDate ? new Date(schedule.startDate).getMonth() : new Date().getMonth());
            const calendarYear = schedule.startDate ? new Date(schedule.startDate).getFullYear() : new Date().getFullYear();
            
            const handleOpenDatePicker = () => {
              const rangeDates = selectedScheduleDates;
              setOpenDatePickerScheduleId(schedule.id);
              setDatePickerTempValues((prev) => ({
                ...prev,
                [schedule.id]: { selectedDates: new Set(rangeDates) },
              }));
              setCalendarDisplayMonth((prev) => ({
                ...prev,
                [schedule.id]: schedule.startDate ? new Date(schedule.startDate).getMonth() : new Date().getMonth(),
              }));
            };

            const handleCloseDatePicker = () => {
              setOpenDatePickerScheduleId(null);
            };

            const handleToggleDateSelection = (dateStr) => {
              const newSet = new Set(selectedDatesSet);
              if (newSet.has(dateStr)) {
                newSet.delete(dateStr);
              } else {
                newSet.add(dateStr);
              }
              setDatePickerTempValues((prev) => ({
                ...prev,
                [schedule.id]: { selectedDates: newSet },
              }));
            };

            const handleApplyDateSelection = () => {
              if (selectedDatesSet.size > 0) {
                const sortedDates = Array.from(selectedDatesSet).sort();
                const minDate = sortedDates[0];
                const maxDate = sortedDates[sortedDates.length - 1];
                // A scope's mandays represent the total for the stage. A single
                // selected date can safely inherit that total; multi-day audits
                // remain explicit so users can assign the intended split.
                const scopeMandays = Number(schedulingScopeMandaysLimit);
                const defaultMandays = sortedDates.length === 1
                  && Number.isFinite(scopeMandays)
                  && scopeMandays > 0
                  ? String(scopeMandays)
                  : '';

                setAuditSchedules((prev) => prev.map((row) => {
                  if (row.id !== schedule.id) return row;

                  const existingMandaysMap = new Map(
                    sanitizeMandayDetails(row.mandayDetails)
                      .filter((detail) => detail.date)
                      .map((detail) => [detail.date, detail])
                  );

                  const existingSitesMap = new Map(
                    sanitizeSiteLocationDetails(row.siteLocationDetails)
                      .filter((detail) => detail.date)
                      .map((detail) => [detail.date, detail])
                  );

                  const nextMandayDetails = sortedDates.map((date) => {
                    const existing = existingMandaysMap.get(date);
                    return existing
                      ? { ...existing, date, mandays: existing.mandays || defaultMandays }
                      : createMandayDetail(date, defaultMandays);
                  });

                  const nextSiteLocationDetails = sortedDates.map((date) => {
                    const existing = existingSitesMap.get(date);
                    return existing
                      ? { ...existing, date }
                      : createSiteLocationDetail(date, []);
                  });

                  return {
                    ...row,
                    startDate: minDate,
                    endDate: maxDate,
                    mandayDetails: nextMandayDetails,
                    siteLocationDetails: nextSiteLocationDetails,
                    mandays: calculateTotalMandays(nextMandayDetails),
                  };
                }));

                setMandaysValidationErrors({});
                setSchedulingConflict(null);
                handleCloseDatePicker();
              }
            };

            const handleChangeCalendarMonth = (offset) => {
              const newMonth = calendarMonth + offset;
              const newYear = newMonth < 0 ? calendarYear - 1 : newMonth > 11 ? calendarYear + 1 : calendarYear;
              const adjustedMonth = ((newMonth % 12) + 12) % 12;
              setCalendarDisplayMonth((prev) => ({
                ...prev,
                [schedule.id]: adjustedMonth,
              }));
            };

            const calendarDays = generateCalendarDays(calendarYear, calendarMonth);
            const monthYearDisplay = new Date(calendarYear, calendarMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

            return (
            <div key={schedule.id} className={`audit-process-scheduling-card audit-process-scheduling-card-date-picker mb-3${isConflictingSchedule ? ' border border-danger' : ''}`}>
              <div className="audit-process-scheduling-card-header">
                <h5 className="mb-0">Auditor -{index + 1}</h5>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => removeAuditSchedule(schedule.id)}
                  title="Remove auditor"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="audit-process-scheduling-card-body">
                <div className="row g-3">
                  <div className="col-12">
                    <div className="audit-process-date-selection-panel">
                      <div className="audit-process-date-selection-main">
                        <label className="form-label required">Select Audit Schedule Dates</label>
                        <div className="audit-process-date-range-picker-wrapper">
                          <button
                            type="button"
                            className="audit-process-date-picker-trigger"
                            onClick={handleOpenDatePicker}
                          >
                            <CalendarDays size={16} className="me-2" />
                            View Calendar
                          </button>
                          {isPickerOpen && (
                            <div className="audit-process-calendar-picker">
                              <div className="calendar-month-header">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link"
                                  onClick={() => handleChangeCalendarMonth(-1)}
                                  style={{ padding: '0 0.5rem' }}
                                >
                                  ←
                                </button>
                                <h6 className="mb-0" style={{ minWidth: '150px', textAlign: 'center' }}>{monthYearDisplay}</h6>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link"
                                  onClick={() => handleChangeCalendarMonth(1)}
                                  style={{ padding: '0 0.5rem' }}
                                >
                                  →
                                </button>
                              </div>
                              <div className="calendar-weekdays">
                                <div>Sun</div>
                                <div>Mon</div>
                                <div>Tue</div>
                                <div>Wed</div>
                                <div>Thu</div>
                                <div>Fri</div>
                                <div>Sat</div>
                              </div>
                              {schedule.employeeId && (
                                <div className="calendar-availability-legend" aria-label="Auditor availability legend">
                                  <span><i className="calendar-availability-swatch half" /> Half booked</span>
                                  <span><i className="calendar-availability-swatch full" /> Fully booked</span>
                                  <span className="text-muted">Select a marked date only if the booking is being replaced.</span>
                                </div>
                              )}
                              <div className="calendar-grid">
                                {calendarDays.map((day, idx) => {
                                  const dateStr = formatDateForCalendar(day);
                                  const isSelected = selectedDatesSet.has(dateStr);
                                  const isCurrentMonth = day.getMonth() === calendarMonth;
                                  const availability = schedule.employeeId && isCurrentMonth
                                    ? getAuditorBookingForDate(schedule.employeeId, dateStr)
                                    : null;
                                  const availabilityLabel = availability
                                    ? `${availability.level === 'full' ? 'Fully' : 'Half'} booked (${availability.mandays} manday${availability.mandays === 1 ? '' : 's'})`
                                    : 'Available';
                                  return (
                                    <button
                                      key={idx}
                                      type="button"
                                      className={`calendar-day ${isCurrentMonth ? '' : 'other-month'} ${availability ? `availability-${availability.level}` : ''} ${isSelected ? 'selected' : ''}`}
                                      onClick={() => isCurrentMonth && handleToggleDateSelection(dateStr)}
                                      disabled={!isCurrentMonth}
                                      title={`${dateStr} — ${availabilityLabel}`}
                                    >
                                      {day.getDate()}
                                    </button>
                                  );
                                })}
                              </div>
                              <div className="calendar-actions">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-secondary"
                                  onClick={handleCloseDatePicker}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={handleApplyDateSelection}
                                  disabled={selectedDatesSet.size === 0}
                                  title={selectedDatesSet.size > 0 ? `Apply ${selectedDatesSet.size} date(s)` : 'Select dates first'}
                                >
                                  Apply <span className="badge bg-light text-dark ms-1">{selectedDatesSet.size}</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {selectedScheduleDates.length > 0 && (
                        <div className="audit-process-date-selection-preview">
                          <div className="audit-process-selected-dates-title">Selected Dates: &nbsp;
                              {selectedScheduleDates.length > 0
                                ? `${selectedScheduleDates.length}`
                                : 'No dates selected'}
                          </div>
                          <div className="audit-process-selected-dates">
                            {selectedScheduleDates.map((date) => (
                              <span key={date} className="audit-process-date-badge">{date}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label required">Staff</label>
                    <select
                      className={`form-control${isConflictingSchedule ? ' is-invalid' : ''}`} required
                      value={schedule.employeeId || ''}
                      onChange={(e) => handleAuditScheduleAuditorChange(schedule.id, e.target.value)}
                    >
                      <option value="" disabled>Please select an auditor...</option>
                      {availableAuditorsForRow.map((auditor) => (
                        <option key={auditor.userId} value={auditor.userId}>
                          {auditor.label || auditor.fullName || auditor.username || `User #${auditor.userId}`}
                        </option>
                      ))}
                    </select>
                    {availableAuditorsForRow.length === 0 && (
                      <div className="text-muted small mt-1 text-danger">
                        {filteredAuditors.length === 0
                          ? 'No auditor qualified with competency for selected project standard.'
                          : 'All auditors are already assigned in previous lists.'}
                      </div>
                    )}
                    {isConflictingSchedule && (
                      <div className="invalid-feedback d-block">This auditor is unavailable for the selected date.</div>
                    )}
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Type</label>
                    <select
                      className="form-control"
                      value={schedule.auditType}
                      onChange={(e) => handleAuditScheduleChange(schedule.id, 'auditType', e.target.value)}
                    >
                      {AUDIT_TYPE_OPTIONS.map((typeOption) => (
                        <option key={typeOption} value={typeOption}>{typeOption}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Schedule Status</label>
                    <select
                      className="form-control"
                      value={schedule.scheduleStatus}
                      onChange={(e) => handleAuditScheduleChange(schedule.id, 'scheduleStatus', e.target.value)}
                    >
                      {SCHEDULE_STATUS_OPTIONS.map((statusOption) => (
                        <option key={statusOption} value={statusOption}>{statusOption}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-12">
                    <label className="form-label">Assign as</label>
                    <select
                      className="form-control"
                      value={schedule.role || ''}
                      onChange={(e) => handleAuditScheduleChange(schedule.id, 'role', e.target.value)}
                      disabled={!schedule.employeeId}
                    >
                      <option value="">Select role</option>
                      {roleOptions.map((roleRow) => (
                        <option key={roleRow.qualificationId || roleRow.qualification} value={roleRow.qualification || ''}>
                          {roleRow.qualification} (Level {roleRow.qualificationLevel ?? '-'})
                        </option>
                      ))}
                    </select>
                    {!!schedule.employeeId && roleOptions.length === 0 && (
                      <div className="text-muted small mt-1">No compatible role for selected auditor competency level.</div>
                    )}
                  </div>
                  <div className="col-12">
                    {selectedScheduleDates.length > 0 ? (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="form-label">Schedule Details <span className="audit-process-mandays-total-label">
                              Total Mandays: {schedule.mandays || '0'}
                            </span></label>
                          {/* <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => addMandayDetailRow(schedule.id)}
                          >
                            <Plus size={14} className="me-1" />
                            Add Date
                          </button> */}
                        </div>
                        {(schedule.mandayDetails || []).length === 0 ? (
                          <div className="text-muted small">Set Start Date and End Date to auto-generate dates, or add date manually.</div>
                        ) : (
                          (schedule.mandayDetails || []).map((mandayRow) => {
                            const siteDetail = (schedule.siteLocationDetails || []).find(sd => sd.date === mandayRow.date);

                            return (
                              <div key={mandayRow.id} className="audit-process-manday-site-row row g-2 mt-2 align-items-start">
                                <div className="col-md-3">
                                  <label className="form-label small text-muted mb-1">Date</label>
                                  <input
                                    type="date"
                                    className="form-control"
                                    value={mandayRow.date || ''}
                                    onChange={(e) => handleMandayDetailChange(schedule.id, mandayRow.id, 'date', e.target.value)}
                                  />
                                </div>
                                <div className="col-md-2">
                                  <label className="form-label small text-muted mb-1" htmlFor={`mandays-${schedule.id}-${mandayRow.id}`}>Mandays <span className="text-danger">*</span></label>
                                  <input
                                    id={`mandays-${schedule.id}-${mandayRow.id}`}
                                    type="number"
                                    className={`form-control ${mandaysValidationErrors[`${schedule.id}:${mandayRow.id}`] ? 'is-invalid' : ''}`}
                                    min="0.5"
                                    step="0.5" max="1"
                                    value={mandayRow.mandays}
                                    placeholder="Required: 0.5 or 1"
                                    required
                                    aria-invalid={Boolean(mandaysValidationErrors[`${schedule.id}:${mandayRow.id}`])}
                                    aria-describedby={mandaysValidationErrors[`${schedule.id}:${mandayRow.id}`] ? `mandays-error-${schedule.id}-${mandayRow.id}` : undefined}
                                    onChange={(e) => handleMandayDetailChange(schedule.id, mandayRow.id, 'mandays', e.target.value)}
                                  />
                                  {mandaysValidationErrors[`${schedule.id}:${mandayRow.id}`] && (
                                    <div id={`mandays-error-${schedule.id}-${mandayRow.id}`} className="invalid-feedback d-block">
                                      {mandaysValidationErrors[`${schedule.id}:${mandayRow.id}`]}
                                    </div>
                                  )}
                                </div>
                                <div className="col-md-6">
                                  <label className="form-label small text-muted mb-1">Sites</label>
                                  <div className="audit-process-site-pills-wrap">
                                    {siteLocations.length === 0 ? (
                                      <span className="text-muted small">No sites yet.</span>
                                    ) : siteLocations.map((site) => {
                                      const isPillSelected = (siteDetail?.siteLocationIds || []).includes(site.id);
                                      return (
                                        <button
                                          key={site.id}
                                          type="button"
                                          className={`audit-process-site-pill${isPillSelected ? ' active' : ''}`}
                                          onClick={() => siteDetail && handleToggleSiteLocationDetail(schedule.id, siteDetail.id, site.id)}
                                          disabled={!siteDetail}
                                        >
                                          {isPillSelected && <Check size={11} className="me-1" />}
                                          {site.siteName}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {!siteDetail && <div className="text-danger small mt-1">Date not in sync.</div>}
                                </div>
                                <div className="col-md-1 d-grid">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => removeMandayDetailRow(schedule.id, mandayRow.id)}
                                    title="Remove date row"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </>
                    ) : (
                      // This is for single date scheduling. Keep the original site selection separate.
                      <div className="text-muted small"></div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );})}

          <div className="audit-process-add-auditor-bottom">
            {isMandaysOverLimit && (
              <span className="audit-process-save-warning-label" role="alert">
                Warning: Total Mandays {currentTotalMandays} exceeds limit {topMandaysLimit}
              </span>
            )}
            <button type="button" className="btn audit-process-scheduling-action-btn audit-process-scheduling-action-btn-secondary" onClick={addAuditSchedule}>
              <Plus size={16} className="me-1" />
              Add Auditor
            </button>
          </div>
        </div>
      );
    }

    if (activeSchedulingSubTab === 'site-location') {
      return (
        <div className="audit-process-scheduling-content">
          {schedulingToolbar}

          {siteLocations.length === 0 ? (
            <div className="text-muted small">No site location yet. Click Add Site Location.</div>
          ) : siteLocations.map((site, index) => {
            const linkedAuditorDates = auditSchedules.flatMap((schedule) => {
              const datesFromDetails = (schedule.siteLocationDetails || [])
                .filter((sd) => (sd.siteLocationIds || []).includes(site.id))
                .map((sd) => sd.date)
                .filter(Boolean);
              const hasSingleDateLink = (schedule.siteLocationIds || []).includes(site.id);
              const allDates = datesFromDetails.length > 0
                ? datesFromDetails
                : hasSingleDateLink
                  ? (schedule.startDate ? [schedule.startDate] : [])
                  : [];
              if (allDates.length === 0) return [];
              return [{ id: schedule.id, auditorName: schedule.employeeName || '-', mandays: schedule.mandays, auditType: schedule.auditType, dates: allDates }];
            });
            return (
              <div key={site.id} className="audit-process-scheduling-card mb-3">
                <div className="audit-process-scheduling-card-header">
                  <h5 className="mb-0">Site - {index + 1}</h5>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => removeSiteLocation(site.id)}
                    title="Remove site location"
                  >
                    <X size={14} />
                  </button>
                </div>
                <div className="audit-process-scheduling-card-body">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Site Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={site.siteName}
                        onChange={(e) => handleSiteLocationChange(site.id, 'siteName', e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Country</label>
                      <input
                        type="text"
                        className="form-control"
                        value={site.country}
                        onChange={(e) => handleSiteLocationChange(site.id, 'country', e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">City</label>
                      <input
                        type="text"
                        className="form-control"
                        value={site.city}
                        onChange={(e) => handleSiteLocationChange(site.id, 'city', e.target.value)}
                      />
                    </div>
                    <div className="col-md-12">
                      <label className="form-label">Site Address</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={site.address}
                        onChange={(e) => handleSiteLocationChange(site.id, 'address', e.target.value)}
                      />
                    </div>

                    {linkedAuditorDates.length > 0 && (
                      <div className="col-12">
                        <label className="form-label small fw-semibold text-muted mb-1">Assigned Auditors</label>
                        <div className="audit-process-linked-auditors">
                          {linkedAuditorDates.map((auditor) => (
                            <div key={auditor.id} className="audit-process-linked-auditor-item">
                              <div className="audit-process-linked-auditor-name">
                                <span className="fw-semibold">{auditor.auditorName}</span>
                                <span className="audit-process-linked-auditor-type">{auditor.auditType || '-'}</span>
                              </div>
                              <div className="audit-process-auditor-dates">
                                {auditor.dates.map((date) => (
                                  <span key={date} className="audit-process-auditor-date-badge">{date}</span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          <div className="audit-process-add-auditor-bottom">
            {isMandaysOverLimit && (
              <span className="audit-process-save-warning-label" role="alert">
                Warning: Total Mandays {currentTotalMandays} exceeds limit {topMandaysLimit}
              </span>
            )}
            <button type="button" className="btn audit-process-scheduling-action-btn audit-process-scheduling-action-btn-secondary" onClick={addSiteLocation}>
              <Plus size={16} className="me-1" />
              Add Site Location
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="audit-process-scheduling-content">
        {schedulingToolbar}
        {accommodations.map((item) => {
          const scheduledRows = auditSchedules
            .map((schedule) => {
              const auditorId = String(schedule?.employeeId || '').trim();
              if (!auditorId) return null;

              const dates = sanitizeMandayDetails(schedule?.mandayDetails)
                .map((row) => String(row?.date || '').trim())
                .filter(Boolean)
                .sort();
              const selectedDates = dates.length > 0 ? dates : buildDateRange(schedule?.startDate, schedule?.endDate);
              if (selectedDates.length === 0) return null;

              const auditorName = schedule?.employeeName
                || auditorOptions.find((auditor) => String(auditor?.userId || '') === auditorId)?.label
                || auditorOptions.find((auditor) => String(auditor?.userId || '') === auditorId)?.fullName
                || `Auditor #${auditorId}`;

              const startDate = selectedDates[0];
              const endDate = selectedDates[selectedDates.length - 1];
              return {
                scheduleId: String(schedule?.id || ''),
                auditorId,
                auditorName,
                startDate,
                endDate,
                duration: selectedDates.length,
                dates: selectedDates,
                label: `${startDate} - ${endDate} (${selectedDates.length} day${selectedDates.length > 1 ? 's' : ''})`,
              };
            })
            .filter(Boolean);

          const scheduledAuditors = Array.from(
            new Map(
              scheduledRows.map((row) => [row.auditorId, { auditorId: row.auditorId, auditorName: row.auditorName }])
            ).values()
          );

          const selectedAuditorSchedules = scheduledRows.filter(
            (row) => row.auditorId === String(item?.scheduledAuditorId || '')
          );
          const availableAuditorScheduleDates = Array.from(new Set(
            selectedAuditorSchedules.flatMap((row) => row.dates || [])
          )).sort();
          const selectedAccommodationDates = availableAuditorScheduleDates.length > 0
            ? ((Array.isArray(item?.selectedScheduleDates) && item.selectedScheduleDates.length > 0)
              ? availableAuditorScheduleDates.filter((date) => item.selectedScheduleDates.includes(date))
              : availableAuditorScheduleDates)
            : [];
          const isAllScheduleDatesSelected = availableAuditorScheduleDates.length > 0
            ? selectedAccommodationDates.length === availableAuditorScheduleDates.length
            : false;

          return (
          <div key={item.id} className="audit-process-scheduling-card mb-3">
            <div className="audit-process-scheduling-card-header">
              <h5 className="mb-0">Accommodation & Transportation</h5>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => removeAccommodation(item.id)}
                title="Remove accommodation"
              >
                <X size={14} />
              </button>
            </div>
            <div className="audit-process-scheduling-card-body">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label d-block">Provided by Client</label>
                  <label className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={Boolean(item.providedByClient)}
                      onChange={(e) => handleAccommodationChange(item.id, 'providedByClient', e.target.checked)}
                    />
                    <span className="form-check-label">Select if the audit execution task requires accommodation</span>
                  </label>
                </div>

                {!item.providedByClient && (
                  <>
                    <div className="col-md-6">
                      <label className="form-label required">Select Purpose</label>
                      <select
                        className="form-control"
                        value={item.purpose}
                        onChange={(e) => handleAccommodationChange(item.id, 'purpose', e.target.value)}
                      >
                        <option value="Transport Ticket">Transport Ticket</option>
                        <option value="Accommodation">Accommodation</option>
                        <option value="Transport & Accommodation">Transport & Accommodation</option>
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label required">Select Auditor</label>
                      <select
                        className="form-control"
                        value={item.scheduledAuditorId || ''}
                        onChange={(e) => handleAccommodationChange(item.id, 'scheduledAuditorId', e.target.value)}
                      >
                        <option value="">Please select scheduled auditor...</option>
                        {scheduledAuditors.map((auditor) => (
                          <option key={auditor.auditorId} value={auditor.auditorId}>{auditor.auditorName}</option>
                        ))}
                      </select>
                      {!!item.scheduledAuditorId && availableAuditorScheduleDates.length === 0 && (
                        <div className="text-muted small mt-1 text-danger">No scheduled dates found for selected auditor.</div>
                      )}
                    </div>

                    {!!item.scheduledAuditorId && availableAuditorScheduleDates.length > 0 && (
                      <div className="col-12">
                        <div className="audit-process-accommodation-schedule-preview audit-process-manday-site-row">
                          <div className="audit-process-accommodation-schedule-head">
                            <label className="form-label mb-0">Schedule Details</label>
                            <span className="audit-process-mandays-total-label">
                              Duration: {selectedAccommodationDates.length} day{selectedAccommodationDates.length > 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="audit-process-accommodation-date-actions">
                            <button
                              type="button"
                              className={`btn btn-sm ${isAllScheduleDatesSelected ? 'btn-primary' : 'btn-outline-primary'}`}
                              onClick={() => handleAccommodationChange(item.id, 'selectedScheduleDates', { dates: availableAuditorScheduleDates })}
                            >
                              Select All
                            </button>
                          </div>
                          <div className="audit-process-accommodation-schedule-meta">
                            <span className="audit-process-site-chip">Start: {selectedAccommodationDates[0] || '-'}</span>
                            <span className="audit-process-site-chip">End: {selectedAccommodationDates[selectedAccommodationDates.length - 1] || '-'}</span>
                          </div>
                          <div className="audit-process-selected-dates audit-process-accommodation-date-grid">
                            {availableAuditorScheduleDates.map((date) => (
                              <button
                                key={`${item.id}-${item.scheduledAuditorId}-${date}`}
                                type="button"
                                className={`audit-process-accommodation-date-pill ${selectedAccommodationDates.includes(date) ? 'active' : ''}`}
                                onClick={() => {
                                  const exists = selectedAccommodationDates.includes(date);
                                  const nextDates = exists
                                    ? selectedAccommodationDates.filter((d) => d !== date)
                                    : [...selectedAccommodationDates, date].sort();
                                  handleAccommodationChange(item.id, 'selectedScheduleDates', { dates: nextDates });
                                }}
                              >
                                {date}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="col-12">
                      <label className="form-label">Amount (refer to CBQA Global Regulation or Else Contract)</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={item.amount}
                        onChange={(e) => handleAccommodationChange(item.id, 'amount', e.target.value)}
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label d-block">Need Pickup/ Drop Off</label>
                      <label className="form-check form-switch">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={Boolean(item.needPickupDropOff)}
                          onChange={(e) => handleAccommodationChange(item.id, 'needPickupDropOff', e.target.checked)}
                        />
                        <span className="form-check-label">The need for pick-up and drop-off at the location</span>
                      </label>
                    </div>

                    {item.needPickupDropOff && (
                      <div className="col-12">
                        <div className="audit-process-accommodation-box">
                          <div className="audit-process-accommodation-box-header">Contact Details</div>
                          <div className="audit-process-accommodation-box-body">
                            {(item.contacts || []).map((contact) => (
                              <div key={contact.id} className="row g-3 mb-2">
                                <div className="col-md-6">
                                  <label className="form-label">Name</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Full name"
                                    value={contact.name}
                                    onChange={(e) => handleAccommodationContactChange(item.id, contact.id, 'name', e.target.value)}
                                  />
                                </div>
                                <div className="col-md-6">
                                  <label className="form-label">Phone Number</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter phone number"
                                    value={contact.phoneNumber}
                                    onChange={(e) => handleAccommodationContactChange(item.id, contact.id, 'phoneNumber', e.target.value)}
                                  />
                                </div>
                                <div className="col-12">
                                  <label className="form-label">Note</label>
                                  <textarea
                                    className="form-control"
                                    rows={2}
                                    placeholder="Enter note"
                                    value={contact.note}
                                    onChange={(e) => handleAccommodationContactChange(item.id, contact.id, 'note', e.target.value)}
                                  />
                                </div>
                              </div>
                            ))}
                            <button
                              type="button"
                              className="btn btn-light"
                              onClick={() => addAccommodationContact(item.id)}
                            >
                              <Plus size={16} className="me-1" />
                              Add another contact
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="col-12">
                      <div className="audit-process-accommodation-box">
                        <div className="audit-process-accommodation-box-header">Supporting Documents</div>
                        <div className="audit-process-accommodation-box-body">
                          <input
                            type="file"
                            className="form-control mb-2"
                            multiple
                            onChange={(e) => handleAccommodationUpload(item.id, e.target.files)}
                          />
                          {(item.supportingDocuments || []).length > 0 && (
                            <div className="audit-process-uploaded-list">
                              {(item.supportingDocuments || []).map((doc) => (
                                <div key={doc.id} className="audit-process-uploaded-item">
                                  <span>{doc.name}</span>
                                  <small>{doc.sizeKb}KB</small>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );})}
        <div className="audit-process-add-auditor-bottom">
          {isMandaysOverLimit && (
            <span className="audit-process-save-warning-label" role="alert">
              Warning: Total Mandays {currentTotalMandays} exceeds limit {topMandaysLimit}
            </span>
          )}
          <button type="button" className="btn audit-process-scheduling-action-btn audit-process-scheduling-action-btn-secondary" onClick={addAccommodation}>
            <Plus size={16} className="me-1" />
            Add Accommodation & Transportation
          </button>
        </div>
      </div>
    );
  };

  // ── Helper: form button label per doc code ─────────────────────────────
  const getFormButtonLabel = (docCode) => {
    const labels = {
      'FAPP-01 Audit Plan': 'Form Audit Plan',
      'FAPP-05 Audit Report': 'Form Audit Report',
      'FAPP-07 Finding Form': 'Input Finding Form',
      'FAPP-06 Certificate Information Form': 'Input Form',
      'FAPP-06 CIF (signed by client)': 'Input Form',
      'FAPP-02 Three Years Audit Program': 'Input Form',
      'FAPP-03 Attendance List': 'Input Form',
      'FAPP-04 Audit Notes': 'Input Form',
      'FAPP-07 Finding Form (accepted)': 'Input Form',
      'FAPP-08 Change Notice Form': 'Input Form',
      'FTRD-01 Technical Review and Certification Decision': 'Input Form',
      'FTRD-02 Certificate Review and Production': 'Input Form',
      'Audit Summary Report': 'Generate Report',
    };
    return labels[docCode] || 'Input Form';
  };

  // ── Helper: inline form heading ────────────────────────────────────────
  const getInlineFormTitle = (docCode) => {
    const titles = {
      'FAPP-01 Audit Plan': 'Audit Plan Form',
      'FAPP-02 Three Years Audit Program': 'Three Years Audit Program',
      'FAPP-03 Attendance List': 'Attendance List',
      'FAPP-04 Audit Notes': 'Audit Notes',
      'FAPP-05 Audit Report': 'Audit Report Form',
      'FAPP-06 Certificate Information Form': 'Certificate Information Form',
      'FAPP-06 CIF (signed by client)': 'Certificate Information Form (Signed)',
      'FAPP-07 Finding Form': 'Audit Finding Form',
      'FAPP-07 Finding Form (accepted)': 'Corrective Action Form',
      'FAPP-08 Change Notice Form': 'Change Notice Form',
      'FTRD-01 Technical Review and Certification Decision': 'Technical Review Form',
      'FTRD-02 Certificate Review and Production': 'Certificate Review & Production Form',
      'Audit Summary Report': 'Audit Summary Report',
    };
    return titles[docCode] || 'Input Form';
  };

  // ── Inline form full-page view ─────────────────────────────────────────
  const renderInlineFormView = () => {
    if (!activeInlineForm) return null;
    const { docCode, category, scopeKey, readOnly: inlineReadOnly = false } = activeInlineForm;
    const tabLabel = DOCUMENT_TABS.find((t) => t.id === category)?.label || category;

    const selectedScopeLabel = scopeKey
      ? schedulingScopes.find(s => s.key === scopeKey)?.label
      : null;
    const selectedScope = scopeKey
      ? (schedulingScopes.find((s) => s.key === scopeKey) || null)
      : null;
    const existingInlineFormData = category === 'technical-review'
      && !isTechnicalReviewAvailableForScope(scopeKey)
      ? null
      : getSavedFormEntry(docCode, scopeKey)?.formDataJson;
    const certificateWorkflowAllowed = canUseCertificateWorkflow(selectedScope || { key: scopeKey });
    const scopeAuditorOptions = getEnrichedAuditorOptionsForScope(scopeKey);
    const scopeAuditSchedules = scopeKey
      ? (schedulingDataByScope?.[scopeKey]?.auditSchedules || [])
      : [];
    const scopeSiteLocations = scopeKey
      ? (schedulingDataByScope?.[scopeKey]?.siteLocations || siteLocations)
      : siteLocations;
    const isCertificateDraftForm = category === 'certificate' && docCode === FTRD_02_DOC_CODE;
    // Saved rows are keyed by the FULL document code; reading a short code such as 'FTRD-02'
    // never matches and silently produced an empty certificate.
    const savedCertificateEntry = parseFormDataEntry(getSavedFormEntry(FTRD_02_DOC_CODE, scopeKey));
    const savedCertificateForm = readFtrd02SharedForm(savedCertificateEntry);
    const certificateInfoForm = parseFormDataEntry(
      getSavedFormEntry('FAPP-06 Certificate Information Form', scopeKey));
    const liveCertificateForm = isCertificateDraftForm
      ? { ...savedCertificateForm, ...(inlineFormDraftData || {}) }
      : savedCertificateForm;
    const certificateIssuanceScope = schedulingScopes.find((scope) => scope.key === scopeKey) || null;
    const isGroupedCertificateIssuance = isCertificateDraftForm
      && isPerStandardCertificateScope(certificateIssuanceScope);
    const certificateTargets = isGroupedCertificateIssuance
      ? describeCertificateIssuanceTargets(certificateIssuanceScope)
      : [];
    const effectiveActiveFtrd02Target = certificateTargets.find((target) =>
      target.projectServiceStandardId === activeFtrd02StandardId)
      || certificateTargets[0]
      || null;
    const standardCertificateForms = isGroupedCertificateIssuance
      ? buildFtrd02StandardForms(
        certificateIssuanceScope,
        liveCertificateForm,
        ftrd02StandardOverrides,
        { ...readFtrd02StandardForms(savedCertificateEntry), ...ftrd02StandardForms },
      )
      : [];
    const activeStandardCertificateForm = effectiveActiveFtrd02Target
      ? standardCertificateForms.find((form) => form.projectServiceStandardId
        === effectiveActiveFtrd02Target.projectServiceStandardId) || liveCertificateForm
      : liveCertificateForm;
    // A standard is ready when it has an identity AND would actually save: the
    // readiness dot, the Issue gate and DocumentFormModal's handleSave all read
    // getDocumentFormFieldErrors, so a tab can never show ready for a form that
    // Save would reject. Previously this checked certificateScope alone, while
    // handleSave also requires Certificate Number and Client Name -- a green dot
    // with Issue enabled for a form that could not be saved.
    const isCertificateStandardFormBlocked = (form) => !form.projectServiceStandardId
      || !form.standardName
      || Object.values(getDocumentFormFieldErrors(FTRD_02_DOC_CODE, form)).some(Boolean);
    const certificateTabBlockingForms = standardCertificateForms.filter(isCertificateStandardFormBlocked);
    // Per-standard readiness, so each tab can show its own state instead of the
    // operator discovering an incomplete standard only at the Issue button.
    const certificateStandardReadiness = new Map(standardCertificateForms.map((form) => [
      form.projectServiceStandardId,
      isCertificateStandardFormBlocked(form) ? 'incomplete' : 'ready',
    ]));
    // Every standard tab is opened with its own identity, even when nothing is saved for it yet.
    // Withholding it (passing null) made DocumentFormModal fill the standard fields from the
    // GROUP — i.e. the first member — so the tab both displayed and saved as standard #1. The
    // suggested certificate number and the certificate dates are resolved per standard by the
    // form itself and are not gated on existingData.
    const groupedCertificateExistingData = JSON.stringify(activeStandardCertificateForm);

    // Which standard a save belongs to. The form carries its own projectServiceStandardId, so
    // the data decides where it is stored; the active tab is only the fallback. Attribution can
    // then never drift to another standard because of a stale closure.
    const resolvePersistStandardId = (nextForm) => {
      const fromForm = Number(nextForm?.projectServiceStandardId);
      return Number.isFinite(fromForm)
        && certificateTargets.some((target) => target.projectServiceStandardId === fromForm)
        ? fromForm
        : effectiveActiveFtrd02Target?.projectServiceStandardId;
    };

    // The saved row is merged under the in-memory map: if this session opened the form before
    // the saved forms finished loading, the standards it never touched are still carried over
    // instead of being dropped from the document.
    const carriedCertificateOverrides = {
      ...readFtrd02StandardOverrides(savedCertificateEntry),
      ...ftrd02StandardOverrides,
    };
    const carriedCertificateForms = {
      ...readFtrd02StandardForms(savedCertificateEntry),
      ...ftrd02StandardForms,
    };

    const updateGroupedCertificateDraft = (nextForm) => {
      if (!effectiveActiveFtrd02Target) return;
      const { shared, overrides } = splitFtrd02FormByStandard(nextForm);
      const standardKey = resolvePersistStandardId(nextForm);
      setInlineFormDraftData((previous) => ({ ...(previous || {}), ...shared }));
      setFtrd02StandardOverrides((previous) => ({ ...previous, [standardKey]: overrides }));
      setFtrd02StandardForms((previous) => ({ ...previous, [standardKey]: { ...shared, ...overrides } }));
    };

    // What the Save (and auto-save) of the active standard's tab actually writes: the shared
    // audit fields plus EVERY standard's certificate fields, so saving ISO 9001 can never wipe
    // what was already entered for ISO 14001.
    const buildGroupedCertificatePersistedForm = (nextForm) => buildFtrd02PersistedForm(
      nextForm,
      resolvePersistStandardId(nextForm),
      carriedCertificateOverrides,
      carriedCertificateForms,
    );

    // Switching standard tabs swaps the fields in place — no remount, so no flicker. The form
    // itself stores the tab being left (from its own live fields, see DocumentFormModal), which
    // is why this does not have to persist anything or wait for the network.
    const switchGroupedCertificateStandard = (nextStandardId) => {
      if (nextStandardId === effectiveActiveFtrd02Target?.projectServiceStandardId) return;
      setActiveFtrd02StandardId(nextStandardId);
    };

    // Short code for breadcrumb (e.g. "FAPP-05")
    const shortCode = (docCode.match(/^(FAPP-\d+|FTRD-\d+)/) || [null, docCode])[1];

    // Left panel: selected scope for scope info
    const firstStage = stages[0] || {};
    const selectedScopeDetails = getScheduleScopeDetails(scopeKey);
    const selectedScopeParts = String(selectedScopeLabel || '')
      .split(' / ')
      .map((part) => part.trim());
    const selectedStandardName = selectedScopeParts[1] || firstStage.standardName || firstStage.serviceItem || '-';

    // Reference docs from sales-pack uploaded files
    const refDocList = Array.isArray(auditDocuments['sales-pack']) ? auditDocuments['sales-pack'] : [];

    const handleBack = () => {
      // These forms can run very long; leaving the (now unmounted) form's
      // scroll position in place drops the operator somewhere past the bottom
      // of the much shorter document list. Remember which row to return to --
      // docCode is only available here, before activeInlineForm is cleared --
      // and the effect below scrolls it into view once the list re-renders.
      pendingFormScrollDocCodeRef.current = docCode;
      setActiveInlineForm(null);
    };

    return (
      <div>
        {/* Breadcrumb + Back.
            The previous version rendered "Audit Execution", "Project Details" and
            {tabLabel} as three separate links that all called handleBack -- three
            labels, one destination, so two of them lied about where they went.
            There is exactly one place to go back to (the document list for this
            tab), so that is exactly one link, plus a real Back button. The project
            crumb is now plain text: it is context, not a destination. */}
        <div className="audit-process-form-topbar mb-3">
          <button
            type="button"
            className="btn btn-outline-secondary audit-process-form-back"
            onClick={handleBack}
          >
            <ArrowLeft size={16} className="me-1" />
            Back to {tabLabel}
          </button>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb audit-process-form-breadcrumb mb-0">
              <li className="breadcrumb-item">
                <span title={projectMeta.auditTitle || ''}>{projectMeta.projectCode || 'Project'}</span>
              </li>
              <li className="breadcrumb-item">
                <button type="button" className="btn btn-link p-0 align-baseline" onClick={handleBack}>
                  {tabLabel}
                </button>
              </li>
              <li className="breadcrumb-item active" aria-current="page">{shortCode}</li>
            </ol>
          </nav>
        </div>

        {/* Page heading */}
        <div className="mb-4">
          <h4 className="fw-bold mb-1">{getInlineFormTitle(docCode)}</h4>
          <p className="text-muted small mb-0">Complete the {docCode} form with accurate and appropriate data.</p>
        </div>

        {/* Read-only audit context. This used to be a persistent col-lg-4 sidebar,
            which spent a third of the viewport on four static values and squeezed
            the form -- worst for FTRD-02's seven-column ISCC material table. It is
            a full-width strip above the form instead, so the form itself gets the
            whole row. Applies to every inline document form, not only FTRD-02. */}
        <section className="card mb-4 audit-process-form-context" aria-label="Audit context">
          <div className="card-body">
            <div className="audit-process-form-context__grid">
              <div>
                <div className="audit-process-form-context__label">Client</div>
                <div className="fw-semibold">{projectMeta.locationName}</div>
              </div>
              <div>
                <div className="audit-process-form-context__label">Standard</div>
                <div className="fw-semibold">{selectedStandardName}</div>
              </div>
              <div>
                <div className="audit-process-form-context__label">Mandays</div>
                <div className="fw-semibold">{selectedScopeDetails.mandays || '-'}</div>
              </div>
              <div>
                <div className="audit-process-form-context__label">EA Code</div>
                <div className="fw-semibold">{selectedScopeDetails.eaCode || '-'}</div>
              </div>
              <div className="audit-process-form-context__wide">
                <div className="audit-process-form-context__label">Scope</div>
                <div className="text-secondary">{
                  (selectedScopeDetails.scope && selectedScopeDetails.scope !== '-')
                    ? selectedScopeDetails.scope
                    : (projectMeta.proposalScope || '-')
                }</div>
              </div>
            </div>

            {/* Reference docs stay up here rather than moving below the form: they
                are consulted WHILE filling it in, so burying them under a long
                form would be worse than the sidebar was. Rendered as wrapping
                chips so a few files cost one row, not one row each. */}
            {refDocList.length > 0 && (
              <div className="audit-process-form-context__docs">
                <div className="audit-process-form-context__label">Reference Documents</div>
                <div className="d-flex flex-wrap gap-2">
                  {refDocList.map((doc) => (
                    <div key={doc.id} className="audit-process-form-context__doc">
                      <FileText size={14} className="text-muted flex-shrink-0" />
                      <span className="text-truncate">
                        {doc.fileName || doc.originalFileName || doc.name || 'Document'}
                      </span>
                      <button
                        type="button"
                        className="btn btn-link p-0 text-muted"
                        title="Preview"
                        onClick={() => {
                          if (doc.fileUrl || doc.url) {
                            setPreviewDocument({ name: doc.fileName || 'Document', url: doc.fileUrl || doc.url });
                            setPreviewModalOpen(true);
                          }
                        }}
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-link p-0 text-danger"
                        title="Delete"
                        onClick={() => handleDocumentDelete('sales-pack', doc.id)}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        <div className="row g-4">
          {/* Form content, now the full width of the row */}
          <div className="col-12">
            <div className="card">
              {isGroupedCertificateIssuance && (
                <nav className="border-bottom px-3 pt-3 audit-process-certificate-standard-tabs" aria-label="Certificate standards">
                  <div className="nav nav-tabs border-bottom-0" role="tablist">
                    {certificateTargets.map((target) => {
                      const isActive = target.projectServiceStandardId === effectiveActiveFtrd02Target?.projectServiceStandardId;
                      const readiness = certificateStandardReadiness.get(target.projectServiceStandardId) || 'incomplete';
                      const standardLabel = target.standardName || `Standard ${target.projectServiceStandardId}`;
                      return (
                        <button
                          key={target.projectServiceStandardId}
                          type="button"
                          className={`nav-link${isActive ? ' active' : ''}`}
                          aria-selected={isActive}
                          title={readiness === 'ready'
                            ? `${standardLabel} — certificate scope complete`
                            : `${standardLabel} — certificate scope still incomplete`}
                          onClick={() => switchGroupedCertificateStandard(target.projectServiceStandardId)}
                        >
                          <span
                            className={`audit-process-certificate-tab-dot audit-process-certificate-tab-dot--${readiness}`}
                            aria-hidden="true"
                          />
                          {standardLabel}
                          <span className="visually-hidden">
                            {readiness === 'ready' ? ' (complete)' : ' (incomplete)'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </nav>
              )}
              <div className="card-body p-0">
                <DocumentFormModal
                  key={`form-${docCode}-${scopeKey}`}
                  formInstanceKey={isGroupedCertificateIssuance
                    ? String(effectiveActiveFtrd02Target?.projectServiceStandardId || '')
                    : ''}
                  show
                  inline
                  onHide={handleBack}
                  onCancel={handleBack}
                  docCode={docCode}
                  category={category}
                  projectId={Number(projectId)}
                  projectMeta={projectMeta}
                  stages={stages}
                  auditorOptions={scopeAuditorOptions}
                  auditSchedule={{
                    startDate: scopeAuditSchedules[0]?.startDate || auditSchedules[0]?.startDate || '',
                    endDate: scopeAuditSchedules[0]?.endDate || auditSchedules[0]?.endDate || '',
                    auditSchedules: scopeAuditSchedules.length > 0 ? scopeAuditSchedules : auditSchedules,
                    siteLocations: scopeKey ? (schedulingDataByScope?.[scopeKey]?.siteLocations || siteLocations) : siteLocations,
                  }}
                  existingData={isGroupedCertificateIssuance
                    ? groupedCertificateExistingData
                    : existingInlineFormData}
                  scopeKey={scopeKey}
                  scopeLabel={selectedScopeLabel || ''}
                  scopeContext={selectedScope}
                  auditMethodology={projectAuditInfo.auditMethodology || ''}
                  methodologyGroup={projectAuditInfo.methodologyGroup}
                  readOnly={inlineReadOnly}
                  onDraftChange={isCertificateDraftForm
                    ? (isGroupedCertificateIssuance ? updateGroupedCertificateDraft : setInlineFormDraftData)
                    : undefined}
                  buildPersistedFormData={isGroupedCertificateIssuance
                    ? buildGroupedCertificatePersistedForm
                    : undefined}
                  onSaved={(code, result, options = {}) => {
                    const saveKey = result?.formDataKey || buildDocumentFormStorageKey(code, activeInlineForm.scopeKey);
                    setSavedFormDataMap((prev) => ({
                      ...prev,
                      [saveKey]: { ...(prev[saveKey] || {}), ...result, formDataJson: result.formDataJson },
                    }));
                    // A silent save is the form storing the standard tab the operator just left.
                    // It must update this page's copy of the document but never close the form.
                    if (options.silent) return;
                    refreshAfterFtrd01Save(code);
                    handleBack();
                  }}
                />
              </div>
            </div>
            {isCertificateDraftForm && certificateWorkflowAllowed && (
              <div className="card mt-4 audit-process-inline-certificate-preview-card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
                    <div>
                      <h5 className="mb-1">
                        {inlineReadOnly ? 'Issued Certificate Preview' : 'Live Certificate Draft Preview'}
                      </h5>
                      <p className="text-muted small mb-0">
                        {inlineReadOnly
                          ? 'View the issued FTRD-02 certificate with the AuditQ-compatible certificate renderer for the active scope.'
                          : 'Preview and issue this FTRD-02 draft with the AuditQ-compatible certificate renderer for the active scope.'}
                      </p>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <span className="badge text-bg-light">Scope: {selectedScopeLabel || 'General'}</span>
                      <span className="badge text-bg-success">Template auto-selected</span>
                    </div>
                  </div>

                  <div className="alert alert-info small d-flex align-items-start gap-2 mb-3">
                    <Info size={17} className="flex-shrink-0 mt-1" />
                    <span>
                      The renderer chooses the approved AuditQ template from the certificate data. For unified ISO certificates issued on or after 01 Jul 2026, it applies the Zidky signature automatically. No template selection is required.
                    </span>
                  </div>

                  {(() => {
                    // Integrated/combined audits issue ONE CERTIFICATE PER STANDARD. Showing the
                    // exact targets up front means the Certificate Issuer can see, before pressing
                    // Issue, that two certificates (not one merged record) are about to be created.
                    if (!isGroupedCertificateIssuance) return null;
                    const targets = certificateTargets;
                    return (
                      <div className="alert alert-warning small mb-3">
                        <div className="d-flex align-items-start gap-2 mb-2">
                          <Info size={17} className="flex-shrink-0 mt-1" />
                          <span>
                            This is a grouped audit scope. Preview each standard here, then issue{' '}
                            <strong>{targets.length} separate certificates</strong> — one per standard,
                            each with its own certificate number and its own row in Certificate Monitoring.
                          </span>
                        </div>
                        <ul className="mb-0 ps-4">
                          {targets.map((target) => (
                            <li key={target.projectServiceStandardId}>
                              {target.standardName || `Standard ${target.projectServiceStandardId}`}
                              {target.expectedPrefix ? (
                                <span className="text-muted"> — certificate number {target.expectedPrefix}100…</span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}

                  {isGroupedCertificateIssuance && certificateTabBlockingForms.length > 0 && (
                    <div className="alert alert-danger small mb-3" role="alert">
                      <div className="mb-2">
                        The batch cannot be issued until every standard is ready. Complete the
                        certificate scope for:
                      </div>
                      {/* Each blocker jumps straight to its own tab — naming the standards in a
                          comma-joined string left the operator to find them tab by tab. */}
                      <div className="d-flex flex-wrap gap-2">
                        {certificateTabBlockingForms.map((form, blockerIndex) => {
                          const blockerLabel = form.standardName
                            || (form.projectServiceStandardId ? `Standard ${form.projectServiceStandardId}` : 'Unidentified standard');
                          const key = `${form.projectServiceStandardId || 'no-id'}-${blockerIndex}`;
                          if (!form.projectServiceStandardId) {
                            return <span key={key} className="badge text-bg-light border">{blockerLabel}</span>;
                          }
                          return (
                            <button
                              key={key}
                              type="button"
                              className="btn btn-outline-danger"
                              onClick={() => switchGroupedCertificateStandard(form.projectServiceStandardId)}
                            >
                              {blockerLabel}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="d-flex flex-wrap gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      disabled={certificateIssuanceState.saving}
                      onClick={() => openAuditqCertificateDraftPreview(
                        activeStandardCertificateForm,
                        certificateInfoForm,
                        scopeKey,
                        isGroupedCertificateIssuance ? [activeStandardCertificateForm] : null,
                      )}
                    >
                      <Eye size={16} className="me-1" />
                      Preview PDF
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={certificateIssuanceState.saving}
                      onClick={() => downloadAuditqCertificateDraft(
                        activeStandardCertificateForm,
                        certificateInfoForm,
                        scopeKey,
                        isGroupedCertificateIssuance ? [activeStandardCertificateForm] : null,
                      )}
                    >
                      <Download size={16} className="me-1" />
                      Download PDF
                    </button>
                    {!inlineReadOnly && (
                      <button
                        type="button"
                        className="btn btn-success"
                        disabled={certificateIssuanceState.saving || certificateTabBlockingForms.length > 0}
                        onClick={() => issueCertificateFromAuditqDraft({
                          certificateForm: activeStandardCertificateForm,
                          certificateInfoForm,
                          scopeKey,
                          targetStandards: isGroupedCertificateIssuance ? standardCertificateForms : null,
                        })}
                      >
                        {certificateIssuanceState.saving && certificateIssuanceState.scopeKey === (scopeKey || '') ? (
                          <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />Issuing...</>
                        ) : (
                          <><Check size={16} className="me-1" />{isGroupedCertificateIssuance ? 'Finalize & Issue All' : 'Finalize & Issue'}</>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderDocumentTabContent = () => {
    // Technical Reviewer role flags are still read here — used further below to keep TR
    // users read-only on every form except FTRD-01 itself, which now hosts their whole
    // OK/Need Revision decision system directly (its own "Stage 1 Decision" dropdown +
    // Save button in DocumentFormModal, no separate panel).
    const { isTechnicalReviewer, isAdminOrManager } = myProjectRole;

    if (activeDocTab === 'checklist-verification') {
      // Sales and Scheduler can inspect the checklist to coordinate revisions and
      // scheduling, but only review-capable roles can record a decision.
      const canDecideApplicationReview = myProjectRole.isAdminOrManager || myProjectRole.isApplicationReviewer
        || myProjectRole.isTechnicalReviewer || myProjectRole.isCertificationAuthority || myProjectRole.isCertificationIssuer;
      const isChecklistReadOnlyForViewer = !canDecideApplicationReview;
      // While a follow-up is actively open (OPEN or NEEDS_REVIEW), AR needs to be able to
      // flip the originally-flagged item(s) back to OK — otherwise "Mark Resolved" can never
      // be satisfied. This narrowly re-unlocks just the OK/Need Revision radios, not the rest
      // of the (still finalized) checklist decision UI.
      const followupActiveForEditing = canDecideApplicationReview
        && applicationReviewFollowup?.exists
        && ['OPEN', 'NEEDS_REVIEW'].includes(String(applicationReviewFollowup?.status || '').toUpperCase());
      // Only the item(s) the follow-up actually flagged unlock — an item that was already
      // OK stays locked even while the follow-up is open.
      const followupFlaggedItemIds = new Set((applicationReviewFollowup?.revisiItems || []).map((item) => item.id));
      const isChecklistItemLocked = (itemId) => (
        (isChecklistReviewMode && !(followupActiveForEditing && followupFlaggedItemIds.has(itemId)))
        || isChecklistReadOnlyForViewer
        || (itemId === REDUCTION_OF_TYPE_ITEM_ID && isReductionOfTypeNA)
      );
      const unresolvedFollowupItems = (applicationReviewFollowup?.revisiItems || [])
        .filter((item) => checklistValues[item.id] !== 'ok');
      const applicationReviewAssigneeName = checklistScopeKey
        ? getAssignedAuditTaskName(
          projectAuditTasks,
          schedulingScopes.find((scope) => (
            scope?.key === checklistScopeKey
            || resolveBaseScheduleKey(scope?.key) === resolveBaseScheduleKey(checklistScopeKey)
          )) || checklistScopeKey,
          (task) => isApplicationReviewTaskCode(task?.taskTypeCode),
        )
        : null;
      return (
        <>
          {!isChecklistReviewMode && (
            <div className="alert alert-info alert-dismissible fade show py-2 mb-3" role="alert">
              <strong>Workflow Guide:</strong> Mark each item as OK or Need Revision, then click "Save" to:
              <ul className="mb-0 mt-2 small">
                <li>If any item needs revision: the existing Application Review task moves to the assigned Sales owner</li>
                <li>If all items are OK: You can then complete the application review and create audit plan tasks</li>
              </ul>
            </div>
          )}

          <div className={`audit-process-scope-selector mb-3 ${checklistScopeKey ? 'is-selected' : 'is-pending'}`}>
            <div className="audit-process-scope-selector__prompt">
              <Info size={16} />
              <span><strong>Select the audit scope first.</strong> The checklist decision is saved only for this scope.</span>
            </div>
            <label className="form-label small fw-medium" htmlFor="application-review-audit-scope">{AUDIT_SCOPE_SELECTOR_LABEL} <span className="text-danger">*</span></label>
            <select
              id="application-review-audit-scope"
              className="form-select audit-process-scope-select"
              value={checklistScopeKey}
              onChange={(e) => {
                const nextScopeKey = syncAuditScopeSelectors(e.target.value);
                if (nextScopeKey && nextScopeKey !== selectedSchedulingScopeKey) {
                  handleSchedulingScopeChange(nextScopeKey);
                }
              }}
              disabled={isChecklistReviewMode && !myProjectRole.isAdminOrManager}
              required
            >
              <option value="" disabled>Select an audit scope...</option>
              {/* AR checklist is ONE per standard: collapse Initial Certification
                  Stage 1/Stage 2 scopes to a single base-key option. */}
              {(() => {
                const seen = new Set();
                const options = [];
                schedulingScopes.forEach((scope) => {
                  const baseKey = resolveBaseScheduleKey(scope.key);
                  if (seen.has(baseKey)) return;
                  seen.add(baseKey);
                  options.push({ key: baseKey, label: String(scope.label || '').split(' — Stage')[0] });
                });
                return options.map((opt) => (
                  <option key={opt.key} value={opt.key}>{opt.label}</option>
                ));
              })()}
            </select>
            {!checklistScopeKey && (
              <small className="text-danger d-block mt-1">Audit Scope is required</small>
            )}
            {checklistScopeKey && <small className="audit-process-scope-selector__hint">All checklist items below apply to this selected audit scope.</small>}
            {applicationReviewAssigneeName && (
              <span className="audit-process-scheduling-assignee mt-2">
                <User size={13} />
                Application Reviewer: <strong>{applicationReviewAssigneeName}</strong>
              </span>
            )}
          </div>

          {checklistScopeKey && projectAuditInfo.multiSite && (
            <div className="audit-process-multisite-coverage mb-3" role="region" aria-label="Multi-site coverage">
              <div className="audit-process-multisite-coverage__header">
                <span className="audit-process-multisite-coverage__icon" aria-hidden="true"><Building2 size={16} /></span>
                <div>
                  <strong>Multi-site coverage</strong>
                  <span>{siteLocations.length} {siteLocations.length === 1 ? 'site' : 'sites'} in scope</span>
                </div>
              </div>
              {siteLocations.length > 0 ? (
                <ul className="audit-process-multisite-coverage__list">
                  {siteLocations.map((site, index) => (
                    <li className="audit-process-multisite-coverage__item" key={site.id || `${site.siteName}-${index}`}>
                      <span className="audit-process-multisite-coverage__number">{index + 1}</span>
                      <span className="audit-process-multisite-coverage__details">
                        <strong>{site.siteName || `Site ${index + 1}`}</strong>
                        {site.address || site.city || site.country
                          ? <span>{[site.address, site.city, site.country].filter(Boolean).join(', ')}</span>
                          : null}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="audit-process-multisite-coverage__empty">No generated sites are available for review.</span>
              )}
              <div className="audit-process-multisite-coverage__hint">Verify every generated site remains in scope before approving this checklist.</div>
            </div>
          )}

          {checklistScopeKey && applicationReviewStatus && (
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="text-muted small fw-medium">Application Review Status:</span>
              <span
                className={`badge badge-outline text-${
                  applicationReviewStatus === 'approved' ? 'success'
                    : applicationReviewStatus === 'approved_with_notes' ? 'orange'
                    : applicationReviewStatus === 'rejected' ? 'danger'
                    : 'secondary'
                }`}
              >
                {APPLICATION_REVIEW_STATUS_LABELS[applicationReviewStatus] || applicationReviewStatus}
              </span>
            </div>
          )}

          {checklistScopeKey && canDecideApplicationReview && (() => {
            // Plain Reject flow (request-revision -> Sales fixes CID -> resubmit-application-review)
            // has no separate follow-up record like Approved with Notes does -- routeResubmission
            // (backend) just flips the task back to status "OPEN" with title "Application Review:
            // CID Resubmitted". That's a real, distinguishable signal already available on the task
            // list; this only surfaces it as a plain heads-up, not a full second workflow.
            const resubmittedTask = selectTasksForAuditScope(
              projectAuditTasks,
              (schedulingScopes || []).find((scope) => (
                scope?.key === checklistScopeKey
                || resolveBaseScheduleKey(scope?.key) === resolveBaseScheduleKey(checklistScopeKey)
              )) || checklistScopeKey,
            )
              .find((task) => isApplicationReviewTaskCode(task?.taskTypeCode)
                && String(task?.status || '').toUpperCase() === 'OPEN'
                && String(task?.title || '').toLowerCase().includes('resubmitted'));
            if (!resubmittedTask) return null;
            return (
              <div className="alert alert-info py-2 mb-3 d-flex align-items-start gap-2" role="alert">
                <Info size={16} className="mt-1 flex-shrink-0" />
                <div>
                  <strong>Resubmitted by Sales — needs review.</strong> The corrected CID documents
                  have been re-uploaded after your earlier revision request. Check the Sales Pack
                  tab, then update this checklist's decision.
                </div>
              </div>
            );
          })()}

          {applicationReviewFollowup?.exists && String(applicationReviewFollowup?.status || '').toUpperCase() === 'OPEN' && (() => {
            // The backend's follow-up payload already carries who the Application Review
            // Follow-up task is currently assigned to (the Sales owner, once revision is
            // requested) — surface it so AR/others viewing this card know who to follow up with.
            const assignedToName = applicationReviewFollowup?.assignedTo;
            return (
            <div className="audit-process-revision-card mb-3">
              <div className="audit-process-revision-card__icon">
                <AlertTriangle size={18} />
              </div>
              <div className="audit-process-revision-card__body">
                <div className="audit-process-revision-card__title">Follow-up pending — the following item(s) need revision</div>
                {assignedToName && (
                  <div className="audit-process-revision-card__assignee mb-2">
                    <User size={13} />
                    Assigned to: <strong>{assignedToName}</strong>
                  </div>
                )}
                <ul className="audit-process-revision-card__meta-list">
                  {(applicationReviewFollowup?.revisiItems || []).map((item) => (
                    <li key={item.id}>
                      <strong>{item.label}</strong>{item.note ? `: ${item.note}` : ' — no notes provided'}
                    </li>
                  ))}
                </ul>
                <div className="audit-process-revision-card__hint">
                  {applicationReviewFollowup?.needsFieldEdit && 'Update Scope / EA Code / Mandays via the item view. '}
                  {applicationReviewFollowup?.needsSalesPack && 'Re-upload the Sales Pack (Sales Pack tab).'}
                </div>
                {applicationReviewFollowup?.needsFieldEdit && myProjectRole.isSales && (
                  <div className="audit-process-revision-card__footer">
                    <button
                      type="button"
                      className="btn btn-teal"
                      disabled={submittingFollowup}
                      onClick={() => setConfirmModalType('submitReReview')}
                    >
                      <Send size={14} className="me-1" />{submittingFollowup ? 'Submitting…' : 'Submit for Re-review'}
                    </button>
                  </div>
                )}
              </div>
            </div>
            );
          })()}
          {applicationReviewFollowup?.exists && String(applicationReviewFollowup?.status || '').toUpperCase() === 'NEEDS_REVIEW' && (
            <div className="audit-process-revision-card audit-process-revision-card--info mb-3">
              <div className="audit-process-revision-card__icon">
                <Eye size={18} />
              </div>
              <div className="audit-process-revision-card__body">
                <div className="audit-process-revision-card__title">Follow-up submitted — awaiting re-review</div>
                {applicationReviewFollowup?.assignedTo && (
                  <div className="audit-process-revision-card__assignee mb-2">
                    <User size={13} />
                    Assigned to: <strong>{applicationReviewFollowup.assignedTo}</strong>
                  </div>
                )}
                <div className="audit-process-revision-card__desc">
                  {unresolvedFollowupItems.length > 0
                    ? `Mark as OK before resolving, or keep as Need Revision to send back to Sales: ${unresolvedFollowupItems.map((item) => item.label).join(', ')}`
                    : 'All flagged items are marked OK — ready to resolve.'}
                </div>
                {canDecideApplicationReview && (
                  <div className="audit-process-revision-card__footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary audit-process-revision-card__btn-ghost"
                      disabled={requestingFollowupRevision || resolvingFollowup || unresolvedFollowupItems.length === 0}
                      title={unresolvedFollowupItems.length === 0 ? 'Mark the flagged item(s) below as Need Revision first' : undefined}
                      onClick={handleRequestFollowupRevision}
                    >
                      <Undo2 size={14} className="me-1" />{requestingFollowupRevision ? 'Sending back…' : 'Need Revision'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-success"
                      disabled={resolvingFollowup || requestingFollowupRevision || unresolvedFollowupItems.length > 0}
                      title={unresolvedFollowupItems.length > 0 ? 'Mark the flagged item(s) below as OK first' : undefined}
                      onClick={handleResolveApplicationReviewFollowup}
                    >
                      <Check size={14} className="me-1" />{resolvingFollowup ? 'Resolving…' : 'Mark Resolved'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {isChecklistReviewMode && (
            <div className="alert alert-secondary py-2 mb-3" role="status">
              This Application Review decision is finalized and read-only.
            </div>
          )}

          <ul className="list-unstyled audit-process-checklist">
            {CHECKLIST_ITEMS.map((item) => {
              const currentValue = checklistValues[item.id];
              const isRevision = currentValue === 'revisi';
              const isOk = currentValue === 'ok';
              
              return (
                <li key={item.id} className="audit-process-checklist-item">
                  <span className="audit-process-checklist-num">{item.id}</span>
                  <span className="audit-process-checklist-label">
                    {item.label}
                    {item.id === REDUCTION_OF_TYPE_ITEM_ID && isReductionOfTypeNA && (
                      <span className="audit-process-checklist-na-badge ms-2" title="This project is audited against a single standard, so there is no type reduction to review.">
                        N/A &mdash; single standard
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="audit-process-doc-icon-btn audit-process-checklist-view-btn ms-2"
                    onClick={() => {
                      if ([1, 2, 3].includes(item.id)) {
                        const matchedStage = findStageByScopeKey(checklistScopeKey);
                        setChecklistFieldStageKey(matchedStage?.key || null);
                        // Initial Certification Stage 1/Stage 2 mandays is only a proposal-derived
                        // suggestion until Audit Scheduling actually enters a value for that
                        // specific stage -- once it has, Scheduling's number is what the stage card
                        // (and everyone downstream) actually shows, so editing Mandays here would
                        // silently write to the wrong place (the standard's total, not the
                        // stage-specific scheduled value) with no visible effect. Lock it in that
                        // case rather than let the edit look like it did nothing.
                        const scheduledMandaysForStage = getTotalScheduledMandays(
                          schedulingDataByScope?.[matchedStage?.key]?.auditSchedules
                        );
                        const mandaysLockedByScheduling = Boolean(
                          resolveScopeStageKey(matchedStage?.key) && scheduledMandaysForStage > 0
                        );
                        setChecklistFieldValues({
                          scope: matchedStage?.scope && matchedStage.scope !== '-' ? matchedStage.scope : '',
                          eaCode: splitEaCodeText(matchedStage?.eaCode),
                          mandays: matchedStage?.mandays && matchedStage.mandays !== '-' ? matchedStage.mandays : '',
                          isSustainability: Boolean(matchedStage?.isSustainability),
                          mandaysLockedByScheduling,
                        });
                        setChecklistFieldEditableRows({ scope: false, eaCode: false, mandays: false });
                        setChecklistFieldModalOpen(true);
                      } else {
                        setChecklistDocsModalOpen(true);
                      }
                    }}
                    title="View Document"
                    aria-label={`View Document for ${item.label}`}
                  >
                    <Eye size={18} />
                  </button>
                  <div className="audit-process-checklist-options">
                    <label className={`audit-process-radio-ok ${isOk ? 'is-selected' : ''}`}>
                      <input
                        type="radio"
                        name={`checklist-${item.id}`}
                        checked={isOk}
                        onChange={() => setChecklistItem(item.id, 'ok')}
                        disabled={isChecklistItemLocked(item.id)}
                      />
                      <span>OK</span>
                    </label>
                    <label className={`audit-process-radio-revisi ${isRevision ? 'is-selected' : ''}`}>
                      <input
                        type="radio"
                        name={`checklist-${item.id}`}
                        checked={isRevision}
                        onChange={() => setChecklistItem(item.id, 'revisi')}
                        disabled={isChecklistItemLocked(item.id)}
                      />
                      <span>Need Revision</span>
                    </label>
                  </div>
                  {isRevision && (
                    <div className="audit-process-checklist-notes mb-3 mt-2">
                      <label className="form-label required">Notes</label>
                      <textarea
                        className={`form-control audit-process-notes ${(checklistNotes[item.id] ?? '').trim() ? '' : 'is-invalid'}`}
                        placeholder="Enter notes (required for Need Revision)"
                        rows={4}
                        value={checklistNotes[item.id] ?? ''}
                        onChange={(e) => setChecklistNote(item.id, e.target.value)}
                        disabled={isChecklistItemLocked(item.id)}
                      />
                      {!(checklistNotes[item.id] ?? '').trim() && (
                        <div className="invalid-feedback d-block">Notes are required when Need Revision is selected.</div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {reviewCompleteError && (
            <div className="alert alert-danger py-2 mb-3 small" role="alert">
              {reviewCompleteError}
            </div>
          )}

          {isChecklistReadOnlyForViewer && (
            <div className="text-muted small mb-2">
              You have view-only access to this checklist. Approve/Reject/Save as Draft are limited to Application Review.
            </div>
          )}

          <div className="d-flex flex-wrap gap-2 justify-content-end">
            {!isChecklistReviewMode && !isChecklistReadOnlyForViewer && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveChecklistDraft}
                disabled={checklistSaving}
              >
                {checklistSaving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FileText size={18} className="me-1" />
                    Save as Draft
                  </>
                )}
              </button>
            )}
            {isChecklistReadOnlyForViewer ? null : reviewCompleted ? (
              <button
                type="button"
                className="btn btn-success"
                disabled
              >
                <CircleCheckBig size={18} className="me-2" />
                Review Completed
              </button>
            ) : isChecklistReviewMode ? null : !hasRevisionItems() && Object.values(checklistValues).some((v) => v != null) ? (
              <button
                type="button"
                className="btn btn-teal"
                onClick={() => setConfirmModalType('agree')}
                disabled={isCompletingReview || !canAgreeAndContinue()}
              >
                {isCompletingReview ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Completing...
                  </>
                ) : (
                  <>
                    <Check size={18} className="me-1" />
                    Approve
                  </>
                )}
              </button>
            ) : (
              <>
                {canSaveChecklist() && checklistValues[4] !== 'revisi' && CHECKLIST_ITEMS.some((item) => item.id !== 4 && checklistValues[item.id] === 'revisi') && (
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={handleApproveWithNotes}
                    disabled={checklistSaving}
                  >
                    <AlertTriangle size={18} className="me-1" />
                    {checklistSaving ? 'Approving…' : 'Approved with Notes'}
                  </button>
                )}
                {canSaveChecklist() && checklistValues[4] === 'revisi' && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleSaveChecklist}
                    disabled={checklistSaving}
                  >
                    <X size={18} className="me-1" />
                    {checklistSaving ? 'Rejecting…' : 'Reject'}
                  </button>
                )}
              </>
            )}
          </div>
        </>
      );
    }

    if (activeDocTab === 'sales-pack') return renderSalesPackContent();

    if (activeDocTab === 'scheduling') {
      const activeWizardIndex = Math.max(0, SCHEDULING_SUB_TABS.findIndex((tab) => tab.id === activeSchedulingSubTab));
      const schedulingWizardProgress = deriveSchedulingWizardProgress({ siteLocations, auditSchedules, accommodations });
      const isWizardStepComplete = (stepId) => schedulingWizardProgress[stepId] === true;
      const moveSchedulingWizard = async (direction) => {
        const nextIndex = Math.min(Math.max(activeWizardIndex + direction, 0), SCHEDULING_SUB_TABS.length - 1);
        if (direction > 0 && canEditScheduling && !schedulingLock?.locked) {
          // Intermediate steps save silently (no modal) and auto-scroll to the
          // next step instead -- the popup is reserved for "Confirm Scheduling".
          const saved = await handleSaveScheduling(null, { silent: true });
          if (!saved) return;
          setSchedulingStepJustSaved(true);
          setTimeout(() => setSchedulingStepJustSaved(false), 1800);
        }
        setActiveSchedulingSubTab(SCHEDULING_SUB_TABS[nextIndex].id);
        requestAnimationFrame(() => {
          schedulingWizardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      };
      return (
        <div ref={schedulingWizardRef}>
          {!canEditScheduling && (
            <div className="alert alert-info py-2 mb-3" role="status">
              Scheduling is read-only for this account. Sign in as a Scheduler or Operation user to update audit dates, auditors, or status.
            </div>
          )}
          <div className="audit-process-scheduling-wizard mb-3" role="tablist" aria-label="Scheduling workflow">
            {SCHEDULING_SUB_TABS.map((tab, index) => {
              const completed = isWizardStepComplete(tab.id);
              return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                className={`audit-process-scheduling-wizard-step ${activeSchedulingSubTab === tab.id ? 'active' : ''} ${completed ? 'completed' : ''}`}
                onClick={() => setActiveSchedulingSubTab(tab.id)}
                aria-selected={activeSchedulingSubTab === tab.id}
              >
                <span className="audit-process-scheduling-wizard-index">
                  {completed ? <Check size={14} /> : index + 1}
                </span>
                <span>{tab.label}</span>
              </button>
              );
            })}
          </div>
          <div className="row g-2 align-items-end mb-3">
            <div className="col-md-12">
              {renderSchedulingScopeSelector()}
            </div>
          </div>
          <fieldset disabled={!canEditScheduling || schedulingLock?.locked} className="border-0 p-0 m-0">
            {renderSchedulingContent()}
          </fieldset>
          {renderSchedulingSupportingDocuments()}
          <div className="audit-process-wizard-actions mt-3">
            <button type="button" className="btn btn-outline-secondary" onClick={() => moveSchedulingWizard(-1)} disabled={activeWizardIndex === 0 || isSavingScheduling}>
              Back
            </button>
            {activeWizardIndex < SCHEDULING_SUB_TABS.length - 1 && (
              <div className="d-flex align-items-center gap-2">
                {schedulingStepJustSaved && (
                  <span className="audit-process-wizard-saved-flash" role="status">
                    <Check size={14} /> Saved
                  </span>
                )}
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => moveSchedulingWizard(1)}
                  disabled={isSavingScheduling || ((canEditScheduling && !schedulingLock?.locked) && !isWizardStepComplete(activeSchedulingSubTab))}
                >
                  {isSavingScheduling ? 'Saving…' : canEditScheduling && !schedulingLock?.locked ? 'Save & Next' : 'Next'}
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    const active = DOCUMENT_TABS.find((tab) => tab.id === activeDocTab);
    if (!active) return null;

    const categoryDocs = auditDocuments[activeDocTab];
    const scopedCategoryDocs = activeDocTab === 'audit'
      ? getAuditDocumentsForScope(categoryDocs, docTabScopeKey)
      : categoryDocs;
    const isLoading = categoryDocs === undefined;
    const uploadedByCode = {};
    if (Array.isArray(scopedCategoryDocs)) {
      scopedCategoryDocs.forEach((doc) => {
        const code = doc?.documentCode || doc?.description || '';
        if (code && !uploadedByCode[code]) {
          uploadedByCode[code] = doc;
        }
      });
    }

    const selectedDocumentScope = docTabScopeKey
      ? schedulingScopes.find((scope) => scope.key === docTabScopeKey)
      : null;
    const isSustainabilityDocumentScope = activeDocTab === 'audit' && isSustainabilityAuditScope(selectedDocumentScope);
    const resolvedAuditDocs = resolveAuditDocumentsForScope({
      activeDocTab,
      baseDocs: active.docs,
      catalogItems: isccFormCatalogItems,
      selectedScope: selectedDocumentScope,
      isStage1: resolveScopeStageKey(docTabScopeKey) === 'stage1',
    });

    if (isLoading) {
      return <div className="text-muted small">Loading documents...</div>;
    }

    const isLoadingSustainabilityCatalog = isSustainabilityDocumentScope && isccFormCatalogLoading;

    if (!isLoadingSustainabilityCatalog && (!resolvedAuditDocs || resolvedAuditDocs.length === 0)) {
      if (isSustainabilityDocumentScope) {
        return (
          <div className="alert alert-warning d-flex align-items-start gap-2 mb-0">
            <AlertTriangle size={16} className="mt-1 flex-shrink-0" />
            <div>
              <div className="fw-semibold">No Sustainability catalog documents found.</div>
              <div className="small">
                The selected scope uses Sustainability/ISCC handling, but the Audit Form Catalog returned no active documents.
              </div>
            </div>
          </div>
        );
      }
      return <div className="text-muted small">No documents defined for this category.</div>;
    }

    const outputType = getAuditOutputTypeForTab(activeDocTab);
    const availableOutputTemplates = getAuditOutputTemplatesForTab(activeDocTab);
    const selectedOutputTemplateId = getSelectedAuditOutputTemplateId(activeDocTab);
    const auditTaskPool = [...(projectAuditTasks || []), ...(projectWorkflowTasks || [])];
    const documentScope = schedulingScopes.find((scope) => scope?.key === docTabScopeKey) || docTabScopeKey;

    // Whoever the Audit (fieldwork) task for this scope is assigned to is the auditor actually
    // doing the audit — same "assigned to" surfacing already done for Application Review and
    // Scheduling. Same fallback shape: scope-aware first (stripping :s1/:s2, since the Audit
    // task is never stage-split), then any Audit task on the project as a last resort.
    const auditAssigneeName = activeDocTab === 'audit'
      ? getAssignedAuditTaskName(auditTaskPool, documentScope, (task) => isAuditTaskCode(task?.taskTypeCode))
      : null;

    // Same treatment for the Technical Review tab — whoever the Technical Review task is
    // assigned to is who's actually doing the review.
    const technicalReviewAssigneeName = activeDocTab === 'technical-review'
      ? getAssignedAuditTaskName(auditTaskPool, documentScope, (task) => isTechnicalReviewTaskCode(task?.taskTypeCode))
      : null;
    const certificateDecisionAssigneeName = activeDocTab === 'certificate'
      ? getAssignedAuditTaskName(auditTaskPool, documentScope, (task) => isCertificateDecisionTaskCode(task?.taskTypeCode))
      : null;
    const certificateIssuerAssigneeName = activeDocTab === 'certificate'
      ? getAssignedAuditTaskName(auditTaskPool, documentScope, (task) => isCertificateIssuanceTaskCode(task?.taskTypeCode))
      : null;

    return (
      <>
        {schedulingScopes.length > 0 && (
          <div className={`audit-process-scope-selector mb-3 ${docTabScopeKey ? 'is-selected' : 'is-pending'}`}>
            <div className="audit-process-scope-selector__prompt">
              <Info size={16} />
              <span><strong>Select the audit scope first.</strong> Forms, status, and saved data below follow this selection.</span>
            </div>
            <label className="form-label" htmlFor="document-audit-scope">{AUDIT_SCOPE_SELECTOR_LABEL} <span className="text-danger">*</span></label>
            <select
              id="document-audit-scope"
              className="form-select audit-process-scope-select"
              value={docTabScopeKey}
              onChange={(e) => {
                const nextScopeKey = syncAuditScopeSelectors(e.target.value);
                if (nextScopeKey && nextScopeKey !== selectedSchedulingScopeKey) {
                  handleSchedulingScopeChange(nextScopeKey);
                }
              }}
              required
            >
              <option value="" disabled>Select an audit scope...</option>
              {schedulingScopes.map((scope) => {
                // Lock the dropdown to the currently active audit process. Scopes that
                // belong to another upcoming cycle are view-only and not selectable.
                // Admins/managers must still be able to inspect completed cycles after
                // activation advances to the next cycle.
                //
                // "Active" is per audit TRACK, not per project: a Single Audit sold with
                // several standards runs one independent audit per standard, so each
                // standard contributes its own active cycle here (an Integrated/Combined
                // group contributes exactly one, since its standards are audited
                // together). Comparing against a single project-wide active cycle locked
                // every other standard's live cycle out of this selector, so the second
                // standard could not be worked on at all until the first one finished.
                const activeBaseKeys = new Set(activeCycleBaseKeys);
                const scopeStage = stages.find((stage) => (
                  resolveBaseScheduleKey(stage?.key) === resolveBaseScheduleKey(scope.key)
                ));
                const canReviewCompletedScope = myProjectRole.isAdminOrManager
                  && Boolean(scopeStage?.isFullyCompleted || scopeStage?.done);
                const selectable = myProjectRole.isAdminOrManager
                  || activeBaseKeys.size === 0
                  || activeBaseKeys.has(resolveBaseScheduleKey(scope.key))
                  || canReviewCompletedScope;
                return (
                  <option key={scope.key} value={scope.key} disabled={!selectable}>
                    {scope.label}{selectable ? '' : ' 🔒'}
                  </option>
                );
              })}
            </select>
            <small className="audit-process-scope-selector__hint">
              {docTabScopeKey
                ? (myProjectRole.isAdminOrManager
                  ? 'The document list is scoped to this selection. Admins can review any audit cycle.'
                  : 'The document list is now scoped to this selection. Only the active audit process can be selected.')
                : (myProjectRole.isAdminOrManager
                  ? 'Choose an audit scope to review its documents and forms.'
                  : 'Choose a scope to enable document actions. Only the active audit process is selectable.')}
            </small>
            {auditAssigneeName && (
              <span className="audit-process-scheduling-assignee mt-2">
                <User size={13} />
                Auditor: <strong>{auditAssigneeName}</strong>
              </span>
            )}
            {activeDocTab === 'technical-review' && docTabScopeKey && (
              <span className="audit-process-scheduling-assignee mt-2">
                <User size={13} />
                Technical Reviewer: <strong>{technicalReviewAssigneeName || 'Unassigned'}</strong>
              </span>
            )}
            {certificateDecisionAssigneeName && (
              <span className="audit-process-scheduling-assignee mt-2">
                <User size={13} />
                Certification Reviewer: <strong>{certificateDecisionAssigneeName}</strong>
              </span>
            )}
            {certificateIssuerAssigneeName && (
              <span className="audit-process-scheduling-assignee mt-2">
                <User size={13} />
                Certificate Issuer: <strong>{certificateIssuerAssigneeName}</strong>
              </span>
            )}
          </div>
        )}
        {isLoadingSustainabilityCatalog && (
          <div className="text-muted small mb-3">Loading Sustainability document catalog...</div>
        )}
        {docTabScopeKey && (
        <div className="py-2">
        {!isLoadingSustainabilityCatalog && resolvedAuditDocs.map((docMeta) => {
          const docCode = docMeta?.code || docMeta;
          const uploaded = uploadedByCode[docCode] || null;
          const uploadKey = `${activeDocTab}::${docCode}`;
          const isUploading = uploadingDocKey === uploadKey;
          const isDeleting = uploaded && deletingDocId === uploaded.id;
          const isFormDoc = FORM_DOC_CODES.has(docCode);
          const isCatalogUploadOnlyDocument = Boolean(docMeta?.uploadOnly || docMeta?.catalogItem);
          const isUploadOnlyDocument = docCode === 'FAPP-06 CIF (signed by client)' || isCatalogUploadOnlyDocument;
          const canUploadDocument = !isFormDoc || isUploadOnlyDocument;
          // Use scope-specific key if a scope is selected, otherwise use docCode only
          const hasSavedForm = Boolean(getSavedFormEntry(docCode, docTabScopeKey));
          // Technical Review cannot be complete before the selected cycle's
          // Audit stage is complete. This prevents stale/accidental FTRD data
          // from making a current or upcoming cycle look review-complete.
          const hasValidSavedForm = activeDocTab === 'technical-review'
            // FTRD-01 is saved only after the backend stage gate accepts the
            // audit package. Once its scoped payload exists, do not hide it
            // behind a stale stage-progress snapshot during the same refresh.
            ? hasSavedForm && (
              isTechnicalReviewAvailableForScope(docTabScopeKey)
              || docCode === 'FTRD-01 Technical Review and Certification Decision'
            )
            : hasSavedForm;
          const selectedDocumentScope = schedulingScopes.find((scope) => scope.key === docTabScopeKey) || { key: docTabScopeKey };
          const selectedCycleStage = stages.find((stage) => stage.key === docTabScopeKey);
          const cycleIsFinished = Boolean(
            selectedCycleStage?.isFullyCompleted
            || isWorkflowFullyCompleted(selectedCycleStage?.workflowProgress)
          );
          // Do not expose an edit action while the selected cycle is still being
          // resolved. A missing stage is treated as unknown, not editable.
          const cycleEditStatusKnown = Boolean(selectedCycleStage);
          // An approval history entry can exist even when an older/missing task
          // path failed to persist the FTRD-01 payload. Keep the Stage 1 FTRD-01
          // repairable for admins in that narrow case; otherwise a completed
          // gate makes the empty review impossible to correct from the UI.
          const canRepairStage1Ftrd01 = Boolean(
            myProjectRole.isAdminOrManager
            && docCode === 'FTRD-01 Technical Review and Certification Decision'
            && String(docTabScopeKey || '').endsWith(':s1')
            && !hasValidSavedForm
          );
          // Certificate work is available only when this cycle's Technical Review
          // decision recommends grant; Stage 1 remains ineligible. Stage 2 audit work
          // may run independently, but certificate work must wait for Stage 1 TR.
          const certificateWorkflowAllowed = shouldIncludeCertificateIssuance(selectedDocumentScope);
          const selectedScopeBaseKey = String(docTabScopeKey || '').replace(/:s[12]$/, '');
          const selectedScopeGate = stageGateMap[selectedScopeBaseKey];
          const certificateStage1Locked = activeDocTab === 'certificate'
            && String(docTabScopeKey || '').endsWith(':s2')
            && selectedScopeGate?.stage1Completed !== true;

          // Audit Summary Report: auto-generated wrap-up, not a fillable form —
          // one "Export PPTX" action generates fresh data then exports, no
          // separate manual "Generate Report" step and no Input Form/Upload UI.
          if (docCode === AUDIT_SUMMARY_DOC_CODE) {
            return (
              <div key={`${docTabScopeKey || 'all'}::${docCode}`} className="border rounded mb-3 overflow-hidden bg-white audit-process-doc-row">
                <div className="audit-process-doc-row-top">
                  <div className="audit-process-doc-row-title">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span className="fw-medium" style={{ fontSize: '0.9rem' }}>{getAuditDocumentDisplayName(docCode)}</span>
                      <span className="badge text-bg-light border">Auto-generated</span>
                    </div>
                    <div className="text-muted small mt-1">
                      Generated automatically from project data and audit findings when exported — read-only.
                    </div>
                  </div>
                  <div className="audit-process-doc-row-actions">
                    <button
                      type="button"
                      className="btn d-flex align-items-center gap-1"
                      style={{ backgroundColor: '#1a767e', color: 'white', borderColor: '#1a767e' }}
                      disabled={!docTabScopeKey || isExportingAuditSummary}
                      title={!docTabScopeKey ? 'Select an audit scope first' : 'Generate and export as PPTX'}
                      onClick={() => handleExportAuditSummaryReport(docTabScopeKey)}
                    >
                      {isExportingAuditSummary ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      ) : (
                        <Download size={16} />
                      )}
                      Export PPTX
                    </button>
                  </div>
                </div>
              </div>
            );
          }

          if (activeDocTab === 'certificate' && (!certificateWorkflowAllowed || certificateStage1Locked)) {
            return (
              <div key={`${docTabScopeKey || 'all'}::${docCode}`} className="border rounded mb-3 p-3 bg-white">
                <div className="d-flex align-items-center gap-2 text-muted">
                  <Info size={16} className="text-info flex-shrink-0" />
                  <span>
                    {certificateStage1Locked
                      ? "Certificate Issuance is locked until Stage 1 Technical Review is completed with the 'Continue to Stage 2' decision. Stage 2 audit work may continue, but certificate work cannot skip this gate."
                      : 'Certificate Issuance is not selected for this audit cycle. The Technical Review decision can either recommend a certificate or continue to the next cycle without one.'}
                  </span>
                </div>
              </div>
            );
          }
          const isCertificateDraftDoc = docCode === 'FTRD-02 Certificate Review and Production';
          const certificateStandardId = String(docTabScopeKey || '')
            .replace(/:s[12]$/, '')
            .split(':')[1] || '';
          const certificateLifecycle = isCertificateDraftDoc
            ? (certificateLifecycleByStandard[certificateStandardId] || certificateLifecycleByStandard.__default)
            : null;
          const certificateIssuanceTaskOpen = isCertificateDraftDoc && projectAuditTasks.some((task) => (
            String(task?.taskTypeCode || task?.taskType || task?.type || '').toUpperCase().replace(/\s+/g, '_') === 'CERTIFICATE_ISSUANCE'
            && String(task?.projectServiceStandardId || '') === certificateStandardId
            && !isTaskDone(task?.status)
            && !['CANCELLED', 'CANCELED'].includes(String(task?.status || '').toUpperCase())
          ));
          const certificateLifecycleStatusKnown = isCertificateDraftDoc
            && certificateLifecycleCheck.available
            && Boolean(certificateLifecycle);
          const certificateStatus = String(certificateLifecycle?.status || '').trim().toUpperCase();
          const certificateIsFinalized = isCertificateDraftDoc && Boolean(
            certificateLifecycle?.finalPdfLocked === true
            || IMMUTABLE_CERTIFICATE_STATUSES.has(certificateStatus)
          ) && !certificateIssuanceTaskOpen;
          const formButtonText = certificateIsFinalized
            ? 'View Finalized'
            : isCertificateDraftDoc
              ? (hasValidSavedForm ? 'Continue Draft' : 'Start Draft')
            : getFormButtonLabel(docCode);
          // FAPP-06 and FAPP-08 are Stage 2 only — disable button when scope is Stage 1
          const shortDocCode = docCode.split(' ')[0];
          const isStage2OnlyFormInStage1 = resolveScopeStageKey(docTabScopeKey) === 'stage1' && ['FAPP-06', 'FAPP-08'].includes(shortDocCode);

          return (
            <div
              key={`${docTabScopeKey || 'all'}::${docCode}`}
              ref={(el) => { docRowRefs.current[docCode] = el; }}
              className="border rounded mb-3 overflow-hidden bg-white audit-process-doc-row"
            >
              {/* Top row: doc name + action buttons. Scope is already shown once
                  in the selector above — repeating it per row was redundant. */}
              <div className="audit-process-doc-row-top">
                <div className="audit-process-doc-row-title">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="fw-medium" style={{ fontSize: '0.9rem' }}>{getAuditDocumentDisplayName(docCode)}</span>
                    <span className={`badge ${isCertificateDraftDoc
                      ? (certificateIsFinalized ? 'text-bg-success' : hasValidSavedForm ? 'text-bg-primary' : 'text-bg-light')
                      : (uploaded || hasValidSavedForm ? 'text-bg-success' : 'text-bg-light')}`}>
                      {isCertificateDraftDoc
                        ? (certificateIsFinalized ? 'Completed' : hasValidSavedForm ? 'Draft' : 'Not Started')
                        : (uploaded || hasValidSavedForm ? 'Completed' : 'Not Started')}
                    </span>
                  </div>
                  {docMeta?.title && (
                    <div className="text-muted small mt-1">{docMeta.title}</div>
                  )}
                </div>
                <div className="audit-process-doc-row-actions">
                  {/* Choose File upload button */}
                  {canUploadDocument && <label
                    className={`btn btn-sm btn-outline-secondary mb-0 d-flex align-items-center gap-1 ${!docTabScopeKey ? 'disabled' : ''}`}
                    style={{ cursor: !docTabScopeKey ? 'not-allowed' : (isUploading ? 'wait' : 'pointer'), fontSize: '0.8rem' }}
                    title={!docTabScopeKey ? 'Select an audit scope first' : `Upload ${docCode}`}
                  >
                    {isUploading ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    ) : (
                      <Upload size={13} />
                    )}
                    Choose File
                    <input
                      type="file"
                      className="d-none"
                      disabled={!docTabScopeKey || isUploading}
                      onChange={(e) => {
                        const file = e.target.files && e.target.files[0];
                        if (file) handleDocumentUpload(activeDocTab, docCode, file);
                        e.target.value = '';
                      }}
                    />
                  </label>}

                  {/* Read-only peek for every structured form. This deliberately
                      bypasses role/stage edit rules and never exposes Save. */}
                  {isFormDoc && !isUploadOnlyDocument && (
                    <button
                      type="button"
                      className="audit-process-doc-icon-btn audit-process-checklist-view-btn"
                      disabled={!docTabScopeKey || isStage2OnlyFormInStage1}
                      title={
                        !docTabScopeKey ? 'Select an audit scope first'
                        : isStage2OnlyFormInStage1 ? 'Not applicable for Stage 1'
                        : 'View Form'
                      }
                      aria-label={`View Form for ${getAuditDocumentDisplayName(docCode)}`}
                      onClick={() => setActiveInlineForm({
                        docCode,
                        category: activeDocTab,
                        scopeKey: docTabScopeKey,
                        readOnly: true,
                      })}
                    >
                      <Eye size={18} />
                    </button>
                  )}

                  {/* Form button (teal) for form-based docs */}
                  {isFormDoc
                    && !isUploadOnlyDocument
                    && !certificateIsFinalized
                    && (!cycleIsFinished || canRepairStage1Ftrd01)
                    && cycleEditStatusKnown
                    && (!isCertificateDraftDoc || certificateLifecycleStatusKnown)
                    && (
                    <button
                      type="button"
                      className="btn d-flex align-items-center gap-1"
                      style={{
                        backgroundColor: '#1a767e',
                        color: 'white',
                        borderColor: '#1a767e',
                      }}
                      disabled={!docTabScopeKey || isStage2OnlyFormInStage1 || certificateStage1Locked}
                      title={
                        !docTabScopeKey ? 'Select an audit scope first'
                        : isStage2OnlyFormInStage1 ? 'Not required for Stage 1'
                        : certificateStage1Locked ? 'Complete Stage 1 Technical Review first'
                        : formButtonText
                      }
                      onClick={async () => {
                        // Stage gate: determine editability from stageGateMap
                        const scopeStr = String(docTabScopeKey || '');
                        const baseScopeKey = scopeStr.replace(/:s[12]$/, '');
                        const gate = stageGateMap[baseScopeKey];
                        const isS1Scope = scopeStr.endsWith(':s1');
                        const isS2Scope = scopeStr.endsWith(':s2');
                        const isStage2OnlyDoc = ['FAPP-06', 'FAPP-08', 'FTRD-02'].includes(docCode);

                        // Stage 1 scope opens in read-only when Stage 2+ is active.
                        // Technical Reviewers can never edit audit forms directly, EXCEPT
                        // FTRD-01 itself — that document now hosts their whole OK/Need
                        // Revision decision system (its own "Stage 1 Decision" dropdown +
                        // the Save button), so it must stay editable for them.
                        const isReadOnly = (isS1Scope && gate?.stage1ReadOnly === true && !canRepairStage1Ftrd01)
                          || (isTechnicalReviewer && !isAdminOrManager
                            && docCode !== 'FTRD-01 Technical Review and Certification Decision');

                        // A certificate can be shown as a completed document while its
                        // lifecycle record is still a draft. Ask the authoritative
                        // certificate endpoint before opening FTRD-02 so FINAL/ISSUED/
                        // LOCKED (and locked final PDFs) can only be viewed.
                        let certificateReadOnly = false;
                        if (isCertificateDraftDoc && projectId) {
                          setCertificateFormLoadingScope(docTabScopeKey);
                          try {
                            const certificateResponse = await api.get(`/audit/projects/${projectId}/certificate`);
                            const certificates = Array.isArray(certificateResponse?.data?.certificates)
                              ? certificateResponse.data.certificates
                              : [];
                            const [, projectServiceStandardId] = baseScopeKey.split(':');
                            const certificate = certificates.find((item) => (
                              projectServiceStandardId
                              && String(item?.projectServiceStandardId || '') === String(projectServiceStandardId)
                            )) || certificateResponse?.data || certificates[0] || null;
                            const status = String(certificate?.status || '').trim().toUpperCase();
                            certificateReadOnly = Boolean(
                              certificate?.finalPdfLocked === true
                              || IMMUTABLE_CERTIFICATE_STATUSES.has(status)
                            ) && !certificateIssuanceTaskOpen;
                          } catch (error) {
                            // Fail closed: an unknown certificate lifecycle must never
                            // result in an editable form.
                            console.warn('Certificate lifecycle status unavailable:', error?.message || error);
                            certificateReadOnly = true;
                          } finally {
                            setCertificateFormLoadingScope('');
                          }
                        }

                        setActiveInlineForm({
                          docCode,
                          category: activeDocTab,
                          scopeKey: docTabScopeKey,
                          readOnly: isReadOnly || certificateReadOnly,
                        });
                      }}
                    >
                      {certificateFormLoadingScope === docTabScopeKey ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      ) : <FileText size={16} />}
                      {certificateFormLoadingScope === docTabScopeKey ? 'Checking certificate…' : formButtonText}
                    </button>
                  )}
                </div>
              </div>

              {/* Uploaded documents section */}
              {uploaded && (
                <div className="px-3 pb-3 border-top" style={{ backgroundColor: '#fafafa' }}>
                  <div className="text-muted mt-2 mb-2" style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                    Uploaded Documents
                  </div>
                  <div className="d-flex align-items-center gap-2 p-2 rounded border bg-white">
                    <FileText size={13} className="text-muted flex-shrink-0" />
                    <span className="small text-truncate" style={{ flex: 1, fontSize: '0.82rem' }}>
                      {uploaded.fileName || uploaded.originalFileName || uploaded.name || 'Uploaded'}
                    </span>
                    <div className="d-flex gap-1">
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-1 text-muted"
                        title="Preview"
                        disabled={isDeleting}
                        onClick={() => downloadAuditProjectDocument(
                          uploaded.id,
                          uploaded.fileName || uploaded.originalFileName || uploaded.name || docCode
                        )}
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-1 text-danger"
                        title="Delete"
                        disabled={isDeleting}
                        onClick={() => handleDocumentDelete(activeDocTab, uploaded.id)}
                      >
                        {isDeleting ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        ) : (
                          <X size={14} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          );
        })}
        </div>
        )}
      </>
    );
  };

  // Once every non-terminal major step is done, the full chip breakdown +
  // celebration banner is pure repetition of what the group-level Stage
  // overview above it already shows (see renderInitialStageOverview) — a
  // single compact strip carries the same information in a fraction of the
  // vertical space.
  const renderCompletedWorkflowSummary = (workflowProgress) => {
    const majorSteps = workflowProgress?.majorSteps || AUDIT_WORKFLOW_MAJOR_STEPS;
    const generatesCertificate = majorSteps.some((step) => step.id === 'certificate-issuance');
    return (
      <div className="audit-process-completed-summary">
        <span className="audit-process-completed-summary-icon" aria-hidden="true">✓</span>
        <span className="audit-process-completed-summary-text">
          {generatesCertificate
            ? 'Audit Completed — certificate auto-generated and available in Certificate Monitoring.'
            : 'Audit Completed — this workflow stage is complete.'}
        </span>
        {generatesCertificate && (
          <a href="/audit/certificate-monitoring" className="audit-process-completed-summary-link">
            View Certificate →
          </a>
        )}
      </div>
    );
  };

  const renderAuditWorkflowStepper = (workflowProgress) => {
    if (isWorkflowFullyCompleted(workflowProgress)) {
      return renderCompletedWorkflowSummary(workflowProgress);
    }

    const currentMajorId = workflowProgress?.currentMajorId;
    const completedMajorIds = new Set(workflowProgress?.completedMajorIds || []);
    const completedSubStepIds = new Set(workflowProgress?.completedSubStepIds || []);
    const currentSubStepId = workflowProgress?.currentSubStepId || null;
    // Stage 1 carries a majorSteps list with Certificate Issuance filtered out
    // (see buildAuditWorkflowProgress's isInitialStage1) -- fall back to the
    // full list for callers that pass a plain/legacy progress object.
    const majorSteps = workflowProgress?.majorSteps || AUDIT_WORKFLOW_MAJOR_STEPS;
    const currentMajor = majorSteps.find((major) => major.id === currentMajorId) || null;
    const activeSubSteps = currentMajor && Array.isArray(currentMajor.subSteps) ? currentMajor.subSteps : [];

    return (
      <div className="audit-process-inline-stepper" aria-label="Audit workflow progress">
        <div className="audit-process-inline-stepper-major" role="list">
          {majorSteps.map((major, idx) => {
            const isCurrent = currentMajorId === major.id;
            const isCompleted = completedMajorIds.has(major.id);
            return (
              <React.Fragment key={major.id}>
                <div
                  role="listitem"
                  className={`audit-process-inline-major-item ${isCurrent ? 'is-current' : ''} ${isCompleted ? 'is-completed' : ''}`}
                >
                  <span className="audit-process-inline-major-num">{idx + 1}</span>
                  <div className="audit-process-inline-major-text">{major.label}</div>
                </div>
                {idx < majorSteps.length - 1 && (
                  <div className="audit-process-inline-major-divider" aria-hidden="true" />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {activeSubSteps.length > 0 && (
          <div className="audit-process-inline-stepper-sub" role="list">
            {activeSubSteps.map((subStep, idx) => {
              const isCompleted = completedSubStepIds.has(subStep.id);
              const isCurrent = currentSubStepId === subStep.id;
              const matchedTask = (projectAuditTasks || []).find((t) =>
                Array.isArray(subStep.codes) && subStep.codes.some((c) => String(c).toUpperCase() === String(t?.taskTypeCode || '').toUpperCase())
              );
              return (
                <React.Fragment key={subStep.id}>
                  <div
                    role="listitem"
                    className={`audit-process-inline-sub-item ${isCurrent ? 'is-current' : ''} ${isCompleted ? 'is-completed' : ''}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <span className="audit-process-inline-sub-dot">
                      {isCompleted ? <Check size={12} /> : ''}
                    </span>
                    <span style={{ flex: 1 }}>{subStep.label}</span>
                    {matchedTask && !isCompleted && (
                      <button
                        type="button"
                        className="btn btn-xs btn-outline-secondary"
                        style={{ fontSize: 11, padding: '1px 7px', lineHeight: 1.6 }}
                        onClick={(e) => { e.stopPropagation(); openReassignModal(matchedTask); }}
                        title={`Re-assign: ${matchedTask.assignedUsername || 'unassigned'}`}
                      >
                        Re-assign
                      </button>
                    )}
                  </div>
                  {idx < activeSubSteps.length - 1 && (
                    <div className="audit-process-inline-sub-divider" aria-hidden="true" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderInitialStageOverview = (group, workflowProgress) => {
    const baseScopeKey = resolveBaseScheduleKey(group?.members?.[0]?.key || '');
    const gate = stageGateMap[baseScopeKey];
    const auditTab = DOCUMENT_TABS.find((tab) => tab.id === 'audit');
    const stage2ScopeKey = `${baseScopeKey}:s2`;
    const stage2AuditFormsCompleted = auditTab
      ? isTabAllFormsSavedForScope(auditTab, stage2ScopeKey)
      : false;
    const stage2SchedulingConfirmed = isSchedulingScopeConfirmed(schedulingDataByScope?.[stage2ScopeKey]);
    // Pass latest TR decision so stepper reflects NEED_REVISION state (2B.3)
    const trDecision = trDecisionData?.decision || null;
    const stageProgress = buildInitialStageStepper({
      gate,
      workflowProgress,
      auditFormsCompleted: stage2AuditFormsCompleted,
      stage2SchedulingConfirmed,
      trDecision,
    });
    if (!stageProgress) return null;

    return (
      <div className="audit-initial-stage-stepper" aria-label="Initial Certification stage progress">
        {stageProgress.steps.map((step, index) => (
          <React.Fragment key={step.id}>
            <div className={`audit-initial-stage-step ${step.current ? 'is-current' : ''} ${step.completed ? 'is-completed' : ''} ${step.locked ? 'is-locked' : ''} ${step.needsRevision ? 'needs-revision' : ''}`}>
              <span className="audit-initial-stage-step__icon">
                {step.completed ? <Check size={14} /> : index + 1}
              </span>
              <span>
                {step.label}
                {step.needsRevision && <span className="ms-1 text-warning fw-semibold" title="TR requested revision">⚠</span>}
              </span>
            </div>
            {index < stageProgress.steps.length - 1 && <span className="audit-initial-stage-step__line" aria-hidden="true" />}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderContactBlock = (title, data, sectionKey, showSameAsRepresentative = false) => {
    const isSameAsRepresentative = Boolean(data?.sameAsRepresentative);
    const isDisabled = showSameAsRepresentative && isSameAsRepresentative;
    const personError = contactValidationErrors[getContactFieldKey(sectionKey, 'person')];
    const phoneError = contactValidationErrors[getContactFieldKey(sectionKey, 'phone')];
    const emailError = contactValidationErrors[getContactFieldKey(sectionKey, 'email')];

    return (
    <div className="audit-process-contact-block">
      <div className="audit-process-contact-block-header">
        <h4 className="audit-process-contact-block-title mb-0">{title}</h4>
        {showSameAsRepresentative && (
          <label className="audit-process-contact-sameas mb-0">
            <input
              type="checkbox"
              checked={isSameAsRepresentative}
              onChange={(e) => handleSameAsRepresentativeChange(sectionKey, e.target.checked)}
            />
            <span>Same as Representative</span>
          </label>
        )}
      </div>
      <div className="audit-process-contact-grid">
        <div className="audit-process-contact-label">Contact Person</div>
        <div className="audit-process-contact-colon">:</div>
        <div className="audit-process-contact-input-wrap">
          <input
            type="text"
            className={`form-control audit-process-contact-input ${personError ? 'is-invalid' : ''}`}
            value={toContactInputValue(data?.person)}
            onChange={(e) => handleContactFieldChange(sectionKey, 'person', e.target.value)}
            disabled={isDisabled}
            placeholder="Enter contact person"
          />
          {personError && <div className="invalid-feedback d-block">{personError}</div>}
        </div>

        <div className="audit-process-contact-label">Phone Number</div>
        <div className="audit-process-contact-colon">:</div>
        <div className="audit-process-contact-input-wrap">
          <input
            type="text"
            className={`form-control audit-process-contact-input ${phoneError ? 'is-invalid' : ''}`}
            value={toContactInputValue(data?.phone)}
            onChange={(e) => handleContactFieldChange(sectionKey, 'phone', e.target.value)}
            disabled={isDisabled}
            placeholder="Enter phone number"
          />
          {phoneError && <div className="invalid-feedback d-block">{phoneError}</div>}
        </div>

        <div className="audit-process-contact-label">Email</div>
        <div className="audit-process-contact-colon">:</div>
        <div className="audit-process-contact-input-wrap">
          <input
            type="email"
            className={`form-control audit-process-contact-input ${emailError ? 'is-invalid' : ''}`}
            value={toContactInputValue(data?.email)}
            onChange={(e) => handleContactFieldChange(sectionKey, 'email', e.target.value)}
            disabled={isDisabled}
            placeholder="Enter email"
          />
          {emailError && <div className="invalid-feedback d-block">{emailError}</div>}
        </div>
      </div>
    </div>
    );
  };

  const workflowPointerCycle = stages.find((stage) => stage.showWorkflowStepper) || stages[0] || null;
  const completedInitialStage2 = stages.filter((stage) => {
    const key = String(stage?.key || '');
    const scopeBaseKey = resolveBaseScheduleKey(key);
    const stageGate = stageGateMap[scopeBaseKey];
    const isInitialCertification = /initial certification|initial audit/i.test(
      String(stage?.serviceItem || stage?.stageName || ''),
    ) || key.endsWith(':s2');
    // Stage 2 completion is authoritative in the stage-gate API. The derived
    // task progress can remain at `audit-completed` while the certificate
    // handoff is already closed, so relying only on isFullyCompleted/done
    // leaves the sticky stepper pointing back at Stage 1.
    const stage2GateCompleted = key.endsWith(':s2')
      && String(stageGate?.activeStage || '').toLowerCase() === 'completed';
    return isInitialCertification && Boolean(
      stage?.isFullyCompleted
      || stage?.done
      || String(stage?.statusLabel || '').toLowerCase() === 'audit completed'
      || isWorkflowFullyCompleted(stage?.workflowProgress)
      || stage2GateCompleted
    );
  }).at(-1);
  const workflowPointerIsInitialStage = /initial certification|initial audit/i.test(
    String(workflowPointerCycle?.serviceItem || workflowPointerCycle?.stageName || ''),
  ) && !/surveillance|recertification/i.test(
    String(workflowPointerCycle?.serviceItem || workflowPointerCycle?.stageName || ''),
  );
  // The backend may leave the workflow pointer on Stage 1 after Stage 2 has
  // completed. Prefer the completed Stage 2 cycle in that specific stale-pointer
  // case so the sticky stepper, scope selector, and document tabs agree.
  const taskDerivedAuditCycle = workflowPointerCycle
    && workflowPointerIsInitialStage
    && completedInitialStage2
    ? completedInitialStage2
    : workflowPointerCycle;

  // The above is still task-derived and only catches Stage 1 -> Stage 2 once Stage 2
  // is FULLY completed. Application Review/Scheduling tasks are deliberately shared
  // by both Initial Certification stages (see selectTasksForAuditScope), so while
  // Stage 2 is merely ACTIVE (in progress, not yet done) the pointer above stays on
  // Stage 1 -- which is exactly what left the sticky/main workflow stepper showing
  // Stage 1 while the operator was already working in Stage 2. Re-resolve through
  // the BE stage gate on top of the task-derived pick (resolveActiveScopeKey is the
  // same gate-alignment the three Audit Scope selectors already use, see
  // autoActiveScopeKey below), and only swap when the gate actually disagrees --
  // it returns the same key unchanged for single-stage cycles (Surveillance/
  // Recertification) or while the gate hasn't loaded yet, so nothing else changes.
  const gateAlignedCycleKey = taskDerivedAuditCycle?.key
    ? resolveActiveScopeKey(taskDerivedAuditCycle.key)
    : '';
  const currentAuditCycle = (gateAlignedCycleKey && gateAlignedCycleKey !== taskDerivedAuditCycle?.key
    ? stages.find((stage) => stage?.key === gateAlignedCycleKey)
    : null) || taskDerivedAuditCycle;

  // Every audit track's own active cycle, as base (stage-suffix-free) scope keys.
  // `showWorkflowStepper` was set from resolveActiveCycleKeys when the stages were
  // mapped, so this stays in lockstep with what the cycle cards display. Falls back
  // to the single page-level cycle for the legacy stage sources that never carry a
  // standard identity (and therefore resolve to one shared track anyway).
  const activeCycleBaseKeys = (() => {
    const fromStages = stages
      .filter((stage) => stage?.showWorkflowStepper && stage?.key != null)
      .map((stage) => resolveBaseScheduleKey(stage.key));
    if (fromStages.length > 0) return Array.from(new Set(fromStages));
    const fallback = resolveBaseScheduleKey(currentAuditCycle?.key || '');
    return fallback ? [fallback] : [];
  })();

  // All workflow tabs should open on the same active audit cycle shown in the
  // sticky progress header. Each tab keeps its own form state, but the initial
  // scope selection must not drift back to the first (often completed) cycle.
  //
  // `autoActiveScopeKey` prefers the BE stage gate over the task-derived cycle
  // pointer (resolveActiveScopeKey), so an Initial Certification cycle whose
  // gate has advanced to Stage 2 auto-selects Stage 2, not Stage 1.
  //
  // The effect only re-applies when the RESOLVED target actually changes
  // (autoSelectedScopeRef). `stages`/`setStages` gets a new array identity on
  // every task refetch and every save, which previously re-ran this effect on
  // every save and snapped a manual selection back to the gate's current
  // stage; comparing against the resolved key instead lets a manual pick
  // survive a save while still following a genuine stage advance.
  const autoActiveScopeKey = currentAuditCycle?.key ? resolveActiveScopeKey(currentAuditCycle.key) : '';
  // Array identity of `stages` changes on every setStages; only the key set matters here.
  const stageKeysSignature = stages.map((stage) => stage?.key).join('|');

  useEffect(() => {
    if (!autoActiveScopeKey || schedulingScopes.length === 0) return;
    if (autoSelectedScopeRef.current === autoActiveScopeKey) return;
    autoSelectedScopeRef.current = autoActiveScopeKey;

    setSelectedSchedulingScopeKey((previous) => previous === autoActiveScopeKey ? previous : autoActiveScopeKey);
    setDocTabScopeKey((previous) => previous === autoActiveScopeKey ? previous : autoActiveScopeKey);
    const currentChecklistScopeKey = resolveBaseScheduleKey(autoActiveScopeKey);
    setChecklistScopeKey((previous) => previous === currentChecklistScopeKey ? previous : currentChecklistScopeKey);
    applySchedulingScopeData(autoActiveScopeKey, schedulingDataByScope);
    setSchedulingScopeMandaysLimit(getTopMandaysLimit(autoActiveScopeKey));
    // Keep the main stage cards aligned with the same cycle as the sticky
    // stepper. This also collapses a stale Stage 1 card when the gate API has
    // advanced the completed Initial Certification to Stage 2. Matches on the
    // base key (not the exact stage key) because Initial Cert Stage 1/Stage 2
    // are merged into a single card.
    setExpandedStages((previous) => {
      if (!stages.some((stage) => stage?.key === currentAuditCycle.key)) return previous;
      return stages.reduce((next, stage) => ({
        ...next,
        [stage.key]: resolveBaseScheduleKey(stage.key) === resolveBaseScheduleKey(autoActiveScopeKey),
      }), {});
    });
    // eslint-disable-next-line
  }, [autoActiveScopeKey, schedulingScopes.length, isLoadingPage, stageKeysSignature]);
  // The active pointer advances to the next cycle after issuance. Keep the
  // global certificate shortcut available when an earlier eligible cycle has
  // already completed, instead of tying it only to the current cycle.
  const completedCertificateStage = stages.find((stage) => (
    (stage?.isFullyCompleted
      || stage?.done
      || String(stage?.statusLabel || '').toLowerCase() === 'audit completed')
    && /certification|recert/i.test(String(stage?.serviceItem || ''))
  ));
  const hasCompletedInitialCertificate = Object.values(stageGateMap).some(
    (gate) => String(gate?.activeStage || '').toLowerCase() === 'completed',
  );
  // Live (re-rendered) workflow progress for ONE cycle. `stage.workflowProgress`
  // is only a snapshot taken while the page loaded, so anything that has to react
  // to the current scheduling cache / task state within the session recomputes it
  // through here. Used for the pointer cycle and, because several cycles can be
  // active at once (one per audit track), for whichever cycle the audit-scope
  // selector is pointing at.
  const buildLiveCycleWorkflowProgress = (cycle) => {
    const scope = schedulingScopes.find((entry) => entry?.key === cycle?.key) || cycle;
    const taskSource = selectTasksForAuditScope(projectWorkflowTasks, scope);
    const auditSchedulingTaskDone = taskSource.some((t) => {
      const tc = String(t?.taskTypeCode || '').toUpperCase();
      return ['AUDIT_SCHEDULING', 'ASSIGN_NEXT_PROCESS_SALES', 'ASSIGN_NEXT_PROCESS_SCHEDULER'].includes(tc) && isTaskDone(t?.status);
    });
    const schedulingCompleted = isSchedulingScopeConfirmed(schedulingDataByScope?.[cycle?.key])
      || Boolean(cycle?.schedulingCompleted)
      || auditSchedulingTaskDone;
    return {
      scope,
      taskSource,
      schedulingCompleted,
      workflowProgress: buildAuditWorkflowProgress(taskSource, {
        schedulingConfirmed: schedulingCompleted,
        isInitialStage1: Boolean(cycle?.key && String(cycle.key).endsWith(':s1')),
        includeCertificateIssuance: shouldIncludeCertificateIssuance(cycle || {}),
        completedSubStepIds: [],
      }),
    };
  };
  const currentCycleLiveProgress = buildLiveCycleWorkflowProgress(currentAuditCycle);
  const isSchedulingCompleted = currentCycleLiveProgress.schedulingCompleted;
  const currentCycleWorkflowProgress = currentCycleLiveProgress.workflowProgress;

  // Check if all form-based docs in a tab are saved (must be defined before use below)
  const savedFormKeys = Object.keys(savedFormDataMap);
  const isTabFormSaved = (docCode) =>
    savedFormKeys.some((key) => key === docCode || key.endsWith(`::${docCode}`));
  const isTabAllFormsSaved = (tab) => {
    if (!tab.docs || tab.docs.length === 0) return false;
    const formDocs = tab.docs.filter((doc) => FORM_DOC_CODES.has(doc) && !OPTIONAL_FORM_DOC_CODES.has(doc));
    if (formDocs.length === 0) return false;
    return formDocs.every((doc) => isTabFormSaved(doc));
  };
  const isTabAllFormsSavedForScope = (tab, scopeKey) => {
    if (!tab?.docs || !scopeKey) return false;
    // FAPP-06/FAPP-08 are Stage-2-only (see resolveAuditDocumentsForScope) — Stage 1's
    // Audit tab must not require them to reach "all forms saved" either.
    const isScopeStage1 = tab.id === 'audit' && resolveScopeStageKey(scopeKey) === 'stage1';
    const formDocs = tab.docs
      .filter((doc) => !(isScopeStage1 && STAGE2_ONLY_AUDIT_DOC_CODES.has(doc)))
      .filter((doc) => FORM_DOC_CODES.has(doc) && !OPTIONAL_FORM_DOC_CODES.has(doc));
    if (formDocs.length === 0) return false;
    return formDocs.every((doc) => Boolean(savedFormDataMap[buildDocumentFormStorageKey(doc, scopeKey)]));
  };

  const getDisplayedWorkflowProgress = (stage, workflowProgress) => {
    if (!workflowProgress) return workflowProgress;
    const scopeKey = String(stage?.key || '');
    const isClosedInitialStage2 = /:s2$/.test(scopeKey)
      && Boolean(stage?.schedulingCompleted || isSchedulingScopeConfirmed(schedulingDataByScope?.[scopeKey]))
      && String(stageGateMap[resolveBaseScheduleKey(scopeKey)]?.activeStage || '').toLowerCase() === 'completed';
    if (isClosedInitialStage2) return markWorkflowCompleted(workflowProgress);

    // Legacy workflow tasks only distinguish Initial Stage 1/2. For other
    // cycles, a completed Initial task can share the same service/standard
    // and be incorrectly matched to Surveillance or Recertification. A
    // recurring cycle therefore needs its own saved audit forms before it may
    // render as completed.
    const isRecurringCycle = /surveillance|recert/i.test(String(stage?.serviceItem || stage?.stageName || ''));
    const auditTab = DOCUMENT_TABS.find((tab) => tab.id === 'audit');
    const hasOwnAuditEvidence = Boolean(auditTab && isTabAllFormsSavedForScope(auditTab, scopeKey));
    if (isRecurringCycle && isWorkflowFullyCompleted(workflowProgress) && !hasOwnAuditEvidence) {
      return markWorkflowPendingAudit(workflowProgress);
    }
    return workflowProgress;
  };
  // Shared by the cycle cards and the sticky stepper so both describe a cycle the
  // same way. The stage-gate API is authoritative for the Initial Certification
  // Stage 1 -> Stage 2 handoff: task progress can stay stale after the TR decision
  // advances the gate, which otherwise leaves Stage 1 looking active.
  const resolveStageDisplayWorkflowProgress = (stage, workflowProgress) => {
    const stageKey = String(stage?.key || '');
    const stageGate = stageGateMap[resolveBaseScheduleKey(stageKey)];
    if (stageKey.endsWith(':s1') && (
      stageGate?.stage1Completed === true
      || ['stage2', 'completed'].includes(String(stageGate?.activeStage || '').toLowerCase())
    )) {
      return markWorkflowCompleted(workflowProgress);
    }
    return getDisplayedWorkflowProgress(stage, workflowProgress);
  };
  // The sticky stepper describes the cycle the user is actually working in -- the
  // one the audit-scope selector points at -- not the project-level workflow
  // pointer. `currentAuditCycle` is the FIRST stage flagged showWorkflowStepper,
  // but resolveActiveCycleKeys deliberately flags one active cycle PER TRACK, so
  // on a multi-standard Single Audit the pointer is routinely a different track
  // than the scope on screen and the stepper showed that other cycle's progress.
  const stickyStepperScopeKey = docTabScopeKey || selectedSchedulingScopeKey || currentAuditCycle?.key;
  const stickyStepperCycle = stages.find((stage) => stage?.key === stickyStepperScopeKey)
    || currentAuditCycle;
  const stickyStepperWorkflowProgress = resolveStageDisplayWorkflowProgress(
    stickyStepperCycle,
    stickyStepperCycle?.key === currentAuditCycle?.key
      ? currentCycleWorkflowProgress
      : buildLiveCycleWorkflowProgress(stickyStepperCycle).workflowProgress,
  );

  // Tab-level completion derived from saved form docs
  const auditDocTabDone = (() => {
    const tab = DOCUMENT_TABS.find((t) => t.id === 'audit');
    return tab ? isTabAllFormsSaved(tab) : false;
  })();
  const technicalReviewTabDone = (() => {
    const tab = DOCUMENT_TABS.find((t) => t.id === 'technical-review');
    return tab ? isTabAllFormsSaved(tab) : false;
  })();
  const isCertificateFinalizedForScope = (scopeKey) => {
    const baseScopeKey = String(scopeKey || '').replace(/:s[12]$/, '');
    const certificateStandardId = baseScopeKey.split(':')[1] || '';
    const lifecycle = certificateLifecycleByStandard[certificateStandardId]
      || certificateLifecycleByStandard.__default;
    const status = String(lifecycle?.status || '').trim().toUpperCase();
    return Boolean(
      lifecycle?.finalPdfLocked === true
      || IMMUTABLE_CERTIFICATE_STATUSES.has(status)
    );
  };

  // Green check reflects completion of the CURRENTLY SELECTED scope in the audit-scope
  // dropdown, not a project-wide flag. Example: Initial Stage 1 done + Stage 2 running →
  // the checks show only when the dropdown selects Stage 1; switching to Stage 2 hides them.
  const isTabCompletedForSelectedScope = (tab) => {
    const scopeKey = docTabScopeKey;
    if (tab.id === 'scheduling') {
      return scopeKey
        ? isSchedulingScopeConfirmed(schedulingDataByScope?.[scopeKey])
        : isSchedulingCompleted;
    }
    if (tab.id === 'checklist-verification') {
      // Checklist state is loaded for the selected cycle. Never reuse the
      // previous cycle's completion marker when no scope is selected.
      return Boolean(scopeKey && (checklistSaved || reviewCompleted));
    }
    if (tab.id === 'audit' || tab.id === 'technical-review' || tab.id === 'certificate') {
      const fullTab = DOCUMENT_TABS.find((t) => t.id === tab.id) || tab;
      if (tab.id === 'audit') {
        const selectedScope = (schedulingScopes || []).find((scope) => scope?.key === scopeKey);
        if (isSustainabilityAuditScope(selectedScope) && !isccFormCatalogLoading
          && isIsccAuditCatalogComplete({
            catalogItems: isccFormCatalogItems,
            uploadedDocuments: getAuditDocumentsForScope(auditDocuments.audit, scopeKey),
            scope: selectedScope,
          })) return true;
      }
      if (scopeKey) {
        if (tab.id === 'technical-review' && !isTechnicalReviewAvailableForScope(scopeKey)) {
          const ftrdSaved = Boolean(getSavedFormEntry('FTRD-01 Technical Review and Certification Decision', scopeKey));
          if (!ftrdSaved) return false;
        }
        if (tab.id === 'certificate') return isCertificateFinalizedForScope(scopeKey);
        return isTabAllFormsSavedForScope(fullTab, scopeKey);
      }
      if (tab.id === 'audit') return auditDocTabDone;
      if (tab.id === 'technical-review') return technicalReviewTabDone;
      return Object.keys(certificateLifecycleByStandard)
        .filter((key) => key !== '__default')
        .some((key) => isCertificateFinalizedForScope(`0:${key}`))
        || (certificateLifecycleByStandard.__default?.finalPdfLocked === true);
    }
    return approvedDocTabIds.includes(tab.id);
  };

  // A scope cannot enter Audit, Technical Review, or Certificate work until
  // that exact scope has a confirmed schedule.  This is deliberately scoped
  // (rather than project-wide) so Stage 1 confirmation cannot unlock Stage 2.
  const isTabSequentialLocked = (tabId) => {
    if (!['audit', 'technical-review', 'certificate'].includes(tabId)) return false;
    const scopeKey = docTabScopeKey || selectedSchedulingScopeKey || currentAuditCycle?.key;
    if (!scopeKey || scopeKey === '0:0') return true;
    return !isSchedulingScopeConfirmed(schedulingDataByScope?.[scopeKey]);
  };

  // 2B.5: true if ANY Initial Cert scope in stageGateMap has activeStage === 'completed'
  const isInitialCertCompleted = Object.values(stageGateMap).some(
    (gate) => gate && String(gate.activeStage).toLowerCase() === 'completed',
  );

  return (
    <div className="audit-process-page">
      {/* Dark header: breadcrumb only */}
      <header className="audit-process-header">
        <div className="audit-process-header-inner container-xl">
          <button
            type="button"
            className="btn btn-outline-white d-none d-sm-inline-block me-3"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="icon icon-tabler icon-tabler-arrow-left" />
            Back
          </button>
          <nav aria-label="breadcrumb" className="audit-process-breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/prospects">Client</Link>
              </li>
              <li className="breadcrumb-item">
                {projectMeta.clientId ? (
                  <Link to={`/audit/clients/${projectMeta.clientId}`}>Client Details</Link>
                ) : (
                  <span>Client Details</span>
                )}
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Audit
              </li>
            </ol>
          </nav>
        </div>
      </header>

      {/* Sticky mini-stepper: keeps Stage 1/2 + Review->Audit->TR->Certificate
          progress visible while the user scrolls past the main stepper card.
          Sits directly under the header (not above Documents/Forms) so it's
          sticky from the top of the page, not just partway down. */}
      {stickyStepperCycle && stages.length > 0 && !isLoadingPage && (() => {
        const miniSteps = stickyStepperWorkflowProgress?.majorSteps || AUDIT_WORKFLOW_MAJOR_STEPS;
        const currentMajorId = stickyStepperWorkflowProgress?.currentMajorId;
        const completedMajorIds = new Set(stickyStepperWorkflowProgress?.completedMajorIds || []);
        const isCertificateReady = isWorkflowFullyCompleted(stickyStepperWorkflowProgress)
          || Boolean(completedCertificateStage)
          || hasCompletedInitialCertificate;
        return (
          <div className="audit-process-sticky-stepper" role="navigation" aria-label="Audit progress (sticky)">
            <div className="audit-process-sticky-stepper-inner">
              <span className="audit-process-sticky-stepper-cycle" title={stickyStepperCycle.stageName}>
                {stickyStepperCycle.stageName}
              </span>
              {stickyStepperCycle.serviceItem && stickyStepperCycle.serviceItem !== '-' && (
                <span className="audit-process-sticky-stepper-cycle-badge">
                  {stickyStepperCycle.serviceItem}
                </span>
              )}
              <div className="audit-process-sticky-stepper-steps" role="list">
                {miniSteps.map((major, idx) => {
                  const isCurrent = currentMajorId === major.id;
                  const isCompleted = completedMajorIds.has(major.id);
                  return (
                    <React.Fragment key={major.id}>
                      <div
                        role="listitem"
                        className={`audit-process-sticky-step ${isCurrent ? 'is-current' : ''} ${isCompleted ? 'is-completed' : ''}`}
                        title={major.label}
                      >
                        <span className="audit-process-sticky-step-dot">
                          {isCompleted ? <Check size={10} /> : idx + 1}
                        </span>
                        <span className="audit-process-sticky-step-label">{major.label}</span>
                      </div>
                      {idx < miniSteps.length - 1 && (
                        <span className="audit-process-sticky-step-divider" aria-hidden="true" />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              {isCertificateReady ? (
                <Link to="/audit/certificate-monitoring" className="audit-process-sticky-stepper-cert-btn">
                  View Certificate →
                </Link>
              ) : (
                <button
                  type="button"
                  className="audit-process-sticky-stepper-cert-btn is-disabled"
                  disabled
                  title="Certificate is available once the audit is completed"
                >
                  View Certificate
                </button>
              )}
            </div>
          </div>
        );
      })()}

      <div className="page-body">
        <div className="container-xl">
          {isLoadingPage && (
            <div className="card audit-process-card mb-3">
              <div className="card-body text-center text-muted">Loading audit process...</div>
            </div>
          )}
          {pageError && (
            <div className="card audit-process-card mb-3 border-danger">
              <div className="card-body text-danger">{pageError}</div>
            </div>
          )}
          {/* Top card: Audit title, location, stages */}
          <div className="card audit-process-card audit-process-card-main mb-4">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
                <div>
                  <h1 className="audit-process-title mb-1">
                    {projectMeta.auditTitle}{' '}
                    {projectId ? (
                      <Link to={`/projects/detail/${projectId}`} className="audit-process-project-id">
                        #{projectMeta.projectCode}
                      </Link>
                    ) : (
                      <span className="audit-process-project-id">#{projectMeta.projectCode}</span>
                    )}
                  </h1>
                  <div className="d-flex align-items-center text-secondary mt-1">
                    <Building2 size={18} className="me-2" />
                    {projectMeta.clientId ? (
                      <Link to={`/audit/clients/${projectMeta.clientId}`}>{projectMeta.locationName}</Link>
                    ) : (
                      <span>{projectMeta.locationName}</span>
                    )}
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {projectMeta.clientId ? (
                    <Link
                      to={`/audit/clients/${projectMeta.clientId}`}
                      className="btn btn-teal"
                    >
                      View Client Details
                    </Link>
                  ) : (
                    <button type="button" className="btn btn-teal" disabled title="Client ID is unavailable">
                      View Client Details
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => setContactModalOpen(true)}
                  >
                    View Audit Contacts
                  </button>
                </div>
              </div>

              {/* Methodology group banner — Integrated / Combined audits span multiple standards (#17) */}
              {isGroupedMethodology(projectAuditInfo.auditMethodology) && (() => {
                const methodologyType = getMethodologyType(projectAuditInfo.auditMethodology);
                const memberStandards = resolveMemberStandardNames(projectAuditInfo.methodologyGroup, schedulingScopes);
                return (
                  <div className="audit-process-methodology-group mb-3">
                    <span className={`badge ${methodologyType === 'integrated' ? 'bg-purple' : 'bg-indigo'} text-white`}>
                      {methodologyType === 'integrated' ? 'Integrated Audit' : 'Combined Audit'}
                    </span>
                    <span className="text-secondary small">One audit process across:</span>
                    {memberStandards.length > 0 ? (
                      memberStandards.map((std) => (
                        <span key={std} className="badge bg-teal-lt text-teal border border-teal">{std}</span>
                      ))
                    ) : (
                      <span className="text-muted small fst-italic">standards load with scheduling scope</span>
                    )}
                  </div>
                );
              })()}

              {/* Once every audit cycle in this project has issued its Certificate, there is
                  no further cycle to activate -- let the user know explicitly instead of
                  silently leaving the last cycle expanded with no obvious next step. */}
              {stages.length > 0 && stages.every((stage) => stage.isFullyCompleted) && (
                <div className="alert alert-success d-flex align-items-center gap-2 mb-3" role="status">
                  <CircleCheckBig size={18} className="flex-shrink-0" />
                  <div>
                    <strong>Audit process complete.</strong> All audit cycles for this project
                    have been completed and certified — there is no further cycle to schedule.
                  </div>
                </div>
              )}

              {/* Collapsible audit stages */}
              <div className="audit-process-stages">
                {stages.length === 0 ? (
                  <div className="text-muted">No audit stages found for this project.</div>
                ) : (() => {
                  // Merge Initial Certification Stage 1 (':s1') & Stage 2 (':s2') into
                  // ONE section that shows both steppers running independently. Every
                  // other cycle keeps its own card. (UAT: unify Initial, two steppers.)
                  const stageGroups = [];
                  const initialByBase = new Map();
                  stages.forEach((stage) => {
                    if (/:s[12]$/.test(stage.key)) {
                      const base = resolveBaseScheduleKey(stage.key);
                      let group = initialByBase.get(base);
                      if (!group) {
                        group = { groupKey: `initial:${base}`, isInitial: true, members: [] };
                        initialByBase.set(base, group);
                        stageGroups.push(group);
                      }
                      group.members.push(stage);
                    } else {
                      stageGroups.push({ groupKey: stage.key, isInitial: false, members: [stage] });
                    }
                  });

                  const wpFor = (stage) => resolveStageDisplayWorkflowProgress(
                    stage,
                    stage.key === currentAuditCycle?.key
                      ? currentCycleWorkflowProgress
                      : stage.workflowProgress,
                  );
                  const statusClassFor = (wp) => (wp?.currentMajorId === 'audit-completed'
                    ? 'badge badge-audit-completed'
                    : 'badge badge-checklist');
                  const renderStageFields = (stage) => (
                    <div className={`audit-process-stage-fields${stage.isSustainability ? ' is-sustainability' : ''}`}>
                      <div className="audit-process-stage-field">
                        <label className="audit-process-stage-field-label">Mandays</label>
                        <button
                          type="button"
                          className="audit-process-stage-mandays-link"
                          onClick={() => openStageCid2SalesPack(stage)}
                          title="View CID 2 Sales Pack document"
                        >
                          <span className="audit-process-stage-field-value">{stage.mandays}</span>
                          <span className="audit-process-stage-mandays-link-hint">
                            <FileText size={14} aria-hidden="true" /> CID 2 Sales Pack
                          </span>
                        </button>
                      </div>
                      {stage.isSustainability ? (
                        <>
                          <div className="audit-process-stage-field">
                            <label className="audit-process-stage-field-label">Registration Number</label>
                            <div className="audit-process-stage-field-value">{stage.registrationNumber}</div>
                          </div>
                          <div className="audit-process-stage-field">
                            <label className="audit-process-stage-field-label">Input Material</label>
                            <div className="audit-process-stage-field-value">{stage.inputMaterial}</div>
                          </div>
                          <div className="audit-process-stage-field">
                            <label className="audit-process-stage-field-label">Output Material</label>
                            <div className="audit-process-stage-field-value">{stage.outputMaterial}</div>
                          </div>
                          <div className="audit-process-stage-field">
                            <label className="audit-process-stage-field-label">GHG Calculation</label>
                            <div className="audit-process-stage-field-value">{stage.ghgCalculation}</div>
                          </div>
                        </>
                      ) : (
                        <div className="audit-process-stage-field">
                          <label className="audit-process-stage-field-label">EA Code</label>
                          <div className="audit-process-stage-field-value">{stage.eaCode}</div>
                        </div>
                      )}
                      <div className="audit-process-stage-field audit-process-stage-field-scope">
                        <label className="audit-process-stage-field-label">Scope</label>
                        <div className="audit-process-stage-field-value">{resolveScopeDisplayText(stage.scope)}</div>
                      </div>
                    </div>
                  );

                  // Once a cycle is fully completed, its Mandays/EA Code/Scope grid
                  // collapses into one compact summary line by default — still
                  // informative (all three values visible), still adaptive
                  // (wraps on narrow screens), and interactive (click to expand
                  // the full grid). In-progress cycles are unaffected.
                  const renderStageFieldsToggle = (stage, workflowProgress) => {
                    if (!isWorkflowFullyCompleted(workflowProgress)) {
                      return renderStageFields(stage);
                    }
                    const isExpanded = Boolean(expandedStageFields[stage.key]);
                    if (isExpanded) {
                      return (
                        <div>
                          {renderStageFields(stage)}
                          <button
                            type="button"
                            className="audit-process-stage-fields-toggle"
                            onClick={() => setExpandedStageFields((prev) => ({ ...prev, [stage.key]: false }))}
                          >
                            Hide details
                          </button>
                        </div>
                      );
                    }
                    return (
                      <button
                        type="button"
                        className="audit-process-stage-fields-summary"
                        onClick={() => setExpandedStageFields((prev) => ({ ...prev, [stage.key]: true }))}
                        aria-expanded="false"
                      >
                        <span><strong>Mandays</strong> {stage.mandays ?? '-'}</span>
                        <span className="audit-process-stage-fields-summary-sep">·</span>
                        <span><strong>EA Code</strong> {stage.eaCode || '-'}</span>
                        <span className="audit-process-stage-fields-summary-sep">·</span>
                        <span className="audit-process-stage-fields-summary-scope">
                          <strong>Scope</strong> {resolveScopeDisplayText(stage.scope) || '-'}
                        </span>
                        <span className="audit-process-stage-fields-summary-cta">Show details</span>
                      </button>
                    );
                  };

                  return stageGroups.map((group) => {
                    // ── Merged Initial Certification card: two independent steppers ──
                    if (group.isInitial) {
                      const isExpanded = group.members.some((m) => expandedStages[m.key]);
                      // Always Stage 1 (group.members[0]) showed here regardless of which
                      // stage was actually active, so the card header kept reading "Stage 1"
                      // even deep into Stage 2. currentAuditCycle is now gate-aligned (see
                      // above), so prefer whichever member it points at; only a group the
                      // operator is not currently in (a different track, or a historical/
                      // upcoming card) falls back to Stage 1 as the default label.
                      const headerStage = group.members.find((m) => m.key === currentAuditCycle?.key)
                        || group.members[0];
                      return (
                        <div key={group.groupKey} className={`audit-process-stage-item ${isExpanded ? 'is-expanded' : ''}`}>
                          <div
                            className="audit-process-stage-item-header"
                            onClick={() => toggleStage(headerStage.key)}
                          >
                            <div className="audit-process-stage-item-header-left">
                              <span className="audit-process-stage-name">{headerStage.stageName}</span>
                              <span className="audit-process-stage-service-badge">Initial Certification</span>
                            </div>
                            <ChevronDown
                              size={20}
                              className={`audit-process-stage-chevron ${isExpanded ? 'open' : ''}`}
                            />
                          </div>
                          {isExpanded && (
                            <div className="audit-process-stage-item-body">
                              {renderInitialStageOverview(group, wpFor(group.members[group.members.length - 1]))}
                              {group.members.map((member, idx) => {
                                const wp = wpFor(member);
                                return (
                                  <div
                                    key={member.key}
                                    style={{
                                      paddingTop: idx > 0 ? 16 : 0,
                                      marginTop: idx > 0 ? 16 : 0,
                                      borderTop: idx > 0 ? '1px dashed #d7e3e5' : 'none',
                                    }}
                                  >
                                    <div className="audit-process-stage-item-header-left" style={{ marginBottom: 10 }}>
                                      {member.serviceItem && member.serviceItem !== '-' && (
                                        <span className="audit-process-stage-service-badge">{member.serviceItem}</span>
                                      )}
                                      <span className={`audit-process-stage-status ${statusClassFor(wp)}`}>
                                        {wp?.currentLabel || member.statusLabel}
                                      </span>
                                    </div>
                                    {renderAuditWorkflowStepper(wp)}
                                    {renderStageFieldsToggle(member, wp)}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    }

                    // ── Standard single-cycle card (view-only; lock lives on the audit-scope
                    // dropdown, not the card — upcoming cycles can still be expanded to view). ──
                    const stage = group.members[0];
                    const displayedWorkflowProgress = wpFor(stage);
                    const displayedStatusLabel = stage.key === currentAuditCycle?.key
                      ? displayedWorkflowProgress?.currentLabel
                      : stage.statusLabel;
                    return (
                      <div key={stage.key} className={`audit-process-stage-item ${expandedStages[stage.key] ? 'is-expanded' : ''}`}>
                        <div
                          className="audit-process-stage-item-header"
                          onClick={() => toggleStage(stage.key)}
                        >
                          <div className="audit-process-stage-item-header-left">
                            <span className="audit-process-stage-name">{stage.stageName}</span>
                            {stage.serviceItem && stage.serviceItem !== '-' && (
                              <span className="audit-process-stage-service-badge">{stage.serviceItem}</span>
                            )}
                            <span className={`audit-process-stage-status ${statusClassFor(displayedWorkflowProgress)}`}>{displayedStatusLabel}</span>
                          </div>
                          <ChevronDown
                            size={20}
                            className={`audit-process-stage-chevron ${expandedStages[stage.key] ? 'open' : ''}`}
                          />
                        </div>
                        {expandedStages[stage.key] && (
                          <div className="audit-process-stage-item-body">
                            {stage.showWorkflowStepper && renderAuditWorkflowStepper(displayedWorkflowProgress)}
                            {renderStageFieldsToggle(stage, displayedWorkflowProgress)}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>

          {/* Two columns or Inline Form View */}
          {activeInlineForm ? (
            <div className="card audit-process-card">
              <div className="card-body">
                {renderInlineFormView()}
              </div>
            </div>
          ) : (
          <div className="row g-3 audit-process-primary-doc-section" ref={primaryDocSectionRef}>
            <div className="col-lg-4">
              <div className="card audit-process-card h-100">
                <div className="card-header audit-process-card-header">
                  <h3 className="card-title mb-0">Documents / Forms</h3>
                </div>
                <div className="card-body p-0">
                  <nav className="audit-process-doc-nav" role="tablist" aria-label="Document tabs">
                    {visibleDocTabs.map((tab) => {
                      const isTabCompleted = isTabCompletedForSelectedScope(tab);
                      const locked = isTabSequentialLocked(tab.id);
                      return (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={activeDocTab === tab.id}
                        disabled={locked}
                        className={`audit-process-doc-nav-item ${activeDocTab === tab.id ? 'active' : ''} ${locked ? 'is-locked' : ''}`}
                        onClick={() => { if (!locked) setActiveDocTab(tab.id); }}
                        style={locked ? { cursor: 'not-allowed', opacity: 0.55 } : undefined}
                        title={locked ? 'Confirm scheduling for this audit scope before starting Audit, Technical Review, or Certificate work.' : undefined}
                      >
                        <span>{tab.label}</span>
                        {locked ? (
                          <span className="ms-auto" style={{ fontSize: '0.8rem' }} aria-label="Locked until Stage 1 completed">🔒</span>
                        ) : (isTabCompleted && (
                          <CircleCheckBig size={20} className="audit-process-doc-nav-check ms-auto" />
                        ))}
                      </button>
                      );
                    })}
                  </nav>
                </div>
              </div>
            </div>
            <div className="col-lg-8">
              <div className="card audit-process-card h-100">
                <div className="card-header audit-process-card-header">
                  <h3 className="card-title mb-0">
                    {visibleDocTabs.find((tab) => tab.id === activeDocTab)?.label || 'Document Detail'}
                  </h3>
                </div>
                <div className="card-body">
                  {renderDocumentTabContent()}
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Project History */}
          <div className="card audit-process-card mt-3">
            <div className="card-header audit-process-card-header d-flex justify-content-between align-items-center">
              <h3 className="card-title mb-0">Project History</h3>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={loadProjectHistory}
                disabled={isRefreshingHistory}
                title="Refresh history"
              >
                {isRefreshingHistory ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                )}
              </button>
            </div>
            <div className="card-body">
              {(() => {
                const historyTotalPages = Math.max(1, Math.ceil(projectHistory.length / HISTORY_PAGE_SIZE));
                const safeHistoryPage = Math.min(historyPage, historyTotalPages);
                const historyPageStart = (safeHistoryPage - 1) * HISTORY_PAGE_SIZE;
                const pagedHistory = projectHistory.slice(historyPageStart, historyPageStart + HISTORY_PAGE_SIZE);
                return (
                  <>
                    <div className="audit-process-timeline">
                      {pagedHistory.length === 0 ? (
                        <div className="text-center text-muted py-2">No project history</div>
                      ) : pagedHistory.map((entry) => {
                        const timestamp = entry.actionTimestamp ? new Date(entry.actionTimestamp).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  }) : 'N/A';
                  
                  const actionTypeRaw = String(entry.actionType || '');
                  const actionBadgeColor = resolveHistoryBadgeColor(actionTypeRaw);
                  const actionBadgeClass = `badge badge-outline text-${actionBadgeColor}`;
                  const actionTypeLabel = formatHistoryActionLabel(actionTypeRaw);
                  const historyScope = getHistoryScopeMetadata(entry);

                  return (
                    <div key={entry.id} className="audit-process-timeline-item">
                      <div className="audit-process-timeline-marker" />
                      <div className="audit-process-timeline-content">
                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                          <div style={{ flex: 1 }}>
                            <div className="d-flex gap-2 align-items-center mb-2">
                              <span className={actionBadgeClass}>{actionTypeLabel}</span>
                              <strong style={{ fontSize: '0.95rem' }}>
                                {entry.entityType === 'AUDIT_TASK' ? `Task ID: ${entry.entityId}` : formatHistoryEntityLabel(entry.entityType)}
                              </strong>
                              {historyScope?.standard && (
                                <span className="badge text-bg-light border">{historyScope.standard}</span>
                              )}
                              {historyScope?.stage && (
                                <span className="badge text-bg-light border">{toSentenceCase(historyScope.stage)}</span>
                              )}
                            </div>
                            <p className="text-secondary mb-2 small" style={{ lineHeight: 1.4 }}>
                              {formatHistoryDescription(entry)}
                            </p>
                            {entry.userName && (
                              <p className="text-muted mb-1 small">{`By: ${entry.userName}${entry.userEmail ? ` (${entry.userEmail})` : ''}`}</p>
                            )}
                            {entry.changedFields && (
                              <p className="text-muted mb-0 small">
                                <strong>Changed:</strong> {entry.changedFields}
                              </p>
                            )}
                          </div>
                          <span className="text-muted small" style={{ whiteSpace: 'nowrap' }}>{timestamp}</span>
                        </div>
                      </div>
                    </div>
                  );
                      })}
                    </div>
                    {historyTotalPages > 1 && (
                      <div className="audit-process-pagination-pill-bar mt-3">
                        <span>
                          Showing <strong className="text-body">{historyPageStart + 1}-{Math.min(historyPageStart + HISTORY_PAGE_SIZE, projectHistory.length)}</strong> of{' '}
                          <strong className="text-body">{projectHistory.length}</strong> history entries
                        </span>
                        <div className="audit-process-pagination-pill">
                          <button
                            type="button"
                            className="audit-process-pagination-pill__btn"
                            disabled={safeHistoryPage <= 1}
                            onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                            aria-label="Previous page"
                          >
                            <ChevronLeft size={16} />
                          </button>
                          <span className="audit-process-pagination-pill__label">
                            {safeHistoryPage} <span className="text-muted fw-normal">/ {historyTotalPages}</span>
                          </span>
                          <button
                            type="button"
                            className="audit-process-pagination-pill__btn"
                            disabled={safeHistoryPage >= historyTotalPages}
                            onClick={() => setHistoryPage((p) => Math.min(historyTotalPages, p + 1))}
                            aria-label="Next page"
                          >
                            <ChevronRight size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Edit modal */}
      {editModalOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${editModalOpen ? ' show' : ''}`}
        style={{ display: editModalOpen ? 'block' : 'none' }}
        id={MODAL_EDIT_ID}
        tabIndex={-1}
        role="dialog"
        data-bs-backdrop="static"
        data-bs-keyboard="true"
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="auditEditModalLabel">
                Edit
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => { setEditModalOpen(false); setEditingStageKey(null); }}
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label required">Project</label>
                  <select
                    className="form-control bg-light"
                    value={editForm.project}
                    onChange={(e) => handleEditFormChange('project', e.target.value)}
                    disabled
                  >
                    {projectOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Service Item</label>
                  <select
                    className="form-control bg-light"
                    value={editForm.serviceItem}
                    onChange={(e) => handleEditFormChange('serviceItem', e.target.value)}
                    disabled
                  >
                    {serviceItemOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <AppSelect
                    name="editFormEaCode"
                    label="EA Code"
                    value={editForm.eaCode}
                    onChange={(_name, val) => handleEditFormChange('eaCode', val)}
                    options={eaCodeSelectOptions}
                    placeholder="Select EA Code..."
                    valueField="value"
                    labelField="label"
                    searchField="label"
                    multiple
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Mandays</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editForm.mandays}
                    onChange={(e) => handleEditFormChange('mandays', e.target.value)}
                  />
                </div>
                <div className="col-md-12 mb-3">
                  <label className="form-label">Scope</label>
                  <textarea
                    className="form-control"
                    value={editForm.scope}
                    onChange={(e) => handleEditFormChange('scope', e.target.value)}
                    rows={4}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-link link-secondary"
                onClick={() => { setEditModalOpen(false); setEditingStageKey(null); }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary ms-auto"
                onClick={handleEditSubmit}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation modal – Confirm to Reject / Confirm to Approve */}
      {!!confirmModalType && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${confirmModalType ? ' show' : ''}`}
        style={{ display: confirmModalType ? 'block' : 'none' }}
        id={MODAL_CONFIRM_ID}
        tabIndex={-1}
        role="dialog"
        data-bs-backdrop="static"
        data-bs-keyboard="true"
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content audit-process-confirm-modal-content">
            <div className="modal-header border-0 pb-0">
              <button
                type="button"
                className="btn-close"
                onClick={() => setConfirmModalType(null)}
                aria-label="Close"
              />
            </div>
            <div className="modal-body pt-0 text-center">
              {confirmModalType && (
                <>
                  <div className={`audit-process-confirm-icon audit-process-confirm-icon-${CONFIRM_MESSAGES[confirmModalType].iconType}`}>
                    {CONFIRM_MESSAGES[confirmModalType].iconType === 'danger' ? (
                      <AlertCircle size={26} strokeWidth={2} />
                    ) : (
                      <AlertTriangle size={26} strokeWidth={2} />
                    )}
                  </div>
                  <h5 className="audit-process-confirm-title mb-2">
                    {CONFIRM_MESSAGES[confirmModalType].title}
                  </h5>
                  <p className="audit-process-confirm-body text-secondary mb-0">
                    {CONFIRM_MESSAGES[confirmModalType].body}
                  </p>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setConfirmModalType(null)}
              >
                Back
              </button>
              <button
                type="button"
                className={`btn ${confirmModalType ? CONFIRM_MESSAGES[confirmModalType].confirmClass : 'btn-primary'} ms-auto`}
                onClick={handleConfirmAction}
                disabled={
                  (confirmModalType === 'submitReReview' && submittingFollowup)
                  || (confirmModalType === 'sendToAr' && sendingCidToAr)
                }
              >
                {confirmModalType === 'agree' && <Send size={18} className="me-1" />}
                {confirmModalType && CONFIRM_MESSAGES[confirmModalType].confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Audit Contacts modal */}
      {contactModalOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${contactModalOpen ? ' show' : ''}`}
        style={{ display: contactModalOpen ? 'block' : 'none' }}
        id={MODAL_CONTACT_ID}
        tabIndex={-1}
        role="dialog"
        data-bs-backdrop="static"
        data-bs-keyboard="true"
      >
        <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Audit Contacts</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setContactModalOpen(false)}
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              {renderContactBlock('Representative', auditContacts.representative, 'representative')}
              {renderContactBlock('Correspondence Contact', auditContacts.correspondence, 'correspondence', true)}
              {renderContactBlock('Finance Contact', auditContacts.finance, 'finance', true)}
              {renderContactBlock('Certificate Contact', auditContacts.certificate, 'certificate', true)}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setContactModalOpen(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary ms-auto"
                onClick={handleSaveAuditContacts}
                disabled={isSavingContacts || !projectId}
              >
                {isSavingContacts ? 'Saving...' : 'Save Contacts'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Checklist "View" modal for Scope / EA Code / Mandays */}
      {checklistFieldModalOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${checklistFieldModalOpen ? ' show' : ''}`}
        style={{ display: checklistFieldModalOpen ? 'block' : 'none' }}
        tabIndex={-1}
        role="dialog"
        data-bs-backdrop="static"
        data-bs-keyboard="true"
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">View Detail</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setChecklistFieldModalOpen(false)}
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              {!checklistFieldValues.isSustainability && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <label className="form-label mb-0">EA Code</label>
                    {!checklistFieldEditableRows.eaCode && canEditChecklistField('eaCode') && (
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-0"
                        onClick={() => setChecklistFieldEditableRows((prev) => ({ ...prev, eaCode: true }))}
                      >
                        <Pencil size={14} className="me-1" />Edit
                      </button>
                    )}
                  </div>
                  <AppSelect
                    name="checklistFieldEaCode"
                    value={checklistFieldValues.eaCode}
                    onChange={(_name, val) => setChecklistFieldValues((prev) => ({ ...prev, eaCode: val }))}
                    options={eaCodeSelectOptions}
                    placeholder="Select EA Code..."
                    valueField="value"
                    labelField="label"
                    searchField="label"
                    disabled={!checklistFieldEditableRows.eaCode}
                    multiple
                  />
                </div>
              )}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label className="form-label mb-0">Mandays</label>
                  {!checklistFieldEditableRows.mandays && canEditChecklistField('mandays') && (
                    <button
                      type="button"
                      className="btn btn-sm btn-link p-0"
                      onClick={() => setChecklistFieldEditableRows((prev) => ({ ...prev, mandays: true }))}
                    >
                      <Pencil size={14} className="me-1" />Edit
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  className="form-control"
                  value={checklistFieldValues.mandays}
                  onChange={(e) => setChecklistFieldValues((prev) => ({ ...prev, mandays: e.target.value }))}
                  disabled={!checklistFieldEditableRows.mandays}
                />
                {checklistFieldEditableRows.mandays && checklistFieldValues.mandaysLockedByScheduling && (
                  <small className="text-muted d-block mt-1">
                    This stage already has auditor(s) scheduled — saving will proportionally reallocate
                    their scheduled mandays to this new total instead of changing the proposal total.
                  </small>
                )}
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label className="form-label mb-0">Scope</label>
                  {!checklistFieldEditableRows.scope && canEditChecklistField('scope') && (
                    <button
                      type="button"
                      className="btn btn-sm btn-link p-0"
                      onClick={() => setChecklistFieldEditableRows((prev) => ({ ...prev, scope: true }))}
                    >
                      <Pencil size={14} className="me-1" />Edit
                    </button>
                  )}
                </div>
                <textarea
                  className="form-control"
                  rows={4}
                  value={checklistFieldValues.scope}
                  onChange={(e) => setChecklistFieldValues((prev) => ({ ...prev, scope: e.target.value }))}
                  disabled={!checklistFieldEditableRows.scope}
                />
              </div>
              {(checklistFieldEditableRows.scope || checklistFieldEditableRows.eaCode) && (
                <div className="form-check mb-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="applyScopeEaCodeToNextCycle"
                    checked={applyScopeEaCodeToNextCycle}
                    onChange={(e) => setApplyScopeEaCodeToNextCycle(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="applyScopeEaCodeToNextCycle">
                    Also apply Scope / EA Code to the next audit cycle
                  </label>
                </div>
              )}
              {!canEditChecklistField('scope') && !canEditChecklistField('eaCode') && !canEditChecklistField('mandays') && (
                <div className="text-muted small">
                  None of these fields are currently flagged "Need Revision" — showing read-only values.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  setChecklistFieldModalOpen(false);
                  setApplyScopeEaCodeToNextCycle(false);
                  setChecklistFieldEditableRows({ scope: false, eaCode: false, mandays: false });
                }}
              >
                Close
              </button>
              {(checklistFieldEditableRows.scope || checklistFieldEditableRows.eaCode || checklistFieldEditableRows.mandays) && (
                <button
                  type="button"
                  className="btn btn-primary ms-auto"
                  onClick={handleChecklistFieldSave}
                >
                  Save
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation modal – Save Scope / EA Code / Mandays: needs an addendum, or Cancel */}
      {checklistSaveConfirmOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${checklistSaveConfirmOpen ? ' show' : ''}`}
        style={{ display: checklistSaveConfirmOpen ? 'block' : 'none' }}
        tabIndex={-1}
        role="dialog"
        data-bs-backdrop="static"
        data-bs-keyboard="true"
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content audit-process-confirm-modal-content">
            <div className="modal-header border-0 pb-0">
              <button
                type="button"
                className="btn-close"
                onClick={handleChecklistFieldSaveCancel}
                aria-label="Close"
              />
            </div>
            <div className="modal-body pt-0 text-center">
              <div className="audit-process-confirm-icon audit-process-confirm-icon-warning">
                <AlertTriangle size={26} strokeWidth={2} />
              </div>
              <h5 className="audit-process-confirm-title mb-2">Save Changes</h5>
              <p className="audit-process-confirm-body text-secondary mb-0">
                Does this change to Scope / EA Code / Mandays need an addendum?
              </p>
            </div>
            <div className="modal-footer justify-content-center border-0 pt-0">
              <button
                type="button"
                className="btn btn-link link-secondary"
                onClick={handleChecklistFieldSaveCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => handleChecklistFieldSaveConfirm(false)}
              >
                No, Doesn't Need Addendum
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => handleChecklistFieldSaveConfirm(true)}
              >
                Yes, Needs Addendum
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Checklist "View Document" modal (Sales Pack Documents) */}
      {checklistDocsModalOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${checklistDocsModalOpen ? ' show' : ''}`}
        style={{ display: checklistDocsModalOpen ? 'block' : 'none' }}
        tabIndex={-1}
        role="dialog"
        data-bs-backdrop="static"
        data-bs-keyboard="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">View Document</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setChecklistDocsModalOpen(false)}
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              {renderSalesPackContent()}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setChecklistDocsModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* CID 2 Sales Pack panel, opened from any Mandays card in the audit stages. */}
      {cid2SalesPackModal.open && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${cid2SalesPackModal.open ? ' show' : ''}`}
        style={{ display: cid2SalesPackModal.open ? 'block' : 'none' }}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cid2-sales-pack-modal-title"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div className="modal-content audit-process-cid2-modal-content">
            <div className="modal-header">
              <div>
                <div className="audit-process-cid2-modal-kicker"><FileText size={15} /> Sales Pack</div>
                <h5 className="modal-title" id="cid2-sales-pack-modal-title">CID 2 document</h5>
                <div className="text-secondary small mt-1">{cid2SalesPackModal.stageName}</div>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={() => setCid2SalesPackModal({ open: false, stageName: '', documents: [] })}
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              {cid2SalesPackModal.documents.length > 0 ? (
                <>
                  <p className="text-secondary small mb-3">Select a document to preview it securely, or download the original file.</p>
                  {renderDocumentRows(cid2SalesPackModal.documents, 'stage-mandays-cid2')}
                </>
              ) : (
                <div className="audit-process-cid2-empty-state" role="status">
                  <div className="audit-process-cid2-empty-icon"><FileText size={25} /></div>
                  <div>
                    <strong>CID 2 is not available</strong>
                    <p className="mb-0 text-secondary">No CID 2 Sales Pack document has been uploaded for this audit scope yet.</p>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary ms-auto"
                onClick={() => setCid2SalesPackModal({ open: false, stageName: '', documents: [] })}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Document preview modal */}
      {previewModalOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${previewModalOpen ? ' show' : ''}`}
        style={{ display: previewModalOpen ? 'block' : 'none' }}
        id={MODAL_PREVIEW_ID}
        tabIndex={-1}
        role="dialog"
        data-bs-backdrop="static"
        data-bs-keyboard="true"
      >
        <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title text-truncate" title={previewDocument.name || 'Document Preview'}>
                {previewDocument.name || 'Document Preview'}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={closeDocumentPreview}
                aria-label="Close"
              />
            </div>
            <div className="modal-body p-0">
              {previewLoading ? (
                <div className="audit-process-preview-state" role="status">
                  <span className="spinner-border text-primary" aria-hidden="true" />
                  <strong>Loading secure document preview…</strong>
                </div>
              ) : previewError ? (
                <div className="audit-process-preview-state text-center" role="alert">
                  <AlertTriangle size={36} className="text-warning" />
                  <strong>Preview unavailable</strong>
                  <span className="text-muted">{previewError}</span>
                </div>
              ) : previewDocument.url ? (
                <iframe
                  src={previewDocument.url}
                  title={previewDocument.name || 'Document Preview'}
                  className="audit-process-doc-preview-frame"
                />
              ) : (
                <div className="p-3 text-muted">No document selected.</div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={closeDocumentPreview}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary ms-auto"
                onClick={() => handleDocumentDownload(previewDocument)}
                disabled={!previewDocument.sourceUrl && !previewDocument.url}
              >
                <Download size={16} className="me-1" />
                Download
              </button>
            </div>
          </div>
        </div>
      </div>

      {schedulingAlertModalOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${schedulingAlertModalOpen ? ' show' : ''}`}
        style={{ display: schedulingAlertModalOpen ? 'block' : 'none' }}
        id={MODAL_SCHEDULING_ALERT_ID}
        tabIndex={-1}
        role="dialog"
        data-bs-backdrop="static"
        data-bs-keyboard="true"
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content audit-process-confirm-modal-content">
            <div className="modal-header border-0 pb-0">
              <button
                type="button"
                className="btn-close"
                onClick={() => setSchedulingAlertModalOpen(false)}
                aria-label="Close"
              />
            </div>
            <div className="modal-body pt-0 text-center">
              <div className={`audit-process-confirm-icon ${
                schedulingAlertType === 'danger'
                  ? 'audit-process-confirm-icon-danger'
                  : schedulingAlertType === 'success'
                    ? 'audit-process-confirm-icon-success'
                    : 'audit-process-confirm-icon-warning'
              }`}>
                {schedulingAlertType === 'danger' ? (
                  <AlertCircle size={26} strokeWidth={2} />
                ) : schedulingAlertType === 'success' ? (
                  <CircleCheckBig size={26} strokeWidth={2} />
                ) : (
                  <AlertTriangle size={26} strokeWidth={2} />
                )}
              </div>
              <h5 className="audit-process-confirm-title mb-2">{schedulingAlertTitle}</h5>
              <p className="audit-process-confirm-body text-secondary mb-0">
                {schedulingAlertMessage}
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary ms-auto"
                onClick={() => setSchedulingAlertModalOpen(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Re-assign Task Modal */}
      {showReassignModal && reassignTargetTask && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }} onClick={() => setShowReassignModal(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Re-assign Task</h5>
                <button type="button" className="btn-close" onClick={() => setShowReassignModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <div className="fw-bold">{reassignTargetTask?.taskType || reassignTargetTask?.taskTypeCode || '-'}</div>
                  <div className="text-muted small">{projectMeta.auditTitle} · {projectMeta.projectCode}</div>
                </div>
                {isLoadingReassignees ? (
                  <div className="text-muted small">Loading eligible users...</div>
                ) : reassignAssignees.length === 0 ? (
                  <div className="text-muted small">No eligible users found for this task type.</div>
                ) : (
                  <>
                    <input
                      className="form-control form-control-sm mb-2"
                      placeholder="Search user..."
                      value={reassignSearch}
                      onChange={(e) => setReassignSearch(e.target.value)}
                    />
                    {!reassignSelectedUser && (
                      <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 6 }}>
                        {reassignAssignees
                          .filter((u) => !reassignSearch || (u.fullName || u.username || '').toLowerCase().includes(reassignSearch.toLowerCase()))
                          .map((u) => (
                            <div
                              key={u.id}
                              className="p-2 border-bottom"
                              style={{ cursor: 'pointer' }}
                              onClick={() => setReassignSelectedUser(u)}
                            >
                              <div className="fw-semibold">{u.fullName || u.username}</div>
                              {u.email && <div className="text-muted small">{u.email}</div>}
                            </div>
                          ))}
                      </div>
                    )}
                    {reassignSelectedUser && (
                      <div className="alert alert-info py-2 mb-2 d-flex justify-content-between align-items-center">
                        <span>Selected: <strong>{reassignSelectedUser.fullName || reassignSelectedUser.username}</strong></span>
                        <button type="button" className="btn btn-sm btn-link p-0" onClick={() => setReassignSelectedUser(null)}>Change</button>
                      </div>
                    )}
                  </>
                )}
                <div className="mt-3">
                  <label className="form-label fw-semibold">Notes <span className="text-danger">*</span></label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Reason for re-assignment..."
                    value={reassignNotes}
                    onChange={(e) => setReassignNotes(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowReassignModal(false)}>Cancel</button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!reassignSelectedUser || !reassignNotes.trim()}
                  onClick={handleAuditReassign}
                >
                  Confirm Re-assign
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <DocumentFormModal
        show={docFormModal.show}
        onHide={() => setDocFormModal({ show: false, docCode: null, category: null, scopeKey: '' })}
        docCode={docFormModal.docCode}
        category={docFormModal.category}
        projectId={Number(projectId)}
        projectMeta={projectMeta}
        stages={stages}
        auditorOptions={getEnrichedAuditorOptionsForScope(docFormModal.scopeKey)}
        auditSchedule={{
          startDate: (docFormModal.scopeKey ? (schedulingDataByScope?.[docFormModal.scopeKey]?.auditSchedules?.[0]?.startDate || '') : '') || auditSchedules[0]?.startDate || '',
          endDate: (docFormModal.scopeKey ? (schedulingDataByScope?.[docFormModal.scopeKey]?.auditSchedules?.[0]?.endDate || '') : '') || auditSchedules[0]?.endDate || '',
          auditSchedules: docFormModal.scopeKey ? (schedulingDataByScope?.[docFormModal.scopeKey]?.auditSchedules || []) : auditSchedules,
          siteLocations: docFormModal.scopeKey ? (schedulingDataByScope?.[docFormModal.scopeKey]?.siteLocations || siteLocations) : siteLocations,
        }}
        existingData={docFormModal.docCode ? getSavedFormEntry(docFormModal.docCode, docFormModal.scopeKey)?.formDataJson : null}
        scopeKey={docFormModal.scopeKey || ''}
        scopeLabel={docFormModal.scopeKey ? (schedulingScopes.find((s) => s.key === docFormModal.scopeKey)?.label || '') : ''}
        scopeContext={docFormModal.scopeKey ? (schedulingScopes.find((s) => s.key === docFormModal.scopeKey) || null) : null}
        auditMethodology={projectAuditInfo.auditMethodology || ''}
        methodologyGroup={projectAuditInfo.methodologyGroup}
        onSaved={(docCode, result) => {
          const saveKey = result?.formDataKey || buildDocumentFormStorageKey(docCode, docFormModal.scopeKey);
          setSavedFormDataMap((prev) => ({
            ...prev,
            [saveKey]: { ...(prev[saveKey] || {}), ...result, formDataJson: result.formDataJson },
          }));
          refreshAfterFtrd01Save(docCode);
        }}
      />

      {/* FindingRequirementForm replaced by inline DocumentFormModal (FAPP-07 Finding Form) */}
    </div>
  );
};

export default AuditProcess;
